/**
 * The quote's line items, edited in place — Xero's own quote grid, but built
 * from OUR contract rather than from Xero's screen.
 *
 * ## ✅ THIS GRID IS THE LIVE EDITING SURFACE, AND THE DEAD MODAL IS NOW DELETED
 *
 * ✅ **`QuoteLineFormModal.tsx` was DELETED on 2026-09-19, on the owner's
 * decision**, together with its invoice twin `InvoiceLineFormModal.tsx`. It was
 * re-verified unimported repo-wide immediately before the deletion: the only
 * references anywhere were comments like this one.
 *
 * ⚠️ *SUPERSEDED, and kept because a grep for the name now finds this note
 * rather than the file: "`QuoteLineFormModal.tsx` still exists and still
 * compiles, and **nothing imports it** — measured repo-wide, its only reference
 * outside itself is a comment inside `InvoiceLineFormModal.tsx`, which is dead
 * for the same reason. `QuoteDetailPage` mounts THIS component; the modal is
 * mounted nowhere."* **Do not read the name below as live code.**
 *
 * ⚠️ **The LESSON outlives the file, which is why the paragraph stays: a line
 * field added to an unmounted modal ships INERT**, and that is not
 * hypothetical. The per-line REGION multiselect was built there first and was
 * unreachable on quotes and invoices from the day it landed — `tsc` clean,
 * every test green, a backend accepting `regionIds` that no screen could ever
 * send. It is the shape this codebase catalogues as **a value nothing
 * renders**, and the modal being a plausible-looking home for the field is
 * exactly what hid it: the diff looked complete.
 *
 * **Add a line field HERE.** Deals are still the one document whose line modal
 * IS mounted (`components/deals/LineItemFormModal.tsx`), so a field belongs in
 * this grid, in the invoice grid, and in that deal modal.
 *
 * ⚠️ *SUPERSEDED 2026-09-19 by the deletion of the two dead modals: "so the
 * same field added in all three places is reachable on one of them — which is
 * how three near-identical modules come to behave differently while every one
 * of them reads as finished."* **There are no longer three near-identical line
 * MODALS** — the quote and invoice ones are gone and only the deal one remains.
 * ⛔ **The hazard is NOT retired, it has MOVED**: the three near-identical
 * surfaces are now this grid, the invoice grid and the deal modal, and a field
 * added to two of the three still behaves differently on the third while every
 * one of them reads as finished.
 *
 * ## Which of Xero's ten columns actually exist here
 *
 * | Xero column | Here |
 * | --- | --- |
 * | Item | `product`, written as `productId`. A Combobox over `/products` |
 * | Description | `description` — a real column, and `null` means DERIVE. While it is null the backend composes the string at push time and `composeLineDescription` is our pinned copy of the same rule — ⚠️ **which head it leads with is NOT fixed**: since `LINE_DESCRIPTION_PREFERS_PROJECT_NAME` is `true` a line naming a project AND carrying an item code leads with the PROJECT name, so do not restate the template here; ask the composer. A stored string is sent to Xero VERBATIM and nothing is appended. It is a real **textarea**, pre-filled from the derivation and re-derived live until somebody types — see "The Description cell" below |
 * | Qty | `quantity` — **integer only.** `z.number().int().positive()`, not coerced: the string `"2"` is a 422. Xero permits fractional quantities and we do not |
 * | Price | `unitPrice` — TRI-STATE, see `PriceMode` |
 * | Disc | `discount`, a **percentage**, never a cash amount |
 * | Account | **omitted, deliberately.** We send `ItemCode` and no `AccountCode`; Xero resolves the revenue account from the Item. Do not invent a default or derive one from the product code, which is identical on 95% of live lines — the worst possible hit rate for that rule |
 * | Tax rate | `taxRate`, a **percentage**, and this is only half of Xero's column — see below |
 * | Location | **Derived from this line's own project.** `quote.xeroTrackingLocation` is a document-level OVERRIDE and is absent from the line schema, so sending it on a line is a 422 — but it is not where a line's Location comes from |
 * | Tour Type | Identically so — derived per line, `quote.xeroTrackingTourType` overriding |
 * | Amount | `lineTotal` — **GENERATED, read-only.** Never sent, never computed here |
 *
 * ⚠️ **Xero tracking is resolved PER LINE, not once for the document.**
 * `resolveLineTracking` derives Location and Tour Type from the LINE's own
 * project, falling back to the document's only when the line has none.
 * Document-level overrides still win over both, and their `null` still means
 * DERIVE rather than "no tracking". A quote spanning two regions reports into
 * two buckets — never compute tracking once outside the `lines.map()` and
 * spread it onto every line; N per-line cells writing one shared field means
 * the last write silently wins.
 *
 * ⚠️ **And tracking is OPTIONAL. A document that can derive none pushes
 * UNTRACKED.** `XERO_QUOTE_NO_PROJECT` / `XERO_INVOICE_NO_PROJECT` were removed
 * server-side and will never be returned again. Most products have no project
 * at all, so most lines cannot name one, and a real share of live Xero lines
 * already carry no tracking. Do not write copy claiming a project is needed to
 * reach Xero, and do not add a client-side guard for a refusal that cannot fire
 * — **untracked is visibly absent, and the unrelated project somebody attaches
 * to satisfy an imagined guard is confidently wrong for ever.**
 *
 * ⚠️ **What did NOT loosen: the PER-LINE guard.** A line whose product IS sold
 * through projects and names none is still refused at write time — refusal
 * `project-required` in `@/lib/line-project`, and every project/city refusal
 * this file renders is untouched. "This line's product is sold through
 * projects and you have not chosen one" is a real refusal; "this quote needs a
 * project or it cannot go to Xero" is not, and never was a line-level rule.
 *
 * And where Xero's grid has **Location** and **Tour Type**, ours has
 * **Project** and **Cities** — because those two Xero cells are DERIVED from
 * the line's project rather than typed. A line names its OWN project, and a
 * SET of that project's cities.
 *
 * ## Project and Cities live UNDER the item, not as columns
 *
 * Stacked inside the Item cell: item picker, then the project picker directly
 * beneath it, then the all-cities toggle and city picker beneath that — one
 * cell, because the three are one decision about what this line sells and
 * where.
 *
 * **They are ONE patch however they are rendered.** Choosing a project clears
 * the city selection in the same `onChange`, because the guard runs on the
 * post-merge state and a project change otherwise re-validates the STORED
 * cities and is refused naming cities nobody touched. See
 * {@link ProjectControl}.
 *
 * ### The project control is SHOWN ONLY WHEN THE PRODUCT HAS PROJECTS
 *
 * ⚠️ **"Sold through projects" is a real server rule and this app cannot fully
 * predict it.** It is derived, never a flag: the guard matches
 * `product_id = ? AND is_active = 1`, byte for byte what
 * `GET /projects?productId=N&isActive=true` asks — reproducible here, and
 * always arrives one project late. Several products have none and never will,
 * and **a line with no project is NORMAL rather than an error.**
 *
 * {@link projectOfferOf} answers it in four states and
 * {@link showsProjectControl} decides from them. ⚠️ *SUPERSEDED 2026-09-19:
 * "Two of the four resolve to SHOW".* **One does** — `'unknown'`, for the same
 * reason `'unknown'` shows the inclusions editor: a control that vanishes
 * because a list failed to load is indistinguishable from a bug, and the line
 * whose product IS sold through projects and names none is refused at write
 * time with no way to fix it from the screen. **`'not-chosen'` now HIDES, on
 * the owner's decision**, which retires the project-first path — the accepted
 * cost is written out at {@link showsProjectControl}.
 *
 * ⚠️ **Hiding the control changes nothing about what is SENT**, and `cityIds`
 * is where that would do real damage — a write REPLACES the set, so `[]`
 * clears it and a hidden block that cleared its draft would destroy a line's
 * cities on the next unrelated save, with a `200` and nothing to restore from.
 * Same three structural protections as the hidden inclusions block below:
 * hiding is a render branch that never touches state, the payload builders
 * decide from the draft alone, and a row already HOLDING a project or cities
 * always shows its controls — data is never hidden, only an unusable choice.
 *
 * ## ⚠️ MONEY STAYS ON THE LINE. Never multiply by `cities.length`
 *
 * `quantity` and `unitPrice` describe the LINE; the cities only say where it
 * runs. "Campus Outreach ×5 at 10,000 across Bangalore, Mumbai and Delhi" is
 * **50,000, not 150,000**. One EMERGE line is one Xero line however many
 * cities it names, which is what makes a quote and the invoice raised from it
 * reconcile line for line — fanning out per city would triple a real
 * customer's total, and the three generated columns are computed by the
 * database, so a client that multiplied would disagree with the figure Xero
 * and the customer both read.
 *
 * There is therefore no arithmetic anywhere in this file that reads
 * `cities.length`. The only things it is allowed to size are the chip list and
 * the description string.
 *
 * ### "Tax rate" is two fields and ONE control
 *
 * `taxRate` is our percentage and drives the generated arithmetic.
 * `xeroTaxType` is Xero's tenant-specific tax CODE — `ZERORATEDOUTPUT`,
 * `TAX001` — and it is **not derivable from the percentage**, because
 * `ZERORATEDOUTPUT`, `NONE` and `EXEMPTOUTPUT` are all 0% in this tenant.
 *
 * So this has one control: `TaxRateControl`, a dropdown of the TENANT's own
 * rates that writes `xeroTaxType` from `rate.taxType` and `taxRate` from
 * `rate.effectiveRate` in the SAME patch. The two cannot drift, because
 * nothing can set one alone.
 *
 * **The fallback is required rather than a nicety.** The rate list belongs to
 * the tenant, so it is unavailable while Xero is disconnected, while the read
 * is failing, and when the tenant genuinely lists none. All three fall back to
 * the bare `%` input: someone with no way at all to record tax is worse off
 * than someone recording a percentage with no Xero code, and the code is
 * nullable on purpose.
 *
 * ⚠️ **The CONTRACT is untouched by any of this.** `createPayload` and
 * `updatePayload` build both keys exactly as they always did, `''` on the code
 * still means "use the company's Xero sales default" and never "no tax", and
 * choosing that option deliberately leaves `taxRate` alone — there is no
 * percentage attached to "whatever the company says", and writing a `0` there
 * would silently rewrite the line's arithmetic.
 *
 * ### `taxRateSource` says where the rate CAME FROM, and it gates the PERCENTAGE
 *
 * A line answers `taxRateSource: 'company' | 'line'`. On `'company'` the rate
 * was TAKEN from the company's `xeroSalesTaxRate`, so the percentage is
 * read-only here and is changed on the company record. On `'line'` the company
 * states no rate, the line's own value stands, and everything is editable.
 *
 * 🔴 **THREE POSITIONS IN ONE DAY, 2026-08-30 — read all three or you
 * will reverse this back.** (1) read-only on `'company'`; (2) *SUPERSEDED:
 * "THE READ-ONLY RULING WAS REVERSED BY THE OWNER ON 2026-08-30 … The rate is
 * editable on every line, and so is the Xero tax CODE beside it … re-attaching
 * it to `disabled` is the regression."* — reversed on the grounds that a frozen
 * `0.00` is indistinguishable from a company that genuinely zero-rates; (3) the
 * live rule above, chosen by the owner with (1) and (2) in front of him.
 *
 * ⚠️ **WHAT MAKES (3) SAFE AND DID NOT EXIST AT (1): THE XERO TAX CODE STAYS
 * EDITABLE.** A rate the code disagrees with is `XERO_TAX_RATE_MISMATCH` at
 * push time, and the code picker — which writes the code and its own percentage
 * in ONE patch — is how a person clears it without leaving this grid. Locking
 * both is what stranded them and produced (2). **Never gate the code on this
 * field.**
 *
 * ⚠️ **And (2)'s objection is answered rather than overruled.** `'company'`
 * means the company STATES a percentage, so a locked figure is never a spurious
 * `0.00`; where it states nothing the line reports `'line'` and nothing locks.
 * The lock is keyed on the figure being PRESENT as well as on the flag, so the
 * ambiguity (2) feared cannot be rendered even if that pairing ever fails.
 *
 * ⚠️ **The field is NOT removed and is still required.** It is read for the
 * hint and for the percentage gate, and for nothing else.
 *
 * ⚠️ **`taxRate` is SENT normally, on every line.** *SUPERSEDED: "accepted
 * and silently ignored for one release while the backend removes the key —
 * expand/contract, the same shape as `confirmAcceptance`."* There is no
 * contraction coming.
 *
 * ⚠️ *SUPERSEDED, half of the same sentence: "the value is the line's own
 * again."* On a `'line'`-sourced line it is. On a `'company'`-sourced one the
 * server owns the figure and the grid sends back what it was given unchanged —
 * which is why the box is read-only rather than the key being dropped. **Not
 * sending it would be a different contract**, and nobody asked for one.
 *
 * ## The Price box shows the CATALOGUE FIGURE, not the word "catalogue"
 *
 * A new line left on `inherit` snapshots the product's catalogue price, so the
 * placeholder is that price — `18,000.00` — rather than a label naming a
 * mechanism.
 *
 * ⚠️ **`unitPrice` is a STRING and `null` is NOT zero.** `null` is unpriced and
 * `"0.00"` is a real price of zero, so the figure goes through
 * `formatDecimalString` and an unpriced product reads `Unpriced` rather than
 * `0.00`. The catalogue price is only KNOWN when the product is in the loaded
 * catalogue — a truncated or failed `/products` read is an unknown, not an
 * absence, and falls back to the old label. Nothing about which key is SENT
 * changed: `inherit` still omits `unitPrice` (snapshot) and `none` still sends
 * an explicit `null` (no price on this line).
 *
 * ## The Disc cell takes an AMOUNT or a PERCENTAGE, and stores a percentage
 *
 * ⚠️ **THERE ARE TWO DISCOUNT COLUMNS NOW, AND NOTHING CONVERTS BETWEEN
 * THEM.** `discount` is a string PERCENTAGE (`0..100`, `NOT NULL`) and
 * `discountAmount` is exact money (nullable). They are **mutually exclusive**:
 * sending both non-zero is a 422 naming `discountAmount`, so a write always
 * carries the PAIR — the live one, and the other said to be off.
 *
 * 🔴 *SUPERSEDED, in full: "There is no amount discount on this API and this is
 * not the start of one … The amount is a CLIENT-SIDE CONVENIENCE over that
 * column: it is converted to a percentage the moment it is typed … Do not
 * 'finish' it by inventing a `discountAmount` key — there is no column behind
 * it."* There is a column behind it, the conversion is DELETED, and
 * `percentageForAmount` no longer exists in `@/lib/decimal`.
 *
 * ⚠️ **DO NOT RE-DERIVE THE CONVERSION.** It was lossy by POUNDS, not pence:
 * £1,000 off an £18,000 line became `5.56%`, which takes £1,000.08 — so the
 * cell had to carry a sentence saying the figure typed was not the figure
 * stored, and three refusals for amounts no percentage could express. All of
 * that was the apology, and all of it is gone with the thing it apologised
 * for.
 *
 * ## ⚠️ NOTHING explanatory renders inside a desktop grid cell
 *
 * Every control here takes `compact`, and under it `Field`'s `hint` is not
 * passed at all: whatever the hint said moves into the input's `placeholder`
 * or a `title`, or it goes.
 *
 * Two things are not prose and stay. A field **error** is a failure rather
 * than commentary. A `Combobox`/`MultiCombobox` **footnote** renders under the
 * option list INSIDE the popover and never in the cell.
 *
 * The stacked card below `md` is not a grid, so it keeps its labels and its
 * hints — `compact` is false there, and that is the only thing the flag does.
 *
 * ## The Description cell types over a live derivation
 *
 * In Xero the Description cell is an editable textarea pre-filled from the
 * item, so this one is too, and it re-derives from `descriptionOf(row)` on
 * every render: choosing a product, moving the project or adding a city
 * rewrites it in front of the person.
 *
 * **Typed text wins and is never clobbered.** The precedence is one decision
 * and one box, so it is a deliberate override rather than an `a ?? b` drop:
 * while `draft.description` is `null` the cell IS the derivation, and the
 * moment somebody types it stops following anything. `Reset to derived` puts
 * the key back to `null` and hands the cell back to the derivation — the only
 * way back.
 *
 * ✅ **It IS persisted, and reaches the customer verbatim.** `description` is
 * a real column, in `Draft`, in `createPayload`/`updatePayload`, and in
 * `ROW_WRITE_KEYS`.
 *
 * **`null` means DERIVE, not "empty"** — one of several nullable columns on
 * this API carrying that meaning, alongside `xeroTaxType`, `xeroLineAmountType`
 * and the two tracking fields. So the contract is four cases and only three of
 * them are spellable in one box:
 *
 * | payload | means |
 * | --- | --- |
 * | `{ description: "Mumbai campus tour" }` | stored, and sent to Xero VERBATIM |
 * | `{ description: null }` | back to deriving — **what `Reset to derived` sends** |
 * | `{}` | unchanged |
 * | `{ description: "" }` | normalised to `null` server-side |
 *
 * ⚠️ **Reset sends an explicit `null` and must never merely omit the key.**
 * Across this API an omitted key means *leave alone* and an explicit `null`
 * means *clear*; Reset means the second, and a Reset that omitted would be a
 * `200` that changed nothing while the box on screen went back to deriving.
 *
 * **An emptied box is folded into Reset HERE, before the request is built** —
 * see {@link descriptionForWrite}. The server would normalise `""` to `null`
 * anyway; doing it on this side means our payload says what we mean, and the
 * two ways of saying "derive again" cannot end up meaning different things.
 *
 * **The typed/deriving state comes from the RECORD, not only from this
 * session.** `draftOf()` reads `line.description` straight through, so a
 * saved line with `null` opens derived-and-untouched and one with a string
 * opens typed — with the Reset control already offered, because somebody in an
 * earlier session took that line off the derivation and this one has to be
 * able to hand it back.
 *
 * ## A product may appear on as many lines as it likes
 *
 * ⚠️ *SUPERSEDED: "**One line per product PER PROJECT.** The unique key is
 * `(quote_id, product_id, project_key)` where `project_key` is
 * `COALESCE(project_id, 0)` … two project-less lines for one product are still
 * refused."* **Migration 129 dropped that key from all three line tables.**
 *
 * It was refusing real data rather than catching mistakes: across the 1,105
 * non-deleted Xero documents, 162 groups sell one item on several lines, 161 of
 * them differ by description, and **not one is a true duplicate**. Under the
 * importer's all-or-nothing rule that cost 267 lines across 125 documents,
 * silently — a short document reads as a complete one.
 *
 * `pairKey()` and `duplicatePairsOf()` survive, and what they feed is now
 * `RowProblems.notice` rather than `RowProblems.error`: a repeated product is
 * still worth pointing out, because billing one thing twice by accident is a
 * real mistake, but it is no longer a reason to refuse a save.
 *
 * ⚠️ **The product picker still deliberately does NOT exclude taken products**,
 * and the reason has got simpler rather than going away. The old one was a
 * deadlock — the project picker is filtered by the row's own product, so
 * excluding a product made its second project unreachable. That still holds,
 * and underneath it is now the plainer fact that **a document may legitimately
 * sell the same thing twice**, so there is nothing to exclude in the first
 * place.
 *
 * ## The project on a line is not the project on the quote
 *
 * `quote.project` is only the DEFAULT a new line starts from. The authority
 * for a line's cities is the LINE's project — a project sells one product and
 * a quote sells many, so filtering a line's cities by the header project is a
 * filter about something else. The `cities` prop is therefore the header's set
 * and is used for exactly two things: seeding a new row, and naming a city we
 * hold no other record of.
 *
 * `projectId` and `cityIds` are **sent together whenever either changes**. The
 * guard runs on the post-merge state, so a `projectId`-only PATCH re-validates
 * the STORED cities and can be refused naming cities the caller never sent.
 * Changing the project therefore clears the city selection in the draft,
 * which makes that state unreachable rather than merely explained.
 *
 * ## ⚠️ "All cities" is a FLAG, and it is not a list of every city
 *
 * A line can name its cities explicitly, or it can carry `allCities` and mean
 * **every city its project has**. The second is resolved live: a city added
 * to that project next month is on the line automatically, and a line that
 * merely listed every city today keeps the list it was given. **Both read
 * identically on screen right now and diverge the moment the project
 * changes.** "All" is never implemented by expanding to every city id at save
 * time, in either direction — not on save, and not by pre-filling the picker
 * with today's resolution when somebody unticks the box.
 *
 * The two are **mutually exclusive on the wire**: `allCities: true` beside a
 * non-empty `cityIds` is a **422**, `allCities: true` clears any explicit set,
 * and sending `cityIds` clears the flag. That 422 is a correct backstop this
 * grid must never actually reach, so the exclusion is structural —
 * {@link createPayload} and {@link updatePayload} both branch, and neither has
 * a path that emits the pair.
 *
 * | the row | what a write sends |
 * | --- | --- |
 * | flagged | `allCities: true`, and **no `cityIds` at all** |
 * | not flagged, was flagged | `cityIds`, plus `allCities: false` to clear it |
 * | not flagged, never was | `cityIds` alone, exactly as before |
 *
 * Three consequences worth stating where they cannot be missed:
 *
 * - ⚠️ **`line.cities` on a flagged line is the RESOLVED set, not a stored
 *   one**, and it looks exactly like a stored one. {@link draftOf} therefore
 *   seeds `cityIds` as `[]` for such a line, and anything reading a line's
 *   `cities` as evidence of an explicit choice — most sharply
 *   `citiesLostByMovingProject` in `@/lib/line-project`, which this file does
 *   not call and must not start calling — produces a confident warning about
 *   cities that cannot be lost.
 * - ⚠️ **The city REFUSALS are statements about an explicit set**, so the
 *   200-city cap, "choose this line's project before naming cities" and the
 *   stray-city check are all skipped while the flag is on. **A project is
 *   still required**, worded for the flag: without one there is nothing for
 *   the server to resolve against.
 * - ⚠️ **The line DESCRIPTION gains no new vocabulary.** It lists the resolved
 *   city NAMES — `Campus Outreach — Bangalore, Mumbai, Delhi` — and never the
 *   words "All cities", on a flagged line exactly as on any other. See
 *   {@link descriptionOf}, which composes over the resolved set precisely so
 *   this preview cannot disagree with the string the backend composes at push
 *   time.
 *
 * **Money is untouched by all of this** — a flagged line is still one line at
 * one price, and nothing multiplies by a city count. See the block above.
 *
 * ## The Amount column previews, off the one shared function
 *
 * `computeGeneratedLinePreview`, exported from `@/lib/decimal` — the one place
 * money arithmetic is allowed to live — verified against the server over
 * 331,680 cases, so the preview is the shared function's answer and there is
 * exactly one copy of the maths.
 *
 * **Do not reimplement or "simplify" it here.** The order is correctness:
 * subtotal rounded, tax charged on the ALREADY-ROUNDED subtotal, total as the
 * sum of the two rounded parts. Rounding once at the end — the obvious-looking
 * tidy-up — disagrees by a cent on some rows, and the stored value is the one
 * Xero reads.
 *
 * Two things the preview refuses to assert, both deliberate:
 *
 * - **`unitPrice: null` returns `null`, never `"0.00"`.** The generated
 *   columns `COALESCE(unit_price, 0)`, so an unpriced line and a line priced
 *   at zero produce identical strings and nothing afterwards can tell them
 *   apart. A row with no price shows no preview.
 * - **A blank box means two different things by row**, so it is resolved
 *   before the call rather than inside it: on a NEW line a blank percentage
 *   takes the server's default of 0 and a blank price snapshots the
 *   catalogue; on an EXISTING line a blank leaves the stored value alone.
 *   Previewing a blank as 0 on an edit would show a figure the save is not
 *   going to produce.
 *
 * The server's three figures always win — they replace the preview off the
 * response the moment the row saves.
 *
 * The document total IS computed, and only from the server's own `lineTotal`
 * values via `sumDecimalStrings` — the database rounds per line, so
 * re-deriving from prices gives a different answer, and the database's is
 * what Xero reads.
 *
 * ## Saving is N requests, and it says so
 *
 * There is no bulk or replace endpoint. Each row is its own `POST` or `PATCH`,
 * run one at a time, with no transaction across them: if the third 409s, the
 * first two are committed. So a partial failure is an ordinary outcome, not an
 * edge case. Every row carries its own status and its own reason, and the run
 * reports "2 of 3 lines saved" rather than one "Saved".
 *
 * **A retry never re-posts a row that already landed.** A successful POST
 * turns its row into a saved row with a real line id, so the next run either
 * skips it (nothing changed) or PATCHes it. Re-posting would 409 on the pairs
 * that clash and, worse, quietly create a genuine duplicate for any row whose
 * project differs.
 *
 * Every line write also rewrites the quote's four header totals in the same
 * transaction and returns none of them, so `onLinesChanged` fires after any
 * successful write and the parent must re-fetch the quote.
 *
 * ## `mode: 'draft'` — lines collected before the quote exists
 *
 * The New Quote modal wants line items entered in the same action that
 * creates the quote. **A line cannot be written before the quote has an id**
 * — every edit in the saved mode above is a request against
 * `/quotes/{id}/products` — so `draft` mode holds the rows entirely in local
 * state, issues **no line request of any kind** (no POST, no PATCH, no
 * DELETE, no basket load) and reports the payloads upward through
 * `onDraftLinesChange`. The parent creates the quote and then writes them.
 *
 * Four things make that mode honest rather than a second, weaker grid:
 *
 * - **One payload builder.** Draft rows go through `createPayload()`, the
 *   same function the saved POST uses, so the shape the parent writes cannot
 *   drift from the shape this table has always sent.
 * - **The money is a PREVIEW and says so.** In draft there is no server value
 *   at all, so every figure on screen is `computeGeneratedLinePreview` —
 *   labelled, never presented as a stored figure. The footer total is the sum
 *   of those previews and is labelled too.
 * - **A repeated product is NOTICED here, and refused nowhere.** ⚠️ *SUPERSEDED:
 *   "The duplicate the server would refuse is caught here … the only thing
 *   standing between the user and a 409 that arrives after the quote has been
 *   created."* Migration 129 dropped the
 *   `(quote_id, product_id, project_key)` index, so there is no 409 left to
 *   stand between anybody and anything — 162 groups across 1,105 real Xero
 *   documents repeat an item and not one is a true duplicate.
 *   `duplicatePairsOf()` survives as a NOTICE, on `RowProblems.notice`, which
 *   `blockedKeys` deliberately does not read.
 * - **Only rows the server would accept are emitted.** A row failing local
 *   validation is withheld and reported through `onDraftBlockedChange`, so the
 *   parent can refuse to submit rather than create a quote and then collect a
 *   422 for a line it cannot show.
 *
 * ⚠️ **The reference-data fetches still run in draft mode**, and must: the
 * products, campaign-inclusions, active-projects and Xero tax-rate lists are
 * what the pickers are made of, and a grid with empty dropdowns is not a
 * no-request grid, it is a broken one. "No requests" means no *line* requests
 * — nothing that writes, and nothing that reads a basket which does not exist.
 *
 * ## `mode: 'convert'` — a DEAL's lines, on the way into a quote
 *
 * The deal→quote modal must present its line items exactly as the New Quote
 * modal does — the owner's instruction, 2026-08-30 — so it mounts this
 * component rather than a table of its own. **A near-identical second table on
 * the same document is the shape this codebase keeps paying for**: two copies
 * agree the day they are written and diverge on the first edit, and the one
 * that drifts is always the one nobody was looking at.
 *
 * ⚠️ **NOTHING HERE IS EVER POSTED.** `POST /deals/{id}/convert` creates every
 * line of the new Draft quote inside the transaction that creates it. A row
 * written from this grid as `POST /quotes/{id}/products` would be a SECOND line
 * beside the one the server already made — a quote billing the customer twice
 * for the same work, from a control that looks exactly like the one on a blank
 * form. What goes upward is the COMPLETE FINAL LIST of lines, through
 * `onConvertLinesChange`.
 *
 * ⚠️ **CORRECTED — THE CONTRACT IS NO LONGER FIVE KEYS, AND THE GRID IS
 * UNLOCKED.** *SUPERSEDED, table included: "THE CONTRACT IS FIVE KEYS PER LINE,
 * AND THE GRID IS SHAPED BY IT rather than by taste. An override names an
 * EXISTING deal line by `dealLineId` … | Item, Project, Cities, Regions,
 * Inclusions | copied verbatim by the server. **Rendered INERT** … | Add row,
 * Delete | **absent.** There is no key that adds a line and none that removes
 * one, so both would be controls whose action cannot be performed |"* The route
 * gained `lineSet`, the complete final list, and every one of those became
 * expressible. The table as it now stands:
 *
 * | on screen | why |
 * | --- | --- |
 * | Qty, Price, Discount, Description | carried per entry — live |
 * | Item, Project, Cities, Regions, Inclusions | carried per entry — **live**, where they were inert |
 * | "All cities" | **still LOCKED.** `lineSet` has `cityIds` and no `allCities` key at all, so the flag alone is disabled while the picker under it is live — see `allCitiesLocked` |
 * | Tax rate | **still a READOUT** — see {@link ConvertedTaxRateCell}. `f_deal_products` has no tax column, neither request key carries one, and a converted line's rate comes from the COMPANY |
 * | Add row, Delete | **present.** An entry with no `dealLineId` is an added line; a row left out of the array is a removed one |
 * | the footer total | **still absent.** The quote's four figures are already on the form's Totals panel, and a second sum could disagree with it |
 *
 * Four things make the mode honest rather than a weaker grid:
 *
 * - **One change test.** `convertLineShapesMatch` against a fresh seed decides
 *   the badge, the Amount cell, the blocked count AND which keys
 *   `convertLineSetEntryOf` puts on the wire. A second rule for "has this moved"
 *   is how a row comes to read as unchanged while a key for it is being sent.
 * - **Only CHANGED rows are validated, and a row that HAS moved is judged in
 *   full.** An untouched carry-over row emits its `dealLineId` and its position
 *   and nothing else, so not one of its stored figures reaches the request and
 *   none of them can be refused. A row that moved — or one somebody added, which
 *   has no seed and is therefore always changed — goes through the same
 *   `validateRow` every other mode uses, because every field it checks is now a
 *   field this request carries.
 * - **The Amount column shows the DEAL's stored figure and never a preview.**
 *   The figure that matters after a conversion is the QUOTE line's, whose tax
 *   comes from the company — a deal-shaped preview would be a confident number
 *   about the wrong document. See {@link AmountCell}.
 * - **A converted row's `saved` stays `null`.** A synthetic `QuoteProduct` was
 *   the obvious alternative and was rejected: half of it would be fabricated.
 *   See {@link Row.convert}.
 *
 * ⚠️ **The Xero tax-rate fetch is the one reference read this mode DOES skip**,
 * and only because there is no control to populate — see `useXeroTaxRates`
 * below. Everything else still runs: a grid with empty dropdowns is not a
 * no-request grid, it is a broken one.
 *
 * ## Campaign inclusions are set PER PRODUCT by an admin
 *
 * Which products offer campaign inclusions is set per product by an
 * administrator, through the product↔inclusion link table
 * (`GET`/`PUT /products/{id}/inclusions`, edited on `/products` via
 * `ProductInclusionsModal`) — not by name, not by category. The rule itself
 * lives in `@/lib/product-inclusions` and is asked of it once per row; this
 * file supplies the *input*, fetching each product's offer — see
 * {@link useProductInclusionOffers}.
 *
 * ### ⚠️ The table decides WHICH as well as WHETHER — the picker is narrowed
 *
 * **There is no server-side backstop on this feature.** Writing `inclusions`
 * on a line takes any active inclusion id, so an unnarrowed picker lets
 * someone tick an inclusion the administrator never linked to that product —
 * it saves cleanly, and it **prints on the customer's Xero quote**. The
 * picker is the check.
 *
 * `offeredInclusionIds(offer)` is the option list and it comes off the SAME
 * offer the gate counts — see {@link RowInclusions.offeredIds}. Three things
 * follow:
 *
 * 1. ⚠️ **`null` means DO NOT NARROW.** An unread offer already shows the
 *    editor (`'unknown'`), and narrowing on it would produce an empty picker
 *    indistinguishable from an administrator having linked none — a load
 *    failure promoted to a business decision.
 * 2. ⚠️ **Narrowing the OPTIONS never narrows the DRAFT.** A line may already
 *    hold an inclusion the product is not linked to, and a write REPLACES the
 *    whole set, so an entry filtered off the screen is an entry deleted on
 *    the next save, with a 200 and no warning. Such entries get their own
 *    block in {@link InclusionsEditor}.
 * 3. ⚠️ **"Not linked to this item" is NOT "retired", and must not borrow
 *    that wording.** The retired block is for a master row an admin
 *    **deactivated**; this is a perfectly active row that simply was not
 *    linked here. They also make opposite promises about saving — a retired
 *    id cannot be re-sent at all (a 422), one of these saves and prints — so
 *    one label for both would be false about a master row and misleading
 *    about a document.
 *
 * Removing an unlinked-but-stored entry **is** offered, deliberately: an
 * administrator who did not link it presumably does not want it on future
 * documents, and refusing would strand the line permanently with no way to
 * correct it from this app. It is a Remove button and nothing else —
 * narrowing itself never removes anything, so the edit is always somebody's
 * explicit click.
 *
 * ⚠️ **The gate is `productOffersInclusions()`, NOT
 * `shouldShowInclusionsEditor()`, and that is a decision rather than an
 * oversight.** The second is `offers OR the line already holds some` and
 * would keep the editor on a non-offering line that already carries
 * inclusions. **The owner was shown that trade-off and chose the strict
 * rule**, accepting that such inclusions become uneditable in this app.
 *
 * So `shouldShowInclusionsEditor` exists, is correct, and is deliberately
 * unused. Do not "fix" the gate back to it — that reverses a decision, it
 * does not repair a bug. If the owner changes their mind, this is the one
 * call that moves.
 *
 * ### ⚠️ The decision hides a CONTROL. It does not license destroying DATA
 *
 * An inclusions write **REPLACES the whole set**: `[…]` replaces, `[]`
 * clears, and the key **omitted** leaves the stored set alone. So if hiding
 * the block also cleared the row's `draft.inclusions`, the "did the set
 * change?" test in {@link updatePayload} would fire, `inclusions: []` would
 * go out, and the line would silently lose its campaign inclusions on the
 * next unrelated save — a `200`, no warning, and nothing to restore them
 * from.
 *
 * Three things keep that unreachable, and all three are structural:
 *
 * 1. **Hiding never touches state.** The block is a render branch and
 *    nothing else; `patchRow` is only ever called by the editor's own
 *    controls, so a hidden block has no way to move the draft at all.
 * 2. **The payload builders take the verdict.** {@link createPayload},
 *    {@link updatePayload}, {@link isDirty} and {@link validateRow} all take
 *    `offersInclusions`, and every one of them omits the key outright when it
 *    is false. No default is supplied on the last three — `tsc` refuses a
 *    call site that forgets, which a guard somebody can delete does not.
 * 3. **`isDirty` agrees with the payload**, so a hidden set can never light
 *    up "Save 1 row" for a change no request carries, and can never produce
 *    the empty PATCH that would be a 422.
 *
 * ### The five states, in all three places inclusions appear
 *
 * | product | line holds | detail panel | grid count | mobile chips |
 * | --- | --- | --- | --- | --- |
 * | **none chosen** | none | **nothing at all** | nothing | nothing |
 * | **none chosen** | some | **nothing at all** — the draft is kept, not sent | count | chips |
 * | qualifies | anything | the editor, **narrowed to the linked set** | count | chips |
 * | `unknown` | anything | the editor, **UNNARROWED** — see below | count | chips |
 * | does not | none | the denial sentence, alone | nothing | nothing |
 * | does not | some | the wording, READ-ONLY, plus one line saying so | count | chips |
 *
 * Nothing renders for "none chosen" — no heading and no sentence, because
 * there is no product for a sentence to be about, and
 * `inclusionsDenialReason` returns `null` rather than claiming inclusions are
 * "not offered on this product". The second row is reachable by ticking
 * inclusions on a qualifying item and then clearing the item box: the draft
 * is left alone, the payload omits the key, and re-picking a qualifying item
 * brings the entries back.
 *
 * **`unknown` still shows, and that is what was NOT changed.** The catalogue
 * not being loaded, or having failed, is not a decision to switch the feature
 * off — `productOffersInclusions` resolves it to `true` on purpose, because a
 * feature that disappears on a load failure is indistinguishable from a bug,
 * while showing it costs at worst a refusal from the server that names the
 * real problem.
 *
 * **A non-qualifying line with inclusions shows them rather than nothing.**
 * That is faithful to what was chosen — *uneditable*, not *invisible*. They
 * print on the customer's document, so a screen that omitted them would be
 * describing a different document from the one that was sent. The wording
 * comes from `@/lib/inclusions` through {@link InclusionSummary} and is
 * never re-implemented here: `unitSingular` at exactly 1, `unitPlural`
 * otherwise, both forms stored, nothing pluralised in code.
 *
 * **A count of zero never renders.** {@link InclusionSummary} returns `null`
 * on an empty set before it consults anything else, so the emptiness test is
 * the same on a qualifying line and a non-qualifying one.
 *
 * ### The signal is the LINK TABLE, keyed on product ID
 *
 * The answer is keyed on the product ID rather than read off a product's
 * fields:
 *
 * - {@link ProductRow} and `ProductSummary` are no longer consulted for the
 *   decision at all. Do not widen either for it.
 * - **A product the active catalogue does not list resolves perfectly.** A
 *   product retired or past the 100-row `perPage` cap still answers, because
 *   `GET /products/{id}/inclusions` works on a retired product — `isActive`
 *   governs what may be CHOSEN, not what history may say.
 *
 * ## Product-first, and ONLY product-first since 2026-09-19
 *
 * 🔴 **THE OWNER HAS RETIRED THE PROJECT-FIRST PATH.** ⚠️ *SUPERSEDED, the
 * whole of this section: "**Project-first as well as product-first** — A line
 * can be started from either end … Choosing the **project** first now works
 * too, and **sets the line's product from that project** in the same update."*
 * The project control is not offered until an item is chosen, so there is one
 * direction into a line: **item, then project.**
 *
 * ⛔ **Recorded as an accepted cost of a decision, not as a tidy-up.** Resolving
 * from either end was a real capability and it is gone; nothing errors and
 * nothing is refused, so the only symptom is a row that looks like it has lost
 * a control. **Do not "restore" it as a bug fix** — see
 * {@link showsProjectControl}, where the cost is written out in full.
 *
 * Choosing the **product** first still filters the project picker to the
 * projects selling it, which was always the original path and is now the only
 * one. The reasoning that made project-first safe is kept at `patchRow`, which
 * is deliberately NOT gutted: a project sells exactly one product, so leaving
 * the two to be reconciled later would let someone build refusal 5 ("this
 * project sells product X, but this line sells product Y") out of two choices
 * that each looked fine. **That branch is now unreachable from the screen**,
 * and it is left whole rather than half-dismantled.
 *
 * ⚠️ The per-line HOOK in `@/lib/use-line-project` is deliberately NOT used
 * here — see `useActiveProjects` for why a grid resolves the list once
 * instead of once per row. Its pure helper `productOfProject()` IS used,
 * though: it is a function over a list rather than a hook, so it carries no
 * fetch, and it is the one copy of "a project implies its product" — a rule
 * that would otherwise be spelled differently in the grid and in the modal,
 * agreeing only until one of them was edited.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **THE SEED SHAPE IS THE MAPPER'S OWN TYPE, IMPORTED — NOT A SECOND ONE
 * DECLARED HERE.** `plannedProductLines()` in `@/lib/api` already decides the
 * single question a seed has to answer: whether a PLANNED quantity — a decimal
 * string — can be a LINE quantity, which is an integer of at least 1. Declaring
 * a look-alike interface in this file would be a second place for that decision
 * to live, and the two would agree only until one of them was edited.
 *
 * ⚠️ **`lineQuantity: null` IS NOT A LICENCE TO SUBSTITUTE 1**, and this grid is
 * where that rule is actually kept — see `seededRowOf` and `validateRow`.
 */
import type { PlannedProductLine } from '@/lib/api';
import { cx } from '@/lib/cx';
/*
 * ⚠️ **The ONE copy of what a deal→quote conversion may carry, and it is not
 * this file's.** The seed, the change test, the validator and the payload
 * builder give a wrong answer independently, so they live together in one
 * module. This grid supplies the rows and reads the answers — it never
 * re-implements one. See `mode: 'convert'` in this file's header.
 *
 * ⚠️ **CORRECTED — the route no longer takes "five per-line keys and no
 * others".** *SUPERSEDED: "`POST /deals/{id}/convert` takes five per-line keys
 * and no others".* That was `lines`, which still exists and is still what the
 * INVOICE route takes. This grid now builds **`lineSet`** — the complete final
 * list, which carries the product, the project, the cities, the regions and the
 * inclusions as well, and in which an omitted row is a row the quote will not
 * have. `taxRate` and `xeroTaxType` are the two keys that are still absent from
 * BOTH, which is why the Tax rate cell stays a readout.
 */
import {
  convertLineNotesWrite,
  convertLineSetEntryIsCarryOver,
  convertLineSetEntryOf,
  convertLineShapesMatch,
  type ConvertLineShape,
  type DealConvertLineSetEntry,
} from '@/lib/deal-convert-lines';
import {
  computeGeneratedLinePreview,
  solveLineAmount,
  splitAmountOverQuantity,
  sumDecimalStrings,
  type GeneratedLineInput,
  type GeneratedLinePreview,
} from '@/lib/decimal';
/*
 * ⚠️ **WORDING, AND THE ONE QUESTION ABOUT THE MODE ITSELF.** The mode's two
 * labels live in one module so the three grids cannot word this choice
 * differently; nothing about the payload, the refusals or the arithmetic comes
 * from there. `discountWriteOf` below is still what decides which of the two
 * columns the wire carries.
 *
 * ⚠️ **`percentageModeIsChosen` does not break that**, and it is imported here
 * for the reason it exists: *has the reader actually PUT this line in
 * percentage mode* is a question about the MODE — that module's whole subject —
 * and it returns a boolean. It computes no money. It is NOT
 * `discountModeOf(...) === 'percentage'`, which is true of every untouched row,
 * and the two must never be swapped for one another; see the `keepPercentage`
 * argument in the Amount cell's `commit`.
 */
import {
  DISCOUNT_MODE_GROUP_LABEL,
  DISCOUNT_MODE_OPTIONS,
  discountModeOf,
  percentageModeIsChosen,
} from '@/lib/discount-mode';
import { formatDecimalString } from '@/lib/format';
import { isValidInclusionQuantity, renderInclusion } from '@/lib/inclusions';
/*
 * ⚠️ **The ONE copy of the join, and it is pinned for a reason nothing here can
 * detect.** The backend composes the SAME string independently when it pushes a
 * line to Xero — that is what `description: null` means — so a joiner of ours
 * that differed would make a line read one way in EMERGE and another on the
 * customer's Xero document, with nothing on either side reporting it. It bites
 * only while `description` is null, which is the common case. Do not
 * re-implement the join, and do not inline it "because it is one template
 * literal": that is exactly the shape this file used to have.
 */
import {
  composeLineDescription,
  effectiveInclusionQuantity,
  type LineDescriptionInclusion,
} from '@/lib/line-description';
/*
 * ⚠️ **THE TWO SEEDING RULES ARE NOT THIS FILE'S AND MUST NOT BE RE-DERIVED
 * HERE.** `@/lib/line-presets` is ONE copy for the quote, invoice and deal
 * grids: what a recommended discount should do to a line that already carries a
 * figure, and what a quantity band should do to a set somebody may have edited.
 * Three grids answering that separately is three chances to disagree about a
 * discount a customer is billed against and a set of inclusions printed on their
 * document.
 *
 * ⚠️ **This grid decides WHEN to ask and owns the requests; the module decides
 * WHAT.** Both functions answer `null` for *change nothing*, and every `null`
 * here is honoured rather than treated as an empty seed — see
 * {@link useLinePresetSeeding}.
 */
import {
  inclusionPresetSeed,
  recommendedDiscountSeed,
  type LineInclusionDraft,
} from '@/lib/line-presets';
import {
  autoLineDescription,
  defaultQuantityFor,
  isAutoDescription,
  perUnitDiscountTotal,
  quantityStepError,
  type AutoLineDescriptionArgs,
} from '@/lib/product-units';
import { lineRefusalOf, type LineRefusal } from '@/lib/line-project';
/*
 * ⚠️ `shouldShowInclusionsEditor` is deliberately NOT imported. See
 * "Campaign inclusions are set PER PRODUCT by an admin" in this file's header —
 * the owner chose the strict predicate over the lenient one.
 */
/*
 * ⛔ `inclusionsRequiredOnLine` and `INCLUSION_REQUIRED_MESSAGE` are GONE, not
 * merely unimported — the at-least-one rule was withdrawn on 11 Sep 2026 and
 * both were deleted from `@/lib/product-inclusions` with it. See `validateRow`.
 */
import {
  hasOrphanedInclusions,
  inclusionsDenialReason,
  offeredInclusionIds,
  productOffersInclusions,
  NO_PRODUCT_CHOSEN,
  type NoProductChosen,
  type ProductInclusionOffer,
} from '@/lib/product-inclusions';
import { productOfProject, projectSellsProduct, useCycleScopeIds } from '@/lib/use-line-project';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  projectOptionsByScope,
  projectScopeFootnote,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { cityLabel } from '@/lib/use-project-options';
/*
 * The `/regions` master, for the LINE's own regions of focus. It already
 * queries `isActive: true`, so a retired region is never OFFERED — offering one
 * is offering a guaranteed 422 — while a region a line already HOLDS is pinned
 * back in by {@link regionOptionsFor} rather than silently dropped.
 */
/*
 * Project pricing (owner 2026-10-27): the price an untouched line inherits is
 * the project price entry for its product × currency × tier, else the legacy
 * city pro-rata — one rule, `computeLinePrice`, never re-implemented here.
 */
import { computeLinePrice, tierOptionsFor } from '@/lib/line-pricing';
import type { ProjectPriceEntry } from '@/lib/project-pricing';
import { useProjectPrices } from '@/lib/use-project-prices';
import { useAuth } from '@/lib/use-auth';
import { useReferenceOptions } from '@/lib/use-reference-options';
import type { XeroPushReport } from '@/lib/xero';
import { useXero } from '@/lib/xero-context';
import {
  companyTaxRateReason,
  expenseRateCount,
  hasEffectiveRate,
  /*
   * The gate for whether this line's tax control may be edited at all —
   * migration 096. It reads the SERVER's answer per line and is never a
   * constant; see its doc comment for why "make it read-only" is a defect if
   * taken literally.
   */
  isCompanySourcedTaxRate,
  isSalesTaxRate,
  lineTaxRates,
  /*
   * The ONE definition of "these two tax codes name the same rate". A stored
   * code and a live tenant code can differ in case — the server normalises on
   * write — so `===` between them is a bug every time. See its doc comment for
   * the double-injection this file shipped by reimplementing it.
   */
  sameTaxCode,
  taxRateHint,
  unquantifiedSalesRateCount,
  useDocumentTaxCodes,
  useXeroTaxRates,
} from '@/lib/xero-tax-rates';
import type {
  CampaignInclusion,
  DealProduct,
  LineCity,
  LineInclusion,
  PriceTier,
  Project,
  ProjectCity,
  QuoteProduct,
  QuoteProductCreateInput,
  QuoteProductUpdateInput,
  ResolvedInclusionPreset,
  XeroTaxRate,
} from '@/lib/types';
import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  SegmentedControl,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  Textarea,
} from '@/components/ui';
import type { ComboboxOption, MultiComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { LineNoteField } from '@/components/documents/LineNoteField';

/* ── The server's own patterns, so a refusal is caught before it is sent ──── */

/**
 * ⛔ **ONE PATTERN BECAME TWO ON 2026-09-12, AND MERGING THEM AGAIN IS A
 * SURCHARGE.** A single `PRICE_PATTERN` served `unitPrice` AND `discountAmount`
 * — correct while both were non-negative, wrong the moment one stopped being.
 *
 * Migration 171 lets a line PRICE be negative (a DEDUCTION LINE: INV-0886
 * carries one at `-4150.00`), while `discountAmount` stays non-negative under
 * `ck_*_discount_amount_sign`. A negative in the first box is a credit; a
 * negative in the second is a **surcharge arriving through the discount
 * field**, which the database refuses only as errno 4025 — a 422 naming no
 * field at all, which nobody can bind to the control they typed in.
 *
 * The backend pins the pair behaviourally in
 * `tests/line-negative-unit-price.test.ts`. **They differ by one character and
 * must never converge.**
 */

/** `unitPrice`: `^-?\d{1,10}(\.\d{1,2})?$`, straight off the POST schema. */
const PRICE_PATTERN = /^-?\d{1,10}(\.\d{1,2})?$/;

/** `discountAmount`: unsigned — a discount is not a surcharge. */
const DISCOUNT_AMOUNT_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;
/** `discount` and `taxRate`: a PERCENTAGE 0..100, `^\d{1,3}(\.\d{1,2})?$`. */
const PERCENT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

/**
 * A tenant's Xero rate, as a string `taxRate` will actually accept.
 *
 * ⚠️ **`String(rate.effectiveRate)` produced rows that could never be saved.**
 * `effectiveRate` is a JSON number and a real tenant rate can carry three
 * decimals — Canada's `14.975` is a live example — while `taxRate` is
 * `^\d{1,3}(\.\d{1,2})?$` on the server and a two-decimal column underneath. So
 * choosing such a rate wrote `"14.975"` into the draft, `validateRow` refused
 * it, and the row was stuck: the percentage has no box of its own once a code is
 * chosen, so there was nothing on screen to correct.
 *
 * Widening the pattern is the wrong repair — it mirrors the server's, so a
 * looser client only swaps a local refusal for an identical 422 later. The
 * value is rounded to what the column can hold instead, and the rounding is
 * **disclosed in the option's own hint** rather than applied quietly: a
 * tax percentage that silently differs from the one Xero shows is exactly the
 * kind of drift this file refuses everywhere else.
 *
 * The common rates pass through untouched — `String(20)`, `String(0)` and
 * `String(12.5)` all satisfy the pattern already, so nothing about an ordinary
 * tenant changes.
 *
 * ⚠️ **The parameter is `number` and STAYS `number`. Do not widen it to accept
 * `effectiveRate` directly.** That field is `number | null` — `null` meaning
 * Xero declined to state a percentage — and this function has no honest answer
 * for it: `String(null)` is `"null"`, which fails the pattern, and
 * `null.toFixed` then throws and takes the whole line grid down with it. The
 * two repairs that suggest themselves are both worse than the crash. `?? 0`
 * writes **0% tax onto a customer's document** off a figure that does not
 * exist; a `null` return moves the decision here, where there is no way to say
 * what should happen to the row.
 *
 * So the CALLER decides, and the type is what makes it. `hasEffectiveRate()` in
 * `@/lib/xero-tax-rates` narrows, {@link lineTaxRates} withholds such rates
 * from the picker in the first place, and the `onChange` below writes the code
 * alone when it meets one — the same thing the empty option does, for the same
 * reason.
 */
function percentForDraft(value: number): string {
  const exact = String(value);
  if (PERCENT_PATTERN.test(exact)) return exact;
  return value.toFixed(2);
}
/** A longer array is a 422 on `inclusions`. */
const MAX_INCLUSIONS_PER_LINE = 50;
/** `/products` caps here; 28 rows exist today, so one page is the whole list. */
const PRODUCTS_PER_PAGE = 100;
/** `/campaign-inclusions` caps here. */
const INCLUSIONS_PER_PAGE = 200;
/*
 * ⚠️ **`PROJECTS_PER_PAGE` IS GONE — the grid PAGES now (2026-09-15).**
 * *SUPERSEDED: `const PROJECTS_PER_PAGE = 100`, the server's cap, sent as the
 * `perPage` of a single read.* It was hit — 118 active projects against it —
 * and the same 18 rows fell off every time. The page size is owned by
 * `api.listAllProjects` and is not a number this file may choose; see
 * `useActiveProjects` for what the truncation actually cost.
 */
/**
 * `cityIds` caps here. It is a denial-of-service bound rather than a business
 * rule — the real limit is how many cities the line's project declares.
 */
const MAX_CITIES_PER_LINE = 200;
/**
 * `description` caps here — the same 16,000 the quote's own `terms` takes.
 *
 * Checked before sending rather than enforced with a `maxLength` on the box: a
 * `maxLength` truncates a PASTE silently, and the tail it drops is wording a
 * customer was meant to read. A refusal that names the limit is recoverable; a
 * paste that arrives 400 characters short is not noticed.
 */
const MAX_DESCRIPTION_LENGTH = 16000;

/**
 * The default explanation for a delete control an account cannot use.
 *
 * ⚠️ **DELETE on a line is `ADMIN_ROLES` while POST and PATCH are
 * `WRITE_ROLES`.** A `basic_user` can add a line and edit it and cannot remove
 * it again — an asymmetry nobody would predict, and one that has to be visible
 * before the row is built rather than discovered afterwards. The wording
 * matches `denialReason()` so the app says one thing.
 */
// 2026-10-01: deletes follow the set. *SUPERSEDED: "restricted to administrators".*
const DEFAULT_DELETE_DENIAL =
  'Your permission set does not include this. An administrator can change that.';

/**
 * The stand-in for `convertLineErrors` outside convert mode.
 *
 * ⚠️ **A module constant, never an inline `{}`.** It is read by a `useMemo` that
 * merges the server's refusals into every row's problems, so a fresh object
 * literal each render would give that memo a new dependency identity on every
 * keystroke in the quote's own header fields.
 */
/*
 * ⚠️ **KEYED BY ROW KEY, not by `dealLineId`.** *SUPERSEDED:
 * `Readonly<Record<number, Partial<Record<ConvertLineField, string>>>>`.* A
 * `lineSet` refusal names an ARRAY POSITION, and a row added on this screen has
 * no `dealLineId` to be addressed by — so the parent routes position → row key
 * through the `rowKeys` array this grid emitted alongside the entries. The value
 * is the same `Record<string, string>` shape as `Row.fieldErrors`, so the merge
 * below stays one line.
 */
const EMPTY_LINE_ERRORS: Readonly<Record<string, Record<string, string>>> = {};

/* ── The two pinned columns ───────────────────────────────────────────────── */

/*
 * ⚠️ **AMOUNT AND THE ACTIONS COLUMN ARE PINNED TO THE TRAILING EDGE, and the
 * alternative was measured and does not exist.**
 *
 * Measured on quote 7 at 1440×900, when the `<colgroup>` summed to 1400 (it is
 * **1492** since 2026-08-31 — Discount 148 → 240 for the mode switch, which
 * makes the shortfall below WIDER by 92 and changes none of the reasoning; see
 * the width block down in the render), the `TableWrap` is 1087px
 * with the sidebar expanded and 1198px collapsed, and AMOUNT's right edge lands
 * at 1452px. So **the figure the whole row exists to produce is the one thing
 * that is never on screen**, and collapsing the sidebar does not rescue it.
 *
 * ⚠️ **Do NOT fix this by narrowing columns, and this is arithmetic rather than
 * taste.** Two passes did exactly that and both reintroduced clipped values —
 * the owner's original defect. The whole saving available from trimming every
 * column back to its content minimum is about 128px against a 202px shortfall,
 * so it cannot work even in principle: the third pass would have had to take the
 * remaining 74px out of digits. Qty currently has 38px of room for `2`, Price
 * 88px for a `123,456.00` placeholder, Discount 216px for a two-option
 * segmented control that MEASURES 193. Those are the numbers to protect.
 *
 * ⚠️ **The sum was still 1400 after Project and Cities left the table**, which
 * was deliberate: their 290px went to Item and Description rather than to
 * narrowing the table, so none of the measurements above moved and the pinning
 * was unchanged. Do not read the eight-column list as a saving that was banked.
 *
 * ⚠️ **Growing the table to 1492 did not disturb the pinning either, and the
 * reason is worth keeping: the offset below is the ACTIONS COLUMN'S width, not
 * the table's total.** A wider table makes the two pinned columns more
 * necessary, never less; the only `<col>` that may not move without moving
 * `right-[94px]` with it is the last one.
 *
 * So the middle of the table scrolls UNDER the two columns that must not move.
 * Three details, each of which is a way this goes wrong:
 *
 * 1. **The offset is the actions column's own width**, 94px, and it is written
 *    here beside the `<colgroup>` value it must equal. Change one and change
 *    both, or the Amount column overlaps its neighbour by the difference.
 * 2. **The pinned cells must be OPAQUE**, or the scrolling cells show straight
 *    through them. The background is a token in both themes and is chosen by a
 *    ternary at the call site rather than by two `bg-` utilities on one element:
 *    same-property Tailwind utilities resolve by stylesheet order, not by the
 *    order they are written in, so `cx('bg-surface', failed && 'bg-…')` is a coin
 *    toss rather than an override.
 * 3. **The leading hairline is an inset shadow, not `border-l`.** The table is
 *    `border-collapse: collapse`, under which a border declared on a cell is
 *    collapsed into the table's own grid and painted in the TABLE's coordinate
 *    space — so it would stay behind while the sticky cell slid away from it. An
 *    inset shadow paints on the element and travels with it. The row's own
 *    `border-b` is unaffected: it is a full-width horizontal line, so it still
 *    runs under the pinned cells wherever they are.
 *
 * Unscrolled — a wrapper at 1492px or wider — sticky positioning does nothing at
 * all and these read as an ordinary column with a rule down its leading edge.
 * That is why the affordance is a hairline and not a shadow or an elevation:
 * house style is hairline borders for structure, and a floating panel that only
 * sometimes floats is worse than a column rule that always reads as one.
 *
 * ⚠️ **None of this may reach the `md:hidden` card**, which is not a table and
 * has no horizontal scroll to pin against.
 */
const PIN_AMOUNT = 'sticky right-[94px] z-10 shadow-[inset_1px_0_0_var(--hairline)]';
/** Hard against the trailing edge; the Amount column is offset by its width. */
const PIN_ACTIONS = 'sticky right-0 z-10';
/*
 * ⚠️ *SUPERSEDED 2026-10-18: `PIN_HEAD_BG` (`bg-surface-sunken`), "`THead`'s own
 * surface, so a pinned header cell is opaque against the header".* The header
 * row is frozen now and the two pinned HEADER cells use `TH frozen="right"`,
 * whose combined literal carries the same surface. These constants are BODY-only.
 */
/** An ordinary body row: the `Panel`/`Modal` surface the table sits on. */
const PIN_ROW_BG = 'bg-surface';
/**
 * A FAILED row's pinned cells.
 *
 * ⚠️ **The row tint is `bg-danger-soft/40` and a pinned cell cannot use it** —
 * 40% of anything is transparent, and a translucent pinned cell is the defect
 * this pins are meant to fix, with the scrolling row showing through. So the
 * exact composite is computed instead: alpha compositing is linear in sRGB, so
 * `color-mix(in srgb, danger-soft 40%, surface)` is byte-for-byte what
 * `bg-danger-soft/40` over `bg-surface` paints. Both halves are `:root`
 * variables that the theme swaps, so this needs no `dark:` variant and cannot
 * drift from the tint on the rest of the row.
 *
 * Without it a failed row would look entirely ordinary in the only two columns
 * that stay on screen.
 */
const PIN_ROW_BG_FAILED = 'bg-[color-mix(in_srgb,var(--danger-soft)_40%,var(--surface))]';

/* ── Props ────────────────────────────────────────────────────────────────── */

/**
 * Whether a quote exists behind this grid, and where its rows come from.
 *
 * - `saved` — the default and the shape this component has always had. Every
 *   edit is a request against `/quotes/{id}/products`.
 * - `draft` — no quote exists yet. Rows are held locally, **no line request is
 *   issued**, and the payloads are reported through `onDraftLinesChange` for
 *   the parent to write once the quote has an id.
 * - `convert` — no quote exists yet **and no row here will ever be POSTed**.
 *   The rows are a DEAL's lines, and what goes upward is a list of per-line
 *   OVERRIDES for the copy `POST /deals/{id}/convert` is about to make. See
 *   the block in this file's header.
 */
export type QuoteLineItemsMode = 'saved' | 'draft' | 'convert';

interface QuoteLineItemsCommonProps {
  /**
   * **The QUOTE's own `cities`** — `quote.cities`, the header project's whole
   * set, derived on every read.
   *
   * ⚠️ **It is NOT a line's option list, and it is no longer what the server
   * validates against.** Since migration 067 a line names its own project, and
   * `cityIds` is checked against THAT project's cities. Offering the header's
   * set on a line whose project differs would offer guaranteed 422s.
   *
   * It is kept for two honest uses: naming a city we have no other record of,
   * and describing what the header project covers. Never pass `/cities` here
   * either — the master offers rows every line write refuses wholesale.
   */
  cities: ProjectCity[];
  /**
   * Whether the QUOTE carries a project at all — `quote.project !== null`.
   *
   * A line's own project is what governs its cities, so this only ever words a
   * hint. ⚠️ `quote.project` being null does not *prove* the quote has no
   * project — the lookup swallows its own failures — so the copy says "none
   * recorded" and never asserts.
   */
  hasProject: boolean;
  /** `quote.currency`. Rendered beside every figure; never converted. */
  currency: string | null;
  /**
   * The COMPANY's own sales tax — **for DISPLAY and PREVIEW, never for a
   * payload.** See {@link CompanySalesTax}, which carries the whole rule.
   *
   * A created line already takes this figure server-side
   * (`input.taxRate ?? companyRate`), so the database is right today and the
   * SCREEN was not: the Tax rate control read "Xero default" and the Amount
   * cell previewed tax at 0.00 on every new row, because a blank `taxRate`
   * resolves to `'0'` in the arithmetic. This prop is what lets both say what
   * the save will actually do. Nothing here reaches `createPayload` or
   * `updatePayload`.
   *
   * ⚠️ **OPTIONAL, and `{ known: false }` when it is absent — which must change
   * nothing on screen.** A mount that does not pass it renders exactly as this
   * grid rendered before the prop existed.
   *
   * ⚠️ *SUPERSEDED 2026-09-27: "`QuoteDetailPage` is such a mount and is
   * deliberately untouched: it has no company read of its own to source this
   * from, and inventing one would be a second request per page load for a
   * label."* Owner's decision: it is not a label — a new row on that page
   * previewed tax at 0.00 over a save taxed at the company rate. The page now
   * reads the company once and passes this, built as `QuoteFormModal` builds it.
   *
   * ⚠️ **{@link QuoteLineItemsConvertProps} narrows it to REQUIRED**, because
   * there the Tax rate column is a readout of this and nothing else.
   */
  companySalesTax?: CompanySalesTax;
  /** `ability.canUpdate('quotes')`. False renders the table read-only. */
  canEdit: boolean;
  /** `ability.canDelete('quotes')` — admin tier, unlike create and update. */
  canDelete: boolean;
  /**
   * Why this account cannot delete, from `denialReason(ability, 'delete',
   * 'quotes')`. Shown on the disabled control rather than hiding it, because a
   * missing control does not explain the create/delete asymmetry.
   */
  deleteDenialReason?: string;
  /**
   * Rendered above the save report — for a parent that created the quote in
   * the same action and needs to say so ("Quote QU-0007 created"). Someone who
   * closes a form after a partial failure believing nothing was created will
   * make a second quote.
   */
  contextNote?: ReactNode;
  /**
   * A save run WROTE at least one line, or a line was DELETED — the document's
   * line items are no longer what a parent fetched.
   *
   * ⚠️ **A NOTIFICATION, NOT A RESULT.** It carries nothing and promises
   * nothing about whether every targeted row landed; `saveAll`'s own boolean
   * answers that, for {@link QuoteLineItemsSavedProps.registerSave}. It fires
   * on a PARTIAL run too — some rows written, others refused — because the
   * parent's copy is stale either way, and a partial run is exactly when a
   * stale list is most misleading.
   *
   * ⚠️ **It is NOT {@link QuoteLineItemsSavedProps.onLinesChanged}, and the two
   * are deliberately not one prop.** `onLinesChanged` says the four header
   * TOTALS moved, and its readers re-fetch the quote they are showing. This
   * says a grandparent LIST — the one carrying the server-computed Projects
   * badge, derived live from these very rows — is now out of date. The two fire
   * at the same moments today and answer to different readers, and collapsing
   * them would make a list refetch conditional on a modal wanting a totals
   * refresh.
   *
   * ⚠️ **It does NOT fire when nothing was written.** `saveAll`'s two early
   * returns (no quote id; no unblocked dirty rows) leave the parent's copy
   * perfectly current, and firing there would make this mean *"somebody pressed
   * a button"* rather than *"the data moved"* — which is the only reading a
   * parent can safely act on.
   *
   * Declared on the COMMON props so all three mounts can pass one value. It is
   * inert in draft and convert mode by construction rather than by a `never`:
   * neither writes a line, so neither can reach a fire site.
   */
  onRowsSaved?: () => void;
}

/** A quote exists: every edit is a request. The original behaviour, unchanged. */
export interface QuoteLineItemsSavedProps extends QuoteLineItemsCommonProps {
  mode?: 'saved';
  quoteId: number;
  /**
   * Called after any successful line write. Every one of them rewrites the
   * quote's four header totals in the same transaction and returns none of
   * them, so the parent must re-fetch the quote.
   */
  onLinesChanged?: () => void;
  /**
   * Hands the page the SERVER'S report of what a save run did to Xero.
   *
   * ⚠️ **Added 2026-08-30 with the move of the push onto the server.** Every
   * line write pushes the parent quote inside the same request and answers
   * `xeroPush` beside `data` — see `SaveEnvelope` in `@/lib/types`. This grid
   * pushes nothing and never did; what changed is that it can now say what the
   * server did.
   *
   * ## ⚠️ WHAT A RUN OF N SAVES REPORTS, AND WHY IT IS NOT A SUMMARY
   *
   * `saveAll` writes N rows one at a time, so a run produces N reports. Exactly
   * one thing is handed up:
   *
   * - **the FIRST report reading `failed`, if any row's did.** A failure must be
   *   visible — that is the whole reason the key exists on the wire.
   * - **`undefined` otherwise**, which every reader turns into "say nothing".
   *   A `pushed` needs no announcement, and `skipped` is the ORDINARY answer
   *   for every quote that is not in Xero yet, which is most of them: alarming
   *   on it would put a warning on nearly every line edit in the system.
   *
   * ⚠️ **The first failure, never a count and never a synthesised sentence.**
   * Two failures in one run are almost always one cause — the same company
   * unlinked, the same tax code refused — and *"3 problems"* is a claim nothing
   * verified. One real refusal, in the server's own words, is what somebody can
   * act on.
   *
   * ⚠️ **`undefined` is an argument, not an omission.** It CLEARS whatever the
   * page is showing, which is right: a standing failure from an earlier save is
   * stale once a later save has pushed. Nothing may read an absent report as
   * *"nothing was sent"* — see `autoPushOutcomeOf`, where that is settled once.
   *
   * ⚠️ **Nothing is reported for a DELETE.** `DELETE /quotes/{id}/products/{lineId}`
   * answers `204`, and a `204` has no body to carry a report — the delete DOES
   * push on the server, it simply cannot say so. See `confirmDelete`.
   */
  onXeroPush?: (report: XeroPushReport | undefined) => void;
  /**
   * How many rows are unsaved, and how many of those cannot be sent.
   *
   * ⚠️ **THIS EXISTS BECAUSE A PARENT WITH ITS OWN SAVE BUTTON WAS LYING.**
   * `QuoteFormModal` disables its footer Save on `!isDirty`, where dirty means
   * the HEADER form — so somebody who edited only line items met a greyed-out
   * "Save changes" while every row beside it read "Unsaved". Measured in the
   * audit log on 2026-09-01: `GET /quotes/392/products` twice and **not one
   * write** — the person pressed the control they could see and it issued
   * nothing. A grid that knows it has unsaved work and never says so upward
   * leaves the parent asserting the opposite in the one place a reader looks.
   *
   * Reported for saved mode only. Draft and convert rows have nowhere to be
   * saved to and their parents already count them through their own props.
   */
  onUnsavedChange?: (state: { dirty: number; blocked: number }) => void;
  /**
   * Hands the parent this grid's own save so one control can finish the job.
   *
   * Called with the function after every render — the closure must be the
   * current one — and with `null` on unmount. It resolves `true` when every
   * row the run targeted landed, so a parent can decide whether to close.
   *
   * 🔴 **PASSING IT HIDES THE GRID'S OWN SAVE CONTROL, AND THAT IS A REVERSAL.**
   * *SUPERSEDED: "The grid still renders its own Save and its own per-row
   * report. This is a second way to reach the same run, not a replacement for
   * it."* Owner's instruction, 10 September 2026: one button, reading "Save
   * changes", with no second save-looking control beside it.
   *
   * **The per-row report is UNCHANGED**, and the half of the old note that
   * argued for it still stands: the report is row-by-row and belongs beside the
   * rows, and a parent that swallowed it would be announcing an outcome it
   * cannot see. Only the BUTTON goes.
   *
   * ⚠️ **A parent that takes the save MUST call it.** The grid stops offering
   * any way to write a row the moment this prop is present, so a parent that
   * registers and never calls leaves line edits with nowhere to go — and
   * nothing on screen would say so.
   */
  registerSave?: (save: (() => Promise<boolean>) | null) => void;
  onDraftLinesChange?: never;
  onDraftBlockedChange?: never;
  /** There is a real basket to read, so nothing may seed rows over it. */
  initialDraftRows?: never;
  dealLines?: never;
  dealLinesLoading?: never;
  dealLinesError?: never;
  convertLocked?: never;
  convertLineErrors?: never;
  onConvertLinesChange?: never;
  onConvertBlockedChange?: never;
}

/**
 * No quote exists yet: hold lines locally, issue no line request, and report
 * them upward. The parent writes them after the quote is created.
 */
export interface QuoteLineItemsDraftProps extends QuoteLineItemsCommonProps {
  mode: 'draft';
  /** There is no quote to address, so there is no id to take. */
  quoteId?: never;
  onLinesChanged?: never;
  /** No request is issued in draft mode, so there is no push and no report. */
  onXeroPush?: never;
  /**
   * Neither applies: there is no save run to register and nothing "unsaved" in
   * the sense the saved-mode pair reports — `onDraftLinesChange` and
   * `onDraftBlockedChange` already tell the parent everything about these rows.
   */
  onUnsavedChange?: never;
  registerSave?: never;
  /**
   * The lines as they stand, in grid order, **exactly the body
   * `POST /quotes/{id}/products` takes** — built by the same `createPayload()`
   * the saved mode POSTs, so the two cannot diverge. Each carries an explicit
   * `sortOrder` (its index), because the parent may write them in any order and
   * `sort_order ASC` is the order the customer's document prints in.
   *
   * ⚠️ **Only rows that would be ACCEPTED are included.** A row with no product,
   * a bad quantity, an out-of-range percentage, a city its project does not
   * declare, or a `(product, project)` pair another row already claims is
   * withheld and counted by {@link onDraftBlockedChange} instead. Creating the
   * quote and then collecting a 422 for a line the person can no longer see is
   * the failure this split prevents — so **read the blocked count before
   * submitting**, never `lines.length` alone.
   *
   * Fires on every change, and only on a change: the callback's own identity is
   * not a trigger, so an inline arrow in the parent is safe.
   */
  onDraftLinesChange: (lines: QuoteProductCreateInput[]) => void;
  /**
   * How many rows are currently withheld from `onDraftLinesChange`. `0` means
   * every row on screen will be written.
   *
   * Optional, and the grid already refuses to hide the problem — it renders the
   * same blocking banner it does in saved mode. This exists so the parent can
   * disable its own "Create quote" button rather than letting someone submit
   * and silently lose a row.
   */
  onDraftBlockedChange?: (blockedRows: number) => void;
  /**
   * Rows the grid OPENS WITH — a planned basket, seeded once at mount.
   *
   * ⚠️ **Read in the `useState` INITIALISER and never again.** An effect that
   * re-ran would re-seed rows the person had already edited or deleted, and
   * deleting a line and watching it come back is worse than no prefill at all.
   * So the array's identity is not a trigger and a parent may build it inline.
   *
   * ⚠️ **A SEED IS NOT A LINE.** These rows are ordinary draft rows the moment
   * they exist: editable, deletable, and written through the same
   * `onDraftLinesChange` payloads as a row somebody typed. Nothing here is
   * pinned and nothing is written twice.
   *
   * ⚠️ **`lineQuantity: null` SEEDS AN EMPTY QUANTITY BOX AND BLOCKS THE SAVE.**
   * A planned `"2.50"` is not a line quantity, and the two wrong answers are
   * both silent: rounding it to 1 puts a figure nobody chose on a customer's
   * document, and leaving the box empty writes NOTHING — which the server reads
   * as 1, arriving at the same wrong number by a longer route. See
   * `validateRow`, which refuses exactly this row, and {@link
   * onDraftBlockedChange}, which is how the parent hears about it.
   */
  initialDraftRows?: readonly PlannedProductLine[];
  dealLines?: never;
  dealLinesLoading?: never;
  dealLinesError?: never;
  convertLocked?: never;
  convertLineErrors?: never;
  onConvertLinesChange?: never;
  onConvertBlockedChange?: never;
}

/**
 * A DEAL is being converted: the rows are the deal's lines, and what this grid
 * produces is the COMPLETE FINAL LIST of lines the server is about to create.
 *
 * ## ⚠️ NOTHING HERE IS EVER POSTED AS A QUOTE LINE
 *
 * `POST /deals/{id}/convert` creates every line of the new Draft quote **inside
 * the transaction that creates it**, from the `lineSet` this grid emits. A row
 * written from this grid as `POST /quotes/{id}/products` would be a SECOND line
 * beside the one the server already made — a quote billing the customer twice
 * for the same work, from a control that looks exactly like the one on a blank
 * form. **This mode must never write a line.**
 *
 * ## ⚠️ CORRECTED: THE STRUCTURE IS EDITABLE NOW, AND ROWS MAY BE ADDED AND REMOVED
 *
 * *SUPERSEDED, in full: "## ⚠️ THE CONTRACT ADMITS FIVE KEYS PER LINE, AND NO
 * ADD AND NO DELETE — An override NAMES AN EXISTING deal line by its
 * `dealLineId`, so there is no way to add a line and no way to remove one at
 * conversion time. That is the ENDPOINT deciding it, not a preference: an Add
 * row control here would build a row nothing could carry, and a Delete control
 * would offer an action that cannot be performed … The five keys are
 * `quantity`, `unitPrice`, the discount PAIR and `description`. **`taxRate` is a
 * 422** … and so is `productId`, `projectId`, `cityIds`, `regionIds` and
 * `inclusions`."*
 *
 * **Every word of that was true of `lines`, and `lines` is not what this mode
 * sends any more.** The endpoint gained `lineSet` — the complete final list —
 * and with it the four things the old paragraph correctly said the endpoint
 * could not express:
 *
 * - a row's **product**, **project**, **cities**, **regions** and
 *   **inclusions** are live controls, because the entry carries all five;
 * - a row **added** here rides on the conversion, as an entry with no
 *   `dealLineId` and a required `productId`;
 * - a row **removed** here is simply absent from the array, and an absent row
 *   does not appear on the quote;
 * - the row ORDER is `sortOrder`, so it is the order the customer's document
 *   prints in.
 *
 * ⚠️ **THE ONE HALF THAT SURVIVES UNCHANGED IS THE TAX.** `taxRate` and
 * `xeroTaxType` are on NEITHER key and sending either is a 422 — a converted
 * line's rate is seeded from the COMPANY inside the conversion — so the Tax rate
 * column stays a READOUT. A control that accepts a value the request cannot
 * carry is a silent discard, and that is still exactly what a live tax control
 * here would be.
 */
export interface QuoteLineItemsConvertProps extends QuoteLineItemsCommonProps {
  mode: 'convert';
  /** There is no quote to address, so there is no id to take. */
  quoteId?: never;
  onLinesChanged?: never;
  /** No request is issued in convert mode, so there is no push and no report. */
  onXeroPush?: never;
  /** Nothing is saved from this mode; the conversion writes every row at once. */
  onUnsavedChange?: never;
  registerSave?: never;
  onDraftLinesChange?: never;
  onDraftBlockedChange?: never;
  /** The deal's own lines are the seed on this path — see `dealLines`. */
  initialDraftRows?: never;
  /**
   * The deal's own lines, in the order they were read.
   *
   * ⚠️ **`null` is "not read", never "none".** A failed read must not be
   * described as an empty basket — every line still crosses either way, and the
   * three states are rendered apart for exactly that reason.
   *
   * ⚠️ **Its IDENTITY re-seeds every row.** The grid holds each row's boxes in
   * its own state and rebuilds them whenever this array changes, so a parent
   * that rebuilds it on every render would wipe what somebody is typing. Hold it
   * in state, as `QuoteFormModal` does; do not map or filter it inline.
   */
  dealLines: DealProduct[] | null;
  /** The read is in flight. The grid renders its own skeletons. */
  dealLinesLoading: boolean;
  /** Why the read failed, or `null`. There is no retry here — the parent owns the fetch. */
  dealLinesError: string | null;
  /**
   * The conversion has LANDED and the boxes are no longer live.
   *
   * 🔴 **This is the one place an editable table could lie.** Once
   * `POST /deals/{id}/convert` has succeeded the quote exists and its lines are
   * already written from whatever overrides were sent — a retried Save re-sends
   * the HEADER PATCH and deliberately never converts a second time. A box left
   * live there is a control somebody types a new price into, presses Save, and
   * watches succeed with the old price on the quote: no error, nothing in a log,
   * and a customer document that disagrees with what was on screen.
   */
  convertLocked: boolean;
  /**
   * The server's per-line refusals, **keyed by this grid's own ROW KEY**.
   *
   * ⚠️ **CORRECTED.** *SUPERSEDED: "keyed by `dealLineId` … ⚠️ **Not by
   * position.** A 422 names `lines.2.quantity`, and `2` is a position in the
   * OVERRIDES array that was sent — shorter than the deal's own line list,
   * because unchanged rows carry no entry."* On `lineSet` the position IS the
   * link and there is nothing else it could be: **a row added on this screen has
   * no `dealLineId` to be addressed by.** The array is also no longer shorter
   * than the grid — every row is an entry — so position maps one-to-one onto the
   * `rowKeys` this grid emits beside the entries.
   *
   * `convertLineSetFieldErrors` in `@/lib/deal-convert-lines` is the one place
   * that translation happens, and it is still the PARENT's to call, because the
   * parent is what holds the arrays it actually sent.
   */
  convertLineErrors: Readonly<Record<string, Record<string, string>>>;
  /**
   * The lines as they stand, in grid order — **exactly the `lineSet` array
   * `POST /deals/{id}/convert` takes**, plus the two things only this grid can
   * say about it.
   *
   * ⚠️ **CORRECTED.** *SUPERSEDED: "Only CHANGED rows appear, and an empty array
   * means send no `lines` key at all."* **EVERY row appears**, because `lineSet`
   * is the complete final list and an omitted row is a line the quote will not
   * have. What is still true is the OMISSION RULE, and it now hangs off
   * `pristine` rather than off a length: a conversion nobody edited must send no
   * `lineSet` key at all, because an absent key is today's proven behaviour and
   * an array restating every line is a request shape nothing has exercised.
   *
   * ⚠️ **`entries` and `rowKeys` are the SAME LENGTH IN THE SAME ORDER, built in
   * one pass**, and they are handed over together for exactly that reason — the
   * parent must be able to turn `lineSet.3.productId` back into a row, and two
   * arrays produced separately would agree until the day they did not.
   *
   * ⚠️ **Blocked rows are NOT withheld**, unlike draft mode's. A refused row is
   * not dropped on the floor, it takes the whole conversion with it — so the
   * answer is to refuse to submit, which is what
   * {@link QuoteLineItemsConvertProps.onConvertBlockedChange} is for. Withholding
   * it instead would silently convert without a line somebody had added.
   *
   * ⚠️ **`null` while the deal's lines have not been read.** Never `[]`: an
   * empty `lineSet` is a quote with NO LINES, so a grid that has not loaded yet
   * must be unable to say anything at all rather than able to say *none*.
   *
   * Fires on every change and only on a change, so an inline arrow is safe.
   */
  onConvertLinesChange: (
    next: {
      entries: DealConvertLineSetEntry[];
      rowKeys: string[];
      /** Every row is the deal's own, in its own order, untouched. */
      pristine: boolean;
    } | null,
  ) => void;
  /**
   * How many rows carry a refusal of OURS. `0` means the conversion can be sent.
   *
   * ⚠️ **Client refusals only.** A server error already sits beside the box that
   * caused it and Convert must stay pressable so the correction can be re-sent;
   * counting one here would disable the one control that clears it.
   */
  onConvertBlockedChange: (blockedRows: number) => void;
  /**
   * The COMPANY’s sales tax, which is what every converted line will be given.
   *
   * ⚠️ **REQUIRED HERE, though it is optional on the common props.** A converted
   * line has no tax rate of its own — `f_deal_products` has no such column — so
   * this is the only figure the Tax rate column can honestly print, and an
   * optional prop would let a call site forget the loader and render the "not
   * read" wording for ever with nothing failing. Narrowing an inherited optional
   * to a required member is the one instrument here that catches a dead loader
   * at compile time, and it is why this line is restated rather than inherited.
   *
   * ⚠️ **Read from the company record the parent already fetches.** Do NOT look
   * the percentage up from `GET /xero/tax-rates`: the server derives and owns
   * this figure, and a client that computes it is a second implementation of a
   * number printed beside a quote.
   */
  companySalesTax: CompanySalesTax;
}

/**
 * The COMPANY's sales tax, as this grid is able to know it.
 *
 * Three states, and the third is the one a boolean would lose. `known: false`
 * is *the company record has not been read* — in flight, failed, or a form that
 * has since moved to another company — and is **not** the same fact as a company
 * that states no rate. Reporting an unread value as an absence is a claim about
 * somebody else’s tax treatment made from a request that never landed.
 *
 * `rate` is the server’s own DECIMAL string (`"20.00"`), never a number and
 * never parsed here: `null` means the company states none, and the line then
 * arrives un-taxed. **`null` is not `"0.00"`** — that would be a company
 * somebody deliberately zero-rated, which is a different answer to the same
 * question, and `NONE` and `ECZROUTPUTSERVICES` are both genuinely 0%. Same
 * shape as `catalogue.known` on {@link PriceControl}.
 *
 * `taxType` is the company's Xero CODE (`"OUTPUT2"`), which is what a blank
 * `xeroTaxType` on a line resolves to at push time. ⚠️ **It is only ever
 * NAMED, never turned into a percentage** — deriving the figure from the code
 * against the tenant's published `EffectiveRate` would be a second
 * implementation of a number the server owns. ⚠️ **And it is resolved with
 * {@link sameTaxCode} before it is shown**: casing is normalised server-side,
 * so a stored `output2` against a live `OUTPUT2` is the same rate.
 *
 * ⚠️ **THIS IS READ AND NEVER SENT, on every mount.** The server already seeds
 * a created line from the company (`input.taxRate ?? companyRate`), so a client
 * that put this figure in a payload would be a SECOND writer of it — and a
 * stale one, since a rate changed while the modal sat open would be written in
 * preference to the current one. Every use below is display or preview.
 */
export type CompanySalesTax =
  | { known: false }
  | { known: true; rate: string | null; taxType: string | null };

/** The stable "not read" identity, so a mount that passes none makes no object. */
const COMPANY_SALES_TAX_UNREAD: CompanySalesTax = { known: false };

export type QuoteLineItemsTableProps =
  | QuoteLineItemsSavedProps
  | QuoteLineItemsDraftProps
  | QuoteLineItemsConvertProps;

/* ── The draft a row is edited as ─────────────────────────────────────────── */

/**
 * What the Price box means.
 *
 * Three states, and two of them are easy to collapse into one wrongly:
 *
 * - `inherit` — the key is **omitted**. On a new row that snapshots the
 *   catalogue price; on an existing row it leaves the stored price alone.
 * - `value` — an explicit decimal string.
 * - `none` — an explicit `null`: **no price on this line**, which is not the
 *   same fact as a price of `"0.00"`, even though both make the three
 *   generated columns read `"0.00"`.
 */
type PriceMode = 'inherit' | 'value' | 'none';

interface InclusionDraft {
  inclusionId: number;
  /** Empty means "omit the key", which the server reads as 1. Zero is refused. */
  quantity: string;
}

interface Draft {
  productId: string;
  /**
   * What Xero prints for this line — **`null` means DERIVE, never "empty"**.
   *
   * While it is `null` the cell follows {@link composeLineDescription} over the
   * line's project, item and cities, and the backend composes the same string
   * again when it pushes. A string is the person's own wording: stored, sent
   * verbatim, and never added to.
   *
   * ⚠️ It is a real `Draft` key **as of the column landing on 2026-08-26**, and
   * for a long time it deliberately was not — see this file's header. Being a
   * key is what makes {@link isDirty} move when somebody types, which is what
   * makes the change saveable at all.
   *
   * `''` is not a third state. It is folded into `null` by
   * {@link descriptionForWrite} before any payload is built, so an emptied box
   * and `Reset to derived` cannot mean two different things.
   */
  description: string | null;
  /**
   * The LINE's own project. `''` is "no project", which is a real and legal
   * state — some products (`Meet Up`, `Profile Evaluations`, `Miscellaneous`)
   * are not sold through projects at all and never will be.
   *
   * It is half of the line's identity: `(product, project)`.
   */
  projectId: string;
  /**
   * **The WHOLE SET of cities this line runs in.** `[]` is "not city-specific",
   * which is the ORDINARY case rather than an omission — most lines name none.
   *
   * Order is the order the server gave (its own `sortOrder` within the line),
   * then the order the user added in. Never re-sorted, and never a source of
   * arithmetic: the money is on the line, not per city.
   */
  cityIds: number[];
  /**
   * **This line means EVERY city its project has**, resolved live rather than
   * listed out. Migration 083.
   *
   * ⚠️ **It is a FLAG, not a shortcut for ticking everything.** A flagged line
   * re-resolves against its project on every read, so a city added to that
   * project next month is on this line automatically; a line that merely lists
   * every city today keeps the list it was given. The two look identical on
   * screen right now and diverge the moment the project changes, which is why
   * "all" is never implemented by expanding {@link Draft.cityIds} at save time.
   *
   * ⚠️ **It participates in dirty detection exactly as `cityIds` does** —
   * ticking it alone marks the row unsaved, and `Revert` puts it back, because
   * {@link draftOf} seeds it from the record rather than from this session.
   *
   * ⚠️ **Ticking it does NOT clear `cityIds`, deliberately.** The explicit set
   * is PARKED, not destroyed, so unticking hands it straight back — see
   * {@link CitiesControl}, which argues that choice. Nothing can send both
   * keys: the payload builders branch, and the pair is a 422.
   */
  allCities: boolean;
  /**
   * **SEED's regions of focus for this line — the WHOLE SET.** Migration 112.
   *
   * ⚠️ **Nothing to do with the cities above, and it is offered in the
   * OPPOSITE case.** Cities are named THROUGH a project, so only a product sold
   * through projects has any; a product that is NOT — `EMBA`, `LAE`,
   * `Virtual Masterclass` — recorded no geography anywhere at all until this
   * existed. ⚠️ *SUPERSEDED: "{@link showsRegionsControl} therefore shows this
   * control exactly where {@link showsProjectControl} takes its block away."*
   * **There is a SECOND case now** — a line whose project declares no cities —
   * and there the PROJECT block stays while the CITY block goes. So the set is
   * offered exactly where {@link showsCitiesControl} takes its block away, which
   * is the wider statement and covers both cases: the two gates read one
   * `projectHasNoCities` between them.
   *
   * ⚠️ **There is no `allRegions` flag and none may be invented.**
   * {@link Draft.allCities} is a live RULE, re-resolved against a project on
   * every read; a line's regions are always its own explicit set, so `[]` here
   * is simply “none” and means it.
   *
   * ⚠️ **Order is not stored.** The read hands these back in the master's own
   * order rather than in a per-line `sortOrder`, which is why
   * {@link regionsKey} is order-INSENSITIVE where {@link citiesKey} is not.
   */
  regionIds: number[];
  quantity: string;
  priceMode: PriceMode;
  price: string;
  /**
   * The line's price tier (migration 328, owner 2026-10-27). `null` = the
   * default tier. It picks the project price entry an UNTOUCHED price follows;
   * a typed price is never overwritten by it.
   */
  priceTierId: number | null;
  /**
   * The discount as a PERCENTAGE, as typed. Live only while
   * {@link Draft.discountAmount} is `null`.
   */
  discount: string;
  /**
   * The discount as an EXACT MONEY AMOUNT, as typed — and the MODE SWITCH.
   *
   *  - `null` — the box is taking a PERCENTAGE, the ordinary case;
   *  - a string (`''` included) — the box is taking an AMOUNT.
   *
   * ⚠️ **A real `Draft` key, unlike the screen-state map it replaced**, for the
   * same reason `description` is one: there is a column behind it, so it has to
   * dirty the row, reach a payload, survive a reload and revert with the rest.
   *
   * ⚠️ **NOTHING CONVERTS BETWEEN THE TWO.** The old amount box wrote
   * `draft.discount` on every keystroke, losing pounds; the switch now moves
   * only itself and leaves both figures where they are.
   */
  discountAmount: string | null;
  taxRate: string;
  /**
   * Xero's tax CODE. `''` is `null`, which means **"use the company's Xero
   * sales default"** and never "no tax" — the label says so, because rendering
   * it as "no tax" states a VAT treatment nobody chose on a document a
   * customer is invoiced against.
   */
  xeroTaxType: string;
  /** Ordered — the index becomes each entry's `sortOrder`. */
  inclusions: InclusionDraft[];
  /**
   * The INTERNAL line note (owner 2026-10-18). Never sent to Xero, never on a
   * PDF; separate from `description`.
   *
   * ⚠️ **Read by the CREATE paths only** — `createPayload` (draft rows and new
   * rows in saved mode) and the convert `lineSet` entry. A SAVED line's note is
   * written on its own by {@link QuoteLineItemsTable}'s `saveLineNote` as a
   * notes-only PATCH, which the server lets through every lock and which never
   * pushes to Xero. So `updatePayload` and `isDirty` deliberately ignore it:
   * folding it into the ordinary PATCH would ride a note on a body the line lock
   * refuses, and would push to Xero for an edit Xero never sees.
   */
  notes: string | null;
}

type RowState = 'idle' | 'saving' | 'saved' | 'failed';

interface Row {
  /** Stable across re-renders and independent of the server id, which new rows lack. */
  key: string;
  /** The last state the server confirmed. `null` on a row that has never been saved. */
  saved: QuoteProduct | null;
  /**
   * The DEAL line this row was seeded from — **`mode: 'convert'` only**, and
   * `null` on every row in the other two modes.
   *
   * ⚠️ **A converted row is NOT a saved quote line, and it must not pretend to
   * be one.** A synthetic `QuoteProduct` was the obvious alternative and was
   * rejected: `f_deal_products` has no `tax_rate`, no `line_subtotal`, no
   * `line_tax`, no `all_cities` and no quote id, so half of that object would be
   * a value nobody measured, rendered by a cell that had no idea. This codebase
   * already catalogues *a TYPE asserting what the runtime lacks* as its own
   * class of defect. Keeping `saved` at `null` and the deal line here makes
   * every one of those fabrications an explicit branch instead.
   *
   * ⚠️ It is REQUIRED on the interface, not optional. An optional member would
   * let a row be built without deciding, and this file's own rule is that
   * `tsc` refusing a call site is the only guard nobody can forget.
   */
  convert: DealProduct | null;
  draft: Draft;
  state: RowState;
  /** Form-level failure text — a 409, a role refusal, a strict-schema rejection. */
  error: string | null;
  fieldErrors: Record<string, string>;
  /**
   * The project/city refusal the last save collected, classified.
   *
   * `null` on every draft-mode row and on any failure that was not one of the
   * seven — there is no server to refuse a draft line, and `lineRefusalOf`
   * returns `null` rather than guessing at anything else.
   *
   * Kept beside `fieldErrors` rather than folded into it because it answers two
   * questions that map alone cannot: WHICH chips are wrong
   * (`offendingCityIds`), and whether the fix is even on this screen
   * (`fixedOn`). Refusal 6 — "that project declares no cities" — arrives on
   * `cityIds` and is fixed on `/projects/{id}`, so marking the city control and
   * saying no more sends the person to a picker that is empty by definition.
   */
  refusal: LineRefusal | null;
  /**
   * The PLANNED quantity this row was seeded from, when that figure **cannot be
   * a line quantity** — `null` on every other row, seeded ones included.
   *
   * ⚠️ **It is a REFUSAL FLAG, not a display value, and it is the only thing
   * standing between a planned `"2.50"` and a line that silently bills 1.** An
   * empty quantity box is ordinarily fine: `createPayload` omits the key and
   * the server defaults it to 1, which is the right answer for a row somebody
   * typed and left alone. It is the WRONG answer for a row seeded from a figure
   * that was measured and could not be expressed — there, an omitted key is a
   * number nobody chose, arriving on a customer's document with a 201 and
   * nothing on either side disagreeing. `validateRow` reads this and refuses
   * the row while the box is empty, which is what puts it in `blockedKeys`,
   * withholds it from `onDraftLinesChange`, and disables the parent's Save.
   *
   * The string is the planned figure verbatim, so the message can name it.
   *
   * ⚠️ REQUIRED on the interface, not optional, for the reason `convert` is: an
   * optional member would let a row be built without deciding, and `tsc`
   * refusing a call site is the only guard nobody can forget.
   */
  plannedQuantity: string | null;
  /**
   * The plan's own flat amount for a seeded row whose seeded PRICE cannot
   * express it exactly — `null` on every other row, seeded ones included.
   *
   * ⚠️ **It is a NOTICE flag, not a refusal, and that is the whole difference
   * from {@link Row.plannedQuantity} beside it.** An unstatable quantity has no
   * right answer and the row is refused until somebody supplies one. An
   * inexact price HAS an answer and it is right to the penny in every case that
   * divides: `10000.00` over 3 seeds `3333.33` and the line comes to `9999.99`,
   * which is a real figure a person may perfectly well accept. Blocking the
   * save over a cent would refuse the feature; saying nothing would put the
   * cent on a document a customer is billed against with nobody able to see it,
   * because **this is the only moment anybody can** — after the create there is
   * no plan beside the line to compare it to.
   *
   * ⚠️ **The two boxes are carried so the notice can STAND DOWN.** It describes
   * the SEEDED state, so the moment the person changes the price or the
   * quantity it stops being true of the row and goes — the same shape as
   * `plannedQuantity`, whose message clears as soon as the box is filled.
   * Without them the sentence would go on naming a total the row no longer has.
   *
   * ⚠️ REQUIRED on the interface, not optional, for the reason `convert` and
   * `plannedQuantity` are: an optional member would let a row be built without
   * deciding, and `tsc` refusing a call site is the only guard nobody can
   * forget.
   */
  plannedShortfall: PlannedShortfall | null;
}

/**
 * A seeded row whose unit price does not divide the plan's amount evenly.
 *
 * Every figure here is a STRING taken from, or computed once against, the seed
 * — never re-derived at render. `lineAmount` comes from
 * `computeGeneratedLinePreview`, the one place money arithmetic lives, so the
 * figure the notice names is the same arithmetic the Amount column shows.
 */
interface PlannedShortfall {
  /** The plan's flat amount for the line, verbatim, so the sentence names it. */
  plannedAmount: string;
  /** The seeded unit price. Displayed, and the stand-down test. */
  unitPrice: string;
  /** The seeded quantity, as the box holds it. Displayed, and the stand-down test. */
  quantity: string;
  /** `quantity × unitPrice` — what the line comes to at the seeded figures. */
  lineAmount: string;
}

/* ── Pure helpers ─────────────────────────────────────────────────────────── */

function draftOf(line: QuoteProduct): Draft {
  return {
    productId: String(line.product.id),
    /*
     * Straight through, both ways. `null` opens the cell derived-and-untouched
     * and a string opens it typed — so "has the user typed" is a fact about the
     * RECORD now, not only about this session, and a line somebody worded last
     * week still offers its way back to the derivation.
     */
    description: line.description,
    projectId: line.project === null ? '' : String(line.project.id),
    /*
     * Already ordered by the server's own per-line `sortOrder`; keep that
     * order. Re-sorting it — by name, "to be tidy" — changes what the customer
     * reads.
     *
     * ⚠️ **A FLAGGED line seeds NO explicit set, and `line.cities` is exactly
     * the trap.** On a flagged line that array is the RESOLVED set — what the
     * project has today, computed server-side on every read — and it looks byte
     * for byte like a stored one. Seeding from it would manufacture an explicit
     * set the record does not hold, and unticking the box would then save
     * today's cities as a snapshot: a live rule quietly converted into a frozen
     * list, with a 200, and nothing on screen changing as it happened.
     */
    cityIds: line.allCities ? [] : line.cities.map((city) => city.id),
    allCities: line.allCities,
    /*
     * Always present and `[]` when none — never `null` — so there is no
     * fallback to write, and `?? []` here would be inventing a case.
     *
     * ⚠️ **Not the `line.cities` trap above.** Regions are STORED rows, not a
     * set resolved live against a project, so seeding straight off the read
     * asserts nothing the record does not already hold.
     */
    regionIds: line.regions.map((region) => region.id),
    quantity: String(line.quantity),
    priceMode: line.unitPrice === null ? 'none' : 'value',
    price: line.unitPrice ?? '',
    // `undefined` is an older API, which stored no tier: the default.
    priceTierId: line.priceTierId ?? null,
    discount: line.discount,
    // A stored amount opens the row in amount mode; `null` opens it on the
    // percentage. The server keeps the pair exclusive, so never both.
    discountAmount: line.discountAmount,
    taxRate: line.taxRate,
    xeroTaxType: line.xeroTaxType ?? '',
    // Already ordered by the server; keep the order it arrived in.
    inclusions: line.inclusions.map((entry) => ({
      inclusionId: entry.id,
      quantity: String(entry.quantity),
    })),
    notes: line.notes ?? null,
  };
}

/**
 * A new row starts with no project and no cities.
 *
 * The header project is deliberately NOT seeded here. A project sells exactly
 * one product and the row has not chosen one yet, so pre-filling it would put a
 * value in the box that the very next choice can make a refusal — and a
 * pre-filled control reads as a decision somebody made.
 */
/** The key of the blank row a new document opens with; Add row keys are `new-N`. */
const DEFAULT_BLANK_ROW_KEY = 'new-default';

/** True while a row still holds exactly what {@link blankDraft} gave it. */
function isUntouchedBlankDraft(draft: Draft): boolean {
  return JSON.stringify(draft) === JSON.stringify(blankDraft());
}

/**
 * A row invented on screen — by Add row, or the one blank row a new quote
 * opens with. One builder for both, so the two cannot differ.
 */
function blankRowOf(key: string): Row {
  return {
    key,
    saved: null,
    /* Not a converted row: this one was invented on screen and will be POSTed. */
    convert: null,
    draft: blankDraft(),
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /*
     * ⚠️ `null`, and it must stay `null`. This row was invented on screen
     * with `blankDraft()`'s quantity of '1' already in the box, so there is
     * no planned figure it failed to express — and marking it would refuse
     * an empty quantity on an ordinary row, where blank legitimately means
     * "let the server default it to 1".
     */
    plannedQuantity: null,
    /*
     * ⚠️ `null` for the same reason. This row came from no plan, so there
     * is no planned amount for its price to fall short of — and a notice
     * about a figure nobody planned would be a sentence about nothing.
     */
    plannedShortfall: null,
  };
}

function blankDraft(): Draft {
  return {
    productId: '',
    // `null` is deriving, which is where a new line starts and where almost
    // every line stays. It is not "blank".
    description: null,
    projectId: '',
    cityIds: [],
    // A new line names cities explicitly or names none. "All" is a deliberate
    // choice somebody makes, never the state a row starts in.
    allCities: false,
    // A new line names its regions or names none, and there is no "all" to
    // start from — see {@link Draft.regionIds}.
    regionIds: [],
    quantity: '1',
    priceMode: 'inherit',
    price: '',
    priceTierId: null,
    discount: '',
    // Percentage mode, which is where nearly every line stays.
    discountAmount: null,
    taxRate: '',
    xeroTaxType: '',
    inclusions: [],
    notes: null,
  };
}

function rowOf(line: QuoteProduct): Row {
  return {
    key: `line-${line.id}`,
    saved: line,
    convert: null,
    draft: draftOf(line),
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /* A stored line HAS a quantity; there is no plan left unsettled here. */
    plannedQuantity: null,
    /* And a stored price, which no plan is standing beside to disagree with. */
    plannedShortfall: null,
  };
}

/**
 * A PLANNED product, as a draft row of this grid — `mode: 'draft'` only, and
 * only at mount. See {@link QuoteLineItemsDraftProps.initialDraftRows}.
 *
 * ⚠️ **THE QUANTITY IS THE WHOLE OF THIS FUNCTION'S JUDGEMENT, and both wrong
 * answers are silent.** `plannedProductLines()` has already decided whether the
 * planned figure can be a line quantity; what happens when it cannot is decided
 * here:
 *
 *  - **NOT rounded.** `"2.50"` → `1` is a figure nobody chose, printed on a
 *    document a customer is billed against, with nothing anywhere disagreeing.
 *  - **NOT merely left blank.** An empty box means "omit the key", and an
 *    omitted `quantity` is **1** on the server — the same wrong number by a
 *    longer route, and harder to see because no line of code states it.
 *
 * So the box opens empty AND the row carries {@link Row.plannedQuantity}, which
 * `validateRow` turns into a refusal. The person supplies the figure; the grid
 * refuses to let the document be created until they do.
 *
 * ⚠️ **PARTLY SUPERSEDED 2026-09-03 — the PRICE half only.** That paragraph
 * read:
 *
 * > *"**Everything else is a blank draft row on purpose.** No project, no
 * > cities, no regions, no price, no tax — a plan records what is to be sold and
 * > how much of it, and nothing else. Seeding a price would freeze a figure the
 * > catalogue is supposed to supply at the moment the line is written, which is
 * > why `PlannedProductLine` deliberately carries none."*
 *
 * A plan records **a flat amount for the line** now (owner's decision), so
 * `PlannedProductLine` does carry a price and a PRICED plan seeds it.
 *
 * 🔴 **AND THE PROJECT FOLLOWED ON 2026-09-11 — the quoted "no project" is now
 * wrong about the FIRST of the four things it lists.** *SUPERSEDED, within the
 * quotation above and nowhere else: "**No project**, no cities, no regions, no
 * price, no tax".* A seed may now name a project, because a caller can exist
 * that knows one: a sales-tracker pursuit is a `(project, company)` pair, and
 * without this the document it raises names no project on any line, the
 * server's `(company, project)` sync never matches, and **the card never
 * advances** — with the document itself perfectly correct and nothing failing.
 * **Cities, regions and tax are untouched and are still a blank draft row for
 * exactly the reason quoted.**
 *
 * ⚠️ **THE PROJECT AND THE CITIES ARE ONE PATCH HERE TOO, and the seed gets
 * that for free rather than by accident.** The project control emits
 * `{projectId, cityIds: []}` in a single `onChange` because the server judges
 * the pair POST-MERGE — split them and it re-validates the STORED cities and
 * refuses, naming cities nobody touched. A seeded row spreads over
 * `blankDraft()`, whose `cityIds` is already `[]` and whose `allCities` is
 * already `false`, so the row arrives in exactly the state a chosen project
 * leaves it in. ⛔ **Never seed a `cityIds` beside a project**: a city write is
 * a whole-set REPLACE and a seed cannot know which cities were meant.
 *
 * ⚠️ **AND THE QUOTED ARGUMENT STILL GOVERNS HALF THE CASES, WHICH IS WHY IT IS
 * KEPT RATHER THAN DELETED.** An UNPRICED plan seeds no price and never will:
 * the box stays empty, `createPayload` omits `unitPrice`, and the server
 * snapshots the catalogue figure at the moment the line is written — the
 * behaviour every unplanned line has always had, and the behaviour the
 * paragraph above was written to protect.
 *
 * ⛔ **`priceMode: 'none'` IS NEVER SEEDED.** It sends an explicit `null`,
 * which is "this line has no price at all" — a claim about the line, not the
 * absence of a claim. An unpriced plan says nothing about price; `'inherit'` is
 * the only spelling of that.
 *
 * ⚠️ **A price that does not divide the plan is SEEDED ANYWAY and reported.**
 * See {@link Row.plannedShortfall}: refusing to seed makes somebody retype a
 * figure that is right to the penny in every case that divides, and rounding
 * the plan away loses a real fact.
 */
function seededRowOf(planned: PlannedProductLine, index: number): Row {
  return {
    /*
     * `seed-` rather than `new-`: `addRow` counts from `new-0` on its own
     * sequence, so a shared prefix would collide on the first added row and two
     * rows sharing a key is a React reconciliation bug, not a display one.
     */
    key: `seed-${index}`,
    saved: null,
    convert: null,
    draft: {
      ...blankDraft(),
      productId: String(planned.productId),
      /*
       * ✅ **THE SEEDED PROJECT — see the block above for why the cities beside
       * it stay `blankDraft()`'s `[]`.**
       *
       * ⚠️ **The absent branch leaves `blankDraft()`'s `''`, which is "no
       * project", and writes nothing.** `createPayload` tests `!== ''` before
       * it sends, so a seed that named no project omits the key entirely — it
       * does not send a `projectId` of `0`, an empty string or a `null`, each
       * of which is a different wrong instruction on a `.strict()` schema.
       *
       * ⛔ **NO CLIENT-SIDE CHECK THAT THIS PROJECT SELLS THIS PRODUCT.** That
       * is refusal 5 and it belongs to the server, which judges the pair
       * POST-MERGE; reproducing it here would refuse a pair the API accepts.
       * The row shows both controls, the person can change either, and a 422
       * routes to `projectId` on this row.
       */
      ...(planned.project === undefined ? {} : { projectId: String(planned.project.id) }),
      // `blankDraft()` opens at '1'; an unstatable plan must NOT inherit that.
      quantity: planned.lineQuantity === null ? '' : String(planned.lineQuantity),
      /*
       * ⚠️ **The `null` branch leaves `blankDraft()`'s `'inherit'` and empty box
       * exactly as they were.** It is not `'none'` — see the block above — and
       * it is not `'value'` with an empty string, which `validateRow` refuses
       * and which would open the form already broken.
       */
      ...(planned.unitPrice === null
        ? {}
        : { priceMode: 'value' as const, price: planned.unitPrice }),
      /*
       * ✅ **THE PLANNED LINE DESCRIPTION (migration 160), AND THE `null`
       * BRANCH IS THE LOAD-BEARING ONE.**
       *
       * ⚠️ **SEEDING THIS PUTS THE CELL IN ITS TYPED STATE PERMANENTLY.** While
       * `draft.description` is empty the cell IS the derivation — product, then
       * cities — and it rewrites itself as the row changes. A value here stops
       * that for good; `Reset to derived` is the only way back, and the cell
       * says which state it is in. That is the owner's decision: a description
       * somebody wrote months ago against this plan outranks a string assembled
       * from the product name.
       *
       * ⛔ **SO THE `null` CASE MUST LEAVE THE KEY ALONE, NOT WRITE `''`.**
       * `blankDraft()` opens it empty, which means DERIVING; writing an empty
       * string here is indistinguishable in the payload and opposite in
       * meaning — every seeded line would arrive frozen and blank, on a
       * document a customer reads, with nothing on screen explaining why the
       * product name never appeared. Same shape as the `unitPrice` branch
       * directly above.
       */
      ...(planned.description === null ? {} : { description: planned.description }),
    },
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    plannedQuantity: planned.lineQuantity === null ? planned.quantity : null,
    plannedShortfall: plannedShortfallOf(planned),
  };
}

/**
 * The shortfall a seeded row carries, or `null` when there is nothing to say.
 *
 * ⚠️ **`unitPriceExact` is the AUTHORITY, and this must not second-guess it.**
 * `plannedProductLines()` did the division; whether it came out even is its
 * answer, not a comparison to be re-run here against two decimal strings whose
 * scales need not match (`"10000"` and `"10000.00"` are the same amount and
 * different strings, and a string compare would report a shortfall on a plan
 * that divides perfectly).
 *
 * The line figure goes through `computeGeneratedLinePreview` — the one place
 * money arithmetic lives — at a neutral discount and tax, because the question
 * is whether the PRICE divides the plan. A discount applied afterwards is a
 * second, deliberate decision and must not read as the seed having gone wrong.
 * A preview that answers `null` yields no shortfall: nothing is invented.
 */
function plannedShortfallOf(planned: PlannedProductLine): PlannedShortfall | null {
  if (planned.unitPriceExact) return null;
  if (planned.unitPrice === null || planned.plannedAmount === null) return null;
  if (planned.lineQuantity === null) return null;
  const quantity = String(planned.lineQuantity);
  const lineAmount = computeGeneratedLinePreview({
    quantity,
    unitPrice: planned.unitPrice,
    discount: '',
    discountAmount: null,
    taxRate: '',
  })?.lineSubtotal;
  if (lineAmount === undefined) return null;
  return {
    plannedAmount: planned.plannedAmount,
    unitPrice: planned.unitPrice,
    quantity,
    lineAmount,
  };
}

/**
 * A DEAL line, as a row of this grid — `mode: 'convert'` only.
 *
 * ⚠️ **Every seeded box is the LINE's own figure and never a default.** The
 * change test compares against a fresh `convertShapeOf` of THIS SAME FUNCTION's
 * output for the same line, so a box seeded any other way reports every row as
 * edited and puts figures nobody touched on the wire. That is also why the seed
 * is built by calling `convertRowOf` again rather than by a second seeder: one
 * function decides both what a row starts as and what it is judged against.
 *
 * The five keys a deal line does not carry — `allCities`, `taxRate`,
 * `xeroTaxType` and the two generated money columns — are simply absent from
 * the draft's meaning here rather than invented: `allCities` is `false` because
 * a deal line has no such flag, and the two tax keys are the neutral values the
 * inert tax cell never reads. Nothing sends any of them.
 *
 * ⚠️ **`key` is namespaced `deal-`, not `line-`.** The two id spaces are
 * unrelated — `f_deal_products.id` and `f_quote_products.id` — and a shared
 * prefix would let a stale `expanded` set from one mode open a row of the other.
 */
function convertRowOf(line: DealProduct): Row {
  return {
    key: `deal-${line.id}`,
    saved: null,
    convert: line,
    draft: {
      productId: String(line.product.id),
      /* Straight through, both ways: `null` opens the cell deriving. */
      description: line.description,
      projectId: line.project === null ? '' : String(line.project.id),
      cityIds: line.cities.map((city) => city.id),
      /* A deal line has no `all_cities` column. `false` is the fact, not a default. */
      allCities: false,
      regionIds: line.regions.map((region) => region.id),
      quantity: String(line.quantity),
      priceMode: line.unitPrice === null ? 'none' : 'value',
      price: line.unitPrice ?? '',
      /* The deal line's tier; the server copies it onto the quote line. */
      priceTierId: line.priceTierId ?? null,
      discount: line.discount,
      /* A stored amount opens the row in amount mode; `null` opens it on the percentage. */
      discountAmount: line.discountAmount,
      /*
       * ⚠️ **Neither is read anywhere on this path.** The Tax rate cell is an
       * inert readout in convert mode — the rate a converted line gets comes
       * from the COMPANY, server-side, and no key on the override contract can
       * carry one — so these are placeholders the payload never sees, not a
       * claim that the line is at 0% and has no code.
       */
      taxRate: '',
      xeroTaxType: '',
      inclusions: line.inclusions.map((entry) => ({
        inclusionId: entry.id,
        quantity: String(entry.quantity),
      })),
      /*
       * The DEAL line's note. The server copies it onto the new quote line by
       * itself, so `convertLineSet` sends a `notes` key only when this moved.
       */
      notes: line.notes ?? null,
    },
    state: 'idle',
    error: null,
    fieldErrors: {},
    refusal: null,
    /* A deal line HAS a quantity; there is no plan left unsettled here. */
    plannedQuantity: null,
    /* And its own price. A conversion is not a plan and has nothing to divide. */
    plannedShortfall: null,
  };
}

/**
 * The stored line a row was seeded from, whichever document it came from.
 *
 * A `QuoteProduct` and a `DealProduct` agree field for field on everything this
 * grid reads a stored line FOR — the product, the project, the cities, the
 * regions, the inclusions and the four figures — so one accessor serves the
 * name maps, the picker pinning and the description derivation without any of
 * them having to know which mode they are in. Where the two DIVERGE (tax, the
 * generated subtotal and tax columns, `allCities`, `invoiced`) the reader must
 * ask `row.saved` explicitly, which is what keeps those reads honest.
 */
function sourceLineOf(row: Row): QuoteProduct | DealProduct | null {
  return row.saved ?? row.convert;
}

/**
 * The inclusions the STORED line carries, in `Draft` shape.
 *
 * ⚠️ **Asked of the stored line rather than of `row.saved`**, so a converted row
 * reports the DEAL line's inclusions instead of an empty set. It is what the
 * read-only orphan block and the "what a save preserves" sentence are both
 * about, and answering `[]` there would describe a document that is not the one
 * crossing.
 */
function storedInclusionsOf(row: Row): InclusionDraft[] {
  const line = sourceLineOf(row);
  if (line === null) return [];
  return line.inclusions.map((entry) => ({
    inclusionId: entry.id,
    quantity: String(entry.quantity),
  }));
}

/**
 * One convert row's boxes AND its structure, in the shape
 * `@/lib/deal-convert-lines` reads.
 *
 * ⚠️ *SUPERSEDED — this was `convertEditOf`, returning the five figures alone,
 * "in the shape `@/lib/deal-convert-lines` reads" when that shape was the
 * `lines` override.* `lineSet` carries the product, the project, the cities, the
 * regions and the inclusions too, so the shape grew and the two halves stayed
 * ONE object: they are compared as one unit, and a change test reading only the
 * figures would report a repointed project as an unchanged row.
 *
 * ⚠️ **`priceMode` STILL has no equivalent on the wire**, which carries a price
 * or nothing. `inherit` and `none` both mean *this box is empty*, and there is
 * no `unitPrice: null` key on either request key — so a price that exists cannot
 * be cleared on this screen, which is why the "record no price" toggle is not
 * rendered in convert mode: it would offer a state the request cannot express.
 *
 * ⚠️ **`offersInclusions` and `offersRegions` are REQUIRED**, and passing them
 * is what keeps a HIDDEN control from writing. The seed is built by this same
 * function with the same two flags, so a hidden set produces the identical value
 * on both sides and no key is emitted at all — where substituting an empty array
 * on one side only would send a CLEAR of a set nobody can see. Same rule, same
 * reason, as `createPayload` and `updatePayload`.
 */
function convertShapeOf(
  draft: Draft,
  offersInclusions: boolean,
  offersRegions: boolean,
): ConvertLineShape {
  return {
    quantity: draft.quantity,
    unitPrice: draft.priceMode === 'value' ? draft.price : '',
    discount: draft.discount,
    discountAmount: draft.discountAmount,
    /* `null` is DERIVE on the draft and `''` is DERIVE on the wire. One state. */
    description: draft.description ?? '',
    productId: draft.productId === '' ? null : Number(draft.productId),
    projectId: draft.projectId === '' ? null : Number(draft.projectId),
    /*
     * ⚠️ **The EXPLICIT set, always.** `allCities` has no key on `lineSet` and
     * the flag is locked on this path — see `allCitiesLocked` in
     * {@link CitiesControl} — so there is no flagged branch to honour here and
     * no parked set to suppress.
     */
    cityIds: draft.cityIds,
    regionIds: offersRegions ? draft.regionIds : [],
    inclusions: offersInclusions ? inclusionsPayload(draft.inclusions) : [],
  };
}

/** An order-sensitive fingerprint, because the index IS the stored `sortOrder`. */
function inclusionsKey(entries: InclusionDraft[]): string {
  return entries.map((entry) => `${entry.inclusionId}:${entry.quantity || '1'}`).join('|');
}

/**
 * An ORDER-SENSITIVE fingerprint of the city set.
 *
 * Order-sensitive because the stored `sortOrder` is this array's order, and
 * reordering the cities on a line changes the string Xero prints — "Bangalore,
 * Mumbai" and "Mumbai, Bangalore" are the same set and two different customer
 * documents. So a reorder is a change and must be saved as one.
 */
function citiesKey(ids: number[]): string {
  return ids.join(',');
}

/**
 * An ORDER-INSENSITIVE fingerprint of the region set.
 *
 * ⚠️ **Deliberately NOT {@link citiesKey}, and copying that one here would be
 * the bug.** A line's cities carry a stored per-line `sortOrder`, and their
 * order is printed on the customer's document, so reordering them is a real
 * change that has to be saved. Regions carry no such order — the read hands
 * them back in the master's own order — so treating a reorder as a change would
 * send a full REPLACE that stores nothing new, on a key whose `[]` form CLEARS
 * the set.
 *
 * Deduplicated for the same reason: the same region named twice is the same
 * set, and the server stores it once either way.
 */
function regionsKey(ids: number[]): string {
  return [...new Set(ids)].sort((a, b) => a - b).join(',');
}

/**
 * The draft's description as the WRITE means it — `null` for "derive".
 *
 * ⚠️ **An emptied box is a Reset, and it is folded into one HERE rather than
 * left to the server.** `{ description: "" }` is normalised to `null` on their
 * side, so relying on that would work; it would also mean our payload said
 * something we did not mean, and the two ways of asking for the derivation back
 * would travel as two different bodies that happened to agree today.
 *
 * The emptiness test TRIMS and the value sent does not. A box holding three
 * spaces is emptied by any reading a person would give it, and storing it would
 * print a blank line on a customer's document while permanently suppressing the
 * derivation — the worst of both. But a description that survives the test goes
 * to Xero **verbatim**, so nothing here rewrites the wording itself.
 */
function descriptionForWrite(value: string | null): string | null {
  if (value === null || value.trim() === '') return null;
  return value;
}

/**
 * The server's own uniqueness key: **(product, project)**.
 *
 * `project_key` is a generated `COALESCE(project_id, 0)` rather than
 * `project_id` itself: MySQL treats NULLs as distinct in a unique index, so
 * keying on the raw column would have lost duplicate protection for every
 * project-less line.
 *
 * ⚠️ **The CITY is not part of this and has not been since migration 068.** It
 * was, for about a day under 057, and the server's own 409 text was still
 * telling people to "name a different city" long after that could not separate
 * two lines — advice which produced the identical 409 a second time. A line now
 * runs in a set of cities, so the set can never be an identity.
 */
function pairKey(productId: string, projectId: string): string {
  return `${productId}:${projectId === '' ? '0' : projectId}`;
}

/**
 * The value the inclusions rule is asked about, for one row: **this row's
 * product's link-table offer**.
 *
 * ⚠️ **TWO distinguishable "nothing" results — do not merge them.**
 *
 * - **{@link NO_PRODUCT_CHOSEN}** — the item box is empty. A fact this row holds
 *   with certainty, not a gap a completed fetch would close. Answers
 *   `'not-chosen'`, so `offers` is false, `denialReason` is `null` and nothing at
 *   all renders where the block would be.
 * - **`null`** — an item IS chosen and its offer is unread: the request is in
 *   flight, or `GET /products/{id}/inclusions` failed. Answers `'unknown'` and
 *   **still shows the block** — hiding a feature on a load failure is
 *   unrecoverable from the user's side.
 * - **A {@link ProductInclusionOffer}** — the read succeeded. `inclusionIds: []`
 *   is a real answer meaning *an admin has linked none*, and is today's answer
 *   for every product. It is **not** the same value as `null`.
 *
 * ⚠️ Do not "simplify" the three returns — collapsing `'not-chosen'` into
 * `'unknown'` renders the whole campaign-inclusions panel under an Item cell
 * still reading "Choose a product".
 *
 * **The catalogue's product fields are not consulted at all** — the answer is
 * keyed on the product id alone, so a retired product or one past the 100-row
 * `perPage` cap still reads back perfectly.
 */
function inclusionsOfferOf(
  row: Row,
  offers: ReadonlyMap<number, ProductInclusionOffer>,
): ProductInclusionOffer | NoProductChosen | null {
  // Not `null`: there is no product here, which is a different claim from not
  // being able to resolve one. See this function's doc.
  if (row.draft.productId === '') return NO_PRODUCT_CHOSEN;

  /*
   * ⚠️ `?? null` is the ONE place the map's `undefined` becomes the "unread"
   * input, and it is a widening rather than a default: `undefined` and `null` are
   * the same claim to `product-inclusions.ts` ("a product is in play and we could
   * not read its offer"), whereas an entry that IS present carries `[]` on its own
   * and never reaches this expression. Never write `?? { inclusionIds: [] }` here
   * — that is the coalesce that turns every failed fetch into an admin decision.
   */
  return offers.get(Number(row.draft.productId)) ?? null;
}

/**
 * One row's answer from `@/lib/product-inclusions`, resolved once and read by
 * every surface — the editor, the two summaries, the payload builders, the
 * dirty test and the local validation.
 *
 * Resolved once because five call sites asking the same question separately is
 * five chances to disagree, and the two that would matter are the ones a reader
 * cannot see disagreeing: the block hiding while the payload still sends, or the
 * dirty test saying "Unsaved" over a body with no keys in it.
 */
interface RowInclusions {
  /**
   * `productOffersInclusions(offer)` — **the gate, and the OWNER's choice.**
   *
   * ⚠️ Not `shouldShowInclusionsEditor(offer, count)`, which is the lenient
   * alternative and would keep the editor on a non-offering line that already
   * holds inclusions. The owner was shown that trade-off and chose the strict
   * rule; see this file's header. Do not swap it back — that reverses a decision
   * rather than repairing a bug.
   *
   * `unknown` resolves to `true`, so an offer still loading or failed outright
   * shows the block rather than reading as a feature that has been removed.
   *
   * ⚠️ **`false` on a row with NO ITEM CHOSEN as well**, which is not a refusal
   * and renders nothing rather than a sentence — see {@link denialReason} and
   * {@link inclusionsOfferOf}.
   */
  offers: boolean;
  /**
   * The block is showing **only because the offer could not be read** —
   * `'unknown'`, not `'yes'`.
   *
   * ⚠️ Kept as its own field rather than inferred from `offers && !denialReason`,
   * which is true for `'yes'` as well and would put a failure notice on every
   * working row.
   *
   * It exists because this feature has **no server-side backstop**. Everywhere
   * else in this app "fail towards showing the control" is cheap, because the
   * write is refused and the refusal names the real problem. Here the write is
   * accepted — the link table governs what a picker OFFERS, never what a document
   * may record — and the owner has decided inclusions print in the line
   * description on the customer's Xero quote. So the only thing standing between
   * an unread offer and an inclusion on a customer document is the person reading
   * the row, and they are told.
   */
  offerUnread: boolean;
  /**
   * `offeredInclusionIds(offer)` — **which** inclusions this product's editor may
   * offer, or `null` meaning *do not narrow*.
   *
   * ⚠️ **`null` is "show everything", NOT "offers nothing".** It accompanies
   * {@link offerUnread} exactly — the offer was never consulted — and narrowing on
   * it would collapse a request in flight or a failed read into an empty picker
   * that reads as an administrator's decision. That is the same `0`-versus-`null`
   * substitution `product-inclusions.ts` forbids on `offeredCount`, arriving in the
   * shape the user actually sees.
   *
   * ⚠️ **This narrows the OPTIONS and never the DRAFT.** An id on the line that is
   * absent from this set is shown in its own labelled block, not filtered away —
   * an inclusions write replaces the whole set, so a picker that hid it would send
   * an array without it on the next save and destroy it with a 200. See
   * {@link InclusionsEditor}.
   *
   * A non-null set here is never empty *in a rendered editor*: an empty offer
   * answers `'no'`, so {@link offers} is false and {@link InclusionsNotOffered}
   * renders instead. The editor still states the case honestly rather than assuming
   * it away, because the set can also be non-empty while every id in it names a
   * master row that has since been retired.
   */
  offeredIds: ReadonlySet<number> | null;
  /**
   * `inclusionsDenialReason(product)`.
   *
   * ⚠️ **`offers` is false for two different reasons and only one is a denial**:
   * a product that does not qualify gets the sentence, and a row with no item
   * chosen gets `null`, because there is no product for "not offered on this
   * product" to be about. So: non-null **implies** `offers` is false; the
   * converse does not hold. Never render a fallback string when this is `null`.
   */
  denialReason: string | null;
  /**
   * Holds inclusions on a product that does not offer them.
   *
   * ⚠️ **The common case now, not an exception.** The link table is empty, so
   * every product answers "offers none" and every line carrying an inclusion is
   * this. This comment named quote 7's two lines as the live example; that quote
   * was deleted on 2026-08-27, and the state it illustrated is now universal.
   */
  orphaned: boolean;
}

/**
 * What a row falls back to when the map has no entry for it.
 *
 * `offers: true` on purpose, and it is the same "fail towards being useful"
 * direction `product-inclusions.ts` takes with `'unknown'`: a missing entry is
 * ignorance, not a verdict, and showing the block costs at worst a refusal that
 * names the real problem while hiding it costs the person the feature with no
 * explanation. `orphaned: false` follows — there is nothing to describe as
 * stranded when the block is being shown.
 *
 * `offerUnread: true` for the same reason, and it is the honest value rather than
 * a cautious one: a row with no entry in the map has genuinely had no offer read
 * for it, so the editor should say the check did not run. It is unreachable in
 * practice — the map is built from the same `rows` array every call site keys
 * into — which is exactly why it must not quietly claim the check passed.
 *
 * `offeredIds: null` is the third face of that same statement and is the only
 * value consistent with the other two: no offer was read, so there is nothing to
 * narrow the picker to, and an empty set here would present ignorance as an
 * administrator's decision. It must move with `offerUnread`, never independently.
 *
 * *SUPERSEDED 11 Sep 2026: a fourth field, `required: false`, resolved the same
 * ignorance in the opposite direction — towards demanding less — because a
 * `true` would have turned a missing map entry into a line that could not be
 * written. The at-least-one rule is gone, so there is nothing left to demand
 * and no fourth face to keep in step.*
 */
const INCLUSIONS_SHOWN: RowInclusions = {
  offers: true,
  offerUnread: true,
  offeredIds: null,
  denialReason: null,
  orphaned: false,
};

function inclusionsPayload(entries: InclusionDraft[]) {
  return entries.map((entry, index) => {
    const quantity = Number(entry.quantity);
    return {
      inclusionId: entry.inclusionId,
      // Omitted means 1. An explicit 0 is refused by zod AND by a CHECK.
      ...(entry.quantity !== '' && isValidInclusionQuantity(quantity) ? { quantity } : {}),
      sortOrder: index,
    };
  });
}

/**
 * A brand-new line. Everything the user did not choose is omitted, never guessed.
 *
 * ⚠️ **This is the ONE builder for a POST body, and draft mode uses it too.**
 * The rows collected in the New Quote modal are written by the parent from
 * exactly this function's output, so a second builder "for the modal" cannot
 * exist and the two paths cannot drift apart.
 *
 * `sortOrder` is the only thing the two modes differ on, and it is a parameter
 * rather than a branch: the saved mode omits it and lets the server append,
 * while draft mode passes the row's index, because the parent may write the
 * rows in any order and `sort_order ASC` is the order the customer's document
 * prints in.
 *
 * `offersInclusions` is the answer `@/lib/product-inclusions` gave for this
 * row's product, and `false` means the inclusions block is not on screen — see
 * the `inclusions` line below and the header section on the per-product rule.
 */
/**
 * What one draft's discount MEANS on the wire.
 *
 * ⚠️ **The two columns are mutually exclusive: sending both non-zero is a 422
 * naming `discountAmount`.** So the pair is resolved once here and both payload
 * builders emit it as a pair — whichever half is being set says the other is
 * off in the same request, because a request that sets one and leaves the other
 * stored IS the refusal.
 *
 * A blank `discount` stays blank rather than becoming `'0'`: on a create that
 * means "take the server's default" and on a patch it means "leave the stored
 * value alone", and only the callers can tell those apart.
 *
 * ⚠️ **An emptied AMOUNT box is a real zero, not a blank.** The percentage
 * control is not on screen in that mode, so "no amount" can only mean "no
 * discount on this line" — leaving a stored percentage standing would show an
 * empty discount box over a line that is still discounted.
 */
interface DiscountWrite {
  /** A percentage, as typed. `''` means "say nothing about it". */
  discount: string;
  /** An exact amount, as typed, or `null` for "not discounted by amount". */
  discountAmount: string | null;
}

function discountWriteOf(draft: Draft): DiscountWrite {
  if (draft.discountAmount === null) {
    return { discount: draft.discount.trim(), discountAmount: null };
  }
  const amount = draft.discountAmount.trim();
  return { discount: '0', discountAmount: amount === '' ? null : amount };
}

/**
 * The price an untouched line inherits (`computeLinePrice`: the project price
 * entry, else the city pro-rata of the catalogue). `known: false` while a read
 * it needs has not landed; `discountAmount` is non-null only for a quantity
 * override that does not divide into whole cents.
 */
interface InheritedPrice {
  known: boolean;
  price: string | null;
  discountAmount: string | null;
}

const INHERITED_UNKNOWN: InheritedPrice = { known: false, price: null, discountAmount: null };

/**
 * A SAVED line's price on its next save, when the save reprices it: the unit
 * price, and — only when present — the discount amount the resolver now gives
 * (`null` = no amount). See `repriceFor`.
 */
interface Reprice {
  unitPrice: string | null;
  discountAmount?: string | null;
}

function createPayload(
  draft: Draft,
  sortOrder: number | undefined,
  offersInclusions: boolean,
  offersRegions: boolean,
  /**
   * What `inherit` resolves to on this row. A known, non-null price is SENT
   * (owner 2026-10-27: the frontend computes the inherited price); unknown or
   * unpriced omits the key, and the server resolves the same rule.
   */
  inherited: InheritedPrice,
): QuoteProductCreateInput {
  const body: QuoteProductCreateInput = { productId: Number(draft.productId) };
  if (draft.priceTierId !== null) body.priceTierId = draft.priceTierId;
  if (sortOrder !== undefined) body.sortOrder = sortOrder;
  /*
   * Omitted when the cell is still deriving, which is what `null` would mean
   * here too — on a POST there is nothing stored for a `null` to clear, so the
   * two are the same instruction and the shorter one is the honest one. On the
   * PATCH beside this they are NOT the same, which is why that branch says
   * `null` out loud.
   */
  const description = descriptionForWrite(draft.description);
  if (description !== null) body.description = description;
  /*
   * Sent together, always. The guard reads the post-merge state, so the pair is
   * judged as one thing — and on a POST there is no stored value to fall back
   * on, which makes an omitted `projectId` mean "no project" rather than
   * "unchanged". Saying that explicitly is what we mean.
   */
  if (draft.projectId !== '') body.projectId = Number(draft.projectId);
  /*
   * ⚠️ **EXACTLY ONE of the two city keys, and never both.** `allCities: true`
   * alongside a non-empty `cityIds` is a 422 — a correct backstop that should
   * never actually fire, because there is no right silent answer when a flag
   * and a list disagree: honouring the flag discards a list somebody typed,
   * honouring the list ignores a box somebody ticked, and both report success.
   * The branch is what makes the pair unsendable rather than merely unlikely.
   *
   * **Flagged**: the flag alone. `cityIds` is not sent at all, so the set a
   * person parked by ticking the box never reaches the server — which is what
   * the server would do with it anyway.
   *
   * **Not flagged**: `[]` is always legal, even on a line with no project — it
   * is the one city input that names nothing and so needs nothing to validate
   * against. Omitting it means the same on a POST; sending it says so. There is
   * no `allCities: false` here: nothing is stored yet for it to clear, and this
   * builder omits everything the user did not choose.
   */
  if (draft.allCities) body.allCities = true;
  else if (draft.cityIds.length > 0) body.cityIds = [...draft.cityIds];
  /*
   * ⚠️ **OMITTED when the control is not on screen**, exactly as `inclusions`
   * is at the foot of this function and for the neighbouring reason: a row can
   * hold regions chosen while its product was projectless and then have its
   * item changed to one that IS sold through projects, at which point the block
   * is gone and the person can no longer see or undo the set. Sending it would
   * store a geography nobody on this screen can look at.
   *
   * `[]` is not sent on a POST — nothing is stored yet for it to clear, and
   * this builder omits everything the user did not choose. On the PATCH beside
   * this one `[]` and an omitted key are OPPOSITE instructions, which is why
   * the rule is stated in both places rather than being true here by accident.
   */
  if (offersRegions && draft.regionIds.length > 0) body.regionIds = [...draft.regionIds];
  // A real JSON number: `quantity` is not coerced and `"2"` is a 422.
  if (draft.quantity !== '') body.quantity = Number(draft.quantity);
  if (draft.priceMode === 'none') body.unitPrice = null;
  else if (draft.priceMode === 'value') body.unitPrice = draft.price;
  else if (inherited.known && inherited.price !== null) body.unitPrice = inherited.price;
  const money = discountWriteOf(draft);
  if (money.discountAmount !== null) {
    // The pair, always. `discount: 0` is what makes the amount the only live
    // discount on the line rather than a second one.
    body.discountAmount = money.discountAmount;
    body.discount = 0;
  } else if (money.discount !== '') {
    body.discount = money.discount;
  } else if (
    draft.priceMode === 'inherit' &&
    inherited.known &&
    inherited.price !== null &&
    inherited.discountAmount !== null &&
    draft.discountAmount === null
  ) {
    // The resolver's rounding discount, only when no discount was typed.
    body.discountAmount = inherited.discountAmount;
    body.discount = 0;
  }
  if (draft.taxRate !== '') body.taxRate = draft.taxRate;
  // Omitted leaves it null, which means "use the company's sales default".
  if (draft.xeroTaxType !== '') body.xeroTaxType = draft.xeroTaxType;
  /*
   * ⚠️ **The key is OMITTED ENTIRELY when the block is hidden**, and never sent
   * as `[]`.
   *
   * An inclusions write is a REPLACE: `[…]` replaces the whole set, `[]` clears
   * it, and the key omitted leaves whatever is stored alone. On a POST there is
   * nothing stored yet, so the two look alike — but they do not on the PATCH
   * beside this one, and this is the one builder both modes use, so the rule is
   * stated in the same terms in both places rather than being true here by
   * accident.
   *
   * The `length > 0` test alone would already omit it on a blank new row. The
   * `offersInclusions` half is what covers the row that had inclusions chosen
   * while its product qualified and then had its item changed to one that does
   * not: the block is gone, so the person can no longer see or undo that set,
   * and sending it would write a set nobody can look at.
   */
  if (offersInclusions && draft.inclusions.length > 0) {
    body.inclusions = inclusionsPayload(draft.inclusions);
  }
  // The internal note. Omitted when empty: on a POST there is nothing to clear.
  const notes = notesForWrite(draft.notes);
  if (notes !== null) body.notes = notes;
  return body;
}

/**
 * Whether two money strings are the same figure to the cent — `"17500"` and
 * `"17500.00"` are. Exact, through `lib/decimal.ts`; a string that is not a
 * two-place decimal is never equal to anything.
 */
function sameMoney(a: string, b: string): boolean {
  const left = splitAmountOverQuantity(a, 1);
  const right = splitAmountOverQuantity(b, 1);
  return left !== null && right !== null && left.unitPrice === right.unitPrice;
}

/** `''` and whitespace-only are "no note" — the server folds them to `null` too. */
function notesForWrite(value: string | null): string | null {
  return value === null || value.trim() === '' ? null : value;
}

/**
 * Only the keys that actually changed.
 *
 * An omitted key is untouched and an explicit `null` clears the field, so
 * spreading a loaded line into a PATCH would wipe every column the user never
 * opened. `inclusions` is the one key read and written under the same name,
 * and sending it REPLACES the whole set.
 *
 * `offersInclusions` is required rather than defaulted on purpose: a call site
 * that forgets it fails at `tsc`, and the thing it guards is irreversible. See
 * the `inclusions` block at the foot of this function.
 */
function updatePayload(
  draft: Draft,
  saved: QuoteProduct,
  offersInclusions: boolean,
  offersRegions: boolean,
  /**
   * The reprice this save carries for an UNTOUCHED price (`repriceFor`), or
   * `undefined` when it leaves the stored price alone. Required so a call site
   * cannot forget it.
   */
  reprice: Reprice | undefined,
): QuoteProductUpdateInput {
  const base = draftOf(saved);
  const body: QuoteProductUpdateInput = {};

  if (draft.productId !== base.productId) body.productId = Number(draft.productId);
  if (draft.priceTierId !== base.priceTierId) body.priceTierId = draft.priceTierId;

  /*
   * ⚠️ **`null` is sent, explicitly, and omitting it here would be the bug.**
   *
   * `Reset to derived` — and an emptied box, which is folded into it by
   * `descriptionForWrite` — means *put this line back on the derivation*. Across
   * this API that is `description: null`; an omitted key means *leave the stored
   * wording exactly as it is*. So a Reset that omitted the key would answer
   * `200`, change nothing, and leave the cell on screen deriving over a line
   * that is still storing somebody's old sentence — a disagreement that survives
   * until the next reload and prints on the customer's document meanwhile.
   *
   * `base.description` is the server's own value and is never `''`, so the
   * comparison is against an already-normalised side.
   */
  const description = descriptionForWrite(draft.description);
  if (description !== base.description) body.description = description;

  /*
   * ⚠️ The project and the cities go together whenever EITHER moved.
   *
   * The guard runs on the post-merge state, so a `projectId`-only PATCH
   * re-validates the STORED cities and can be refused naming cities the caller
   * never sent — a 422 about a control the user did not touch. The backend
   * deliberately does not auto-clear them, because silently dropping a recorded
   * geography would be data loss returning 200. So the two keys travel as one
   * fact, and `null` clears the project while `[]` clears the cities.
   *
   * `null` on `cityIds` is a 422: omitting and `[]` already express both
   * intents, so null could only be a guess about which was meant.
   *
   * ⚠️ **`allCities` is the THIRD member of that one fact, and it is EXCLUSIVE
   * with `cityIds`.** The flag `true` beside a non-empty list is a 422, so the
   * branch below can only ever emit one of the two:
   *
   * | the draft | what goes out |
   * | --- | --- |
   * | flagged | `allCities: true`, and **no `cityIds` at all** |
   * | not flagged, was flagged | `cityIds`, plus `allCities: false` to clear it |
   * | not flagged, never was | `cityIds` alone, exactly as before |
   *
   * The flag moving is a change in its own right, so it joins `projectMoved`
   * and `citiesMoved` in deciding whether this group is written: ticking the
   * box and touching nothing else has to mark the row unsaved AND has to send
   * something, or {@link isDirty} would offer a Save that produced an empty
   * PATCH — a 422.
   *
   * ⚠️ **A flagged line's PARKED `cityIds` are invisible here, in both
   * directions.** They are not sent — the server discards the stored rows on
   * `allCities: true`, so sending them would be a 422 and a claim about
   * something that does not survive — and a change to them does not count as a
   * change, so re-picking cities under a ticked box cannot light up "Save 1
   * row" for a body with nothing in it. Same rule the hidden inclusions set
   * follows two blocks down: the dirty test agrees with the payload.
   */
  const projectMoved = draft.projectId !== base.projectId;
  const citiesMoved =
    !draft.allCities && citiesKey(draft.cityIds) !== citiesKey(base.cityIds);
  const allCitiesMoved = draft.allCities !== base.allCities;
  if (projectMoved || citiesMoved || allCitiesMoved) {
    body.projectId = draft.projectId === '' ? null : Number(draft.projectId);
    if (draft.allCities) {
      body.allCities = true;
    } else {
      body.cityIds = [...draft.cityIds];
      // Only when it was ON. An unchanged `false` is an unchanged key, and this
      // builder sends nothing it was not asked to change.
      if (allCitiesMoved) body.allCities = false;
    }
  }
  /*
   * ⚠️ **Sent ONLY when the person actually moved it, and the ABSENT form is
   * the load-bearing one.** `regionIds` is a full REPLACE: the key present
   * replaces the whole set, `[]` CLEARS it, and omitting it leaves the stored
   * set exactly as it is. So an unconditional send wipes a set another surface
   * populated, on a save that only changed the price — the `ContactPersons: []`
   * shape this codebase has paid for repeatedly.
   *
   * ⚠️ **It is deliberately NOT coupled to `projectId` the way `cityIds` is.**
   * Cities travel with the project because the server re-validates them against
   * the POST-MERGE project and can refuse naming a control nobody touched.
   * Regions are validated against the `/regions` master alone, so a project move
   * says nothing about them and re-sending them would be a REPLACE nobody asked
   * for.
   *
   * `offersRegions` is the same gate `inclusions` takes below. It cannot fire on
   * its own — hiding never touches the draft, so a hidden set stays
   * byte-identical to the stored one and `regionsKey` already finds no change —
   * which is precisely why it is written into the payload rather than left true
   * by accident, one edit away from somebody normalising a hidden set to `[]`.
   */
  if (offersRegions && regionsKey(draft.regionIds) !== regionsKey(base.regionIds)) {
    body.regionIds = [...draft.regionIds];
  }
  if (draft.quantity !== '' && draft.quantity !== base.quantity) {
    body.quantity = Number(draft.quantity);
  }
  // `inherit` on an existing row means "leave the stored price alone" — no key.
  if (draft.priceMode === 'none' && base.priceMode !== 'none') body.unitPrice = null;
  if (draft.priceMode === 'value' && draft.price !== base.price) body.unitPrice = draft.price;
  /*
   * ⚠️ **The discount travels as a PAIR whenever either half moves**, because
   * the server refuses two live discounts — setting one while the other stays
   * stored is exactly the 422. A blank percentage box still says nothing at
   * all, which is what keeps "leave the stored value alone" available.
   */
  const money = discountWriteOf(draft);
  const baseMoney = discountWriteOf(base);
  const amountMoved = money.discountAmount !== baseMoney.discountAmount;
  const percentMoved = money.discount !== '' && money.discount !== baseMoney.discount;
  if (amountMoved || percentMoved) {
    body.discountAmount = money.discountAmount;
    body.discount = money.discount === '' ? 0 : money.discount;
  }
  /*
   * The reprice of an UNTOUCHED price (owner 2026-10-27): sent explicitly, and
   * only when it is a different figure — an equal one is no change, and must
   * not make the row read unsaved. A typed price or a typed discount already in
   * the body always wins.
   */
  if (reprice !== undefined && body.unitPrice === undefined && reprice.unitPrice !== null) {
    if (saved.unitPrice === null || !sameMoney(reprice.unitPrice, saved.unitPrice)) {
      body.unitPrice = reprice.unitPrice;
    }
  }
  if (
    reprice !== undefined &&
    reprice.discountAmount !== undefined &&
    body.discountAmount === undefined &&
    body.discount === undefined
  ) {
    const next = reprice.discountAmount;
    const stored = saved.discountAmount;
    const same = next === null ? stored === null : stored !== null && sameMoney(next, stored);
    if (!same) {
      body.discountAmount = next;
      body.discount = 0;
    }
  }
  // Omitting this leaves the stored rate alone rather than zeroing it.
  if (draft.taxRate !== '' && draft.taxRate !== base.taxRate) body.taxRate = draft.taxRate;
  if (draft.xeroTaxType !== base.xeroTaxType) {
    // An explicit null puts the line back on the company's sales default.
    body.xeroTaxType = draft.xeroTaxType === '' ? null : draft.xeroTaxType;
  }
  /*
   * ⚠️ **THE key that must never be sent from a hidden block, and `[]` is the
   * shape that would do the damage.**
   *
   * The write is a REPLACE: `[…]` replaces the whole set, `[]` CLEARS it, and
   * the key **omitted leaves the stored set exactly as it is**. So `[]` and an
   * omitted key are not two spellings of "nothing to say" — one destroys and one
   * preserves, silently, with a `200` either way.
   *
   * When `offersInclusions` is false the editor is not on screen (the owner's
   * strict rule — see this file's header), so this key is omitted outright. That
   * is what keeps a line that already holds inclusions on a product offering
   * none — with the link table empty, EVERY such line — holding them through
   * every unrelated save: the person edits the
   * quantity, the PATCH carries `quantity` and nothing else, and the set the
   * customer's document prints is untouched because nothing asked about it.
   *
   * The `inclusionsKey` comparison alone would already omit it — hiding the
   * block never clears the draft, so the two fingerprints stay equal — but that
   * is a property of state nobody edits, and this key is one line away from
   * being the place somebody "normalises" a hidden set to `[]`. The flag makes
   * it a property of the payload instead.
   *
   * A genuine clear is still expressible: it comes from the editor emptying the
   * set on a line that DOES offer them, where `offersInclusions` is true and the
   * fingerprints differ.
   *
   * ⚠️ **A project MOVE also sends it — migration 096.** The post-merge trap that
   * already couples `projectId` to `cityIds` gained a second axis: a product may
   * offer an inclusion only in certain REGIONS, and a line's region comes from
   * its project, so a `projectId`-only PATCH re-validates the inclusions the
   * line already holds and can answer `422 INCLUSION_NOT_OFFERED_IN_REGION`
   * naming a key nobody sent. Re-sending the draft makes the refusal describe
   * what is on screen. See `patchRevalidatesStoredInclusions` in
   * `@/lib/line-project`.
   *
   * ⚠️ **The `offersInclusions` gate is UNCHANGED by that, and everything above
   * about it still holds.** A hidden editor omits the key on a project move too:
   * `[]` destroys, and a refusal on a row whose editor is not on screen could
   * not be acted on there in any case.
   */
  const inclusionsMoved = inclusionsKey(draft.inclusions) !== inclusionsKey(base.inclusions);
  if (offersInclusions && (inclusionsMoved || projectMoved)) {
    body.inclusions = inclusionsPayload(draft.inclusions);
  }
  return body;
}

/**
 * Whether this row has anything to send.
 *
 * Asked of the PATCH body rather than of the draft, because "the user cleared
 * the price box back to inherit" changes the draft and changes nothing on the
 * server — and an empty PATCH is a 422.
 *
 * ⚠️ **`description` moves it in BOTH directions, and both are the point.**
 * Typing must mark the row dirty. And emptying a box that was already
 * deriving must NOT: `descriptionForWrite`
 * folds `''` to `null`, the comparison finds no change, no key is contributed,
 * and the row that would otherwise offer a Save and then send `{}` — a 422 —
 * stays clean. Asking the same builder the same question is what keeps the two
 * halves in step; a second test here would be a second answer.
 *
 * ⚠️ **It takes `offersInclusions` so that it cannot disagree with the payload.**
 * A hidden inclusions set must not make a row read "Unsaved", offer a Save
 * button, and then send a body with no keys in it — which is the 422 this
 * function exists to prevent, arriving from a control that is not on screen.
 * Asking the same builder the same question is what keeps the two in step.
 */
function isDirty(
  row: Row,
  offersInclusions: boolean,
  offersRegions: boolean,
  reprice: Reprice | undefined,
): boolean {
  if (row.saved === null) return true;
  return (
    Object.keys(updatePayload(row.draft, row.saved, offersInclusions, offersRegions, reprice))
      .length > 0
  );
}

/**
 * The four figures `computeGeneratedLinePreview` reads, resolved the way the
 * SERVER will read them — which is not the way the boxes read.
 *
 * A blank box means two different things depending on the row, and the shared
 * function cannot know which: it takes strings and has no idea whether this is
 * a POST or a PATCH. On a NEW line a blank percentage takes the server's
 * default of 0 and a blank price snapshots the catalogue; on an EXISTING line a
 * blank leaves the stored value alone. So the fallback is applied here, once,
 * against the row's own saved state.
 *
 * `catalogPrice` is the chosen product's `unitPrice` from `/products` — what a
 * new line on `inherit` will actually be given. `null` is a real answer there
 * (an unpriced product), and it is not zero.
 *
 * `companySalesTax` is the same idea for the PERCENTAGE, and it closes the
 * "Tax 0.00" the owner reported: `quote-products.service.ts` resolves a created
 * line's rate as `input.taxRate ?? companyRate ?? undefined`, so a blank box on
 * a NEW row is not 0% — it is the company's rate. Left unresolved it reaches
 * `computeGeneratedLinePreview` as `''`, which `@/lib/decimal` reads as `'0'`,
 * and the Amount cell then previews a figure the save will not produce.
 *
 * ⚠️ **RESOLVED HERE, NEVER WRITTEN INTO THE DRAFT.** The draft stays blank, so
 * `createPayload` still omits `taxRate` and the SERVER still decides. Seeding
 * the box instead would make this app a second writer of the figure and would
 * send a rate captured when the modal opened rather than the one in force when
 * Save is pressed.
 */
function previewInputOf(
  row: Row,
  catalogPrice: string | null,
  companySalesTax: CompanySalesTax,
  /**
   * The city pro-rata price the server will store on this SAVED line's next
   * save (see `repricedFor`), or `undefined` when the save leaves the price
   * alone. `null` is a real answer: the new product is unpriced.
   */
  repriced: string | null | undefined,
  /**
   * The discount amount the save carries without anybody typing it: a saved
   * line's reprice (`Reprice.discountAmount`), or a new `inherit` line's
   * resolver rounding discount. `undefined` = none.
   */
  pricedDiscount: string | null | undefined,
): GeneratedLineInput {
  const { draft, saved } = row;
  /** Blank falls back to what the save will leave in place, not to zero. */
  const stored = (typed: string, value: string | undefined) => (typed !== '' ? typed : (value ?? ''));
  const typedMoney = discountWriteOf(draft);
  const untypedDiscount =
    saved === null
      ? typedMoney.discountAmount === null && typedMoney.discount === ''
      : typedMoney.discountAmount === saved.discountAmount &&
        (typedMoney.discount === '' || typedMoney.discount === discountWriteOf(draftOf(saved)).discount);
  const money: DiscountWrite =
    pricedDiscount !== undefined && untypedDiscount
      ? { discount: pricedDiscount === null ? typedMoney.discount : '0', discountAmount: pricedDiscount }
      : typedMoney;
  /*
   * ⚠️ **NEW ROWS ONLY, and `saved === null` is the whole test on this grid.**
   * On an EXISTING line a blank percentage means *omit the key*, which leaves
   * the stored rate alone — so falling back to the company's there would
   * preview a change no PATCH is going to make. The two blanks look identical
   * in the box and mean opposite things, which is what this function exists for.
   *
   * ⚠️ **A convert row takes it too, and that is correct rather than
   * incidental**: `convertRowOf` seeds `taxRate: ''` and the conversion applies
   * the company's rate server-side, which the Tax rate cell beside this already
   * says out loud. Without this the readout and the Amount disagreed on one row.
   *
   * ⚠️ **`''` — not `'0'` — when the company states none or has not been read.**
   * That is the old behaviour byte for byte, which is what keeps a mount passing
   * no `companySalesTax` rendering exactly as it did.
   */
  const companyRate =
    companySalesTax.known && companySalesTax.rate !== null && companySalesTax.rate.trim() !== ''
      ? companySalesTax.rate.trim()
      : '';

  return {
    quantity: stored(draft.quantity, saved === null ? undefined : String(saved.quantity)),
    unitPrice:
      draft.priceMode === 'none'
        ? // An explicit null: no price on this line, which is not a price of zero.
          null
        : repriced !== undefined
          ? // A saved line still on its pro-rata price whose cities moved: the
            // server reprices it, so the preview shows the figure it will store.
            repriced
          : draft.priceMode === 'value'
            ? draft.price
            : // `inherit` — the stored price on an existing line, the catalogue
              // (city pro-rata) snapshot on a new one. Either can legitimately
              // be null.
              saved === null
              ? catalogPrice
              : saved.unitPrice,
    /*
     * ⚠️ Read off the RESOLVED pair, so the figure previewed is the one the
     * payload will carry: in amount mode the percentage is written as ZERO, so
     * falling back to the stored one here would take BOTH discounts off the
     * line and preview a total the database will never produce.
     */
    discount: money.discountAmount === null ? stored(draft.discount, saved?.discount) : '0',
    discountAmount: money.discountAmount,
    taxRate: stored(draft.taxRate, saved === null ? companyRate : saved.taxRate),
  };
}

/* ── The Disc cell: two columns, and nothing converts between them ────────── */

/*
 * ⚠️ **THE AMOUNT↔PERCENTAGE CONVERSION IS DELETED. DO NOT BRING IT BACK.**
 *
 * `f_quote_products` carries `discount_amount` beside `discount`, mutually
 * exclusive, so an amount is stored as the amount. What stood here —
 * `centsOf`, `grossOf`, `discountMoneyOf`, a `DiscountAmountVerdict` with three
 * refusals, and a `DiscountEntry` carrying the money the stored percentage
 * really took off — existed only because there was ONE column and it was a
 * percentage.
 *
 * The refusals were not validation, they were the cases the conversion could
 * not express: an amount larger than the line, a line worth `0.00`, a line with
 * no price. The server owns those now, and the only thing left worth checking
 * here is that the amount is a well-formed money string.
 */

/**
 * ⛔ **`DISCOUNT_AMOUNT_PATTERN`, NOT `PRICE_PATTERN` — and this function used
 * to read the other one.** Its doc line said *"an amount is money, so it is a
 * price"*, which was true until 2026-09-12 and is now precisely the confusion
 * to avoid: a price may be negative, a discount may not. See the block at both
 * declarations.
 */
function isWellFormedAmount(value: string): boolean {
  return DISCOUNT_AMOUNT_PATTERN.test(value.trim());
}

/* ── Whether this row's product is sold through projects ──────────────────── */

/**
 * ⚠️ **"Sold through projects" is DERIVED server-side and never a flag**, so
 * this is a reproduction of the server's own test rather than a reading of one:
 * the guard matches `product_id = ? AND is_active = 1`, and
 * `GET /projects?productId=N&isActive=true` asks byte for byte the same
 * question. It therefore always arrives one project late, knowingly.
 *
 * Four states, because two of them are not "no":
 *
 * - `not-chosen` — no item on the row yet. Nothing can be said about a product
 *   that has not been named. ⚠️ *SUPERSEDED 2026-09-19: "and the PROJECT-FIRST
 *   path lives in this state."* It did, and the owner has since decided this
 *   state HIDES the control, which is what ends that path — see
 *   {@link showsProjectControl}. The state itself is unchanged and still
 *   distinct from `none`: **not knowing is not a no.**
 * - `offers` — at least one active project sells this item.
 * - `none` — a definite no, from a complete list.
 * - `unknown` — the list failed, is still loading, or was truncated at 100. A
 *   truncated list cannot say "none": the 101st project may sell this very
 *   item.
 */
type ProjectOffer = 'not-chosen' | 'offers' | 'none' | 'unknown';

/**
 * The verdict, from the row's item and the state of the active-project list.
 *
 * `matching` is how many ACTIVE projects sell this item — the same filtered
 * list the picker is built from, so the control and the gate cannot disagree
 * about what is on offer.
 */
function projectOfferOf(
  productId: number | null,
  matching: number,
  list: { loading: boolean; failed: boolean; truncated: boolean },
): ProjectOffer {
  if (productId === null) return 'not-chosen';
  if (list.loading || list.failed) return 'unknown';
  if (matching > 0) return 'offers';
  // ⚠️ A truncated list cannot say "none" — the 101st project may sell exactly
  // this item, and `/projects` has no "give me all".
  return list.truncated ? 'unknown' : 'none';
}

/**
 * Whether the project control is on screen for this row.
 *
 * 🔴 **`not-chosen` NOW HIDES — owner's decision, 2026-09-19.** ⚠️ *SUPERSEDED:
 * "**`unknown` and `not-chosen` both SHOW** … `not-chosen` shows because
 * choosing a project is how the item gets picked on the project-first path."*
 * The control is not offered until an item is chosen on the row.
 *
 * ⛔ **THE ACCEPTED COST, AND IT IS A DECISION RATHER THAN A TIDY-UP: THIS
 * REMOVES THE PROJECT-FIRST PATH.** A line resolved from EITHER end — picking a
 * project SET the item, which is how half this grid was used — and there is now
 * one direction only: item first, then project. Nothing errors and nothing is
 * refused; the second way in is simply gone, so somebody who worked project-first
 * meets a row that appears to have lost a control rather than one that is waiting
 * for a product. **Do not "restore" it as a bug fix**; it was put to the owner
 * with that cost named and taken anyway.
 *
 * ⚠️ **`'unknown'` STILL SHOWS, and that arm is untouched.** A list that is
 * loading, failed or truncated is not evidence a product has no projects, and the
 * line whose product IS sold through projects and names none is refused at write
 * time (`project-required`) with nothing on screen to satisfy it. Same decision
 * the inclusions gate takes for its own `'unknown'`. `'none'` is unchanged too.
 *
 * ⚠️ **Written as a DENY-list, deliberately — the two hiding arms are named and
 * everything else shows.** This gate's stated design is to fail towards SHOWING,
 * so a fifth `ProjectOffer` member must default to visible; an allow-list of the
 * showing arms would silently hide it.
 *
 * ⚠️ **A row already HOLDING a project always shows it.** Hiding a stored value
 * makes real data invisible and uneditable, which is not what this gate is for
 * — it hides a choice that cannot be made, never a fact the line records. That
 * clause is what keeps a project-first row that HAS already resolved visible, so
 * the change reaches only rows where nothing has been chosen at either end.
 */
function showsProjectControl(row: Row, offer: ProjectOffer): boolean {
  if (row.draft.projectId !== '' || row.saved?.project != null) return true;
  return offer !== 'none' && offer !== 'not-chosen';
}

/**
 * Whether the cities block is on screen for this row — **both** the "All cities"
 * flag and the city picker, which stand or fall together because the flag means
 * "every city this project has" and is unreadable beside a hidden picker.
 *
 * Cities are named THROUGH a project — the option list is the project's own —
 * so without one there is nothing to offer and the control could only render
 * disabled beside a sentence saying why.
 *
 * ⚠️ **`projectHasNoCities` HIDES it, and that is the owner's, asked for four
 * times.** *SUPERSEDED, the whole third sentence: "It follows the project
 * control, plus the same never-hide-data rule: a row holding cities, or flagged
 * for all of them, keeps its block whatever else is true."* Two things were
 * wrong with it. It described a gate that took **only** the project verdict, so
 * a line on a project declaring no cities — `Virtual Masterclasses`, `LAE`, the
 * products the owner names — got an **empty, greyed picker and an All-cities box
 * over nothing**, which is refusal 6 (`project-has-no-cities`) whose own
 * documented fix is **on the project, not on this line**. And its
 * "whatever else is true" was already false for `!showsProject`, which returned
 * `false` ahead of it: a clause that read as a guarantee and was one branch
 * short of being one.
 *
 * ⚠️ **It is the same fact {@link showsRegionsControl} opens the regions block
 * on, read from the other end**, and the two are deliberately one value rather
 * than two tests of one idea — a row whose project declares no cities loses its
 * city controls and gains its region ones in the same render. Recomputing "does
 * this project declare cities" here would be a second answer, and the two would
 * come apart on exactly the row that has both.
 *
 * ⚠️ **`projectHasNoCities` is a DEFINITE no and never an unresolved one.** It
 * is `false` for a project still loading, a failed or truncated list, and a
 * retired project an active-only fetch never returns — so **unknown keeps the
 * control up**. See {@link RowProject.projectHasNoCities}, where the three
 * conditions are set. A control hidden because a list had not arrived is
 * indistinguishable from a bug and unrecoverable from the user's side.
 *
 * ⚠️ **NEVER-HIDE-DATA, and it sits ABOVE the new test on purpose.** A row that
 * holds cities, or carries the flag, keeps its block even on a project that
 * declares none — and that pair is **reachable, not defensive**: a project whose
 * cities were removed after the line was written leaves `draft.cityIds` full
 * while `selected.cities` is empty, and those chips are pinned back into
 * {@link RowProject.cityOptions} labelled "no longer on this project". Hidden,
 * they would be invisible and unremovable while {@link writesCities} still sent
 * them.
 *
 * ⚠️ **Hiding it must never clear `draft.cityIds`.** A cities write REPLACES
 * the set, so `[]` clears it — a hidden block that tidied its own draft would
 * destroy a line's stored cities on the next unrelated save, with a `200`.
 * Hiding is a render branch and nothing else; the payload builders read the
 * draft, which nothing but the control itself ever moves. ⚠️ **Unlike
 * `offersRegions` and `offersInclusions`, this verdict is deliberately NOT a
 * parameter of {@link createPayload}, {@link updatePayload} or
 * {@link convertShapeOf}** — those omit a key because the key is a full REPLACE
 * of a set the person cannot see, whereas `cityIds` here is the set they can
 * still see everywhere else and `allCities` is a live rule. Wiring this gate
 * into a builder is how the hiding becomes the data loss.
 */
function showsCitiesControl(
  row: Row,
  showsProject: boolean,
  projectHasNoCities: boolean,
): boolean {
  if (!showsProject) return false;
  if (row.draft.allCities || row.draft.cityIds.length > 0) return true;
  if (projectHasNoCities) return false;
  return row.draft.projectId !== '';
}

/**
 * Whether the LINE's own regions block is on screen for this row.
 *
 * ⚠️ **It used to be the pure COMPLEMENT of the project block, and it is not
 * any more.** Regions began as the geography of a product with no projects at
 * all — `EMBA`, `LAE`, `Virtual Masterclass` — which carried none anywhere
 * before migration 112. The owner then added the second case below, so this now
 * answers a two-part question rather than negating one verdict. It still reads
 * the verdict {@link showsProjectControl} returns rather than asking “is this
 * sold through projects” a second time and drifting from it.
 *
 * ⚠️ **`projectHasNoCities` is the SECOND case, and it is the owner's, 2026-08-31.**
 * A line whose project declares no cities got an empty city picker and nothing
 * else — which is refusal 6, `project-has-no-cities`, whose own documented fix is
 * **on the project and not on this line**. So the person was shown a control they
 * could not act on and no way to record the line's geography at all. Regions are
 * what such a line can honestly carry.
 *
 * ⚠️ **`showsProject` fails towards SHOWING, so this fails towards HIDING.** A
 * loading, failed or truncated project list keeps the project block up and
 * leaves this one down. That is the right direction: a region set offered on a
 * row that turns out to be project-backed invites somebody to record a geography
 * the push does not read there, where a region set briefly absent costs one
 * re-render. `projectHasNoCities` is built the same way — it is `false` for
 * every state that is merely UNRESOLVED, and true only where a chosen project
 * came back and declared nothing. See {@link RowProject.projectHasNoCities}.
 *
 * ⚠️ **A row that already HOLDS regions keeps its block whatever else is
 * true** — the same never-hide-data rule the two controls above follow. Hiding a
 * stored value makes real data invisible and uneditable, and this set is what
 * gives a projectless line its Xero tracking Location.
 *
 * Hiding is a render branch and nothing else. It never touches
 * `draft.regionIds`: a regions write REPLACES the set, so a block that tidied
 * its own draft on the way to being hidden would clear a line's stored regions
 * on the next unrelated save, with a `200` and nothing to restore from.
 */
function showsRegionsControl(
  row: Row,
  showsProject: boolean,
  projectHasNoCities: boolean,
): boolean {
  /*
   * 🔴 **A ROW WITH NO ITEM OPENS NOTHING — added 2026-09-19 WITH the project
   * gate above, and it is not optional tidying.** `!showsProject` used to mean
   * one thing, "this product is not sold through projects", and since that gate
   * started hiding `not-chosen` it means two. Left as it was, the empty row that
   * just lost its project picker would have GAINED a regions picker in the same
   * render — a straight swap of one control for another on a line that has not
   * said what it sells.
   *
   * That is the direction this function's own doc calls wrong: a region set
   * offered on a row that turns out to be project-backed invites somebody to
   * record a geography the push does not read there. A row with no item is the
   * purest case of "turns out to be" — nothing whatever is known yet.
   *
   * ⚠️ It reads `draft.productId` directly rather than taking a fourth boolean:
   * "has an item been chosen" is a one-field read off the row this function
   * already has, not a second derivation of "is this sold through projects",
   * which is the thing that must keep coming from `showsProject` alone.
   *
   * The never-hide-data rule still outranks it — a row already holding regions
   * keeps them, or a stored set becomes invisible and unremovable.
   */
  if (row.draft.productId === '') {
    return row.draft.regionIds.length > 0 || (row.saved?.regions.length ?? 0) > 0;
  }
  if (!showsProject) return true;
  if (projectHasNoCities) return true;
  return row.draft.regionIds.length > 0 || (row.saved?.regions.length ?? 0) > 0;
}

interface RowProblems {
  fieldErrors: Record<string, string>;
  /** A reason the row cannot be sent at all, shown at row level. */
  error: string | null;
  /**
   * Something true about the row that is **not** a reason to refuse it.
   *
   * ⚠️ **`notice` AND `error` ARE NOT TWO TONES OF ONE THING — only `error`
   * blocks.** `blockedKeys` reads `error` and never this, which is the whole
   * point of the field existing rather than the duplicate message simply being
   * softened in place: a message that still sat on `error` would have gone on
   * disabling Save no matter how gently it was worded.
   *
   * Its one producer today is the repeated-product check. Migration 129 dropped
   * the (quote_id, product_id, project_key) unique key, so a document may sell
   * one product on as many lines as it likes — measured across 1,105 real Xero
   * documents, 162 groups do exactly that and not one is a true duplicate. The
   * grid still says so, because billing the same thing twice by accident is
   * worth catching; it just no longer pretends the server would refuse it.
   */
  notice: string | null;
}

/**
 * Whether this save will actually send `cityIds`.
 *
 * It matters because the whole set is re-validated when it is sent and skipped
 * entirely when it is not — which is what keeps a line editable after its
 * project stops visiting one of the cities it already names. A check that
 * refused those rows unconditionally would make a historical line unsaveable
 * over a field nobody touched.
 */
function writesCities(row: Row): boolean {
  /*
   * ⚠️ A flagged line sends `allCities` INSTEAD of `cityIds` — see the two
   * payload builders — so its parked explicit set is never written and never
   * checked. Answering `true` here would refuse the row over chips no request
   * carries, and the person could not clear the error without unticking.
   */
  if (row.draft.allCities) return false;
  if (row.saved === null) return row.draft.cityIds.length > 0;
  const base = draftOf(row.saved);
  return (
    row.draft.projectId !== base.projectId ||
    citiesKey(row.draft.cityIds) !== citiesKey(base.cityIds) ||
    // Unticking the flag writes `cityIds` even when the array itself is the
    // same `[]` it has been all along.
    row.draft.allCities !== base.allCities
  );
}

/**
 * ⚠️ **Lifted to a constant only so `validateRow` can assemble the row's
 * notices in a LIST.** The wording is unchanged and its history is recorded at
 * the return site; nothing else about the message moved.
 */
const DUPLICATE_PRODUCT_NOTICE =
  'This product is on more than one line of this quote. That is allowed — check it is deliberate.';

/**
 * Everything that can be refused without a request.
 *
 * `retiredIds` is the deactivation trap: a line still READS a retired
 * inclusion happily, but re-sending its id is a 422 even when nothing about it
 * changed. So a retired entry is only fatal when the set is actually being
 * written.
 *
 * `declaredCityIds` is the resolved project's own city ids, and **`null` means
 * "we cannot say"** — the project list failed, or was truncated at 100, or the
 * line holds a retired project that an active-only fetch will never return.
 * Nothing is refused on a `null`: guessing a refusal from a list we know is
 * incomplete would block a save the server would have accepted.
 *
 * `offersInclusions` matches the payload builders exactly. When it is false the
 * inclusions checks are skipped outright, because the key is not being sent: a
 * refusal over a set no request carries would be an error on a control that is
 * not on screen, which is unfixable from this page and blocks every other edit
 * to the row.
 *
 * *SUPERSEDED 11 Sep 2026: a sixth parameter, `inclusionsRequired`, carried the
 * owner's at-least-one rule as a second and strictly narrower inclusions gate.
 * The rule was withdrawn, so the parameter went with it rather than being left
 * accepted and ignored — an argument nothing reads is the shape this repo has
 * already been bitten by.*
 */
function validateRow(
  row: Row,
  duplicatePairs: Set<string>,
  retiredIds: Set<number>,
  declaredCityIds: Set<number> | null,
  offersInclusions: boolean,
): RowProblems {
  const fieldErrors: Record<string, string> = {};
  const draft = row.draft;

  if (draft.productId === '') fieldErrors.productId = 'Choose a product.';

  /*
   * Measured on the value that will actually be SENT, not on what is in the
   * box: a box holding only whitespace is a `null` and has no length to refuse.
   * A stored description can never exceed this — the server refuses it too — so
   * the only way to reach it is by typing or pasting, which is always a write.
   */
  const description = descriptionForWrite(draft.description);
  if (description !== null && description.length > MAX_DESCRIPTION_LENGTH) {
    fieldErrors.description = `At most ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters — this is ${description.length.toLocaleString()}.`;
  }

  /*
   * ⚠️ **Every city check below is a statement about an EXPLICIT set, and a
   * flagged line has none.** The 200-city cap, "choose this line's project
   * before naming cities" and the stray-city check all describe an array this
   * row is not going to send — `allCities` goes in its place. Running them
   * against the parked set would refuse a row over chips no request carries,
   * and the error could not be cleared without unticking the box.
   *
   * ⚠️ **A PROJECT is still required, and that is the one refusal that
   * survives.** "All cities" is meaningless without one: there is nothing for
   * the server to resolve against, and the line would come to mean whatever a
   * project chosen later happened to declare. Worded for the FLAG rather than
   * for a list, because there is no list here to name.
   *
   * Marked on `allCities` rather than on `cityIds` so it names the control that
   * caused it — and so a server 422 on either key lands somewhere visible,
   * since {@link CitiesControl} renders both.
   */
  if (draft.allCities) {
    if (draft.projectId === '') {
      fieldErrors.allCities =
        'Choose this line’s project — “all cities” resolves against it.';
    }
  } else if (draft.cityIds.length > MAX_CITIES_PER_LINE) {
    fieldErrors.cityIds = `At most ${MAX_CITIES_PER_LINE} cities on one line.`;
  } else if (draft.cityIds.length > 0 && draft.projectId === '') {
    /*
     * Cities named on a line with no project is its own server refusal, and
     * which of the two sentences it gets depends on whether the PRODUCT is sold
     * through projects at all — a fact only the server holds. So this says the
     * thing that is true either way and names no cause it cannot know.
     */
    fieldErrors.cityIds = 'Choose this line’s project before naming cities.';
  } else if (declaredCityIds !== null && writesCities(row)) {
    // Every offender, not the first, so the wrong chips are all marked in one
    // pass instead of the user fixing one and resubmitting to find the next.
    const stray = draft.cityIds.filter((id) => !declaredCityIds.has(id));
    if (stray.length > 0) {
      fieldErrors.cityIds = `Not on this line’s project: ${stray.length} of ${draft.cityIds.length} chosen.`;
    }
  }

  /*
   * ⚠️ **AN EMPTY BOX IS FINE ON AN ORDINARY ROW AND A REFUSAL ON A SEEDED ONE,
   * AND THE ASYMMETRY IS THE POINT.**
   *
   * `createPayload` omits `quantity` when the box is empty, and an omitted
   * `quantity` is **1** on the server. On a row somebody typed that is a
   * convenience nobody has to think about. On a row seeded from a PLANNED
   * figure that could not be expressed — `"2.50"`, `"0.5"` — it is the exact
   * silent substitution `plannedProductLines()` refuses to make on the way in,
   * arriving anyway by the back door: a 201, a well-formed document, and a
   * quantity nobody chose printed on it.
   *
   * So the seeded row is refused while the box is empty. That puts it in
   * `blockedKeys`, withholds it from `onDraftLinesChange`, raises the grid's own
   * blocking banner, and disables the parent's Save — which is a person being
   * asked one question, rather than a wrong number nobody is ever asked about.
   */
  if (draft.quantity === '') {
    if (row.plannedQuantity !== null) {
      // The planned figure is named, because "enter a quantity" over a box that
      // opened empty on a row the person did not create explains nothing.
      fieldErrors.quantity = `Planned ${row.plannedQuantity} — enter a whole number of 1 or more.`;
    }
  } else {
    const quantity = Number(draft.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      // Integer only — the schema does not coerce, and a fraction is a 422
      // even though Xero itself allows one.
      fieldErrors.quantity = 'A whole number of 1 or more.';
    }
  }

  if (draft.priceMode === 'value' && !PRICE_PATTERN.test(draft.price.trim())) {
    /*
     * ✅ **THE NEGATIVE BRANCH IS GONE — a line price MAY be negative since
     * migration 171 (owner's instruction, 2026-09-12).** This read
     * *"A line price cannot be negative."*, justified as *"nothing on a LINE may
     * be negative, and the server refuses one too"*. **Both halves are now
     * false**: a DEDUCTION LINE is ordinary business here — INV-0886 carries
     * one reading "Deduction for Cape Town Participation Fee" at `-4150.00` —
     * and the API's `UNIT_PRICE_PATTERN` gained a `-?` in the same change.
     *
     * ⚠️ **The seeded-row case that made this branch reachable is UNCHANGED and
     * is now simply correct.** A planned line amount could always be negative
     * (`splitAmountOverQuantity` divides a signed amount deliberately, for a
     * credit note), so a seeded row could open holding `-3333.33` and be
     * refused by the grid it had just been seeded into. It now saves.
     */
    const typed = draft.price.trim();
    fieldErrors.unitPrice =
      typed === ''
        ? 'Enter a price, or record the line as having none.'
        : 'Up to two decimal places, and no currency symbol.';
  }

  /*
   * ⚠️ **The DISCOUNT is checked in exactly one of its two shapes, because only
   * one is on screen.** The mode decides which control the person can see, and
   * reporting the other's format would name a box that is not there.
   */
  if (draft.discountAmount === null) {
    const trimmed = draft.discount.trim();
    // `Number()` here compares a bound, never money. The value itself is sent
    // as the string the user typed, untouched by a float.
    if (trimmed !== '' && (!PERCENT_PATTERN.test(trimmed) || Number(trimmed) > 100)) {
      fieldErrors.discount = 'A percentage between 0 and 100.';
    }
  } else {
    const amount = draft.discountAmount.trim();
    if (amount !== '' && !isWellFormedAmount(amount)) {
      /*
       * ⚠️ **THE NEGATIVE BRANCH BECAME THE LIKELY MISTAKE ON 2026-09-12**, when
       * a line PRICE gained a sign (migration 171, deduction lines). Typing
       * `-500` here is the natural way to say "take this off" — and the generic
       * sentence answers it by naming DECIMAL PLACES, a rule the figure already
       * keeps, which sends the reader to look at the wrong thing.
       *
       * ⛔ A discount may never be negative: `ck_*_discount_amount_sign` refuses
       * it, and the database reports that only as errno 4025 — a 422 naming no
       * field at all. So this sentence is the only place a person is told what
       * the box will not take, and what to use instead.
       *
       * The same sentence is in `deal-convert-lines.ts` and in the other two
       * grids. Four copies of a refusal is this file's existing shape (each grid
       * owns its own refusal set, deliberately — see the discount section); the
       * WORDING must not drift, because a person billing a quote meets more than
       * one of them on the way to a single document.
       */
      fieldErrors.discountAmount = amount.startsWith('-')
        ? 'A discount cannot be negative. To reduce a line below zero, use a negative price.'
        : 'Up to two decimal places, and no currency symbol.';
    }
  }

  const taxRate = draft.taxRate.trim();
  if (taxRate !== '' && (!PERCENT_PATTERN.test(taxRate) || Number(taxRate) > 100)) {
    fieldErrors.taxRate = 'A percentage between 0 and 100.';
  }

  /*
   * ⚠️ Skipped wholesale when the block is hidden — the same condition the two
   * payload builders use, and for the same reason. Nothing below can be true of
   * a key that is not sent, and an error marking a control the person cannot see
   * would leave the row permanently unsaveable over a field they never touched.
   */
  if (offersInclusions) {
    /*
     * ⛔ AN EMPTY SET IS NOT REFUSED, AND THAT IS THE RULE RATHER THAN A GAP.
     *
     * *SUPERSEDED, owner's decision 11 Sep 2026: a line whose product offered
     * inclusions had to name one, and an empty set rendered
     * `INCLUSION_REQUIRED_MESSAGE`.* That rule predated per-inclusion
     * requiredness: with no way to say WHICH inclusion mattered, "at least one"
     * was the only thing the client could ask for. Migration 159 lets an admin
     * mark each offer required, and the owner chose PRE-TICK, STILL REMOVABLE —
     * so a refusal here would contradict the removability that was the whole
     * point of the choice.
     *
     * ⚠️ **Nothing on the server ever backed this up.** A line with no
     * inclusions is a clean `2xx` on all three endpoints, and the backend
     * declined enforcement explicitly (migration 159's header) because this app
     * hides the inclusions block on lines whose product is not gated for it, so
     * an enforced refusal could name a control the person cannot see — the
     * shape `PROJECT_HAS_NO_CITIES` already has. **Do not reinstate this as a
     * client-side rule; it would be a convention dressed as a contract.**
     *
     * The three checks below all object to something PRESENT in the set, so
     * every one of them is unreachable on an empty set and none needs the
     * removed branch ahead of it.
     */
    if (draft.inclusions.length > MAX_INCLUSIONS_PER_LINE) {
      fieldErrors.inclusions = `At most ${MAX_INCLUSIONS_PER_LINE} inclusions on one line.`;
    } else if (
      draft.inclusions.some(
        (entry) => entry.quantity !== '' && !isValidInclusionQuantity(Number(entry.quantity)),
      )
    ) {
      fieldErrors.inclusions = 'Every count must be a whole number of 1 or more.';
    } else {
      const writingSet =
        row.saved === null
          ? draft.inclusions.length > 0
          : inclusionsKey(draft.inclusions) !== inclusionsKey(draftOf(row.saved).inclusions);
      if (writingSet && draft.inclusions.some((entry) => retiredIds.has(entry.inclusionId))) {
        fieldErrors.inclusions =
          'One of these inclusions has been retired. Remove it to save.';
      }
    }
  }

  const duplicate =
    draft.productId !== '' && duplicatePairs.has(pairKey(draft.productId, draft.projectId));

  /*
   * ⚠️ **A NOTICE AND NEVER A FIELD ERROR, because the seeded price is not
   * wrong.** `10000.00` over 3 is `3333.33` and the line comes to `9999.99`;
   * that is the best a per-unit price can do and a person may accept it. A
   * `fieldErrors.unitPrice` here would land the row in `blockedKeys` and
   * disable the parent's Save over a penny — a button wrongly disabled is a
   * false statement with nobody watching. So it says what is true and refuses
   * nothing.
   *
   * ⚠️ **It stands down the moment either box moves.** The sentence is about
   * the SEEDED figures; once the price or the quantity changes it no longer
   * describes the row, and a stale total naming an amount the line no longer
   * has is worse than no sentence at all.
   */
  const shortfall = row.plannedShortfall;
  const shortfallNotice =
    shortfall !== null &&
    draft.priceMode === 'value' &&
    draft.price === shortfall.unitPrice &&
    draft.quantity === shortfall.quantity
      ? `Planned ${shortfall.plannedAmount} — ${shortfall.unitPrice} × ${shortfall.quantity} comes to ${shortfall.lineAmount}. The planned amount does not divide evenly.`
      : null;

  /*
   * ⚠️ **BOTH notices render, joined — never one `??` the other.** A repeated
   * product and a plan that will not divide are two different true things about
   * one row, and coalescing would drop whichever came second exactly when the
   * row is under the most scrutiny.
   */
  const notices = [shortfallNotice, duplicate ? DUPLICATE_PRODUCT_NOTICE : null].filter(
    (line): line is string => line !== null,
  );

  return {
    fieldErrors,
    /*
     * ⚠️ **A REPEATED PRODUCT IS NO LONGER AN ERROR, AND THE PROJECT NO LONGER
     * COMES INTO IT.** *SUPERSEDED: an `error` reading "Already on this quote
     * under that project. Choose a different one." — with a second sentence for
     * the project-less case telling the reader to "give one of them a project".*
     *
     * Both sentences instructed the reader to change something to make a save
     * possible, and there is nothing left to change: migration 129 dropped the
     * (quote_id, product_id, project_key) key, so the save was going to succeed
     * either way. Keeping the wording and merely relaxing the gate would have
     * left advice that reads as a requirement.
     *
     * ⚠️ This is the THIRD wording of this message and the second time it told
     * people to do something that could not work — it named a CITY until
     * migration 068, then a PROJECT until 129. Each time the schema moved and
     * only the sentence was missed. It now names neither, and says what is true
     * rather than what to do.
     */
    error: null,
    notice: notices.length === 0 ? null : notices.join(' '),
  };
}

/**
 * The (product, project) pairs that more than one row claims.
 *
 * Checked across every row because the whole document is loaded — `GET
 * /quotes/{id}/products` is unpaginated — so this is complete rather than a
 * guess about a page. An inline grid lets someone create the clash by repointing
 * a project after both rows already exist, which no per-row check would catch.
 */
function duplicatePairsOf(rows: Row[]): Set<string> {
  const seen = new Map<string, number>();
  for (const row of rows) {
    if (row.draft.productId === '') continue;
    const key = pairKey(row.draft.productId, row.draft.projectId);
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [key, count] of seen) if (count > 1) duplicates.add(key);
  return duplicates;
}

/**
 * The `path` values a 422 may name that a ROW can actually MARK.
 *
 * ⚠️ **These are the keys a row SENDS, not the keys its draft holds.** A 422's
 * `path` names a request key, so the price is `unitPrice` here even though the
 * draft splits it across `priceMode` and `price` — and those two are the only
 * place the draft shape and the write shape diverge, which is why they are
 * named rather than the whole list being typed out. Everything else comes
 * straight off `blankDraft()`, so a field added to `Draft` cannot leave a stale
 * list behind.
 *
 * Anything outside this list belongs in the ROW-LEVEL message, because a path
 * can be non-empty and still bind to nothing — the worse case, since it looks
 * bindable: it is routed to a cell that does not exist and disappears from the
 * row's own error line entirely. The live example is the retired singular
 * `cityId` against this grid's `cityIds`.
 */
const ROW_WRITE_KEYS: readonly string[] = [
  ...Object.keys(blankDraft()).filter((key) => key !== 'priceMode' && key !== 'price'),
  'unitPrice',
];

/**
 * A failed write, in the words that help.
 *
 * The unroutable messages lead: on a 422 the top-level message is the generic
 * "Validation failed", and the key that was actually rejected is named only in
 * a detail — one whose `path` is empty, or one whose path names something this
 * grid has no cell for. Neither can be marked, so neither may be dropped.
 */
function describeFailure(error: unknown): {
  message: string;
  fieldErrors: Record<string, string>;
  /**
   * The refusal, classified, when it was one of the seven project/city ones.
   *
   * `null` means "not one of these" and the caller falls back to `message`
   * exactly as before — never to a blank. An unrecognised SENTENCE does not
   * produce `null`; that is `kind: 'unknown'` with the server's own words and
   * the right control still marked.
   */
  refusal: LineRefusal | null;
} {
  if (error instanceof ApiError) {
    /*
     * `inclusions.0.quantity` and friends ARE routable — the inclusions editor
     * owns the whole set — so they are declared known and folded onto that one
     * key below rather than being read as unbindable.
     */
    const inclusionPaths = Object.keys(error.fieldErrors).filter(
      (path) => path === 'inclusions' || path.startsWith('inclusions.'),
    );
    const { fields, unroutable } = error.routeFieldErrors([...ROW_WRITE_KEYS, ...inclusionPaths]);

    const inclusionMessages: string[] = [];
    for (const path of inclusionPaths) {
      const message = fields[path];
      if (message === undefined) continue;
      inclusionMessages.push(message);
      delete fields[path];
    }
    if (inclusionMessages.length > 0) fields.inclusions = inclusionMessages.join(' ');

    /*
     * `422 QUANTITY_NOT_MULTIPLE` names `quantity`, so it routes above. A body
     * that names no path still belongs on the Qty box — matched on the CODE.
     */
    if (error.code === 'QUANTITY_NOT_MULTIPLE' && fields.quantity === undefined) {
      fields.quantity = error.formMessage ?? error.message;
    }

    return {
      /*
       * A 409's own message is the useful one and is matched on `code`
       * elsewhere, never on its text — the wording changed with migration 057.
       * A 403 body carries no `details` at all, so `unroutable` is empty there
       * and the message renders as a row-level banner rather than per field,
       * which is the only correct place for it.
       */
      message: unroutable.length > 0 ? unroutable.join(' ') : error.message,
      fieldErrors: fields,
      refusal: lineRefusalOf(error),
    };
  }
  return { message: 'The request did not reach the server.', fieldErrors: {}, refusal: null };
}

/* ── Reference data ───────────────────────────────────────────────────────── */

interface ProductRow {
  id: number;
  productName: string;
  productCode: string | null;
  /**
   * The catalogue price, exactly as the string it is stored as.
   *
   * Read for ONE reason: a new line left on `inherit` snapshots this at POST
   * time, so it is what the Amount preview must use for such a row. `null`
   * means unpriced and is NOT `"0.00"` — never `?? 0`, and never through a
   * float.
   */
  unitPrice: string | null;
  /**
   * What a line selling this product should START its discount at — migration
   * 172.
   *
   * ⚠️ **`null` is NO RECOMMENDATION; `"0.00"` is a recommendation of nothing
   * off.** The same distinction {@link ProductRow.unitPrice} draws one field up,
   * and the same `?? 0` that would destroy it. `recommendedDiscountSeed` reads
   * `null` as *leave the line alone* and `"0.00"` as a figure to seed, so
   * collapsing them here would start discounting lines to zero — in AMOUNT mode,
   * which is a different write from the percentage the line was using.
   *
   * ⛔ **Carried on the CATALOGUE read that was already being made**, never a
   * second fetch. `/products` serves it, this hook already asks for every active
   * product, and a per-line lookup would be a request per row against a limit
   * shared across the office. ⚠️ *SUPERSEDED: "120/60s."* It is **12,000/60s**
   * (corrected 2026-09-17); the objection is the per-row SHAPE, not the
   * arithmetic.
   */
  recommendedDiscountAmount: string | null;
  /**
   * Lines must carry a whole multiple of this (`lib/product-units.ts`).
   * `null` = any quantity; an older API that omits the key reads as `null`.
   */
  quantityStep: number | null;
  /** What one unit is called, SINGULAR ("license"). `null` = not sold per unit. */
  unitLabel: string | null;
}

/**
 * A product sold PER UNIT — it names a unit or a quantity step (coordinator
 * ruling, 2026-10-16). Only for these is `recommendedDiscountAmount` a per-unit
 * figure, scaled by the quantity before it is seeded; every other product's
 * recommendation is a whole-line amount, unchanged.
 */
function isPerUnitProduct(product: ProductRow | undefined): boolean {
  if (product === undefined) return false;
  return (product.unitLabel?.trim() ?? '') !== '' || product.quantityStep !== null;
}

/**
 * The quantity-step refusal for a row, or `null`.
 *
 * ⚠️ **A STORED line is judged only once its quantity or product has moved.**
 * Legacy lines (one bundle at quantity 1) predate the step and must stay
 * editable on every other field; the server checks the step only when the
 * quantity is written. A row never written (new, seeded, converted) is always
 * judged, because its quantity IS being sent.
 */
function quantityStepProblemOf(row: Row, product: ProductRow | undefined): string | null {
  if (product === undefined) return null;
  if (row.saved !== null) {
    const base = draftOf(row.saved);
    if (row.draft.quantity === base.quantity && row.draft.productId === base.productId) return null;
  }
  return quantityStepError(product, row.draft.quantity);
}

/**
 * The fieldErrors key the step refusal travels under — NOT `quantity`, so the
 * Qty box can hold it back while the person is still typing (5 on the way to
 * 50) and show it on blur. It still lands in `fieldErrors`, so the row is
 * blocked from saving either way.
 */
const QUANTITY_STEP_ERROR_KEY = 'quantityStep';

/**
 * The product catalogue, once.
 *
 * Fetched whole and filtered inside each Combobox rather than searched
 * server-side: there are 28 products, every row needs the same list, and one
 * shared search term driving eight dropdowns reads as a bug.
 */
function useProducts() {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listProducts({ perPage: PRODUCTS_PER_PAGE, active: true }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(
          result.data.map((row) => ({
            id: row.id,
            productName: row.productName,
            productCode: row.productCode,
            unitPrice: row.unitPrice,
            recommendedDiscountAmount: row.recommendedDiscountAmount,
            quantityStep: row.quantityStep ?? null,
            unitLabel: row.unitLabel ?? null,
          })),
        );
        setTruncated(result.meta.totalPages > 1);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      });
    return () => controller.abort();
  }, []);

  return { rows, truncated, failed };
}

/**
 * The campaign-inclusions master.
 *
 * `?isActive=true` is opt-in with no default: called bare it returns retired
 * rows too, every one of which is a guaranteed 422 on write. There is no
 * `sort` param — rows arrive in `sortOrder, name`, already in picker order.
 */
function useCampaignInclusions() {
  const [rows, setRows] = useState<CampaignInclusion[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCampaignInclusions({ isActive: true, perPage: INCLUSIONS_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      });
    return () => controller.abort();
  }, []);

  return { rows, failed };
}

/**
 * Which campaign inclusions each product on this document OFFERS — the input to
 * the rule in `@/lib/product-inclusions`.
 *
 * ## The fetching strategy, and why it is per DISTINCT PRODUCT
 *
 * There is no bulk endpoint: `GET /products/{id}/inclusions` answers for one
 * product, and `/products` does not carry the set. So the choice is which ids to
 * ask about, and there were three candidates:
 *
 * | | requests | why not |
 * | --- | --- | --- |
 * | per LINE | one per row, re-fired on every product change | a rate limit shared across the office — ⚠️ *SUPERSEDED: "120/60s"*, it is **12,000/60s** (corrected 2026-09-17) — spent on the same id repeatedly |
 * | the whole CATALOGUE | 28, up front | 27 of them for products this document does not sell |
 * | **per distinct product on the document** | **one per distinct product, ever** | ← this |
 *
 * A document typically sells one to three distinct products, so this is one to
 * three requests for the life of the component. It is the same reasoning as
 * {@link useActiveProjects} — resolve for the grid, not for the row — but it
 * cannot be one request, so it is the smallest set that answers every row.
 *
 * ⚠️ **`requested` is a ref, and it is what makes the count a CEILING rather than
 * an average.** An id is asked about at most once per mount, no matter how many
 * rows use it, how often the effect re-runs, or how many times the person toggles
 * an item box back and forth between two products. Without it, flipping a picker
 * between two products in a grid of eight rows is an unbounded request loop
 * against a shared limit.
 *
 * ⚠️ **A FAILED read is never retried, and that is deliberate rather than an
 * oversight.** The id stays in `requested` and out of the map, so the row answers
 * `'unknown'` for ever and the editor stays visible **saying the check did not
 * run** — see {@link RowInclusions.offerUnread}. Retrying on every render would
 * turn one 500 into a request storm; retrying on a timer would hide the failure
 * behind a control that flickers between two meanings. There is no retry button
 * because there is nothing a person could usefully do with one that reloading the
 * page does not do better.
 *
 * ⚠️ **`params` is memoised BY THE CALLER and must stay that way.** The effect
 * depends on the array's identity, so a fresh array each render is a fresh effect
 * each render — harmless only because of `requested`, which is not a reason to
 * rely on it.
 *
 * ⚠️ **One `AbortController` for the whole component, not one per effect run.**
 * The obvious shape — a controller created in the effect and aborted in its
 * cleanup — would cancel every in-flight read the moment a row's product changed,
 * while `requested` had already recorded those ids as asked. They would never be
 * re-requested and would sit at `'unknown'` for ever. So the controller is created
 * once and aborted only on unmount.
 */
function useProductInclusionOffers(
  productIds: readonly number[],
): ReadonlyMap<number, ProductInclusionOffer> {
  const [offers, setOffers] = useState<ReadonlyMap<number, ProductInclusionOffer>>(
    () => new Map(),
  );
  const requested = useRef<Set<number>>(new Set());
  const controller = useRef<AbortController | null>(null);
  controller.current ??= new AbortController();

  useEffect(() => {
    /*
     * ⚠️ **RE-ARM, because a torn-down mount leaves this ref holding an ABORTED
     * controller while `requested` still holds every id — and nothing retries.**
     *
     * The controller is one per COMPONENT rather than one per effect run, for
     * the reason this hook's doc gives, and that half is right. What it did not
     * anticipate is that the cleanup below runs without the component being
     * gone: in development React invokes setup, cleanup, then setup again on
     * every mount, with **no render in between** — so the `??=` above cannot
     * replace it. The first pass fired the reads, the cleanup aborted them, and
     * the second pass skipped every id because `requested` had already recorded
     * it. The offers map then stayed **empty for the life of the grid**.
     *
     * ⚠️ Silent in both directions, which is why it survived: an absent offer is
     * `'unknown'`, which is the deliberate fail-towards-useful answer — so every
     * row showed the editor with its "couldn't check" notice, the picker was
     * never narrowed to what an administrator linked, and the at-least-one rule
     * never applied. The screen looked like a working feature, and development —
     * the only place this app is ever driven — disagreed with production about a
     * set that PRINTS on the customer's Xero quote.
     *
     * ⚠️ Clearing `requested` here does **not** reinstate a retry on a failed
     * read: a rejected fetch never aborts this controller, so the never-retry
     * rule above is untouched. Only a torn-down mount reaches this branch.
     */
    if (controller.current === null || controller.current.signal.aborted) {
      controller.current = new AbortController();
      requested.current = new Set();
    }
    /*
     * ⚠️ Read through the ref at cleanup time rather than capturing it above:
     * the branch above may have swapped it, and aborting the stale one would
     * leave the live reads running past unmount.
     */
    return () => controller.current?.abort();
  }, []);

  useEffect(() => {
    const signal = controller.current?.signal;
    if (signal === undefined) return;
    for (const productId of productIds) {
      if (requested.current.has(productId)) continue;
      requested.current.add(productId);
      api
        .productInclusions(productId, signal)
        /*
         * ⚠️ No query parameters, and none may be added. This endpoint's query
         * schema is an EMPTY `.strict()` object, so `?isActive=true` — the obvious
         * thing to reach for, since the campaign-inclusions master itself accepts
         * it — is a **422 naming the key**, not a filter. Hence no `listQuery()`.
         */
        .then((result) => {
          if (signal.aborted) return;
          const inclusionIds = result.data.map((inclusion) => inclusion.id);
          /*
           * A new Map rather than a mutation, because the identity is what every
           * `useMemo` downstream depends on. `[]` is stored exactly as it arrived
           * — it is the ANSWER "an admin has linked none", which is today's answer
           * for every product, and it is a different fact from this id being
           * absent from the map.
           */
          setOffers((previous) => new Map(previous).set(productId, { inclusionIds }));
        })
        .catch(() => {
          if (signal.aborted) return;
          /*
           * ⚠️ **Nothing is written, and nothing may be.** Leaving the id out of
           * the map is what makes the row answer `'unknown'` and keep its editor.
           * Writing `{ inclusionIds: [] }` here would be indistinguishable from an
           * administrator having decided this product offers none — a load failure
           * silently promoted to a business decision, on a feature that prints on
           * a customer's document. See `product-inclusions.ts`'s header.
           */
        });
    }
  }, [productIds]);

  return offers;
}

/**
 * How long the quantity box may keep changing before its band is asked for.
 *
 * ⚠️ **The preset is keyed by (product, QUANTITY), so a keystroke is a request
 * unless something stops it.** Typing `120` into an empty box is three distinct
 * quantities — 1, 12, 120 — and two of them are answers nobody will ever see.
 * ⚠️ *SUPERSEDED: "against a limit of **120 requests per 60s shared across the
 * office**."* It is **12,000/60s** (corrected 2026-09-17), so the reason to
 * debounce is that two of the three are answers nobody reads, not that the
 * bucket runs out. The
 * house figure for a debounced input is 250ms and this is the same decision, so
 * it is the same number rather than a second one somebody has to reconcile.
 */
const PRESET_DEBOUNCE_MS = 250;

/**
 * The (product, quantity, project) a preset is cached under. Parsed back out of
 * the key with `split(':')`. The project segment is empty for a line with no
 * project: a project ladder for (project, product) wins over the product's own
 * (2026-10-18), so the same pair under two projects is two answers.
 */
function presetKeyOf(productId: number, quantity: number, projectId: number | null): string {
  return `${productId}:${quantity}:${projectId === null ? '' : projectId}`;
}

/**
 * The line quantity this row may ask a band about, or `null` for *do not ask*.
 *
 * ⚠️ **It is deliberately stricter than the Quantity box's own validation**, and
 * it is not a second copy of it: `validateRow` decides whether the row may be
 * SAVED and reports to the person, while this decides whether a URL may be
 * built. A blank box, a `0`, a half-typed `1.` and a pasted essay all answer
 * `null` here and are simply not asked about — no request, no refusal, and the
 * line keeps whatever inclusions it has.
 *
 * The six-digit ceiling is a URL bound, not a business rule: nothing should be
 * able to put an arbitrary string into a query parameter, and a line selling a
 * million of anything is not a band somebody has configured.
 */
function presetQuantityOf(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d{1,6}$/.test(trimmed)) return null;
  const quantity = Number(trimmed);
  return quantity >= 1 ? quantity : null;
}

/**
 * What this grid has already seeded onto one row, and what it has already
 * DECIDED about — which are two different facts and both are needed.
 *
 * ⚠️ **`discountFor` and `inclusionsFor` record a question that has been
 * ANSWERED, not a value that has been written.** A product whose catalogue row
 * has not arrived, and a band still in flight, leave their half of this record
 * standing at the old question, so the seeder simply asks again when the data
 * lands. Advancing them on the attempt instead would consume the change: the
 * answer would arrive to a row that no longer looked as though it had moved, and
 * the line would sit unseeded with nothing on screen to say why.
 *
 * ⚠️ **`discountSeeded` and `inclusionsSeeded` are what `@/lib/line-presets`
 * calls "what this module last seeded", and they are the whole re-apply rule.**
 * A seed may overwrite a seed and may never overwrite a person, so a value this
 * grid did not put there is somebody's own and is left alone for ever.
 *
 * ⛔ **SCREEN STATE, NEVER DRAFT STATE.** It lives in a ref rather than on
 * {@link Row} deliberately: it must not reach a payload, must not move
 * {@link isDirty}, and must not survive a reload — the seed itself is in the
 * draft, where a person sees it before saving, and that is the only record of it
 * that means anything to the server.
 *
 * ⚠️ **`null` ON EITHER QUESTION IS A THIRD STATE — "NOTHING HAS BEEN DECIDED
 * FOR THIS ROW YET" — AND IT IS NOT `''`.** On `inclusionsFor`, `''` already
 * means *cannot ask*, which is an ANSWER; a row opened with `null` is one whose
 * first pass through the seeder is a real pass rather than a priming one. See
 * {@link seedsAtFirstSight} for which rows start that way and why.
 */
interface LineSeedRecord {
  /** The `productId` whose recommendation has been decided, or `null` for "none yet". */
  discountFor: string | null;
  discountSeeded: string | null;
  /**
   * The quantity a PER-UNIT recommendation was last scaled for. A change while
   * the line still holds the seeded figure re-scales it; a hand-typed discount
   * is never touched (`recommendedDiscountSeed` stands down on it).
   */
  discountQuantity: string | null;
  /**
   * The {@link presetKeyOf} whose band has been decided, `''` for "cannot ask",
   * or `null` for "nothing decided yet".
   */
  inclusionsFor: string | null;
  inclusionsSeeded: LineInclusionDraft[] | null;
  /**
   * The auto-description inputs last decided for this row, as a key (`''` =
   * the product names no unit), or `null` for "nothing decided yet".
   */
  autoFor: string | null;
  /** The arguments behind {@link LineSeedRecord.autoFor}, so a stale auto text is recognisable. */
  autoArgs: AutoLineDescriptionArgs | null;
  /**
   * A stored or converted row records its inputs as they stand — once the
   * catalogue can answer for its product — and fills nothing at that moment.
   * Opening a quote must never rewrite a saved line's wording.
   */
  autoPrimePending: boolean;
}

/**
 * Whether this row is seeded on the seeder's FIRST sight of it, rather than
 * primed and left alone.
 *
 * 🔴 **OWNER'S DECISION, 2026-09-13: A SEEDED LINE CARRIES THE PRODUCT'S
 * RECOMMENDED DISCOUNT, ON ALL THREE GRIDS — and this function is the quote
 * grid's half of that correction.** `initialDraftRows` opens this grid with rows
 * that already name a product, so under the old unconditional prime every one of
 * them recorded `discountFor: row.draft.productId` on the first pass and
 * `continue`d: the question was answered before it was asked, the row never
 * looked as though it had moved again, and **the recommendation — and the
 * inclusion preset — were never applied to a seeded line at all.**
 *
 * The owner's reasoning, which is about money rather than tidiness: a seeded
 * line is byte-for-byte the state of a row somebody adds by hand and picks the
 * same product on — inherit price, quantity 1, no discount. Withholding the
 * recommendation means a seeded line silently carries a DIFFERENT discount from
 * an identical hand-added line one row below it: same product, same quantity,
 * two figures, nothing on screen explaining it, on money a customer is billed
 * against.
 *
 * ⚠️ **THE THREE GRIDS WERE BRIEFLY INCONSISTENT AND THIS IS THE CORRECTION, NOT
 * A NEW RULE.** `DealLineItemsTable` has always seeded a seeded row, because it
 * gates on `seedsFromCatalogue(row)` — *is this a line the catalogue may still
 * seed into* — rather than on whether the seeder has seen the row before. Read
 * any later difference between the grids here as a regression, not as a
 * deliberate divergence.
 *
 * ⛔ **A SAVED LINE IS STILL NEVER SEEDED AT FIRST SIGHT, AND THAT GUARD IS THE
 * WHOLE REASON THE PRIMING PASS EXISTS.** `@/lib/line-presets` can refuse to
 * overwrite a HAND-EDITED figure; it cannot refuse to overwrite a STORED one,
 * because it is handed a draft and knows nothing about what the server holds. A
 * saved line whose product recommends a discount, or whose inclusions are
 * legitimately empty, would otherwise be rewritten the moment the quote opened —
 * every row reading **Unsaved** on arrival, offering to change a figure agreed
 * months ago. Nothing would fail.
 *
 * ⛔ **AND A CONVERT CARRY-OVER IS EXCLUDED FOR EXACTLY THAT REASON, NOT BY
 * OVERSIGHT.** `row.convert` is a stored DEAL line being carried across, so its
 * discount is a figure somebody already set — on the deal, where the deal grid
 * offered it this same recommendation. Seeding it again here would replace an
 * agreed figure on the way into a document, with a `201` and nothing on screen
 * saying so. It is a stored line that happens not to be stored on a quote.
 *
 * ⚠️ **A hand-added row reaches this as `true` and nothing changes for it.** It
 * arrives from `blankDraft()` naming no product, so the discount branch records
 * `discountFor: ''` and seeds nothing and the band cannot be asked for — which
 * is byte-for-byte what priming did. The two paths are unified rather than the
 * hand-added row being given a new one.
 */
function seedsAtFirstSight(row: Row): boolean {
  return row.saved === null && row.convert === null;
}

/**
 * A row's inclusions in the shape `@/lib/line-presets` compares.
 *
 * ⚠️ **An empty quantity box is `1`, not `0`.** The box means *omit the key* and
 * the server reads an omitted quantity as 1, so `Number('')`'s `0` would report
 * every blank-count entry as differing from a band that presets one of it — and
 * the line would read as hand-edited and stop following its quantity, silently.
 * {@link effectiveInclusionQuantity} is the one copy of that rule and is already
 * what the description preview reads.
 */
function presetShapeOf(entries: readonly InclusionDraft[]): LineInclusionDraft[] {
  return entries.map((entry) => ({
    inclusionId: entry.inclusionId,
    quantity: effectiveInclusionQuantity(entry.quantity),
  }));
}

/**
 * A band's inclusions in the shape the draft holds — counts as typed strings,
 * and the array order becomes each entry's `sortOrder`, which is the server's
 * own band order rather than anything invented here.
 */
function inclusionDraftsOf(entries: readonly LineInclusionDraft[]): InclusionDraft[] {
  return entries.map((entry) => ({
    inclusionId: entry.inclusionId,
    quantity: String(entry.quantity),
  }));
}

/**
 * Which band covers each (product, quantity) on this document — the input to
 * `inclusionPresetSeed`.
 *
 * ## Why it is cached per PAIR, and debounced
 *
 * `GET /products/{id}/inclusion-presets/resolve?quantity=N` answers for one
 * product at one quantity, so the pair IS the cache key: two rows selling the
 * same product at the same count share one answer for the life of the
 * component, and a quantity moved from 5 to 7 and back again asks twice, not
 * three times. `requested` is a ref, which is what makes the request count a
 * CEILING rather than an average — exactly the arrangement
 * {@link useProductInclusionOffers} uses one function up, and for the same
 * shared rate limit.
 *
 * ⚠️ **`pairsKey` is a STRING and the dependency is that string, not an array.**
 * A fresh array each render is a fresh effect each render, and a fresh `Set`
 * each pass is a fresh value on every keystroke; the caller joins and sorts so
 * that "the same wants" is recognisable as the same wants. Same idiom as
 * `inclusionProductIds`.
 *
 * ⚠️ **The caller asks only for what it is MISSING, not for every pair on the
 * document** — see `wantedPresetKey`. An untouched quote issues no requests here
 * at all.
 *
 * ⚠️ **A FAILED read writes NOTHING and is never retried.** The pair stays in
 * `requested` and out of the map, so the seeder sees `null` — *no preset was
 * read* — and {@link inclusionPresetSeed} answers *change nothing*. Caching an
 * empty result instead would be indistinguishable from an administrator having
 * configured a band that presets nothing, which is a decision that reaches the
 * customer's document. The `null`-versus-`[]` rule `product-inclusions.ts`
 * states for offers is the same rule here, one resource over.
 *
 * ⚠️ **One `AbortController` for the whole component, re-armed on remount.** The
 * obvious per-effect controller would cancel every in-flight read whenever any
 * row's quantity moved, while `requested` had already recorded those pairs as
 * asked — they would never be re-requested and every line would sit unseeded.
 * The re-arm exists because development invokes setup, cleanup, then setup again
 * with no render in between, which would otherwise leave this holding an aborted
 * controller for the life of the grid.
 */
function useInclusionPresets(pairsKey: string): ReadonlyMap<string, ResolvedInclusionPreset> {
  const [presets, setPresets] = useState<ReadonlyMap<string, ResolvedInclusionPreset>>(
    () => new Map(),
  );
  const requested = useRef<Set<string>>(new Set());
  const controller = useRef<AbortController | null>(null);
  controller.current ??= new AbortController();

  useEffect(() => {
    if (controller.current === null || controller.current.signal.aborted) {
      controller.current = new AbortController();
      requested.current = new Set();
    }
    // Read through the ref at cleanup time: the branch above may have swapped it.
    return () => controller.current?.abort();
  }, []);

  /*
   * ⚠️ **The debounce is on WHAT TO ASK FOR, not on the answer.** Holding the
   * key back leaves the previous answers in the map untouched, so a row whose
   * quantity is mid-edit keeps showing the band it already had rather than
   * flickering through an empty state on every keystroke.
   */
  const [settledKey, setSettledKey] = useState(pairsKey);

  useEffect(() => {
    const timer = setTimeout(() => setSettledKey(pairsKey), PRESET_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [pairsKey]);

  useEffect(() => {
    const signal = controller.current?.signal;
    if (signal === undefined) return;
    for (const pair of settledKey.split(',')) {
      if (pair === '') continue;
      if (requested.current.has(pair)) continue;
      const [productPart = '', quantityPart = '', projectPart = ''] = pair.split(':');
      const productId = Number(productPart);
      const quantity = Number(quantityPart);
      const projectId = projectPart === '' ? null : Number(projectPart);
      if (!Number.isFinite(productId) || !Number.isFinite(quantity)) continue;
      if (projectId !== null && !Number.isFinite(projectId)) continue;
      requested.current.add(pair);
      api
        .resolveProductInclusionPreset(productId, quantity, signal, projectId)
        .then((result) => {
          if (signal.aborted) return;
          /*
           * A new Map rather than a mutation: the identity is what the seeding
           * effect depends on. `band: null` is stored exactly as it arrived — it
           * is the ANSWER "no band covers this quantity", which is a different
           * fact from this pair being absent from the map, and the two send
           * `inclusionPresetSeed` down different branches.
           */
          setPresets((previous) => new Map(previous).set(pair, result.data));
        })
        .catch(() => {
          if (signal.aborted) return;
          /* ⚠️ Nothing is written, and nothing may be — see this hook's doc. */
        });
    }
  }, [settledKey]);

  return presets;
}

/**
 * The active projects, once for the whole table.
 *
 * ⚠️ **Deliberately one request, not one per row.** `useLineProject` in
 * `@/lib/use-line-project` is the per-line picker and is the right shape for a
 * MODAL, where exactly one line is open. In a grid it would be a fetch per row,
 * re-fired on every product change, against a rate limit shared across the
 * office — ⚠️ *SUPERSEDED: "120/60s"*, it is **12,000/60s** (corrected
 * 2026-09-17), so the objection is the per-row SHAPE rather than the
 * arithmetic — and the rows all want the same list anyway. Each row's
 * options are this list filtered by its own `productId` through
 * `projectSellsProduct`, which is the only filter that changes the answer here:
 * a project that does not sell the line's product is a named refusal on save.
 * ⚠️ Since migration 156 a project sells SEVERAL products, so that test is over
 * the whole set and not over the deprecated primary.
 *
 * ⚠️ **`?isActive=true` is opt-in with no default.** Called bare it returns
 * retired projects too, and every one of them is a guaranteed
 * `Project {id} is no longer active` on write.
 *
 * ## 🔴 IT PAGES, AND THE CAP IT PAGES PAST WAS BEING HIT IN PRODUCTION
 *
 * ⚠️ *SUPERSEDED: one `listProjects` read of `perPage: 100`, reporting
 * `truncated` when `meta.total` exceeded what arrived.* `/projects` caps
 * `perPage` at **100, the server's own ceiling**, and production held **118
 * active projects on 2026-09-15**. One page therefore arrived 18 short — and
 * because the order is `name` ascending it was **the same 18 every time**, the
 * tail of the alphabet, which reads as a naming convention rather than as a
 * truncation.
 *
 * **Measured that day: all eight projects selling `Virtual Masterclasses` sat
 * at ranks 106-113**, so every one fell past the cut, `projectSellsProduct`
 * below matched nothing, and that product's picker was **empty on every row of
 * every grid**. Two more products were wholly unreachable the same way. Nothing
 * on screen said so: the truncation footnote had been removed on 2026-09-05,
 * `projectScopeFootnote` speaks only when the OTHER pill has some, and the
 * "No active project sells this item." sentence was correctly withheld by the
 * very `truncated` flag this change retires.
 *
 * ⛔ **The fix is paging, NEVER a bigger `perPage`** — 101 is a 422 and there is
 * no larger value in existence to reach for. {@link api.listAllProjects} owns
 * the loop and its three terminators, and **throws rather than resolving
 * short**.
 *
 * ⚠️ **`sort` is still sent, and under paging it is load-bearing rather than
 * cosmetic.** An unstable order across pages repeats some rows and skips
 * others, so the list would be complete in COUNT and wrong in CONTENT — which
 * no count on this side could detect.
 *
 * ## ⚠️ `truncated` can no longer be true, and its handling is kept anyway
 *
 * `listAllProjects` resolves with every row or throws, so an incomplete list
 * now arrives as `failed`. The downstream `true` branch — treat the list as
 * "cannot say" rather than asserting "none" — remains the correct handling of
 * an incomplete list and is what would be needed again the moment anything
 * points this hook back at a single page. **Do not read the field as evidence
 * that truncation is still reachable here.**
 */
interface ProjectsState {
  rows: Project[];
  loading: boolean;
  failed: boolean;
  truncated: boolean;
}

function useActiveProjects(): ProjectsState {
  const [rows, setRows] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listAllProjects(controller.signal, { isActive: true, sort: 'name', order: 'asc' })
      .then((all) => {
        if (controller.signal.aborted) return;
        setRows(all);
        setFailed(false);
      })
      .catch(() => {
        /*
         * ⛔ A partial list is DISCARDED, never rendered — `listAllProjects`
         * throws for exactly this reason. A project list that stops early looks
         * identical to a complete one, and this grid ASSERTS over it ("No
         * active project sells this item."), so rendering what arrived would
         * turn a flaky second page into a confident false statement.
         *
         * The cost, stated rather than hidden: the control locks and, since the
         * failed-read footnote was removed on 2026-09-05, nothing says why.
         * Re-adding that sentence is the owner's call, not this file's.
         */
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  /* Never true: see the doc above. Written as a literal so it reads as settled. */
  return { rows, loading, failed, truncated: false };
}

/* ── Small shared bits ────────────────────────────────────────────────────── */

function ErrorBlock({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" title={title}>
      {children}
    </Notice>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </svg>
  );
}

/** The Price cell's "no price at all" toggle — a struck-through circle. */
function NoPriceIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <circle cx="12" cy="12" r="8" />
      <path d="m6.5 6.5 11 11" />
    </svg>
  );
}

function LinesIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 7h16M4 12h16M4 17h10" />
    </svg>
  );
}

/*
 * ⚠️ `StaticField` — a local copy of `Field`'s markup for a body that was not a
 * control — is GONE, and deliberately not replaced.
 *
 * Its two callers were the Price cell's "No price" read-back and the tax
 * control's disconnected read-back, and both were cells that showed a WORD
 * where every other row showed a box. Xero's grid is one control per cell, so
 * each of them became a real control instead: the price toggle now lives inside
 * the price input, and the tax cell falls back to the `%` input. There is
 * nothing left in this file that renders a field-shaped thing with no field in
 * it, which is what the house rule wanted all along.
 */

function StatusBadge({ row, dirty }: { row: Row; dirty: boolean }) {
  if (row.state === 'saving') return <Badge tone="info">Saving…</Badge>;
  if (row.state === 'failed') return <Badge tone="danger">Not saved</Badge>;
  if (row.state === 'saved' && !dirty) return <Badge tone="success">Saved</Badge>;
  /*
   * ⚠️ **A converted row is neither "New" nor "Unsaved".** It is a line that
   * already exists on the DEAL and is about to be copied; what has changed is
   * what the copy will say. "Unsaved" would send somebody looking for a Save
   * control that does not exist on this path, and "New" would claim a line
   * nobody has written anywhere.
   */
  if (dirty) {
    return (
      <Badge tone="warning">
        {row.convert !== null ? 'Edited' : row.saved === null ? 'New' : 'Unsaved'}
      </Badge>
    );
  }
  return <span className="text-xs text-fg-subtle">—</span>;
}

/* ── The per-field controls, shared by the table and the card layout ───────── */

interface ControlProps {
  row: Row;
  problems: RowProblems;
  disabled: boolean;
  /**
   * The DESKTOP GRID.
   *
   * It does two things and only two: the column heading already names the
   * field, so the label is hidden; and Xero's grid carries no sentence in any
   * cell, so no `hint` is passed at all. Errors still render — a refusal is not
   * commentary — and a `footnote` still renders, because it lives under the
   * option list inside the popover rather than in the cell.
   *
   * False in the stacked card below `md`, which is not a grid and keeps both.
   */
  compact: boolean;
  onChange: (patch: Partial<Draft>) => void;
}

function ProductControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
  // `footnote` was a required prop here. Its only source was the load-failure /
  // truncation string removed on 2026-09-05, so the prop went with it.
}: ControlProps & { options: ComboboxOption[] }) {
  /*
   * ⚠️ **Under `table-fixed` this cell cannot grow, so the NAME has to give.**
   * It was the widest intrinsic content in the grid — a long product name took
   * Item from its asked-for 190 to 277 and the difference came off Description
   * and the number boxes. The Combobox trigger's label is already
   * `min-w-0 flex-1 truncate`, so it ellipses rather than overflowing; what it
   * cannot do is tell you what was cut off. This `title` does, and it is the
   * same place the no-prose-in-a-grid-cell rule already puts a wording the
   * trigger cannot afford.
   *
   * Only in the grid. The card below `md` is full width, shows the whole label,
   * and gains nothing from a tooltip repeating what is on screen.
   */
  const selectedLabel = options.find((option) => option.value === row.draft.productId)?.label;

  const control = (
    <Field label="Item" hideLabel={compact} error={problems.fieldErrors.productId}>
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={row.draft.productId}
          onChange={(value) => onChange({ productId: value })}
          options={options}
          placeholder="Choose a product"
          searchPlaceholder="Search products"
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.productId)}
        />
      )}
    </Field>
  );

  if (!compact || selectedLabel === undefined) return control;
  return <div title={selectedLabel}>{control}</div>;
}

/**
 * Xero's Description cell — **a textarea over a live derivation**.
 *
 * See "The Description cell types over a live derivation" in this file's header
 * for the whole rule. What this component is responsible for:
 *
 * - **`derived` is recomputed by the caller on every render**, so an untouched
 *   cell follows the item, the project and the cities as they change. That is
 *   the "real time" the owner asked for, and it is a property of where the value
 *   comes from rather than of an effect that could miss a dependency.
 * - **`description !== null` is the whole override test**, and `''` is a typed
 *   value HERE: somebody mid-edit who has just cleared the box must not have the
 *   derivation reappear under their cursor, which is the clobber this shape
 *   exists to prevent. `''` is folded back to "derive" at the PAYLOAD, by
 *   `descriptionForWrite`, which is the last moment at which it changes nothing
 *   on screen.
 * - ⚠️ **It says the one thing the screen cannot show.** Typing replaces the
 *   whole string — the cities included, with nothing appended by the push — so
 *   **the moment somebody types, the city list stops maintaining itself**. Add a
 *   city to that line afterwards and the customer's Xero document will not
 *   mention it. Nothing about that is visible: the chips change, the money is
 *   unaffected, and the description simply sits there being out of date.
 *
 *   **Where it renders is a decision, not a default.** The full wording is the
 *   card's `hint`, because the card is not a grid and keeps its hints. In the
 *   GRID no cell may grow a sentence — and a `title` is not enough on its own
 *   for this clause, because a tooltip is only read by somebody who already
 *   suspects there is something to read. So the grid renders **one short line,
 *   and only on a row that has been typed on**, beside the Reset control: that
 *   is the only row where the clause is true, so the grid stays exactly as clean
 *   as it was on every other row, and the person who caused the state is the one
 *   who is told about it. The `title` carries the long version in both layouts.
 * - **It GROWS rather than scrolls**, which is the other half of "Xero's own
 *   behaviour". A fixed `rows` puts the tail of a description behind a scrollbar
 *   inside a table row, which is the one place nobody thinks to scroll — so the
 *   only field on a Xero quote line a customer actually reads would be half
 *   hidden on the screen it is written on.
 */
function DescriptionControl({
  disabled,
  compact,
  derived,
  description,
  saved,
  error,
  onTyped,
  onReset,
  onRestore,
}: {
  disabled: boolean;
  compact: boolean;
  /** `descriptionOf(row)` — `null` while the row has no item to describe. */
  derived: string | null;
  /**
   * The DRAFT's own value: `null` while the cell is still deriving, a string
   * once it has been typed on — in this session or in an earlier one, since it
   * is read straight off the saved line.
   */
  description: string | null;
  /**
   * The wording this line is SAVED with — `row.saved?.description ?? null`.
   *
   * ⚠️ **NOT the same question as `description` above, which is the DRAFT.** A
   * line that has been typed on in this session has a draft value and a
   * different stored one; a line nobody has touched has the same in both. The
   * gap between them is exactly what {@link onRestore} puts back.
   *
   * `null` means the saved line derives its wording, so there is no original
   * to go back to and the control is not offered.
   */
  saved: string | null;
  /** A 422 naming `description`, or the local over-length refusal. */
  error: string | undefined;
  onTyped: (value: string) => void;
  onReset: () => void;
  /** Put the SAVED wording back into the draft. */
  onRestore: () => void;
}) {
  const edited = description !== null;
  /*
   * ⛔ **THE UNDO FOR `Reset to derived`, ADDED 2026-09-12 ON THE OWNER'S
   * REPORT — AND IT IS AN UNDO, NOT A RECOVERY.**
   *
   * `Reset to derived` sends an explicit `null`, which DESTROYS the stored
   * wording. Until now that was a one-way door with no control beside it: a
   * mis-click looked like the cell simply re-deriving, the row went Unsaved
   * like any other edit, and the only way back was to retype a sentence nobody
   * had a copy of. It happened on INV-0770.
   *
   * ⚠️ **IT ONLY REACHES BACK AS FAR AS THE LAST SAVE, AND THAT LIMIT IS REAL.**
   * Once a `description: null` is SAVED, `row.saved.description` IS null and
   * there is nothing here to restore from — this control is what stops the
   * mis-click becoming permanent, not what undoes one that already has. The
   * recovery after a save is the customer's own document: the line's wording is
   * still in Xero, because a push to an approved or paid invoice comes back
   * `skipped`. **A "restore from Xero" control would close that half and does
   * not exist.**
   *
   * The test is `description !== saved`, not `edited`: after `Reset to derived`
   * the draft is `null` and the saved value is a string, which is precisely the
   * state this exists for and the one an `edited` test would miss.
   */
  const restorable = saved !== null && description !== saved;
  /*
   * ⚠️ Not a coalesce between two things that both need showing — there is one
   * box and exactly one of these may be in it. The typed text WINS, deliberately
   * and permanently, until `Reset to derived` is pressed.
   */
  const value = edited ? description : (derived ?? '');

  /*
   * 🔴 **THE STATE NOTE IS GONE — owner's stricter pass, 2026-09-05.** The two
   * sentences were:
   *
   *   "Your wording — it no longer follows this line's cities. Reset to derived
   *    to hand it back."
   *   "Derived from this line's item, project and cities. Typing replaces the
   *    whole string."
   *
   * ⚠️ **`edited` is still the state and the cell still behaves both ways** —
   * typed text wins permanently until `Reset to derived` is pressed, and the
   * button below is still the only way back. What is lost is the reader being
   * told WHICH of the two states the box is in, and the clause they cannot
   * discover from the screen: the cities go INTO the string, so a line whose
   * project or cities change stops re-deriving once anybody types here.
   *
   * The `Reset to derived` control is untouched — a control is not copy — so
   * the way back is still visible even though the state is not.
   */

  return (
    <Field label="Description" hideLabel={compact} error={error}>
      {(fieldProps) => (
        <div className="grid gap-1">
          <Textarea
            {...fieldProps}
            /*
             * ⚠️ **Both bounds, always, and NEVER `0` on either.** `maxRows: 0`
             * collapses the box to near-nothing and `minRows: 0` emits an
             * invalid `rows="0"`; the primitive guards neither, so this call
             * site is the guard. There is no partial form of the prop — a
             * missing half is the fixed-rows behaviour back again, silently.
             *
             * The grid box is 212px inside a 236px column, where the live
             * `Sales - Business School Festivals — Mumbai, Bangalore` already
             * wraps to about two lines: 2 is therefore the resting height and
             * not a ceiling anything normal hits. The card is full width and can
             * afford more, so it starts at 3 and runs to 10.
             *
             * Growing makes the ROW taller, which under `table-fixed` is the only
             * direction available to it — the column widths are the
             * `<colgroup>`'s and no content can widen a cell or spill out of one.
             */
            autoGrow={compact ? { minRows: 2, maxRows: 6 } : { minRows: 3, maxRows: 10 }}
            value={value}
            onChange={(event) => onTyped(event.target.value)}
            placeholder={derived ?? 'Choose an item'}
            disabled={disabled}
            invalid={Boolean(error)}
          />
          {/*
            🔴 **The sub-`md` card's city clause is GONE too — owner's stricter
            pass, 2026-09-05.** It read "Fixed wording — adding a city will not
            update it." and rendered only on an edited row, in the card layout
            where there is no hint.

            ⚠️ It was the one place the clause was legible to somebody who did
            not already suspect it: a `title` alone was rejected for exactly
            that reason. `edited` is still the state; the `Reset to derived`
            button below is now the only sign the cell is in it.
          */}
          {(edited || restorable) && !disabled && (
            /*
             * ⚠️ **Two ways back, and they are different destinations.** One row
             * rather than two stacked buttons, so a cell that gains the undo does
             * not get taller than its neighbours.
             */
            <div className="flex flex-wrap items-center gap-1">
              {edited && (
                /*
                 * The way back to the derivation. Offered rather than taken:
                 * nothing in this cell overwrites typing on its own, so without
                 * this control a person who typed once could never get the
                 * derivation back. It sends an explicit `null` — see
                 * `updatePayload`; omitting the key would leave the stored
                 * wording in place behind a cell that had gone back to deriving.
                 */
                <Button type="button" variant="ghost" size="sm" onClick={onReset}>
                  Reset to derived
                </Button>
              )}
              {restorable && (
                /*
                 * ⚠️ **The undo for the control beside it.** It puts the SAVED
                 * wording back into the draft — the wording the customer's
                 * document carries right now, since nothing has been written
                 * yet. Pressing it and saving is a no-op on the line rather than
                 * a second edit.
                 *
                 * ⚠️ **The word is "original", not "undo".** After a `Reset to
                 * derived` the cell is showing derived text and the reader is
                 * looking for the sentence that used to be there; naming the
                 * destination is what makes it findable. `title` carries the
                 * text itself so it can be recognised before it is restored —
                 * a value the cell is not currently showing, which is the
                 * documented exception to no-prose-in-a-cell rather than advice.
                 */
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onRestore}
                  title={`Put back: ${saved}`}
                >
                  Reset to original
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Field>
  );
}

/**
 * What one row knows about its own project, resolved once by the component.
 *
 * A row's project is the authority for its cities, so the two are resolved and
 * passed together — a screen that fed the city control from the QUOTE's project
 * would be offering the wrong set the moment the two differ, which on any
 * multi-product quote is every line but one.
 */
interface RowProject {
  /**
   * Active projects selling this row's product, plus the stored one if retired.
   *
   * **With no item chosen this is every active project**, which is the
   * project-first path: choosing one sets the line's item from it. Empty under
   * EVERY pill means there is genuinely nothing to offer — the list failed, or
   * no active project sells this item — and only then is the control disabled
   * rather than shown empty.
   *
   * ⚠️ **One list per Current / Previous pill, with the row's stored project
   * pinned into BOTH** — see `projectOptionsByScope`, which is where that
   * invariant lives. The pill is per row and its state sits in
   * {@link ProjectControl}; the `/cycles` read behind the split is once per
   * grid.
   */
  options: Record<ProjectCycleScope, ComboboxOption[]>;
  /** The cities the resolved project declares, in the master's own order. */
  cityOptions: MultiComboboxOption[];
  /**
   * **What `allCities` resolves to TODAY** — every city the LINE's project
   * declares, in the master's own order. `null` means the project itself could
   * not be resolved: the list failed, was truncated at 100, or the line sits on
   * a retired project an active-only fetch never returns.
   *
   * ⚠️ **Not {@link cityOptions}, and the difference is not cosmetic.** That
   * list also carries the row's OWN stored cities that the project no longer
   * declares, injected so a historical line stays editable. They are real
   * entries and a flagged line does not mean them, so reading the option list
   * as "all cities" would name places the push will not.
   */
  projectCities: { name: string; label: string }[] | null;
  /**
   * The ids that project declares. **`null` means "we cannot say"** — the list
   * failed, or was truncated, or the row holds a project no active-only fetch
   * returns. Never guess a refusal from it.
   */
  declaredCityIds: Set<number> | null;
  /**
   * The regions the LINE's project declares, in the order the project read gave
   * them. **`null` means the project could not be resolved** — the list failed,
   * was truncated at 100, or the line holds a retired project an active-only
   * fetch never returns — and is NOT the same as a project that declares none,
   * which is `[]`.
   *
   * ⚠️ **This is what the region picker OFFERS on a project-backed row**, on the
   * owner's decision: a line stays inside the geography its project actually
   * covers, rather than being offered all six of SEED's regions. A projectless
   * row has no project to take a set from and keeps the `/regions` master — see
   * {@link RegionsControl}.
   *
   * ⚠️ **It never NARROWS what the line already holds.** Regions are validated
   * against the `/regions` master alone server-side, so a region absent from
   * this list is not a refusal — it is pinned back into the options and
   * labelled, exactly as a stray city is.
   */
  projectRegions: { id: number; name: string }[] | null;
  /**
   * ⚠️ **A DEFINITE "this project declares no cities", and nothing weaker.**
   *
   * True only when a project is chosen AND was resolved AND its own city list is
   * empty. Every unresolved state — no project picked yet, the list still
   * loading, failed, truncated, or a retired project — is `false`, because this
   * gates {@link showsRegionsControl} and that gate must fail towards hiding.
   *
   * ⚠️ Read off `selected.cities`, the project's own declaration, and **never
   * off {@link cityOptions}**, which also carries the row's stored cities the
   * project no longer declares. Those are real entries on the line and say
   * nothing about what the project offers today.
   */
  projectHasNoCities: boolean;
  /** Wording for the project control when there is nothing useful to offer. */
  projectHint: string | undefined;
  /** Per pill: "No projects in Current. 5 in Previous." or nothing at all. */
  projectFootnotes: Record<ProjectCycleScope, string | undefined>;
  /** Wording for the city control when it cannot be operated. */
  cityHint: string | undefined;
  citiesUnavailable: boolean;
  /**
   * Whether this row's product is sold through projects at all — the gate on
   * the project block. See {@link projectOfferOf}; it is computed from the same
   * filtered list {@link RowProject.options} is built from, so the verdict and
   * the picker can never disagree.
   */
  projectOffer: ProjectOffer;
}

/**
 * The line's own project — Xero's `Location`/`Tour Type`, at the end this app
 * is allowed to write.
 *
 * ⚠️ **Changing the project CLEARS the cities**, in the draft, before anything
 * is sent. The guard runs on the post-merge state, so a project change
 * re-validates the stored cities and is refused naming cities the person never
 * touched — a 422 about a control that is not on screen. Clearing makes that
 * state unreachable rather than merely explained, the same shape as the
 * cycle→intake reset on a project itself.
 *
 * ⚠️ **This renders directly under the ITEM picker, with the city control under
 * it, and they are still ONE patch.** The arrangement has been a stack, then
 * two columns, and is a stack again — none of that ever changed why the two keys
 * travel together, which is the server's post-merge guard and not the screen's
 * layout. Do not "tidy" the clear out of here on the grounds that the two
 * controls now sit in one cell, any more than it could be tidied out when they
 * sat in two.
 *
 * ⚠️ **It is not always rendered.** {@link showsProjectControl} hides it on a
 * row whose product is definitely not sold through projects — and hiding is a
 * render branch that never touches the draft, because `cityIds: []` CLEARS a
 * stored set. Nothing in this component may start clearing state on the way to
 * being hidden.
 */
/** Every pill empty. Written as a `Record` so a third scope fails the build here. */
const NO_PROJECT_OPTIONS: Record<ProjectCycleScope, ComboboxOption[]> = {
  current: [],
  previous: [],
};

/** Nothing to say under any pill — see `projectScopeFootnote`. */
const NO_PROJECT_FOOTNOTES: Record<ProjectCycleScope, string | undefined> = {
  current: undefined,
  previous: undefined,
};

/**
 * The Current / Previous pills, inside the project dropdown.
 *
 * ⚠️ **Current means current AND UPCOMING** — the owner's own clarification, and
 * the reason the rule is one `endDate >= today` comparison rather than two. See
 * `src/lib/project-cycle-scope.ts`.
 *
 * ⛔ **No hint, no sentence, no tooltip. The two labels are the whole
 * explanation**, which is what keeps this inside the no-helper-text rule while
 * rendering in a control that lives inside a desktop grid cell. The only prose
 * the popover may carry is the `footnote`, which states a fact about the data.
 *
 * `SegmentedControl` rather than hand-rolled pills: it already gives the group
 * label, `aria-pressed` per segment, a tick so selection is not colour alone,
 * and a constant width across selections. Its segments size to their content
 * and the popover is `width: max-content` with a 240px floor, so two short
 * labels fit at 320px.
 */
function ProjectScopePills({
  value,
  onChange,
}: {
  value: ProjectCycleScope;
  onChange: (next: ProjectCycleScope) => void;
}) {
  return (
    <SegmentedControl
      label="Show projects from"
      value={value}
      onChange={onChange}
      options={PROJECT_CYCLE_SCOPES.map((scope) => ({
        value: scope,
        label: PROJECT_CYCLE_SCOPE_LABELS[scope],
      }))}
    />
  );
}

function ProjectControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  resolved,
}: ControlProps & { resolved: RowProject }) {
  /*
   * ⚠️ **Per ROW, and `current` on every mount — owner's instruction.** It is a
   * filter over what is offered and NOT a value, so it is deliberately local
   * state and is never written anywhere: a saved pill would be a second
   * authority over what a person may pick, and the row's own project already
   * says which half it came from.
   *
   * A shared grid-level pill would mean switching to Previous on row 3 silently
   * re-listing rows 1 and 2, which is a dropdown changing under somebody who
   * never opened it.
   */
  const [scope, setScope] = useState<ProjectCycleScope>(DEFAULT_PROJECT_CYCLE_SCOPE);
  const options = resolved.options[scope];

  return (
    <Field
      label="Project"
      hideLabel={compact}
      error={problems.fieldErrors.projectId}
      {...(!compact && resolved.projectHint ? { hint: resolved.projectHint } : {})}
    >
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={row.draft.projectId}
          onChange={(value) =>
            /*
             * Both keys move together. `cityIds: []` is always legal — it is
             * the one city input that names nothing and so needs nothing to
             * validate against — so this can never itself be a refusal.
             *
             * 🔴 ⚠️ *SUPERSEDED 2026-09-19: "On a row with no item yet this is
             * the PROJECT-FIRST path, and `patchRow` sets the line's product
             * from the project chosen here."* **This control is no longer on
             * screen for a row with no item** — owner's decision, see
             * {@link showsProjectControl} — so that branch of `patchRow` can no
             * longer be reached from here.
             *
             * ⛔ **`patchRow` IS DELIBERATELY NOT GUTTED.** It still sets the
             * product from the project, and that half is right whenever it does
             * run: a project sells exactly one product, so the two are one fact,
             * and leaving them to be reconciled later is how refusal 5 gets
             * built out of two choices that each looked fine on their own.
             * Half-dismantling a mechanism is worse than either end of it, and
             * the reversal of this decision would need it back intact.
             */
            onChange({ projectId: value, cityIds: [] })
          }
          options={options}
          emptyOptionLabel="No project"
          placeholder="No project"
          searchPlaceholder="Search projects"
          header={<ProjectScopePills value={scope} onChange={setScope} />}
          /*
           * ⚠️ Disabled on the WHOLE row's project list, never on this pill's.
           * An empty Current with rows under Previous must still open, or the
           * only control that could reach them is behind a disabled trigger.
           */
          disabled={disabled || PROJECT_CYCLE_SCOPES.every((s) => resolved.options[s].length === 0)}
          invalid={Boolean(problems.fieldErrors.projectId)}
          {...(resolved.projectFootnotes[scope]
            ? { footnote: resolved.projectFootnotes[scope] }
            : {})}
        />
      )}
    </Field>
  );
}

/**
 * The line's price TIER (owner 2026-10-27), directly under the project picker.
 *
 * Offers the tiers the project prices this product in for the document
 * currency, plus the default tier always (`tierOptionsFor`); a tier the line
 * already holds is pinned in. Choosing the default tier stores `null`, which is
 * what "default" means on the wire. It moves the price only while the price is
 * untouched — the reprice rules live in `catalogueFor` / `repriceFor`.
 */
function PriceTierControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
  defaultTierId,
  allTiers,
  lockedReason,
}: ControlProps & {
  options: PriceTier[];
  defaultTierId: number | null;
  allTiers: PriceTier[];
  /** Set when the control cannot be used on this row: why. */
  lockedReason: string | null;
}) {
  const current = row.draft.priceTierId ?? defaultTierId;
  const listed: PriceTier[] = [...options];
  if (current !== null && !listed.some((tier) => tier.id === current)) {
    const held = allTiers.find((tier) => tier.id === current);
    if (held !== undefined) listed.push(held);
  }
  const comboOptions: ComboboxOption[] = listed.map((tier) => ({ value: String(tier.id), label: tier.name }));
  const control = (
    <Field label="Price" hideLabel={compact} error={problems.fieldErrors.priceTierId}>
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={current === null ? '' : String(current)}
          onChange={(value) => {
            const id = value === '' ? null : Number(value);
            onChange({ priceTierId: id === null || id === defaultTierId ? null : id });
          }}
          options={comboOptions}
          searchPlaceholder="Search price tiers"
          disabled={disabled || lockedReason !== null}
          invalid={Boolean(problems.fieldErrors.priceTierId)}
        />
      )}
    </Field>
  );
  return lockedReason === null ? control : <div title={lockedReason}>{control}</div>;
}

/**
 * The set of the project's cities this line runs in.
 *
 * ⚠️ **Zero cities is the ORDINARY case, not an omission.** It reads as "not
 * city-specific" and nothing here reserves layout for a set that is usually
 * absent.
 *
 * **The trigger COUNTS rather than lists** — `maxVisibleChips={0}` with
 * `summaryNoun` gives "4 cities" in one line, since the chips it replaces would
 * wrap and drag the row down. The card layout below `md` keeps the chips —
 * both settings are omitted there.
 *
 * ⚠️ **It renders only when a project can name cities** —
 * {@link showsCitiesControl}. Hiding it NEVER clears `draft.cityIds`: a write
 * replaces the whole set, so `[]` destroys it, with a `200` and nothing to
 * restore from.
 *
 * ## ⚠️ "All cities" is a FLAG in this cell, and the picker goes away
 *
 * A line can instead mean **every city its project has**, resolved live. A
 * hand-picked line that happens to name every city today would look identical,
 * so:
 *
 * - **there is no picker at all while the box is ticked** — nothing to read as
 *   chips somebody chose.
 * - **the sentence leads with the rule and dates the list** — "Every city its
 *   project runs in — today Bangalore, Mumbai" — so the list reads as a
 *   consequence that will change on its own.
 *
 * ### Switching between the two
 *
 * The two are mutually exclusive server-side, so one always loses, and — same
 * rule as the hidden inclusions block — **a control that stops being operable
 * must not reach into the draft and destroy what it was showing.**
 *
 * - **Ticking PARKS the explicit set; it does not clear it.** Unticking hands
 *   it straight back, so a mis-click costs nothing.
 * - **Saving is where it stops being free** — `allCities: true` clears the
 *   stored rows, so the row says so before the request.
 * - **Unticking a line that arrived flagged has nothing to hand back** — the
 *   picker opens empty, and the row says that too. ⚠️ It is deliberately NOT
 *   pre-filled with today's resolution: that would write a snapshot in place
 *   of a live rule, which is the exact distinction the flag exists to record.
 *
 * ⚠️ **Do not add a "these cities will be lost" warning off
 * `citiesLostByMovingProject` here.** This grid clears the selection in the
 * same `onChange` instead, making the bad state unreachable — and on a
 * FLAGGED line that helper must never be fed `line.cities`: it is the
 * RESOLVED set, not stored, and would produce a confident list of names the
 * move cannot lose.
 */
function CitiesControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  resolved,
  /**
   * ⚠️ **The "All cities" FLAG alone is locked, while the picker below it stays
   * live — and the split is the wire contract, not a preference.**
   *
   * `mode: 'convert'` passes `true`. A `lineSet` entry carries `cityIds` and has
   * **no `allCities` key at all**, so ticking the box would collect a choice the
   * conversion cannot express: the quote would come back with the line's
   * explicit cities and no flag, silently, on a 201. A deal line has no
   * `all_cities` column either, so the unticked box a converted row shows is the
   * FACT rather than a default being frozen — which is why this is a disabled
   * checkbox and the Tax cell next door is a readout instead.
   */
  allCitiesLocked = false,
}: ControlProps & { resolved: RowProject; allCitiesLocked?: boolean }) {
  /*
   * ⚠️ Every offending chip, marked at once. `offendingCityIds` is refusal 7's
   * whole list, so the person fixes all of them in one pass rather than
   * resubmitting to discover the next. It is `[]` on every other refusal AND
   * whenever the sentence could not be parsed with certainty — `[]` is never
   * evidence that no city is wrong, only that there is nothing safe to mark.
   */
  const refusal = row.refusal;
  const invalidCityValues =
    refusal !== null && refusal.path === 'cityIds'
      ? refusal.offendingCityIds.map(String)
      : [];
  /*
   * Refusal 6 is fixed on `/projects/{id}` and nowhere else — the project has no
   * cities, so the picker this error points at is empty by definition and
   * telling someone to choose different cities sends them nowhere. `fixedOn`
   * says so; `kind` is deliberately not re-derived here.
   *
   * ⚠️ **It rendered in NEITHER layout, and the two reasons were different.** In
   * the grid it rode on a wrapper `div`'s `title`, which needs a mouse: no
   * keyboard reaches it and nobody on a touch screen sees it at all. In the card
   * it was a `Field` hint — and `Field` suppresses `hint` whenever `error` is
   * set, which this refusal ALWAYS is, since it arrives on `cityIds` and marks
   * this very control. So the one sentence that says the fix is on another screen
   * was unreachable everywhere, while the error above it advised a change that
   * cannot be made.
   *
   * It is therefore rendered outright, in both layouts, and only while the
   * refusal is live. That is not the ambient per-cell prose the owner objected
   * to: a server refusal is the category this file already renders unconditionally
   * — the same reason a field error is not commentary.
   */
  const fixIsOnTheProject =
    refusal !== null && refusal.path === 'cityIds' && refusal.fixedOn === 'the-project';
  const fixLocation = fixIsOnTheProject
    ? 'The fix is on the project, not on this line — open it and give it its cities.'
    : null;

  const flagged = row.draft.allCities;
  /*
   * ⚠️ **Both keys, joined — never `a ?? b` between them.** A 422 can name
   * `cityIds` and `allCities` in the same response, and this cell is the only
   * control either one can be marked on. Coalescing would silently drop one of
   * two true statements, which is the shape this repo has catalogued four
   * times; dropping `allCities` in particular would be worse than showing
   * nothing, because a path that binds to no control still counts towards "one
   * field needs attention" while highlighting nothing.
   */
  const cityError =
    [problems.fieldErrors.cityIds, problems.fieldErrors.allCities]
      .filter((message): message is string => message !== undefined)
      .join(' ') || undefined;
  /*
   * The two states the switch can strand somebody in, and at most one is ever
   * true — the first needs the box ticked and the second needs it unticked — so
   * this is a decision between exclusive cases, not a coalesce between two live
   * facts. See this control's header for why neither is repaired automatically.
   */
  const parkedCities = flagged ? row.draft.cityIds.length : 0;
  const switchNote =
    parkedCities > 0
      ? `Saving discards the ${parkedCities} ${
          parkedCities === 1 ? 'city' : 'cities'
        } chosen here. Untick to keep ${parkedCities === 1 ? 'it' : 'them'}.`
      : !flagged && row.saved?.allCities === true && row.draft.cityIds.length === 0
        ? 'Was every city. Choose the ones it runs in, or tick the box again.'
        : null;
  /*
   * What the flag means TODAY. The project is the live source; a saved flagged
   * line's own `cities` is the same set resolved a moment earlier and stands in
   * when the project cannot be resolved at all. Not a drop between two true
   * things — one fact, read from the better source when there is one.
   */
  const resolvedCities =
    resolved.projectCities ??
    (row.saved?.allCities === true
      ? row.saved.cities.map((city) => ({ name: city.name, label: cityLabel(city) }))
      : null);
  /*
   * Not a drop: `resolved.cityHint` describes why the picker cannot be operated,
   * which on refusal 6 is the same fact stated more weakly. The refusal names the
   * screen to go to and the ambient wording does not, so it supersedes rather
   * than sitting beside it.
   */
  const cityHint = fixLocation ?? resolved.cityHint;

  const control = (
    <Field
      label="Cities"
      hideLabel={compact}
      error={cityError}
      {...(!compact && cityHint ? { hint: cityHint } : {})}
    >
      {(fieldProps) => (
        <div className="grid gap-1.5">
          <Checkbox
            /*
             * ⚠️ The `Field`'s wiring goes to whichever control is LIVE, so the
             * error is announced against something that is on screen. While the
             * box is ticked the box is the only control in this cell; while it
             * is not, the picker below takes it.
             */
            {...(flagged ? fieldProps : {})}
            label="All cities"
            checked={flagged}
            disabled={disabled || allCitiesLocked}
            {...(allCitiesLocked
              ? {
                  /*
                   * ⚠️ **RENDERED, not a `title`.** This control's own header
                   * records what a `title` cost the last time one carried a
                   * sentence somebody needed — no keyboard reaches it and nobody
                   * on a touch screen sees it at all — and a disabled checkbox
                   * with no reason beside it reads as a bug.
                   */
                  description: 'A conversion names each line’s cities explicitly.',
                }
              : {})}
            onChange={(event) =>
              /*
               * ONE key. `cityIds` is deliberately untouched in both directions
               * — see this control's header. Ticking parks the explicit set so
               * unticking can hand it back, and the payload builders are what
               * guarantee the two never travel together.
               */
              onChange({ allCities: event.target.checked })
            }
          />
          {flagged ? (
            /*
             * ⚠️ Not the picker, disabled — NO picker. A greyed-out multiselect
             * showing four chips reads as a hand-picked list that happens not to
             * be editable, which is the one reading this cell must not allow.
             * These names are a read-out of a rule, and they say so.
             */
            <p
              className="text-2xs leading-snug text-fg-muted"
              {...(resolvedCities === null || resolvedCities.length === 0
                ? {}
                : { title: resolvedCities.map((city) => city.label).join(', ') })}
            >
              {resolvedCities === null
                ? // The project could not be resolved, so the rule is all that
                  // can honestly be stated. Never a guessed list.
                  'Every city this line’s project runs in.'
                : resolvedCities.length === 0
                  ? 'Every city this line’s project runs in — it declares none today.'
                  : `Every city its project runs in — today ${resolvedCities
                      .map((city) => city.name)
                      .join(', ')}.`}
            </p>
          ) : (
            <MultiCombobox
              {...fieldProps}
              values={row.draft.cityIds.map(String)}
              invalidValues={invalidCityValues}
              onChange={(values) =>
                /*
                 * The array's ORDER is the stored `sortOrder`, and it is what
                 * Xero prints — "Bangalore, Mumbai" and "Mumbai, Bangalore" are
                 * the same set and two different customer documents. So the
                 * order the control hands back is kept exactly, never sorted.
                 */
                onChange({ cityIds: values.map(Number) })
              }
              options={resolved.cityOptions}
              placeholder="Not city-specific"
              searchPlaceholder="Search this project’s cities"
              disabled={disabled || resolved.citiesUnavailable}
              invalid={Boolean(cityError)}
              maxSelected={MAX_CITIES_PER_LINE}
              {...(compact
                ? { maxVisibleChips: 0, summaryNoun: { singular: 'city', plural: 'cities' } }
                : {})}
            />
          )}
        </div>
      )}
    </Field>
  );

  /*
   * Rendered rather than hinted, for both reasons above — `Field` would swallow
   * them under the error they arrive with, and a `title` is mouse-only.
   *
   * `switchNote` joins `fixLocation` rather than displacing it: they are about
   * different things (where a refusal is fixed, and what the switch is about to
   * do to a set on screen) and can be true at once, so neither may stand in for
   * the other. Both are read-outs of a live state and both vanish with it,
   * which is what keeps them past the no-prose-in-a-grid-cell rule.
   */
  const notes = [fixLocation, switchNote].filter((note): note is string => note !== null);
  const withNotes =
    notes.length === 0 ? (
      control
    ) : (
      <div className="grid gap-1">
        {control}
        {notes.map((note) => (
          <p key={note} className="text-2xs text-fg-muted">
            {note}
          </p>
        ))}
      </div>
    );

  if (!compact || !cityHint) return withNotes;
  return <div title={cityHint}>{withNotes}</div>;
}

/**
 * SEED's regions of focus for this line — the geography a line carries when it
 * has no cities to carry one.
 *
 * ⚠️ **This is the control the feature shipped WITHOUT.** `regionIds` was
 * accepted on write and returned on every read while no quote or invoice screen
 * could send it: the picker was built into `QuoteLineFormModal`, which nothing
 * mounted — ✅ **and that file was DELETED on 2026-09-19.** The name survives
 * here as the record of where the picker used to sit; it is not live code. See
 * this file's header — the grid is the live editing surface.
 *
 * ## ⚠️ TWO rows reach it now, and they offer DIFFERENT LISTS
 *
 * *SUPERSEDED, 2026-08-31: "no project to narrow against. This control only
 * exists on rows that have no project, so there is no second list to filter by."*
 * The owner added the second case, so there is now a project on most of the rows
 * that render this:
 *
 * | the row | the picker offers | “All regions” |
 * | --- | --- | --- |
 * | no project at all | the `/regions` **master** | **absent** |
 * | a project declaring **no cities** | that **project's own** regions | shown |
 *
 * A projectless line has nothing to be "all" OF — the only bounded set available
 * would be SEED's whole master, and ticking a box that meant *every region SEED
 * sells in* is a claim about the business, not about the line. So the checkbox
 * renders only where a project supplies the bound.
 *
 * ## ⛔ “All regions” IS A SELECT-ALL. IT IS NOT `allCities`, AND MUST NOT BECOME IT
 *
 * ⚠️ **There is no `allRegions` key on the wire and none may be invented** —
 * measured in `quote-products.schema.ts` / `invoice-products.schema.ts`, which
 * declare `allCities` and nothing beside it, and both are `.strict()`. So this
 * box writes an EXPLICIT set of ids and there is nothing else it could write.
 *
 * That makes the two boxes look alike and mean different things, which is the
 * whole hazard here:
 *
 * - **`allCities` is a live RULE.** The server re-resolves it against the
 *   project on every read, so a city added to the project tomorrow is on the
 *   line tomorrow. That is why its picker DISAPPEARS while it is ticked —
 *   there are no chips somebody chose.
 * - **“All regions” is a SNAPSHOT.** A region added to the project tomorrow is
 *   not on this line. That is why the picker STAYS, showing the explicit chips
 *   it just selected — they are the value, and they are the honest read-out of
 *   it.
 *
 * ⚠️ **Do not "finish" this by hiding the picker to match the city cell.** The
 * chips are the one thing on screen that distinguishes a set from a rule, and
 * hiding them would assert a liveness the wire cannot deliver.
 *
 * ⚠️ **The tick is DERIVED, never stored.** {@link Draft} gains no flag: the box
 * is checked when the draft equals the project's set and unchecks itself when the
 * project gains a region. Unticking itself is what a person did; a box that
 * unticks on its own is the snapshot going stale in public, which is the visible
 * wrong answer rather than the silent one.
 *
 * ⚠️ **Rendered only by {@link showsRegionsControl}.** Hiding never touches
 * `draft.regionIds` — a regions write REPLACES the set, so a block that tidied
 * its own draft on the way to being hidden would clear a line's stored regions
 * with a `200` and nothing to restore from.
 */
function RegionsControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
  loading,
  projectRegions,
}: ControlProps & {
  options: MultiComboboxOption[];
  loading: boolean;
  /** The resolved project's own regions, or `null` when there is no project to take a set from. See {@link RowProject.projectRegions}. */
  projectRegions: { id: number; name: string }[] | null;
}) {
  /*
   * One key, one control. Unlike the city cell there is no second path a 422
   * can arrive on — `regionIds` is the only region key this grid sends, and the
   * checkbox below writes that same key — so there is nothing to join and no
   * coalesce that could drop half of a true statement.
   */
  const error = problems.fieldErrors.regionIds;

  /*
   * ⚠️ Offered only when the project declares regions. A box reading "All
   * regions" over a project that has none can only ever select nothing, which is
   * the affordance-with-one-outcome this codebase refuses everywhere else.
   */
  const bounded = projectRegions !== null && projectRegions.length > 0 ? projectRegions : null;
  /*
   * ⚠️ Set equality, both directions. `length` alone would tick the box for a
   * line holding the right NUMBER of the wrong regions — including a region the
   * project no longer declares, which is exactly the pinned-back option above.
   */
  const chosen = new Set(row.draft.regionIds);
  const isAll =
    bounded !== null &&
    chosen.size === bounded.length &&
    bounded.every((region) => chosen.has(region.id));

  const control = (
    <Field
      label="Regions of focus"
      hideLabel={compact}
      error={error}
      {...(!compact && projectRegions === null
        ? // True only on the projectless row. On a project-backed one the cities
          // control directly above already states that the project declares
          // none, and repeating it here would be prose for its own sake.
          { hint: 'Not sold through projects, so no cities — regions still apply.' }
        : {})}
    >
      {(fieldProps) => (
        <div className="grid gap-1.5">
          {bounded !== null && (
            <Checkbox
              label="All regions"
              checked={isAll}
              disabled={disabled}
              onChange={(event) =>
                /*
                 * ⚠️ ONE key, and it writes the ids OUT — there is no flag to
                 * set. Unticking clears rather than handing a parked set back,
                 * because ticking never parked one: it replaced the selection
                 * with the project's, and re-ticking rebuilds exactly the same
                 * set. Nothing is lost that one more click does not restore.
                 */
                onChange({
                  regionIds: event.target.checked ? bounded.map((region) => region.id) : [],
                })
              }
            />
          )}
          <MultiCombobox
            {...fieldProps}
            values={row.draft.regionIds.map(String)}
            /*
             * The order handed back is kept as it comes. It is not stored and
             * {@link regionsKey} ignores it, so re-sorting here would be churn
             * that changes nothing — and sorting a set the user just arranged is
             * the kind of tidying that reads as a bug.
             */
            onChange={(values) => onChange({ regionIds: values.map(Number) })}
            options={options}
            placeholder="No regions"
            searchPlaceholder="Filter regions"
            disabled={disabled}
            invalid={Boolean(error)}
            loading={loading}
            /*
             * ⚠️ Unlike the CITIES control on this same grid, regions have no
             * `allCities`-style flag to be confused with, so the row is the
             * only way to say "all of them" here and cannot mean two things.
             */
            selectAll
            {...(compact
              ? { maxVisibleChips: 0, summaryNoun: { singular: 'region', plural: 'regions' } }
              : {})}
          />
        </div>
      )}
    </Field>
  );

  /*
   * ⚠️ **The one sentence that keeps this box from being read as `allCities`.**
   * It states the fact a reader cannot otherwise see — that the set is frozen —
   * and it prevents a specific wrong conclusion about a value that decides Xero
   * tracking, which is the category the no-helper-text rule protects rather than
   * the category it bans. It renders only while the box is ticked, and never
   * inside a desktop grid cell, where no prose may go.
   */
  if (compact || !isAll) return control;
  return (
    <div className="grid gap-1">
      {control}
      <p className="text-2xs leading-snug text-fg-muted">
        This line’s own set, as it stands today. A region added to the project
        later is not added here.
      </p>
    </div>
  );
}

/**
 * A saved line's cities, read back.
 *
 * ⚠️ **All of them.** Truncating to the first is the wrong-document failure the
 * singular `city` was deleted to prevent: a line running in three cities that
 * renders as one is wrong on screen and invisible, and the customer's copy says
 * something else. `sortOrder` is the display order WITHIN THIS LINE and the
 * array already arrives sorted by it — never re-sort, not even by name.
 *
 * ⚠️ **The name it was GIVEN.** Reads join `f_cities` with no `isActive` filter,
 * so a city retired in the master after the document was sent still reads back
 * correctly; looking it up in a cached `?isActive=true` picker list and blanking
 * it when it is missing is how a historical document loses its geography.
 */
function CityChips({ cities }: { cities: LineCity[] }) {
  if (cities.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1">
      {cities.map((city) => (
        <li key={city.id}>
          <Badge tone="neutral" variant="outline" wrap>
            {cityLabel(city)}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function NumberControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  field,
  label,
  placeholder,
  hint,
  quantityStep,
}: ControlProps & {
  field: 'quantity' | 'discount' | 'taxRate';
  label: string;
  placeholder: string;
  /**
   * The one explanation, rendered where each layout can carry it.
   *
   * ⚠️ **A `title` alone reaches nobody on a touch screen**, and this control is
   * the tax fallback's whole voice: with Xero disconnected or the rate read
   * failed, "Xero is not connected, so this organisation's tax rates cannot be
   * listed" and "this line uses the company's Xero sales default" exist nowhere
   * else on screen. A tooltip needs hover, so at 320px that is a sentence with
   * no reader.
   *
   * So it is a `hint` in the CARD — which has no column header to carry meaning
   * and is not a grid — and the `title` in the GRID, where the
   * no-prose-in-a-cell rule holds and a sentence would cost every row its
   * height. Same shape as `CitiesControl`'s `cityHint`, and ONE string either
   * way: two copies of a sentence agree only until one of them is edited.
   */
  hint?: string;
  /**
   * The product's quantity step — `quantity` only. It becomes the box's
   * `step`/`min`, and its refusal ({@link QUANTITY_STEP_ERROR_KEY}) is held back
   * while the box has focus, so typing 5 on the way to 50 is not shouted at.
   */
  quantityStep?: number | null;
}) {
  const [focused, setFocused] = useState(false);

  function emit(value: string) {
    if (field === 'quantity') onChange({ quantity: value });
    else if (field === 'discount') onChange({ discount: value });
    else onChange({ taxRate: value });
  }

  const stepError =
    field === 'quantity' && !focused ? problems.fieldErrors[QUANTITY_STEP_ERROR_KEY] : undefined;
  const error = problems.fieldErrors[field] ?? stepError;
  const step = field === 'quantity' && quantityStep != null && quantityStep > 1 ? quantityStep : null;

  return (
    <Field
      label={label}
      hideLabel={compact}
      error={error}
      {...(!compact && hint ? { hint } : {})}
    >
      {(fieldProps) => (
        <Input
          {...fieldProps}
          inputMode={field === 'quantity' ? 'numeric' : 'decimal'}
          {...(step !== null ? { type: 'number', step, min: step } : {})}
          value={row.draft[field]}
          onChange={(event) => emit(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          invalid={Boolean(error)}
          className="text-right tabular-nums"
          {...(compact && hint ? { title: hint } : {})}
        />
      )}
    </Field>
  );
}

/**
 * The picker option that reveals the bare `%` box.
 *
 * ⚠️ **It is a VIEW action wearing an option's clothes, and that is the point.**
 * `Combobox` hands its `onChange` a value, so this sentinel is intercepted there
 * and returns before anything touches the draft: it can set no `xeroTaxType`, no
 * `taxRate`, and so it cannot be the route by which a code and a percentage come
 * to disagree. It is also never listed while a code is chosen, which is what
 * keeps the box and a code off the same cell.
 *
 * The value cannot collide with a real choice. Xero's tax types are
 * `[A-Z0-9]`-shaped (`ZERORATEDOUTPUT`, `TAX001`) and the empty option is `''`,
 * so no tenant rate and no "use the company's default" can ever equal this.
 */
const EDIT_RATE_OPTION = '__edit-rate__';

/**
 * Xero's "Tax rate" column — **ONE control over BOTH of our fields**.
 *
 * `taxRate` is our percentage and drives the generated arithmetic; `xeroTaxType`
 * is the tenant's code, and the two are **not derivable from each other** —
 * `ZERORATEDOUTPUT`, `NONE` and `EXEMPTOUTPUT` are all 0% on this tenant, so
 * the percentage cannot tell you the treatment the customer agreed to. Choosing
 * a rate here writes both keys in the same patch, so the two cannot drift.
 *
 * The option list is the tenant's own, read live from `api.xeroTaxRates()`,
 * never a table of ours.
 *
 * ⚠️ **It is the tenant's SALES rates, not all of them** — `lineTaxRates`
 * filters to `canApplyToRevenue`, because Xero refuses an expense rate on a
 * quote (`422 XERO_TAX_TYPE_NOT_REVENUE`).
 *
 * ⚠️ **And not every sales rate either: one whose `effectiveRate` is `null` is
 * withheld too.** `null` is Xero declining to state a percentage, never a zero,
 * and this control writes `taxRate` from that number — such a row could only
 * ever crash (`null.toFixed`) or, "repaired" with a `?? 0`, record 0% tax off a
 * figure that does not exist. That is why this picker uses `lineTaxRates` while
 * the company's Sales-default picker uses the wider `sellableTaxRates`, which
 * stores the code alone and has no percentage to derive.
 *
 * In all three cases the exception is a **stored** code, injected back so the
 * row does not render blank and clear itself.
 *
 * ⚠️ **The empty option means `null`, which means "use the company's Xero sales
 * default".** It never means "no tax" — it is the one choice with no percentage
 * attached, so it deliberately leaves `taxRate` where it stands; inventing a
 * `0` there would rewrite the line's arithmetic off a choice about a code.
 *
 * ⚠️ **The rate lives on the TRIGGER, not a second box** — `20% · Xero default`
 * — because a rate that renders nowhere is worse than an extra control.
 *
 * ⚠️ **The rate stays EDITABLE without asserting a tax code.** Moving a line
 * from 20% to 5% must not require choosing a Xero code, which would assert a
 * VAT treatment the customer never agreed to. So the `%` box is revealed on
 * request, by an option INSIDE the picker ({@link EDIT_RATE_OPTION}) — it costs
 * the row no height, keeps "edit the percentage" inside the one control that
 * owns tax, and **writes nothing**: choosing it moves local view state, never a
 * draft key.
 *
 * ⚠️ **They still cannot disagree, structurally.** The reveal option is listed
 * ONLY while `xeroTaxType` is blank, and choosing any real code clears the ask
 * in the same handler that writes the code's own rate — the box is never on
 * screen beside a code implying a different percentage.
 *
 * The one deliberate exception is the escape hatch: if a stored `taxRate` is
 * refused while a code IS chosen, the box appears on its own so the refusal has
 * somewhere to be corrected. An uncorrectable row is worse than a crowded one.
 *
 * 🔴 **THE PERCENTAGE IS READ-ONLY ON A COMPANY-SOURCED LINE. THE XERO
 * CODE NEVER IS.** Owner, third position of 2026-08-30 — the sequence was
 * read-only, then always editable, then this.
 *
 * *SUPERSEDED, and it is the version most likely to be remembered: "THE
 * CONTROL IS ALWAYS EDITABLE — the owner reversed the read-only ruling on
 * 2026-08-30 … Nothing here is locked by that field any more … The rate and the
 * CODE are editable TOGETHER, and that pairing survives the reversal — it is
 * now the reason for it."* The pairing argument is still sound and its
 * conclusion moved: the two keys still travel in one patch, but only the picker
 * writes them, and the bare percentage box is withheld while the company owns
 * the figure.
 *
 * ⚠️ **What the lock touches, exactly: the `%` box and the option that reveals
 * it.** The Combobox stays enabled, because it is the mitigation — a rate the
 * code disagrees with is `XERO_TAX_RATE_MISMATCH` at push time, and choosing a
 * different Xero rate here rewrites the pair in agreement. That a code choice
 * therefore moves the percentage is not a hole in the lock; it is the point of
 * leaving the code open, and locking it "for consistency" strands the row.
 *
 * ⚠️ **`taxRateSource` did NOT go away and is still required.** `'company'`
 * says the company states a rate and this line holds it — a gate on the
 * percentage AND a sentence to show. See {@link isCompanySourcedTaxRate}.
 *
 * ⚠️ **The escape hatch outranks the lock.** If the server has refused this
 * line's `taxRate`, the box appears and is typeable whatever the provenance
 * says: an uncorrectable row is worse than an editable one.
 */
function TaxRateControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  rates,
  ratesLoading,
  ratesError,
  connected,
  companySalesTax,
}: ControlProps & {
  rates: XeroTaxRate[];
  ratesLoading: boolean;
  ratesError: string | null;
  connected: boolean;
  /** For DISPLAY only — see {@link CompanySalesTax}. Nothing here is sent. */
  companySalesTax: CompanySalesTax;
}) {
  const stored = row.draft.xeroTaxType;
  // Only the tax types set on the billing-region defaults (owner, 2026-10-12).
  const allowedTaxCodes = useDocumentTaxCodes();

  /*
   * ⚠️ **WHERE THIS LINE'S RATE CAME FROM.** The server seeds a created line
   * from the company — `quote-products.service.ts` resolves
   * `input.taxRate ?? companyRate ?? undefined` — and `taxRateSource: 'company'`
   * records that it did.
   *
   * ⚠️ *SUPERSEDED, and it named the wrong migration: "**Migration 096 writes
   * the company's `xeroSalesTaxRate` onto a line where the company has one**."*
   * **096 is the region split.** The column arrived in
   * `092_add_companies_sales_tax_rate.sql`, and the seeding is SERVICE-LAYER
   * TypeScript rather than SQL — which matters, because somebody reading 096 to
   * check this finds a migration about regions and no answer either way.
   *
   * 🔴 **IT IS A GATE ON THE PERCENTAGE AGAIN — owner, third position of
   * 2026-08-30.** *SUPERSEDED: "IT IS NOT A GATE. THE OWNER REVERSED THAT ON
   * 2026-08-30 … The rate is the line's to change again, so this is read for
   * ONE purpose: a quiet hint … Do not reattach it to `disabled`. A hidden or
   * frozen tax control is how a line goes out at `0.00` with nobody able to say
   * so — and a read-only `0.00` is indistinguishable from a real company rate
   * of zero."*
   *
   * ⚠️ **That `0.00` objection is answered, not ignored, and `rateLocked` below
   * is where the answer lives** — the lock requires a percentage to actually be
   * present, so nothing freezes over a blank or an invented zero.
   *
   * ⚠️ **It gates the PERCENTAGE ONLY. Never the Combobox.** The code picker
   * open is what keeps `XERO_TAX_RATE_MISMATCH` clearable from this grid, and
   * it is the entire reason the lock could come back.
   *
   * Read from `row.saved`, so a row that has never been saved has no provenance
   * to report, shows no hint, and is never locked.
   */
  const companySourced = isCompanySourcedTaxRate(row.saved?.taxRateSource);
  /*
   * The sentence: where the figure came from, and the two places it can be
   * changed. It is the one place the company percentage is guaranteed to be
   * legible — with a code chosen the trigger reads the code's NAME and the
   * figure appears nowhere else in the cell. `undefined` when the line states
   * its own rate, so nothing is said about a control behaving ordinarily.
   */
  const companyReason = companySourced ? companyTaxRateReason(row.draft.taxRate) : undefined;
  /*
   * ⚠️ **Declared HERE rather than beside `showRateBox` because the fallback
   * below returns before that point and needs the same rule.** One statement of
   * "the server has refused this rate", read by both layouts.
   */
  const rateNeedsFixing = problems.fieldErrors.taxRate !== undefined;
  /*
   * ⚠️ **THE LOCK, AND BOTH EXTRA CONJUNCTS ARE LOAD-BEARING — neither is
   * defensive padding.**
   *
   * `row.draft.taxRate.trim() !== ''` is the answer to the objection that
   * reversed this ruling once already: a frozen blank, or a frozen `0.00` the
   * company never stated, is indistinguishable from a real zero-rating. The
   * server only reports `'company'` when the company states a percentage, so
   * this should always hold — and where it somehow does not, the control **fails
   * OPEN** rather than freezing over a figure nobody chose.
   *
   * `!rateNeedsFixing` is the escape hatch, and it outranks the lock for the
   * reason this file already gives about the box sharing the cell with a code:
   * an uncorrectable row is worse than a crowded one. If the server has refused
   * this line's rate, it must be typeable here whatever the provenance says.
   *
   * ⚠️ **It is never passed to the Combobox.** Read the two `disabled` props
   * below: the `%` box takes it, the picker does not.
   */
  const rateLocked = companySourced && row.draft.taxRate.trim() !== '' && !rateNeedsFixing;

  /*
   * ⚠️ **Whether the bare `%` box has been ASKED for on this row.** View state,
   * not draft state: nothing here is sent, nothing here can make a row dirty,
   * and closing it loses no value — the percentage itself lives in
   * `draft.taxRate` and is echoed on the trigger whether the box is open or not.
   *
   * Declared before the `unavailable` return below so the hook order is fixed.
   * That path renders the box unconditionally and never reads this.
   *
   * The grid and the `md:hidden` card each mount an instance of this control per
   * row, so crossing the `md` breakpoint with the box open closes it. Nothing is
   * lost when that happens, which is why it is not lifted out of this component
   * the way a typed description is — that one holds a value, and it lives on
   * `draft.description` because it is a real column.
   */
  const [rateBoxAsked, setRateBoxAsked] = useState(false);

  /*
   * ⚠️ **The fallback, and it is required.** The rates belong to the tenant, so
   * there is nothing to list while Xero is disconnected, nothing to list when
   * the read failed, and nothing to list when the organisation genuinely has
   * none. Leaving the cell inoperable in those three would leave a person with
   * NO way at all to record tax on a line — worse than a percentage carrying no
   * Xero code, which is a nullable field and the state every live row is in
   * today. So the bare `%` input stands in, and `taxRate` keeps the validation
   * and the payload behaviour it has always had.
   *
   * `ratesLoading` is deliberately NOT a fallback: the dropdown renders with
   * its own loading state instead, because swapping the control out and back
   * under someone's cursor is worse than a spinner.
   */
  const unavailable = !connected || ratesError !== null || (!ratesLoading && rates.length === 0);
  if (unavailable) {
    return (
      <NumberControl
        row={row}
        problems={problems}
        /*
         * ⚠️ The caller's own `disabled` — read-only mode, a save in flight, a
         * permission denial — OR the company owning the figure.
         *
         * ⚠️ *SUPERSEDED: "and nothing else … A company-sourced rate no longer
         * disables anything; it is reported in the hint below instead."* It
         * disables again, per the owner's third position of 2026-08-30. This is
         * the ONE branch where the box is the whole control, so the hint below
         * carries the refusal AND both routes out; a locked box whose only
         * explanation is its own greyness is the failure this repo names
         * repeatedly.
         */
        disabled={disabled || rateLocked}
        compact={compact}
        onChange={onChange}
        field="taxRate"
        label="Tax rate"
        placeholder="0"
        /*
         * ⚠️ **This is the whole explanation of a degraded control, so it has to
         * be READABLE and not merely present.** Why the dropdown is gone, and
         * which Xero code the line is on while it is — neither is anywhere else
         * on the screen, and the code in particular is invisible without it,
         * since the bare `%` box shows a percentage and says nothing about the
         * treatment. `NumberControl` renders it as the card's `hint` and the
         * grid's `title`; passed as a `title` alone it was unreachable at 320px,
         * where there is no hover.
         *
         * ⚠️ The ownership clause leads when there is one, because it is the
         * reason the box cannot be typed in — the reader meets the refusal
         * before the explanation of a list they were not going to get anyway.
         * (That sentence was written for the original read-only ruling, went
         * false during the 2026-08-30 reversal while nobody corrected it, and is
         * true again under the third position. It is the leading clause because
         * it explains the LOCK, so it leads exactly when `rateLocked` holds —
         * which is whenever `companyReason` is present here, bar the two
         * fail-open cases, where it explains the value instead.)
         */
        hint={[
          ...(companyReason === undefined ? [] : [companyReason]),
          !connected
            ? 'Xero not connected — no tax rates to list.'
            : (ratesError ?? 'This Xero organisation lists no tax rates.'),
          stored === ''
            ? 'Using the company’s Xero default.'
            : `Xero tax code on this line: ${stored}`,
        ].join(' ')}
      />
    );
  }

  /*
   * ⚠️ **REVENUE RATES ONLY, and this is a refusal moved forward rather than a
   * tidier list.** The tenant lists 15 active rates and only 7 carry
   * `canApplyToRevenue`. Offering all 15 is what put `INPUT2` —
   * "20% (VAT on Expenses)" — on quote 9 line 1, where the push answered
   * `422 XERO_TAX_TYPE_NOT_REVENUE`: *an EXPENSE tax rate … but a quote is a
   * sales document. Did you mean OUTPUT2 (20% (VAT on Income))?*
   *
   * ⚠️ **The expense rates WERE marked, and marking was not enough.** The hint
   * read `INPUT2 · 20% · expense rate` and the label read "20% (VAT on
   * Expenses)", one word from "20% (VAT on Income)" and identical in its
   * percentage. A distinction a reader must notice is not a guard; the owner's
   * decision is that a rate Xero will refuse is not offered at all.
   *
   * The filter lives in `sellableTaxRates` and NOT here, because the same
   * question is asked wherever a sales document picks a tax code — one copy of
   * the rule, the way `inclusions.ts` owns the wording rule. Never re-derive it
   * from `canApplyToRevenue` at a call site.
   *
   * ⚠️ **`lineTaxRates` and not `sellableTaxRates`, because THIS picker writes a
   * percentage as well as a code.** It is `sellableTaxRates` plus one further
   * withholding: a rate whose `effectiveRate` is `null` — Xero declining to
   * state a percentage — cannot be applied to a line, because the `onChange`
   * below derives `taxRate` from that number. Offering it puts a row on screen
   * whose only outcomes are a crash or a **0% tax rate recorded on a customer's
   * document** off a figure that does not exist. The company Sales-default
   * picker keeps the wider list on purpose: it stores only the code. Both
   * decisions live in `xero-tax-rates.ts`; neither is re-derived here.
   */
  const offered = lineTaxRates(rates, stored, allowedTaxCodes);

  const options: ComboboxOption[] = offered.map((rate) => {
    /*
     * ⚠️ **INJECTION CASE C — a code with NO PERCENTAGE from Xero.** Reached
     * only as the stored value: `lineTaxRates` withholds every other one.
     *
     * Returned before `percentForDraft` is anywhere near it, which is the
     * point — this is the branch that keeps `null.toFixed` unreachable rather
     * than merely unlikely, and the compiler enforces it, since
     * `hasEffectiveRate` is what narrows `effectiveRate` to a `number` below.
     *
     * It is shown to be READ and replaced, exactly like case A. The hint says
     * Xero has published no percentage rather than implying zero — this tenant
     * lists three rates that really are 0%, so "unknown" and "nothing to
     * charge" would read identically if this said `0%`.
     */
    if (!hasEffectiveRate(rate)) {
      return {
        value: rate.taxType,
        label: rate.name,
        hint: `${taxRateHint(rate)} · on this line, but has no percentage — choose a rate below`,
      };
    }
    /*
     * ⚠️ Say so when the stored figure will not be Xero's own. See
     * {@link percentForDraft}: a three-decimal tenant rate cannot be held by
     * `taxRate`, and rounding it without a word would put a percentage on a
     * customer's document that differs from the one the accountant reads in
     * Xero. The popover is where this may be said — a `hint` renders under the
     * option list inside it, never in the cell.
     */
    const rounded = percentForDraft(rate.effectiveRate);
    const rephrased = rounded !== String(rate.effectiveRate);
    const hint = rephrased
      ? `${taxRateHint(rate)} · recorded here as ${rounded}%`
      : taxRateHint(rate);
    /*
     * ⚠️ **INJECTION CASE A — present in the tenant list, but an EXPENSE rate.**
     * `sellableTaxRates` puts it back, first, because it is the code this line
     * already holds: dropped, the control would render blank over a real VAT
     * treatment and the next save would silently clear it — the same
     * stored-value rule every picker in this app follows.
     *
     * So it is the one non-sales rate that can reach this map, and it is
     * offered only to be READ and replaced. The hint says Xero's own objection
     * rather than the neutral "expense rate" that failed to stop this: the row
     * is not merely unusual, it CANNOT be pushed while it stands.
     */
    if (!isSalesTaxRate(rate)) {
      return {
        value: rate.taxType,
        label: rate.name,
        /*
         * ⚠️ "below" is not a turn of phrase — it is load-bearing, and it was
         * "above" for one commit. `sellableTaxRates` returns
         * `[stored, ...sellable]`, so this injected row is ALWAYS index 0 and
         * every sales rate sits under it. If that ordering is ever changed,
         * this word changes with it; pointing someone at an empty half of a
         * list is the same class of confidently-wrong copy as the heading this
         * whole pass replaced.
         */
        hint: `${hint} · on this line, but an expense rate — choose a sales rate below`,
      };
    }
    return { value: rate.taxType, label: rate.name, hint };
  });
  /*
   * ⚠️ **INJECTION CASE B — absent from the tenant list ENTIRELY.** A different
   * case from A, and the two must not both fire for one code.
   *
   * They cannot, structurally, because this test runs over the POST-`sellable`
   * options rather than over `rates`: case A has already put the stored expense
   * code into `options`, so `options.some(...)` is true and this branch is
   * skipped. Only a code `sellableTaxRates` could not find at all — retired or
   * deleted in Xero since the line was saved — reaches here, and it is the only
   * one for which "no longer listed by Xero" is true. Keep the test on
   * `options`; moved back onto `rates` it would double-inject every stored
   * expense code.
   *
   * ⚠️ **CORRECTION 2026-08-27 — running over `options` is NECESSARY BUT NOT
   * SUFFICIENT, and the paragraph above claimed it was the whole guarantee.**
   * The comparison SHAPE is the other half. This test read `option.value ===
   * stored`, and those two strings come from different places: `option.value`
   * is the **live tenant's** spelling (`sellableTaxRates` deliberately injects
   * the live rate object, not the stored string), while `stored` is the
   * **stored** spelling — and this file records in three places that the server
   * normalises casing on write, so the two can legitimately differ in case.
   *
   * Stored `output2` against live `OUTPUT2` therefore double-injected: case A
   * kept the real rate, this test missed it, and a second row went in labelled
   * with the bare code and hinted "no longer listed by Xero" — a false sentence
   * about a code Xero lists right now. Worse, `Combobox` selects by
   * `option.value === value`, so the **phantom** was the selected row: the
   * trigger read `output2` instead of `20% (VAT on Income)`, and choosing it
   * took the no-rate-object branch and wrote the code WITHOUT the percentage —
   * defeating the one-patch guarantee this control exists to provide.
   *
   * `sellableTaxRates`, `lineTaxRates`, `XeroSalesDefaultsFields` and
   * `CompanyDetailPage` all compare these two case-insensitively. This was the
   * only site that did not, which is exactly the "third place casing has to
   * agree" that `sellableTaxRates`'s own doc warns about.
   */
  if (stored !== '' && !options.some((option) => sameTaxCode(option.value, stored))) {
    options.unshift({ value: stored, label: stored, hint: 'no longer listed by Xero' });
  }

  /*
   * ⚠️ **The percentage on its own has to stay EDITABLE, not merely visible.**
   *
   * `xeroTaxType` is null on every live row while `taxRate` is set, so "a rate
   * with no Xero code" is not an edge case — it is the state of the whole
   * database. Merging the two boxes into one picker fixed a real bug (see this
   * component's header) and took that state's only control away with it: moving
   * a line from 20% to 5% meant choosing a Xero code, which asserts a VAT
   * treatment the customer never agreed to. That was a loss, not a decision.
   *
   * ⚠️ **But `stored === ''` on its own is not a gate, it is every row.** That
   * is exactly what the previous repair used, so the box rendered beside the
   * picker on all of them and the cell was a stacked pair again — the thing the
   * owner objected to, arriving from the direction of a fix. The gate is now
   * `stored === ''` **and the person asked**, so the default is one control and
   * the capability is one choice inside it.
   *
   * ⚠️ **The disagreement is still UNREACHABLE rather than unlikely.** The old
   * failure was a percentage box reading `0` beside a code box reading
   * `5% (VAT on Income)` — two facts, contradicting. That needs a CODE to
   * contradict, and `stored === ''` is still a conjunct here, so wherever the box
   * is on screen the rate is the single fact in the cell. Choosing a code clears
   * the ask in the same handler that writes the code's own rate.
   *
   * The second condition is the escape hatch, and it exists so a row can never
   * become unfixable: if `taxRate` is somehow refused while a code IS chosen,
   * the box appears on its own so the refusal has somewhere to be corrected. It
   * is the one state in which a box and a code share the cell, and it is
   * deliberate — an uncorrectable row is worse than a crowded one.
   *
   * `rateNeedsFixing` and `rateLocked` are declared above, beside
   * `companySourced`: the disconnected fallback returns before this point and
   * has to apply the identical rule.
   */
  /*
   * 🔴 **A LOCKED LINE NEVER OPENS THE BOX — owner, third position of
   * 2026-08-30.** *SUPERSEDED: "It is, since the owner's reversal of
   * 2026-08-30, so the provenance of the figure has no say in whether the box
   * opens."* It has a say again, and `!rateLocked` is where it is said.
   *
   * ⚠️ **`rateLocked` is a conjunct of the FIRST disjunct only, and that is the
   * escape hatch surviving intact.** `rateNeedsFixing` still opens the box on
   * its own — and it cannot contradict the lock, because `rateLocked` already
   * requires `!rateNeedsFixing`. So the two can never both hold, and a refused
   * rate is always correctable.
   *
   * ⚠️ It is also belt and braces against ORDERING: `rateBoxAsked` is view
   * state that outlives a save, so a line that was `'line'`-sourced with the box
   * open can come back `'company'`-sourced. Testing the lock here shuts the box
   * on that save rather than leaving a stale open one typeable.
   */
  const showRateBox = (stored === '' && rateBoxAsked && !rateLocked) || rateNeedsFixing;

  /*
   * ⚠️ **The reveal, and its two conditions are both load-bearing.**
   *
   * `stored === ''` is what keeps the box structurally unable to sit beside a
   * code: the only way to open it is not listed once a code is chosen.
   * `!showRateBox` is merely tidy — an option offering to do what is already
   * done reads as a second, different action.
   *
   * ⚠️ The wording says what it RECORDS, not what it opens. "Enter a percentage
   * instead" is a choice about tax; "show a box" is a choice about the screen,
   * and this control is read by people deciding what a customer is charged.
   */
  /*
   * 🔴 **WITHHELD ON A LOCKED LINE — owner, third position of
   * 2026-08-30.** *SUPERSEDED: "The server no longer undoes it, so the option
   * is offered on every line whose code is blank."*
   *
   * ⚠️ **Withheld rather than listed-and-disabled**, because it is the second
   * half of one decision with the box itself: an option that opens a control
   * nobody may type in is a promise the next click breaks. What replaces it is
   * `companyReason`, which names the company AND says a different Xero rate is
   * the other way out — a route that still exists on this very picker.
   */
  if (stored === '' && !showRateBox && !rateLocked) {
    options.push({
      value: EDIT_RATE_OPTION,
      label: 'Enter a percentage instead…',
      hint: 'A rate with no Xero code — the company’s Xero default decides the treatment.',
    });
  }

  /*
   * ⚠️ The percentage must never become invisible. With no code chosen there is
   * no option label to carry it, and every live row is in exactly that state —
   * `xeroTaxType` is null on all of them while `taxRate` is set. So the empty
   * option says the number out loud **when nothing else does** — and once the
   * box below is on screen, something else does, so the echo stands down rather
   * than printing the same number twice in one cell.
   *
   * ⚠️ **This echo is the DEFAULT rendering of a live row, not a fallback.**
   * The box is closed until it is asked for, so on every row in the database
   * this label is the only place the rate appears.
   *
   * ⚠️ **It reads `0.00% · Xero default`, and the ORDER is load-bearing.** This
   * was `Company’s Xero default · 0.00%`, which is 30 characters: the trigger
   * truncates, so the half that survived was the words and the half that
   * vanished was the number the line above insists must not. Leading with the
   * rate means truncation can only ever eat the wording, and a clipped
   * `0.00% · Xero de…` still cannot be misread as "no tax" — it reads as
   * clipped. The unabbreviated sentence is in the popover `footnote` and on the
   * cell's own `title`, which is where the no-prose-in-a-grid-cell rule puts it.
   *
   * The typed rate is echoed as the string it is — `0.00%`, not `0%`. Trimming
   * it would be this file inventing a display rule for a value the server owns,
   * to save three pixels.
   */
  const typedRate = row.draft.taxRate.trim();
  /*
   * 🔴 **WHAT A BLANK PERCENTAGE ON THIS ROW WILL ACTUALLY BE — the defect the
   * owner reported on 11 September 2026: *"the tax rate in quote / invoice line
   * items should be the company master one and not the Xero default"*.**
   *
   * The DATABASE was already right. `quote-products.service.ts` resolves a
   * created line's rate as `input.taxRate ?? companyRate ?? undefined`, so a
   * blank box on a new row has always been saved at the company's percentage.
   * What was wrong was the SCREEN: `companySourced` below reads
   * `row.saved?.taxRateSource`, which no unsaved row has, so the empty option
   * read `Xero default` and the Amount cell previewed tax at 0.00 beside it.
   *
   * ⚠️ **`row.saved === null` is the whole gate, and it is not tidiness.** On an
   * EXISTING line a blank percentage means *omit the key*, which leaves the
   * stored rate alone — the company's figure is not what happens there, so
   * naming it would be a label for a save nobody is about to make. Identical
   * reasoning to {@link previewInputOf}'s, and the two must agree: a cell that
   * says `20.00%` over an Amount computed at 0% is worse than either alone.
   *
   * ⚠️ **`!showRateBox`** keeps the existing rule that the echo stands down once
   * the number has a box of its own, so one cell never prints two percentages.
   *
   * ⚠️ **A rate of `"0.00"` IS a rate and reaches this** — 63 of the 100 live
   * companies measured on 11 September 2026 are genuinely 0% (`NONE` and
   * `ECZROUTPUTSERVICES`). Only `null`, *the company states none*, falls
   * through to the wording below. Never `?? 0`, never `'0.00'` read as absence.
   */
  const companyRateSeed =
    row.saved === null &&
    typedRate === '' &&
    !showRateBox &&
    companySalesTax.known &&
    companySalesTax.rate !== null &&
    companySalesTax.rate.trim() !== ''
      ? companySalesTax.rate.trim()
      : null;
  /*
   * ⚠️ **On a company-sourced line the WORDS change, and this is the only place
   * "which it is" survives without a hover.**
   *
   * ⚠️ *SUPERSEDED, and it was stale under BOTH later positions: "A disabled
   * `<button>` swallows pointer events in Chrome, so neither its own `title`
   * nor the wrapper's is reachable by hovering the trigger — the
   * `disabledReason` degrades to a tooltip on a control that cannot be
   * hovered."* That was written when `taxRateSource: 'company'` disabled the
   * TRIGGER. It has not since the 2026-08-30 reversal, and it still does not
   * under the third position: only the `%` box locks, never the picker. The
   * reason survives on a plainer fact — **a `title` needs a pointer, and a
   * touch device has none**, so the source has to be readable in the cell
   * itself rather than only on hover.
   *
   * `Company rate` and `Xero default` are the same length to the character, so
   * this cannot change what the `table-fixed` column truncates. The rate still
   * LEADS for the reason argued below: truncation may only ever eat the wording.
   *
   * ⚠️ It says where the figure came FROM, never what it amounts to — `0.00% ·
   * Company rate` is a rate an administrator chose, and must not be readable as
   * "no tax".
   */
  /*
   * ⚠️ **`Company rate` is REUSED rather than a new word being invented**, and
   * that is what keeps the character discipline the paragraph above depends on:
   * it is the same 12 characters as `Xero default`, so this branch cannot change
   * what the `table-fixed` column truncates. The rate still LEADS, so truncation
   * may only ever eat the wording — `20.00% · Company ra…` still cannot be
   * misread as "no tax".
   *
   * ⚠️ It says where the figure COMES FROM, never what it amounts to.
   * `0.00% · Company rate` is a treatment an administrator chose — 63 of 100
   * live companies — and must not read as an absence of one.
   */
  const emptyLabel =
    companyRateSeed !== null
      ? `${formatDecimalString(companyRateSeed)}% · Company rate`
      : typedRate === '' || showRateBox
        ? companySourced
          ? 'Company rate'
          : 'Xero default'
        : `${typedRate}% · ${companySourced ? 'Company rate' : 'Xero default'}`;

  /*
   * ⚠️ A `footnote` renders under the option list INSIDE the popover, never in
   * the cell, which is why this one survives the no-prose rule and the `hint`
   * that used to sit beside it does not. It is also the only place the empty
   * option's meaning can be written down — `emptyOptionLabel` is a bare string
   * — and that meaning is correctness, not colour: blank is the company's sales
   * default and never "no tax", on a document a customer is invoiced against.
   *
   * ⚠️ **Count what is WITHHELD from the filtered list, never what the
   * organisation holds overall** — a count of the tenant's total rates says
   * nothing about what is on screen once the list is filtered, and nothing
   * typechecks a UI sentence against the data it describes.
   *
   * It says three things, in the order a reader needs them: what the list is,
   * why the missing rates are missing, and what blank means. The middle one is
   * the sentence that prevents a specific wrong conclusion — without it a
   * finance user who knows this organisation has a 20% expense rate reads its
   * absence as a broken picker and goes hunting for it.
   */
  /*
   * ⚠️ **TWO reasons a rate is withheld now, and the sentence has to carry
   * both.** `lineTaxRates` drops expense rates AND rates Xero has published no
   * percentage for. Counting only the first would leave the list shorter than
   * the sentence explains — which is the precise failure this footnote exists
   * to prevent, since a list that silently shrinks is indistinguishable from a
   * failed read.
   *
   * Counted separately and never summed: they send the reader to different
   * conclusions, and `unquantifiedSalesRateCount` deliberately counts sales
   * rates only, so an expense rate that also lacks a percentage is reported
   * once, under the reason that can actually be acted on.
   */
  const withheldExpense = expenseRateCount(rates);
  const withheldUnquantified = unquantifiedSalesRateCount(rates);
  /*
   * The one withheld rate that is on screen anyway — the stored code, put back
   * by `lineTaxRates` (cases A and C above). So the withheld sentence must not
   * claim every such rate is absent while one of them is being displayed.
   *
   * ⚠️ Found STRUCTURALLY, by asking the offered list which of its rows breaks
   * the rule, rather than by comparing `stored` to a code a second time. The
   * filter removed every other one, so a survivor can only be the injection —
   * and a fresh string comparison here would be a third place casing has to
   * agree, which is exactly the gap `sellableTaxRates` documents on its own
   * lookup.
   */
  const injected = offered.find((rate) => !isSalesTaxRate(rate) || !hasEffectiveRate(rate));
  const withheldClauses: string[] = [];
  if (withheldExpense === 1) {
    withheldClauses.push(
      'one expense rate is hidden, since Xero refuses one on a sales document',
    );
  } else if (withheldExpense > 1) {
    withheldClauses.push(
      `${withheldExpense} expense rates are hidden, since Xero refuses them on a sales document`,
    );
  }
  /*
   * Worded to stand alone rather than to follow the clause above, because
   * either can be the only one present. "one further rate…" reads as a
   * continuation and would be the first half of a sentence with no first half
   * whenever this organisation holds no expense rates at all.
   */
  if (withheldUnquantified === 1) {
    withheldClauses.push(
      'one sales rate is hidden, since Xero publishes no percentage for it',
    );
  } else if (withheldUnquantified > 1) {
    withheldClauses.push(
      `${withheldUnquantified} sales rates are hidden, since Xero publishes no percentage for them`,
    );
  }
  const listSentence =
    injected !== undefined
      ? !isSalesTaxRate(injected)
        ? 'Sales rates only. The expense code on this line blocks the push until it is changed.'
        : 'Sales rates only. The code on this line has no percentage, so it cannot be applied.'
      : withheldClauses.length === 0
        ? 'Sales rates only.'
        : `Sales rates only — ${withheldClauses.join('; ')}.`;
  /*
   * ⚠️ **WHICH rate "the company's Xero sales default" actually is — NAMED, and
   * never turned into a percentage.**
   *
   * The sentence below has always said blank means the company's default; with
   * the company read, it can say which one, which is the difference between a
   * rule and an answer. It is `rates` and not `offered` deliberately: this only
   * ever NAMES the rate, so a default that happens to be withheld from the
   * picker still has a true name to print.
   *
   * ⛔ **`name` ONLY. The company's `xeroSalesTaxRate` is the figure and the
   * server owns it** — resolving a percentage from this code against Xero's
   * published `EffectiveRate` would be a second implementation of a number
   * printed on a customer's document. See {@link CompanySalesTax}.
   *
   * ⚠️ **`sameTaxCode`, never `===`.** Casing is normalised server-side, so a
   * stored `output2` and a live `OUTPUT2` are one rate; an exact compare would
   * miss and this clause would silently vanish on exactly the companies it is
   * most wanted for. It is also why the code itself is NOT printed when the
   * lookup misses — a bare unlisted code beside a live list reads as the
   * "no longer listed by Xero" phantom rather than as an unresolved name.
   */
  const companyDefaultCode =
    companySalesTax.known &&
    companySalesTax.taxType !== null &&
    companySalesTax.taxType.trim() !== ''
      ? companySalesTax.taxType.trim()
      : null;
  const companyDefaultName =
    companyDefaultCode === null
      ? undefined
      : rates.find((rate) => sameTaxCode(rate.taxType, companyDefaultCode))?.name;
  const companyDefaultClause =
    companyDefaultName === undefined ? '' : ` This company’s default is ${companyDefaultName}.`;
  const footnote =
    rates.length > 0
      ? `${listSentence} Blank means the company’s Xero sales default, never “no tax”.${companyDefaultClause}`
      : undefined;

  /*
   * ⚠️ BOTH messages, never `a ?? b`. One control now owns two keys, so a 422
   * can name either — and a coalesce between two messages that can both be true
   * drops one of them silently, which is the exact failure this repo has
   * catalogued four times.
   *
   * The `taxRate` half is folded in only when the rate has **no box of its own**.
   * When `showRateBox` is true it is marked on that box instead, beside the
   * value it is about — repeating it here as well would print one refusal twice
   * in one cell and leave the reader looking for a second problem.
   */
  const errorText =
    [problems.fieldErrors.xeroTaxType, showRateBox ? undefined : problems.fieldErrors.taxRate]
      .filter((text): text is string => text !== undefined)
      .join(' ') || undefined;

  /*
   * The wording the trigger cannot afford, in the two places a grid cell may
   * carry one: the popover's `footnote` and the cell's `title`. Blank meaning
   * "the company's Xero sales default" and never "no tax" is a VAT treatment on
   * a document a customer is invoiced against, so it is stated in full
   * somewhere reachable — just not inside a 145px cell.
   */
  /*
   * ⚠️ The rate clause stands down when `companyReason` is carrying the figure,
   * so the `title` states it once. Both are true — this is not a `??` dropping
   * one of two facts — they are the same fact, and printing `20.00%` twice in
   * one tooltip reads as two different numbers that happen to match.
   */
  const fullMeaning =
    stored === ''
      ? `Blank means the company’s Xero sales default, never “no tax”.${companyDefaultClause}${
          typedRate === '' || companySourced ? '' : ` This line’s rate is ${typedRate}%.`
        }`
      : `Xero tax code on this line: ${stored}.`;

  /*
   * ⚠️ **The trigger LABEL, in front of the meaning, because under `table-fixed`
   * the label is the half that gets cut.** This column's intrinsic width was
   * taking it to 197px against an asked-for 145 and the difference came out of
   * Qty and Disc %; it can no longer do that, so `5% (VAT on Income)` now
   * ellipses in a 118px cell. The `title` is the only place the whole thing
   * survives — and the meaning below it is still the part that is correctness
   * rather than convenience, so both are carried, never one instead of the other.
   */
  const selectedLabel = options.find((option) => option.value === stored)?.label;
  const fullTitle = selectedLabel === undefined ? fullMeaning : `${selectedLabel} — ${fullMeaning}`;

  const control = (
    <Field
      label="Tax rate"
      hideLabel={compact}
      /*
       * ⚠️ The `disabledReason`, and ONLY in the card. `Field` renders a hint as
       * a paragraph under the control, which the desktop grid forbids inside a
       * cell — there it reaches the reader on the `title` below instead. The
       * card is where 320px lands, and 320px has no hover, so this is the one
       * layout in which the sentence must be real text.
       */
      {...(!compact && companyReason !== undefined ? { hint: companyReason } : {})}
      {...(errorText ? { error: errorText } : {})}
    >
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={stored}
          onChange={(value) => {
            /*
             * ⚠️ BOTH keys, one patch. `effectiveRate` is a number **when Xero
             * stated one — it is `number | null`, corrected 2026-08-27** — and
             * `taxRate` is sent as the string the box holds, so it goes through
             * {@link percentForDraft} and never through money arithmetic —
             * `String(20)`, `String(0)` and `String(12.5)` pass PERCENT_PATTERN
             * untouched, and a three-decimal tenant rate is rounded to what the
             * column can hold rather than written as a value no save can carry.
             *
             * The empty option carries no rate, so it moves the code alone;
             * see this component's header for why that is deliberate.
             */
            if (value === EDIT_RATE_OPTION) {
              /*
               * ⚠️ **Returns before touching the draft, and that is the whole
               * safety of the sentinel.** It opens a box; it records no tax. A
               * branch here that "helpfully" seeded `taxRate` would be this
               * component inventing a percentage for a line, which is the class
               * of bug the merged control was built to remove.
               */
              setRateBoxAsked(true);
              return;
            }
            /*
             * ⚠️ Any real choice closes the ask, INCLUDING the empty option —
             * which is also the way back out of the box, and the reason opening
             * it is not a one-way door. Closing it on a real code is the part
             * that is correctness: the code owns the rate from here, and the two
             * must never share the cell.
             */
            setRateBoxAsked(false);
            /*
             * ⚠️ **Two ways there is no percentage to write, and they take the
             * same branch: the code alone moves, and `taxRate` is left exactly
             * where it stands.**
             *
             * `rate === undefined` is the stored code Xero no longer lists
             * (injection case B) — there is no rate object to read.
             * `!hasEffectiveRate` is a rate Xero lists but declines to
             * quantify (case C), reachable only by re-choosing the code the
             * line already holds.
             *
             * ⚠️ **`?? 0` here is the bug this whole branch exists to make
             * unreachable.** `null` means Xero did not state a percentage, not
             * that it stated zero, and this tenant lists three rates that
             * really are 0% — so a coalesce would record "no tax" on a line of
             * a document a customer is invoiced against, off a figure that does
             * not exist, with both screens looking correct. Leaving `taxRate`
             * alone is the same thing the empty option does, for the same
             * reason: a choice about a CODE must not silently rewrite the
             * line's arithmetic.
             */
            const rate = rates.find((candidate) => candidate.taxType === value);
            if (rate === undefined || !hasEffectiveRate(rate)) onChange({ xeroTaxType: value });
            else onChange({ xeroTaxType: value, taxRate: percentForDraft(rate.effectiveRate) });
          }}
          options={options}
          emptyOptionLabel={emptyLabel}
          placeholder={emptyLabel}
          searchPlaceholder="Search tax rates"
          loading={ratesLoading}
          /*
           * 🔴 **THE CALLER'S `disabled` AND NEVER `rateLocked`. This is
           * the whole reason the percentage may be locked at all.**
           *
           * A rate the code disagrees with is `XERO_TAX_RATE_MISMATCH` at push
           * time — a refusal that names this line and can only be cleared by
           * moving one of the two keys. With the percentage read-only, this
           * picker is the only key left that moves, and it writes the code and
           * its own percentage in ONE patch, so it lands the pair in agreement
           * rather than deepening the disagreement.
           *
           * ⚠️ **So a code choice DOES change a "read-only" rate, and that is
           * correct.** It is not a hole in the lock; the lock is on typing a
           * percentage that nothing reconciles. Adding `|| rateLocked` here to
           * make the cell look consistent strands every mismatched
           * company-sourced line — which is exactly what the 2026-08-30
           * reversal was reacting to.
           */
          disabled={disabled}
          invalid={Boolean(errorText)}
          {...(footnote ? { footnote } : {})}
        />
      )}
    </Field>
  );

  /*
   * ⚠️ **Two controls, one fact — which is the opposite of the pair this
   * component was built to remove.** The picker writes `xeroTaxType` (and, when
   * a code carries one, `taxRate` with it); the box writes `taxRate` alone, and
   * is on screen only while there is no code for it to contradict. Nothing here
   * can put a rate beside a code that implies a different one.
   *
   * ⚠️ **And it is no longer the DEFAULT shape of the cell.** `showRateBox` is
   * false until somebody asks, so on a live row this branch is not taken at all
   * and the cell is the picker alone. Reintroducing an unconditional box here —
   * "so the rate is always editable" — is the regression, not the repair: the
   * rate is always *visible* on the trigger and always one choice from editable.
   *
   * `NumberControl` is the same component the disconnected fallback uses, so the
   * bare `%` box behaves identically however it was reached, and its own `hint`
   * carries the meaning in the card while the grid keeps it on a `title`.
   */
  const stacked = !showRateBox ? (
    control
  ) : (
    <div className="grid gap-1.5">
      {control}
      <NumberControl
        row={row}
        problems={problems}
        /*
         * ⚠️ **The caller's own `disabled` only, and `rateLocked` is provably
         * false here rather than merely unlikely.** `showRateBox` is the only
         * way into this branch and both its disjuncts exclude the lock — the
         * first by testing `!rateLocked`, the second because `rateLocked`
         * itself requires `!rateNeedsFixing`. So passing the lock here would be
         * dead code, and reading its absence as "the lock was forgotten" is the
         * misreading this comment exists to stop.
         */
        disabled={disabled}
        compact={compact}
        onChange={onChange}
        field="taxRate"
        label="Rate %"
        placeholder="0"
        hint={
          rateNeedsFixing
            ? 'Correct this line’s tax percentage here.'
            : 'No Xero tax code on this line, so the company’s Xero default decides the treatment.'
        }
      />
    </div>
  );

  if (!compact) return stacked;
  /*
   * ⚠️ The grid's only voice. The ownership clause goes on the `title` rather
   * than into the cell, per the no-prose-in-a-grid-cell rule, and it is appended
   * rather than substituted: what the cell shows and why it cannot be changed
   * are two facts, and the reader needs both.
   */
  return (
    <div title={companyReason === undefined ? fullTitle : `${fullTitle} ${companyReason}`}>
      {stacked}
    </div>
  );
}

/**
 * The Price cell — **one box for all three of `PriceMode`'s states**.
 *
 * The toggle sits inside the input on the trailing edge, so the cell is a
 * single control however wide it is, and the three states are told apart by
 * what the box says:
 *
 * | state | box | meaning |
 * | --- | --- | --- |
 * | `inherit` | empty, the CATALOGUE FIGURE / "Unchanged" | key OMITTED |
 * | `value` | the typed decimal | an explicit price |
 * | `none` | empty, "No price" | an explicit `null` |
 *
 * ⚠️ **The placeholder on a new line is the catalogue AMOUNT, not the word
 * "Catalogue".** A new line left on `inherit` snapshots that price, so the
 * useful thing to show is the figure it is about to store — `18,000.00`, right
 * where the typed price would be. The word told the reader a mechanism and
 * withheld the number, which is what the owner asked for back.
 *
 * Three states and they are NOT interchangeable:
 *
 * | the catalogue says | placeholder |
 * | --- | --- |
 * | `"18000.00"` | `18,000.00`, through `formatDecimalString` |
 * | `null` — genuinely unpriced | `Unpriced`, **never `0.00`** |
 * | nothing, because the read failed or truncated at 100 | `Catalogue` |
 *
 * ⚠️ `unitPrice` is a STRING and `null` is not zero — an unpriced product and a
 * product priced at zero are different facts, and `?? 0` or `Number(price)`
 * destroys the distinction. And an UNKNOWN catalogue is not an absent price:
 * the old label is the honest answer when nothing was read.
 *
 * ⚠️ **`none` is not a price of `0.00`** — the generated columns
 * `COALESCE(unit_price, 0)`, so both make the three figures read `"0.00"` and
 * nothing downstream can tell them apart again. The toggle is `aria-pressed`
 * so that distinction is announced rather than only shown.
 *
 * Typing anything always moves the row to `value`; emptying the box returns it
 * to `inherit`, which is the state it was in before someone touched it.
 */
/**
 * What a converted line's Tax rate cell SAYS, in one place, for each of the
 * three states it can be in.
 *
 * ⚠️ *SUPERSEDED — this was one constant, `CONVERTED_TAX_RATE_REASON`, reading:
 * "A deal line carries no tax rate. The conversion takes it from the company;
 * with none recorded the line arrives at 0.00 — unset, not zero-rated." Its own
 * note required that wording to stay CONDITIONAL, "because no live company
 * records a rate today and a sentence promising one would be a dated reading
 * printed on screen".* **That justification is spent.** The owner asked for the
 * actual figure; the company record carries it and is now read; and a cell that
 * names only its SOURCE withholds the one number the person is looking at the
 * column for. The conditional sentence became three unconditional ones, each
 * true of exactly the state that renders it and of no other.
 *
 * 🔴 **`null` MAY NEVER RENDER AS `0.00` OR `0%`.** Null is *the company has
 * stated no rate*; `"0.00"` is a company somebody deliberately zero-rated. Only
 * the second is a figure anyone chose, and the difference is the amount a
 * customer is asked for — so the null branch carries no number at all.
 *
 * ⚠️ *SUPERSEDED: "**Measured: no live company states a rate**, so that branch
 * is what renders on every conversion today. It is the common case, not an
 * edge."* **Re-measured 11 September 2026 over 100 live companies: ALL 100 state
 * both a code and a rate** — 37 `OUTPUT2 / 20.00`, 37 `ECZROUTPUTSERVICES /
 * 0.00`, 25 `NONE / 0.00`, 1 `EXEMPTOUTPUT / 0.00`. So the null branch has gone
 * from the common case to one nobody meets, and **63 of those 100 are a real,
 * chosen `0.00`** — which is precisely why the two must stay apart rather than
 * being folded now that one of them looks unreachable.
 *
 * ⚠️ The rate LEADS the wording, as the editable control's label does: a
 * `table-fixed` column truncates from the right, and truncation may only ever
 * eat the words, never the figure.
 */
function convertedTaxRateCopy(rate: CompanySalesTax): { value: string; title: string } {
  if (!rate.known) {
    /*
     * ⚠️ Not "no rate" and not a figure. The company record has not been read —
     * in flight, failed, or the form has moved on — and reporting that as an
     * absence is a claim about a customer's tax treatment sourced from a request
     * that never landed. It names the SOURCE, which is the one thing still true.
     */
    return {
      value: 'From the company',
      title:
        'This rate comes from the company record, which has not been read yet. It is not editable on a conversion.',
    };
  }
  /*
   * ⚠️ An empty string joins `null` rather than reaching the formatter, which
   * answers an em dash for it — `—% · Company rate` would be a figure-shaped
   * nothing. A DECIMAL column cannot produce one today; this costs a line and
   * removes the one output that could not be read as either state.
   */
  if (rate.rate === null || rate.rate.trim() === '') {
    return {
      value: 'No company rate',
      title:
        'This company states no tax rate, so every line arrives un-taxed — unset, not zero-rated. Set one on the company record.',
    };
  }
  return {
    value: `${formatDecimalString(rate.rate)}% · Company rate`,
    title:
      'The company’s own rate, applied to every line by the conversion. Change it on the company record.',
  };
}

/**
 * The Tax rate cell on a CONVERTED row — a READOUT, and nothing that takes a value.
 *
 * ⚠️ **THE COLUMN STAYS AND THE CONTROL GOES, and both halves are the point.**
 * The column stays because this grid is meant to read as the New Quote grid
 * reads, and a column that vanishes on one path is a difference somebody has to
 * account for. The control goes because `POST /deals/{id}/convert` has no
 * `taxRate` key and no `xeroTaxType` key — the body schema is `.strict()`, so
 * either would be a 422 — which means a live dropdown here would take a choice
 * and discard it silently. **Read-only is not the same as ignored**, and only
 * the first is honest.
 *
 * ⚠️ **IT PRINTS THE PERCENTAGE NOW.** *SUPERSEDED: "IT DOES NOT PRINT A
 * PERCENTAGE, and that is measured rather than lazy. The rate a converted line
 * gets is derived server-side from the COMPANY's `xeroSalesTaxRate` — a column
 * this app has never been served: `Company` in `@/lib/types` carries
 * `xeroSalesLineAmountType`, `xeroSalesTaxType` and `xeroSalesTaxName`, and no
 * rate … Where the figure is genuinely unknown the honest cell names its
 * SOURCE."* **The premise was the type, not the wire.** The server has always
 * sent `xeroSalesTaxRate` on every company read (`toCompany`); `Company` simply
 * did not declare it, so the value arrived and the type hid it. Declaring it and
 * passing it down is what changed — **nothing is derived here.** The rule the
 * old note was protecting survives unweakened: this app must never compute the
 * figure itself from the tax CODE against the tenant's published
 * `EffectiveRate`, which would be a second implementation of a number the server
 * owns, printed beside a quote.
 *
 * ⚠️ **`TaxRateControl` is deliberately NOT reused with `disabled`.** It reads
 * `row.draft.taxRate` and `row.draft.xeroTaxType`, which a converted row does
 * not have — see {@link convertRowOf} — so a disabled instance would render a
 * frozen `0%` and a frozen "use the company default", both of them values
 * nobody measured. **A disabled control still makes a claim**, and it would be
 * making the one claim this cell exists to avoid.
 */
function ConvertedTaxRateCell({
  compact,
  rate,
}: {
  compact: boolean;
  rate: CompanySalesTax;
}) {
  const { value, title } = convertedTaxRateCopy(rate);
  if (compact) {
    /*
     * ⚠️ No prose inside a desktop grid cell — the rule this whole file is
     * written against. The sentence lives on a `title`, which is where every
     * other cell's explanation already went.
     */
    return (
      <span className="block truncate text-sm text-fg-muted" title={title}>
        {value}
      </span>
    );
  }
  /*
   * The stacked card is not a grid, so it keeps its label — and the sentence
   * survives the no-helper-text prohibition on the exception that matters here:
   * it says WHY a control is absent. Without it an inert cell reads as a
   * temporarily unavailable dropdown.
   */
  return (
    <div className="grid gap-1">
      <span className="text-xs font-medium text-fg-secondary">Tax rate</span>
      <span className="text-sm text-fg-muted">{value}</span>
      <span className="text-2xs leading-snug text-fg-muted">{title}</span>
    </div>
  );
}

function PriceControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  currency,
  catalogue,
  repriced,
  allowNoPrice,
  unitLabel,
}: ControlProps & {
  currency: string | null;
  /**
   * The city pro-rata price a SAVED line's next save will store — its cities
   * moved and its stored price was never typed over — or `undefined`. The box
   * shows it in place of the stored figure; typing over it is a typed price.
   */
  repriced: string | null | undefined;
  /**
   * This row's product in the loaded catalogue. `known: false` means the
   * catalogue could not answer — a failed or truncated `/products` read — which
   * is NOT the same as `price: null`, an actual unpriced product.
   */
  catalogue: { known: boolean; price: string | null };
  /**
   * Whether the "record no price at all" toggle may be offered.
   *
   * ⚠️ **REQUIRED, and `false` in convert mode.** `PriceMode` is a TRI-state and
   * the deal→quote override contract is not: it carries a price string or it
   * carries nothing, and there is no `unitPrice: null` key to send. Offering the
   * toggle there would offer a state the request cannot express — a control
   * whose action cannot be performed, which is the same reason convert mode has
   * no Add row and no Delete. Not optional, so a fourth call site cannot inherit
   * a default that happens to be right for three of them.
   */
  allowNoPrice: boolean;
  /** The product's unit, SINGULAR — renders "per license" under the box. `null` = none. */
  unitLabel: string | null;
}) {
  const mode = row.draft.priceMode;
  const unit = unitLabel?.trim() ?? '';
  /*
   * ⚠️ **A CONVERTED row is not a new one**, and `isNew` decides which sentence
   * the empty box gets. On a new quote line blank snapshots the catalogue price;
   * on a converted row blank on a line that HAD a price is a refusal, because
   * the override has no way to say "no price". Three states, not two.
   */
  const convertLine = row.convert;
  const isNew = row.saved === null && convertLine === null;
  const noPrice = mode === 'none';

  /*
   * The catalogue figure this row would snapshot, formatted. `null` covers both
   * "unpriced" and "we could not read the catalogue"; the two are told apart
   * below, because they are different sentences.
   */
  const cataloguePrice =
    catalogue.known && catalogue.price !== null ? formatDecimalString(catalogue.price) : null;

  /*
   * `inherit` is the empty box, and it means two different true things
   * depending on the row: omit the key on a POST and the catalogue price is
   * snapshotted; omit it on a PATCH and the stored price is left alone. In the
   * grid that is carried by the PLACEHOLDER — the catalogue figure, or
   * "Unchanged" — and by the `title`, because Xero's grid has no sentence in
   * any cell.
   */
  const hint = noPrice
    ? 'No price at all — not a price of zero.'
    : convertLine !== null
      ? /*
         * ⚠️ Nothing about "blank" here, because blank is not an instruction on
         * this path — it is either a line that records no price or a figure
         * somebody has cleared, and the second is refused by name.
         */
        currency
        ? `In ${currency}.`
        : undefined
      : mode === 'inherit'
      ? isNew
        ? cataloguePrice !== null
          ? `Blank stores the catalogue price of ${cataloguePrice}${currency ? ` ${currency}` : ''}.`
          : catalogue.known
            ? // ⚠️ Unpriced is a real catalogue answer and is not a price of
              // zero. Blank on this row stores nothing at all.
              'No catalogue price, so blank records none here.'
            : 'Blank uses the catalogue price — it couldn’t be read here.'
        : repriced !== undefined
          ? currency
            ? `In ${currency}.`
            : undefined
          : `Blank leaves ${formatDecimalString(row.saved?.unitPrice ?? null)} unchanged.`
      : currency
        ? `In ${currency}.`
        : undefined;

  /*
   * ⚠️ The FIGURE, not the word. Three distinct answers, and the fallback to
   * the old label is reserved for the one state where there genuinely is no
   * figure to show — see this control's header.
   */
  const placeholder = noPrice
    ? 'No price'
    : convertLine !== null
      ? // The deal's own figure is already IN the box; the placeholder only
        // shows once somebody empties it, where it says what a price looks like
        // rather than promising a default that does not exist on this path.
        '0.00'
      : !isNew
        ? repriced !== undefined
          ? repriced === null
            ? 'Unpriced'
            : formatDecimalString(repriced)
          : 'Unchanged'
        : (cataloguePrice ?? (catalogue.known ? 'Unpriced' : 'Catalogue'));

  const toggleLabel = noPrice
    ? 'Record a price on this line'
    : 'Record this line as carrying no price at all';

  return (
    <Field
      label="Price"
      hideLabel={compact}
      error={problems.fieldErrors.unitPrice}
      {...(!compact && hint ? { hint } : {})}
    >
      {(fieldProps) => (
        <>
        <Input
          {...fieldProps}
          inputMode="decimal"
          value={
            noPrice
              ? ''
              : // A saved line the server will reprice shows the new figure;
                // in `inherit` the box stays empty and the placeholder says it.
                repriced !== undefined && mode === 'value'
                ? (repriced ?? '')
                : row.draft.price
          }
          onChange={(event) => {
            const next = event.target.value;
            // Typing is always a price. Emptying returns the row to `inherit`,
            // never to `none` — clearing a box is not a decision to record none.
            onChange({ price: next, priceMode: next === '' ? 'inherit' : 'value' });
          }}
          placeholder={placeholder}
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.unitPrice)}
          className="text-right tabular-nums"
          {...(hint ? { title: hint } : {})}
          {...(disabled || !allowNoPrice
            ? {}
            : {
                trailingSlot: (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconOnly
                    // `iconOnly` DROPS children — the icon arrives as
                    // `leadingIcon` or the control is a blank square.
                    leadingIcon={<NoPriceIcon />}
                    aria-pressed={noPrice}
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    onClick={() =>
                      onChange(
                        noPrice
                          ? { priceMode: 'inherit', price: '' }
                          : { priceMode: 'none', price: '' },
                      )
                    }
                  />
                ),
              })}
        />
        {unit !== '' && !noPrice && (
          <span className="block text-right text-2xs text-fg-muted">per {unit}</span>
        )}
        </>
      )}
    </Field>
  );
}

/**
 * The Disc cell — **one box that takes either a percentage or an amount**, and
 * two real columns behind it.
 *
 * 🔴 *SUPERSEDED, in full: "**Only a percentage is ever STORED** … there is no
 * amount column and there is not going to be one. Typing an amount converts it,
 * here, on the keystroke … **The conversion loses money and the cell says how
 * much.**"* `f_quote_products.discount_amount` exists, so an amount is stored
 * as the amount and **nothing is converted anywhere**.
 *
 * What went with the conversion, and must not come back: the `DiscountEntry`
 * plumbing, the three refusals for amounts no percentage could express, and the
 * line under the box stating that the STORED percentage takes a different
 * figure off than the one just typed. That sentence was the apology for the
 * loss, and the loss was in POUNDS — £1,000 off an £18,000 line rounded to
 * `5.56%`, taking £1,000.08.
 *
 * ⚠️ **The two columns are MUTUALLY EXCLUSIVE**, so the mode is not decoration:
 * `draft.discountAmount === null` is percentage mode, a string is amount mode,
 * and {@link discountWriteOf} turns the mode into the pair the wire carries.
 * Setting one while the other stays stored is a 422 naming `discountAmount`.
 *
 * ⚠️ **The mode is DRAFT state now, not view state**, because there is a column
 * behind it — so switching modes is revertible, reaches `ROW_WRITE_KEYS`, and
 * is part of what makes a row dirty. It writes only the box on screen: touching
 * the other one is how a percentage silently changes while somebody types an
 * amount.
 */
function DiscountControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  onModeChange,
}: ControlProps & {
  /** `true` switches the box to money, `false` back to a percentage. */
  onModeChange: (toAmount: boolean) => void;
}) {
  const amountText = row.draft.discountAmount;
  const amountMode = amountText !== null;

  /*
   * Exactly one of the two can be wrong at a time, because exactly one is on
   * screen — `validateRow` reports the percentage at `discount` and the amount
   * at `discountAmount`, and a server 422 names whichever key was sent. Reading
   * the live one is what keeps a stale refusal on the hidden half from
   * colouring the control the person is actually using.
   */
  const error = amountMode ? problems.fieldErrors.discountAmount : problems.fieldErrors.discount;

  return (
    <Field
      /*
       * The accessible name says WHICH figure the box is taking, in both
       * layouts — under `compact` it is the only thing that does.
       *
       * ⚠️ **It no longer names the CURRENCY, and that is the point.** It read
       * `Discount amount in EUR`, so the same control announced itself
       * differently on every document and said only `Discount amount` on one
       * whose company was not yet chosen. The unit belongs on the figure; the
       * label names the kind of figure.
       */
      label={amountMode ? 'Discount (amount)' : 'Discount (percentage)'}
      hideLabel={compact}
      {...(error ? { error } : {})}
    >
      {(fieldProps) => (
        <div className="grid gap-1.5">
          <Input
            {...fieldProps}
            inputMode="decimal"
            value={amountMode ? amountText : row.draft.discount}
            onChange={(event) => {
              // ⚠️ Writes ONLY the box that is on screen. The other column is a
              // different figure, not another spelling of this one.
              if (amountMode) onChange({ discountAmount: event.target.value });
              else onChange({ discount: event.target.value });
            }}
            placeholder={amountMode ? '0.00' : '0'}
            disabled={disabled}
            invalid={Boolean(error)}
            className="text-right tabular-nums"
          />
          {/*
            ⚠️ **The switch is a SEGMENTED CONTROL under the box, not a glyph
            inside it.** It was an icon-only button in the input's trailing
            slot, labelled `%` or the document's currency code — which made it
            read as a unit annotation on the number rather than as one of two
            choices, and made it say `EUR`, `GBP` or `Amt` depending on the
            document and on whether a company had been picked yet. Two named
            options, always the same two words, is what the owner asked for.

            Moving it out of the input also gives the figure back the 36px the
            trailing slot's `pr-9` was reserving.
          */}
          {!disabled && (
            <SegmentedControl
              label={DISCOUNT_MODE_GROUP_LABEL}
              value={discountModeOf(amountText)}
              options={DISCOUNT_MODE_OPTIONS}
              /*
               * ⚠️ Moves the switch and NOTHING else. Pre-filling the amount
               * from what the percentage takes off — which this used to do — is
               * the deleted conversion arriving from the other direction, and
               * it would overwrite a figure somebody typed with one nobody
               * chose.
               */
              onChange={(mode) => onModeChange(mode === 'amount')}
            />
          )}
        </div>
      )}
    </Field>
  );
}

/**
 * The Amount cell — **the figure, plain**, exactly as Xero shows it.
 *
 * A clean row shows the server's own `lineTotal`. A changed row shows the
 * shared preview: `computeGeneratedLinePreview`, the one copy of that
 * arithmetic.
 *
 * ⚠️ **The word "Preview" under the figure is GONE and the claim it made is
 * not.** It was a second line in a numeric cell, which is the noise the owner
 * circled; it now rides on the cell's own `title`, and the row already says the
 * same thing twice over in ways that cost no height — the status badge reads
 * "Unsaved" or "New" whenever this figure is a preview, and it cannot read
 * anything else, because `dirty` is what drives both. That is a second signal,
 * so this is not colour-alone.
 *
 * `preview === null` is the ordinary answer for a row with no price, or one
 * whose boxes are not yet a figure at all. It renders as no preview and never
 * as `0.00`.
 */
function AmountCell({
  row,
  dirty,
  currency,
  preview,
  /**
   * ⚠️ **REQUIRED, and it exists for the ADDED row.** A row somebody added on
   * the convert path has neither a `saved` line nor a `convert` one, so without
   * this it falls through to the ordinary draft branch and renders
   * `computeGeneratedLinePreview` — a figure computed at the blank draft's zero
   * tax, on a document whose tax comes from the COMPANY. That is the exact
   * "confident number about the wrong document" the carried-over branch below
   * refuses to print, arrived at by a different route.
   */
  convertMode,
  input,
  disabled,
  onChange,
}: {
  row: Row;
  dirty: boolean;
  currency: string | null;
  preview: GeneratedLinePreview | null;
  convertMode: boolean;
  /**
   * The SAME resolved figures `computeGeneratedLinePreview` reads for this row.
   *
   * ⚠️ **Passed in rather than re-derived, and that is the point.** The box
   * solves backwards from the figure the preview solves forwards to; deriving
   * the price twice is two chances to disagree, and the disagreement would be a
   * discount computed against a price the row is not actually using.
   */
  input: GeneratedLineInput;
  disabled: boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  /*
   * ⚠️ **A CONVERTED ROW SHOWS NO PREVIEW, and that is a decision rather than
   * an omission.** `computeGeneratedLinePreview` is the right answer on the
   * three saved grids because they are previewing the document the figures
   * belong to. Here they do not: the figure that matters after a conversion is
   * the QUOTE line's, whose tax comes from the COMPANY server-side — so a
   * deal-shaped preview, computed at the zero tax `f_deal_products` has no
   * column for, would be a confident number about the wrong document. It would
   * also be wrong in the reassuring direction, reading as a total somebody could
   * quote down the phone.
   *
   * So an untouched row shows the deal's own stored `lineTotal` — at least true
   * of the deal — and an edited one says the figure is being recalculated,
   * because that is the whole of what is known. The wording is the grids' own
   * sentence for exactly this state so one screen does not word it two ways.
   */
  if (convertMode && row.convert === null) {
    /*
     * ⚠️ A line ADDED here has no stored figure on either document and no
     * honest preview — its tax is the company's and is applied server-side — so
     * it says what is being done rather than a number nobody measured. Worded
     * "conversion", matching the carried-over branch below, so one screen does
     * not word one state two ways.
     */
    return <span className="text-xs text-fg-subtle">Calculated on conversion</span>;
  }
  if (row.convert !== null) {
    if (dirty) {
      return <span className="text-xs text-fg-subtle">Recalculated on conversion</span>;
    }
    const dealTotal = row.convert.lineTotal;
    return (
      <span className="tabular-nums text-sm text-fg">
        {formatDecimalString(dealTotal)}
        {dealTotal !== null && currency && (
          <span className="ml-1 text-xs text-fg-muted">{currency}</span>
        )}
      </span>
    );
  }
  return (
    <EditableAmount
      row={row}
      dirty={dirty}
      currency={currency}
      preview={preview}
      input={input}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

/**
 * The Amount cell, **typed into**.
 *
 * Owner's instruction, 2026-09-12: *"make the line item amount editable and auto
 * calc between sku price, discount, line item amount — 2 filled should auto
 * calculate the third."*
 *
 * ⛔ **THE AMOUNT IS A GENERATED COLUMN AND IS NEVER SENT.** `line_subtotal`,
 * `line_tax` and `line_total` are `GENERATED STORED`; naming one in a write is a
 * 422 from the driver, which is why `ROW_WRITE_KEYS` has never carried them.
 * So typing here is not a write — it is a QUESTION: *which writable field,
 * changed to what, makes this line come out at this figure?* `solveLineAmount`
 * in `@/lib/decimal` is the one place that is answered, shared with the invoice
 * and deal grids, and it answers by WHICH FIELD IS MISSING:
 *
 *   no price yet  ->  the amount SETS the price      (amount ÷ quantity)
 *   a price set   ->  the amount sets the DISCOUNT   (quantity × price − amount)
 *
 * which is the three-way rule: fill any two of price, discount and amount and
 * the third follows.
 *
 * ⚠️ **THE BOX IS THE PRE-TAX SUBTOTAL — owner's decision, taken against the
 * alternative.** Tax is charged on the already-rounded subtotal, so solving back
 * from a tax-INCLUSIVE total is not always possible to the penny: at 20% the
 * reachable totals step in 1.2p and a figure between two of them can only be
 * approximated. Against the subtotal every answer is exact. Where the line
 * carries tax, the tax-inclusive total is shown UNDER the box — it is the figure
 * that used to be in this cell, and removing it silently would change what a
 * money column means.
 *
 * 🔴 **THE DISCOUNT IS SOLVED IN THE MODE THE READER CHOSE — owner's
 * instruction, 2026-09-13: *"on line item amount change the discount type
 * should not change and be calculated in the type it is in."*** ⚠️
 * *SUPERSEDED: "**The discount is solved as an AMOUNT and never as a
 * percentage**, and the line's mode is flipped to match, saying so … **Never
 * write a rounded percentage here.**"*
 *
 * ⚠️ **THE ARITHMETIC THAT MOTIVATED THAT RULE IS UNCHANGED — it is now SAID
 * rather than avoided.** `discount` is `DECIMAL(5,2)`, so the subtotals a
 * percentage can express are `quantity × unitPrice × 0.0001` apart — **£1.80 on
 * an 18,000 line** — the lossy conversion migration 111 exists to have removed
 * and `percentageForAmount()` was deleted for. So a percentage is written only
 * where somebody deliberately chose that mode, and where it cannot land on the
 * typed figure the cell NAMES the figure the line will actually come to. **It
 * is never rounded away and never refused.**
 *
 * ⚠️ **A line with NO discount still solves an exact AMOUNT, flip and all.**
 * `discountAmount === null` is how percentage mode is spelled, so it reads as
 * percentage on every untouched row — the mode is the default there, not a
 * choice. `percentageModeIsChosen()` is the one place that distinction is
 * drawn; see the `keepPercentage` argument below.
 *
 * ⚠️ **It commits on BLUR, not on the keystroke.** Solving mid-type would write
 * a discount off `6`, then `60`, then `600` on the way to `6000`, and each of
 * those is a real edit to a real money column that the row's Unsaved badge would
 * report. The derived figure returns the moment the box is emptied.
 */
function EditableAmount({
  row,
  dirty,
  currency,
  preview,
  input,
  disabled,
  onChange,
}: {
  row: Row;
  dirty: boolean;
  currency: string | null;
  preview: GeneratedLinePreview | null;
  input: GeneratedLineInput;
  disabled: boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  /** `null` means the box is showing the derived figure rather than a typed one. */
  const [typed, setTyped] = useState<string | null>(null);
  /**
   * What the last commit did, or why it could not.
   *
   * ⚠️ **NOT helper text.** It renders only after somebody has typed and only
   * where the outcome is otherwise invisible — a refusal, a mode flip, or a
   * division that does not come out even. The no-helper-text rule forbids prose
   * attached to a control; this names a state the reader cannot otherwise see,
   * which is the documented exception.
   */
  const [note, setNote] = useState<{ tone: 'error' | 'warn'; text: string } | null>(null);

  const derived = dirty ? (preview?.lineSubtotal ?? null) : (row.saved?.lineSubtotal ?? null);
  const taxed = dirty ? (preview?.lineTotal ?? null) : (row.saved?.lineTotal ?? null);
  /*
   * The two agree on a line with no tax, which is most of them — printing the
   * same figure twice would read as a second, different fact.
   */
  const showsTaxed = taxed !== null && derived !== null && taxed !== derived;

  const shown = typed ?? (derived === null ? '' : derived);

  /*
   * ⚠️ **READ-ONLY IS PLAIN TEXT, NEVER A DISABLED BOX.** The house rule, and it
   * is about what the two SAY: a disabled control reads as something the person
   * has not earned the right to use, where text reads as a fact. A `viewer`
   * meets this cell on every line of every quote, and a greyed-out input on all
   * of them would report a permissions problem that does not exist.
   *
   * It also covers the transient `saving` state, where the figure is genuinely
   * not editable for the moment and an input would invite a keystroke that goes
   * nowhere.
   *
   * The figure shown is the one this cell has always shown when it could not be
   * typed into — the tax-INCLUSIVE total — so a read-only reader sees exactly
   * what they saw before the cell became editable.
   */
  if (disabled) {
    const readOnlyFigure = dirty ? (preview?.lineTotal ?? null) : (row.saved?.lineTotal ?? null);
    return (
      <span
        className="tabular-nums text-sm text-fg"
        {...(dirty ? { title: 'Preview — recalculated by the server on save.' } : {})}
      >
        {formatDecimalString(readOnlyFigure)}
        {readOnlyFigure !== null && currency && (
          <span className="ml-1 text-xs text-fg-muted">{currency}</span>
        )}
      </span>
    );
  }

  const commit = () => {
    if (typed === null) return;
    const amount = typed.trim();
    // Emptying the box hands the cell back to the derivation, exactly as
    // "Reset to derived" does on the description. It is not a figure of zero.
    if (amount === '') {
      setTyped(null);
      setNote(null);
      return;
    }

    const solution = solveLineAmount({
      // A blank quantity box means the column default of 1, which is what the
      // preview beside this already assumes.
      quantity: input.quantity.trim() === '' ? '1' : input.quantity,
      unitPrice: input.unitPrice,
      amount,
      /*
       * 🔴 **THE MODE SURVIVES TYPING HERE — owner's instruction, 2026-09-13:
       * *"on line item amount change the discount type should not change and be
       * calculated in the type it is in."*** Typing an Amount used to flip every
       * percentage line to an amount: a mode change nobody asked for, on the
       * column a customer's discount is billed from.
       *
       * ⛔ **`percentageModeIsChosen`, NEVER `discountAmount === null`.** That
       * test is true of every UNTOUCHED line — `null` is how percentage mode is
       * spelled — so passing it would put the commonest edit in this grid
       * through the lossy `DECIMAL(5,2)` arithmetic below, where the reachable
       * subtotals are `quantity × unitPrice × 0.0001` apart (**£1.80 on an
       * 18,000 line**). Owner's decision: a line nobody has discounted has no
       * mode to preserve, so it deliberately still solves an exact
       * `discountAmount`, mode-flip note and all.
       */
      keepPercentage: percentageModeIsChosen(row.draft.discountAmount, row.draft.discount),
    });

    if (solution === null) {
      setNote({ tone: 'error', text: 'A figure, up to two decimal places.' });
      return;
    }

    if (solution.kind === 'above-gross') {
      /*
       * `discountAmount` is non-negative by CHECK, so an amount above
       * quantity × price cannot be reached by discounting — the price has to
       * move. The gross is named because "too large" without it is a refusal
       * nobody can act on.
       */
      setNote({
        tone: 'error',
        text: `More than this line’s ${formatDecimalString(solution.gross)}${
          currency ? ` ${currency}` : ''
        } — raise the price, or clear it and let the amount set one.`,
      });
      return;
    }

    if (solution.kind === 'unit-price') {
      onChange({ price: solution.unitPrice, priceMode: 'value' });
      setTyped(null);
      setNote(
        solution.exact
          ? null
          : {
              tone: 'warn',
              // ⚠️ `exact: false` is SAID, never rounded away. 10,000 over 3 is
              // 3,333.33 a unit and a line of 9,999.99 — a penny under what
              // somebody typed, on a figure a customer is billed against.
              text: `Doesn’t divide evenly — priced at ${formatDecimalString(
                solution.unitPrice,
              )} a unit, so the line is a little under.`,
            },
      );
      return;
    }

    if (solution.kind === 'percentage-unreachable') {
      /*
       * ⛔ **A COLUMN THAT CANNOT EXPRESS THE ANSWER, not a rounding failure.**
       * `discount` is `CHECK (0..100)`, so on a gross of zero or below no
       * percentage produces any discount at all — and on a deduction line a
       * percentage of a negative gross would make the line LESS negative, which
       * is the direction `discount_amount`'s own non-negative CHECK forbids.
       *
       * ⛔ **REPORTED, never silently solved into the amount column.** A quiet
       * fallback would change the mode — the one thing the owner's 2026-09-13
       * instruction exists to stop — and it would do it on exactly the rows
       * where nobody would look, because the typed figure would land perfectly.
       *
       * The gross is named for the same reason `above-gross` names it, and both
       * ways out are named because "cannot" without one is a refusal nobody can
       * act on.
       */
      setNote({
        tone: 'error',
        text: `A percentage can’t discount this line’s ${formatDecimalString(solution.gross)}${
          currency ? ` ${currency}` : ''
        } — enter the discount as an amount, or move the price.`,
      });
      return;
    }

    if (solution.kind === 'discount-percentage') {
      /*
       * Percentage mode was CHOSEN on this line, so the answer goes back as a
       * percentage — through the same `onChange({ discount })` the Discount cell
       * itself writes, so the two cells cannot disagree about which column a
       * percentage lives in. `discountAmount` is left `null`, and that IS the
       * mode: nothing here moves it, which is what preserving the mode means.
       *
       * ⚠️ **`exact: false` IS SAID, NEVER ROUNDED AWAY.** `discount` is
       * `DECIMAL(5,2)`, so the subtotals it can express are
       * `quantity × unitPrice × 0.0001` apart — **£1.80 on an 18,000 line** —
       * and typing 17,000 against an 18,000 gross lands on 5.56%, a discount of
       * 1,000.80 and a line of **16,999.20**. Naming only the percentage would
       * report a figure nobody typed as though it were the one they did. The
       * note carries BOTH, and the figure it names is `solution.amount` — read
       * back through the preview's own rounding step — so the sentence and the
       * figure in the box beside it cannot drift apart.
       *
       * ⚠️ **An EXACT answer gets no note at all.** The mode did not change and
       * the figure landed, so there is no state a reader cannot already see, and
       * a sentence there would be prose attached to a control — which this file
       * forbids.
       */
      onChange({ discount: solution.discountPercent });
      setTyped(null);
      setNote(
        solution.exact
          ? null
          : {
              tone: 'warn',
              text: `Nearest percentage is ${formatDecimalString(
                solution.discountPercent,
              )}% — this line comes to ${formatDecimalString(solution.amount)}${
                currency ? ` ${currency}` : ''
              }, not ${formatDecimalString(amount)}.`,
            },
      );
      return;
    }

    /*
     * ⚠️ **A LINE WITH NO DISCOUNT DELIBERATELY STILL SOLVES AN AMOUNT.**
     * `keepPercentage` was false, so either this line is already in amount mode
     * — nothing moves and there is nothing to say — or nobody has discounted it
     * at all, where `discountAmount === null` is the DEFAULT rather than a
     * choice and an exact amount is what makes the typed figure land to the
     * penny. The flip note fires only in that second case, because that is the
     * only one where the mode genuinely moved; a mode change nobody asked for
     * has to be stated, and one that did not happen must not be.
     */
    const flipped = row.draft.discountAmount === null;
    onChange({ discountAmount: solution.discountAmount });
    setTyped(null);
    setNote(
      flipped
        ? {
            tone: 'warn',
            text: `Discount set to ${formatDecimalString(
              solution.discountAmount,
            )} as an amount, not a percentage.`,
          }
        : null,
    );
  };

  return (
    <div className="grid justify-items-end gap-0.5">
      <Input
        inputMode="decimal"
        aria-label="Amount before tax"
        value={shown}
        onChange={(event) => {
          setTyped(event.target.value);
          setNote(null);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        placeholder={derived === null ? 'Amount' : ''}
        disabled={disabled}
        invalid={note?.tone === 'error'}
        className="text-right tabular-nums"
        title={
          dirty
            ? 'Before tax. Preview — recalculated by the server on save.'
            : 'Before tax. Type a figure to set the discount, or the price on a line that has none.'
        }
      />
      {/*
        ⚠️ **The currency code, which an INPUT cannot carry inside it.** This cell
        used to render the code beside the figure; once the figure moved into a
        box the code survived only on the `incl. tax` line below — which renders
        only where tax actually moves the total. So on a 0%-tax line, which is
        most of them here, the code vanished from the row entirely: a bare figure
        on a money column of a document a customer is billed against. Reported by
        the owner. Ported from the deal grid, which has no second figure at all
        and met this first.

        **`!showsTaxed` is the whole condition.** Where the taxed line does
        render it already ends `… USD incl. tax`, so a second code would print it
        twice. The two branches are complementary and deliberately adjacent.

        The empty-box guard is the deal grid's, and it is not tidiness: an
        emptied box has handed the cell back to the derivation and a code with no
        figure beside it reads as a value of nothing. It is the UNIT of the one
        figure in the box, never a second figure — so it stacks UNDER the box,
        which costs HEIGHT and no width. No column is re-cut.
      */}
      {!showsTaxed && currency && shown.trim() !== '' && (
        <span className="text-2xs text-fg-muted">{currency}</span>
      )}
      {showsTaxed && (
        <span className="text-xs text-fg-muted tabular-nums">
          {formatDecimalString(taxed)}
          {currency && ` ${currency}`} incl. tax
        </span>
      )}
      {note && (
        <span
          className={cx('text-xs', note.tone === 'error' ? 'text-danger' : 'text-fg-muted')}
          role={note.tone === 'error' ? 'alert' : undefined}
        >
          {note.text}
        </span>
      )}
    </div>
  );
}

/* ── The inclusions block, in an expandable panel under the row ────────────── */

function InclusionsEditor({
  row,
  problems,
  disabled,
  master,
  masterFailed,
  offerUnread,
  offeredIds,
  onChange,
}: {
  row: Row;
  problems: RowProblems;
  disabled: boolean;
  master: CampaignInclusion[];
  masterFailed: boolean;
  /**
   * This editor is on screen only because the product's link-table offer could
   * not be read — see {@link RowInclusions.offerUnread}. It says so rather than
   * presenting itself as a product that offers inclusions.
   *
   * ⚠️ Two different failures, and they are two notices rather than one, because
   * they send the reader to different places and either can happen without the
   * other: `masterFailed` is *the list of inclusions could not be loaded* (there
   * is nothing to tick), while this is *whether this product offers any could not
   * be checked* (there may be plenty to tick, and it may all be wrong).
   */
  offerUnread: boolean;
  /**
   * Which inclusions an administrator linked to this line's product, or `null`
   * meaning *do not narrow* — see {@link RowInclusions.offeredIds}.
   *
   * ⚠️ It narrows the OPTIONS only. Nothing here filters `row.draft.inclusions`,
   * and nothing here may: an inclusions write replaces the whole set, so an entry
   * dropped from the screen is an entry deleted on the next save, with a 200 and
   * no warning.
   */
  offeredIds: ReadonlySet<number> | null;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const chosen = new Map(row.draft.inclusions.map((entry) => [entry.inclusionId, entry.quantity]));

  /*
   * A retired inclusion already on the line still reads back with its name and
   * both unit forms, so it can be rendered — it just cannot be re-sent. Its
   * wording comes from the line itself, never from the active master.
   */
  const retired: LineInclusion[] = (sourceLineOf(row)?.inclusions ?? []).filter(
    (entry) => chosen.has(entry.id) && !master.some((candidate) => candidate.id === entry.id),
  );

  /*
   * ── The option list ───────────────────────────────────────────────────────
   *
   * ⚠️ **`offeredIds === null` is tested INSIDE each callback rather than hoisted
   * into a boolean.** Narrowing a prop does not survive into a closure, and the
   * shape that would have compiled — a hoisted `narrowed` flag plus a
   * non-null assertion — is exactly the shape that turns a null into an empty
   * filter the day someone reorders these lines.
   *
   * `null` therefore leaves `offered` as the whole master and `unlinked` empty:
   * an unread offer narrows NOTHING. See {@link offerUnread} — the block is
   * already carrying a notice saying the check did not run, and an empty picker
   * underneath it would say the opposite of that notice while looking like an
   * administrator's decision.
   */
  const offered = master.filter(
    (candidate) => offeredIds === null || offeredIds.has(candidate.id),
  );

  /*
   * ⚠️ **Active, on the line, and NOT linked to this product — a state of its own,
   * and the reason this whole block exists.**
   *
   * Real today: quote 9 line 10 carries four inclusions while the link table is
   * empty. Filtering the picker without this list would take that entry off the
   * screen, and because a write REPLACES the whole set the next save would send an
   * array without it and **destroy it silently, returning 200**. So it is shown.
   *
   * ⚠️ **It is NOT the retired case and must never borrow that wording.** The
   * master row is perfectly active; it is simply not one an administrator linked to
   * this item. Calling it "retired" would be a false statement about a master row —
   * an admin reading it would go looking for a deactivation that never happened.
   * The two blocks also make opposite promises about saving: a retired id **cannot
   * be re-sent at all** (a 422), while one of these saves cleanly and prints.
   *
   * Sourced from `chosen` (the DRAFT), not from `row.saved`, so it covers both
   * routes in: an entry stored before the admin's decision, and one ticked in this
   * session while the offer was still unread and narrowed out when it arrived.
   *
   * Drawn from `master`, so a retired entry can never land in both lists — it is
   * absent from `master` by definition, which is what puts it in `retired`.
   */
  const unlinked = master.filter(
    (candidate) =>
      offeredIds !== null && !offeredIds.has(candidate.id) && chosen.has(candidate.id),
  );

  /*
   * `narrowedAway` — `offeredIds !== null && offered.length < master.length` —
   * is gone with the caption it gated (removed 2026-09-05). It rendered only
   * when narrowing had actually removed something, which is what made it a fact
   * about the screen rather than prose explaining a rule.
   */

  /*
   * `422 INCLUSION_NOT_OFFERED_IN_REGION` — migration 096. Taken from the
   * REFUSAL and not from `fieldErrors`: both carry the server's sentence, but
   * only the refusal knows this is the one refusal in the line guard with THREE
   * destinations — the line, the PRODUCT (admin-only, another screen, usually
   * another person) and the PROJECT.
   *
   * ⚠️ The indexes are positions in the array we SENT, and the payload is built
   * from `draft.inclusions` in order. An index the draft no longer holds is
   * dropped, so a refusal left over from before an edit marks nothing rather
   * than marking the wrong chip.
   */
  const rowRefusal = row.refusal;
  const regionRefusal =
    rowRefusal !== null && rowRefusal.kind === 'inclusion-not-offered-in-region'
      ? rowRefusal
      : null;
  const refusedIds = new Set(
    (regionRefusal?.offendingInclusionIndexes ?? [])
      .map((index) => row.draft.inclusions[index]?.inclusionId)
      .filter((id): id is number => id !== undefined),
  );

  function toggle(inclusionId: number, checked: boolean) {
    if (checked) {
      onChange({ inclusions: [...row.draft.inclusions, { inclusionId, quantity: '1' }] });
    } else {
      onChange({
        inclusions: row.draft.inclusions.filter((entry) => entry.inclusionId !== inclusionId),
      });
    }
  }

  function setQuantity(inclusionId: number, quantity: string) {
    onChange({
      inclusions: row.draft.inclusions.map((entry) =>
        entry.inclusionId === inclusionId ? { ...entry, quantity } : entry,
      ),
    });
  }

  return (
    <div className="grid gap-3">
      {/*
        The heading carries the contract in four words. Saving REPLACES the
        whole set — an entry left out is removed, not kept — and that is not
        style: there is no per-inclusion endpoint, so a form that sent one entry
        would make it the only one. It is the shortest true wording, not a
        shortened one.
      */}
      <p className="text-xs font-medium text-fg-secondary">
        Campaign inclusions{' '}
        <span className="font-normal text-fg-muted">— saving replaces the whole set</span>
      </p>

      {masterFailed && <ErrorBlock title="The campaign-inclusions list could not be loaded." />}
      {/*
        ⚠️ **A warning, not an error, and it is here because nothing downstream
        catches this.** Everywhere else in this app a control shown on a failed
        check is cheap: the write is refused and the refusal names the problem.
        The link table governs what a picker OFFERS, never what a document may
        record, so anything ticked here saves cleanly — and inclusions print in
        the line description on the customer's Xero quote. The person reading the
        row is the only check left, so they are told the other one did not run.
      */}
      {offerUnread && (
        <Notice tone="warning">
          Couldn’t check what this product offers. Anything chosen here will save and print.
        </Notice>
      )}
      {problems.fieldErrors.inclusions && (
        <ErrorBlock title={problems.fieldErrors.inclusions}>
          {/*
            ⚠️ The server's own sentence is the TITLE; this sits BENEATH it and
            never in front. It names the inclusion, the region and the product,
            none of which this app can derive.

            Three destinations, and two of them are somewhere else — naming only
            the line would send somebody to a picker that will never list what
            they came for, and they would read that as the app being wrong.
          */}
          {regionRefusal !== null && (
            <ul className="grid list-disc gap-0.5 pl-4">
              <li>Choose another inclusion, or move this line to a project in its region.</li>
              <li>Widen what this item offers, from Inclusions on the product.</li>
              <li>Give this line’s project the region, on the project record.</li>
            </ul>
          )}
        </ErrorBlock>
      )}

      {/*
        🔴 **Three captions went in the owner's stricter pass, 2026-09-05.**
        None was an `EmptyState`, so none is among the five kept.

        1. "Only the campaign inclusions an administrator has linked to this
           item are listed." — rendered only when narrowing actually removed
           something (`narrowedAway`). It stopped a list shorter than the one on
           the previous row reading as a loading bug.
        2. "No campaign inclusions are available." — the master is empty.
        3. "Every inclusion linked to this item has been retired." — the master
           is not empty, this item is linked to some, and every one has since
           been deactivated.

        ⚠️ **2 and 3 were deliberately two sentences because they send the
        reader to different screens**, and an empty editor now says neither.
        `narrowedAway` and `masterFailed` are still computed.
      */}

      <ul className="grid gap-2 sm:grid-cols-2">
        {offered.map((inclusion) => {
          const quantityText = chosen.get(inclusion.id);
          const picked = quantityText !== undefined;
          // The parsed `quantity` was read only by the Checkbox `description`
          // removed on 2026-09-05; the quantity INPUT below uses the raw text.
          return (
            <li
              key={inclusion.id}
              className={cx(
                'flex items-start gap-2',
                // Every chip the server named, marked in one pass — it names all
                // the offenders rather than the first, so the person fixes them
                // together instead of resubmitting to find the next.
                refusedIds.has(inclusion.id) &&
                  'rounded-md bg-danger-soft px-2 py-1 ring-1 ring-danger-border',
              )}
            >
              <Checkbox
                label={inclusion.name}
                /*
                  🔴 **The Checkbox `description` is GONE — owner's stricter
                  pass, 2026-09-05.** It rendered `renderInclusion(...)`, i.e.
                  the exact wording that PRINTS on the customer's document ("4
                  campuses", "1 campus"), under each ticked inclusion.

                  ⚠️ The wording rule itself is untouched and still lives in
                  `@/lib/inclusions` — the chips further down still call
                  `renderInclusion`. What is gone is the readback beside the
                  tickbox, so the printed text is no longer visible at the point
                  the count is chosen.
                */
                checked={picked}
                disabled={disabled}
                onChange={(event) => toggle(inclusion.id, event.target.checked)}
              />
              {picked && (
                <Input
                  aria-label={`Count for ${inclusion.name}`}
                  inputMode="numeric"
                  value={quantityText ?? ''}
                  onChange={(event) => setQuantity(inclusion.id, event.target.value)}
                  disabled={disabled}
                  className="w-16 shrink-0 text-right tabular-nums"
                />
              )}
            </li>
          );
        })}
      </ul>

      {/*
        ⚠️ **Deliberately NOT warning-toned, and that is the second signal that
        separates it from the retired block below.**

        The two states are adjacent and are not equally severe. A retired entry
        carries a real constraint — it cannot be re-sent at all — and is at risk.
        This one is on the line, saves cleanly and prints; the only thing untrue
        about it is that nobody linked it to this item. A neutral surface says
        "here is a fact" where the block below says "mind this", and the two
        headings differ as well, so tone is never the only difference.
      */}
      {unlinked.length > 0 && (
        <div className="grid gap-1.5 rounded-md border border-border bg-surface px-3 py-2">
          <p className="text-xs font-medium text-fg-secondary">
            On this line, but not offered on this item
          </p>
          {/*
            ⚠️ Both halves are true at once and both are said. It stays and it
            prints — so nobody goes hunting for the missing checkbox — AND removing
            it is a real edit to the customer's document, so nobody clicks Remove
            expecting to tidy a list.
          */}
          <p className="text-2xs text-fg-muted">
            Not linked to this item, but already on the line — they will print. Removing one takes
            it off the document.
          </p>
          {unlinked.map((inclusion) => {
            /*
             * ⚠️ The count is guarded exactly as the checkbox list guards it. An
             * entry can arrive here mid-edit — ticked and half-typed while the
             * offer was still unread, then narrowed out when it landed — and
             * `renderInclusion` would print `NaN campuses` on a screen describing a
             * customer's document. The bare name is the honest fallback; the row's
             * own validation is what reports the bad count.
             */
            const quantity = Number(chosen.get(inclusion.id) ?? '1');
            return (
              <div key={inclusion.id} className="flex items-center justify-between gap-2">
                {/*
                  Wording from `@/lib/inclusions` via `renderInclusion`, never
                  re-implemented: `unitSingular` at exactly 1, `unitPlural`
                  otherwise, nothing pluralised in code.

                  ⚠️ No quantity box, deliberately. The count is shown and is not
                  editable here — raising it would be building on a set the
                  administrator did not sanction, which is the thing the narrowing
                  exists to stop. Keep or remove are the two honest actions.
                */}
                <span className="text-xs text-fg-secondary">
                  {isValidInclusionQuantity(quantity)
                    ? renderInclusion(inclusion, quantity)
                    : inclusion.name}
                </span>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggle(inclusion.id, false)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {retired.length > 0 && (
        <Notice
          tone="warning"
          title="Retired, and kept only while this line’s inclusions are left untouched"
        >
          <div className="grid gap-1.5">
          {retired.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between gap-2">
              <span className="text-xs text-fg-secondary">
                {renderInclusion(entry, Number(chosen.get(entry.id) ?? entry.quantity))}
              </span>
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => toggle(entry.id, false)}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          </div>
        </Notice>
      )}
    </div>
  );
}

/**
 * What this line's inclusions say, on the row itself so the block is not hidden.
 *
 * ⚠️ **Two renderings, and the `count` one exists for a MEASURED reason.** The
 * chips are the honest, complete version and they are what the card below `md`
 * and the expanded detail panel show. In the desktop grid they were the widest
 * thing in the Item cell: a single `Campus Outreach: 5 campuses` chip took that
 * column from the 220px it asked for to 277px measured, and an auto-layout table
 * takes that back off whichever neighbour compresses most — which is how the
 * Price box ended up 84px wide and rendering `12000.00` as `1200(`. So the grid
 * gets a count the cell can hold at a fixed width, and the chips move to the two
 * places that are full width.
 *
 * ⚠️ **The count writes BOTH word forms out.** `1 inclusion` and `2 inclusions`
 * are two literals, never a name with an `s` appended — the same rule
 * `@/lib/inclusions` enforces for the units themselves, and for the same reason:
 * this file is one edit away from being the place somebody pluralises
 * `campus`.
 *
 * The wording is not lost, only moved: every chip's text rides on the count's
 * own `title`, and the detail panel spells each one out in full beside its
 * checkbox.
 */
function InclusionSummary({
  row,
  master,
  variant = 'chips',
  source = 'draft',
  error,
}: {
  row: Row;
  master: CampaignInclusion[];
  /** `count` is the desktop grid; `chips` is every full-width surface. */
  variant?: 'chips' | 'count';
  /**
   * Which set this rendering is OF — and it must match what the copy around it
   * claims, which is the whole reason it is a parameter rather than always the
   * draft.
   *
   * ⚠️ `'draft'` is what is on SCREEN and `'saved'` is what is on the DOCUMENT,
   * and on a read-only orphan block those differ: the block promises what
   * survives a save, and what survives is the stored set, because the PATCH
   * omits the key entirely. Rendering the draft under that sentence made a row
   * holding three and keeping two describe a document that was never going to
   * exist.
   */
  source?: 'draft' | 'saved';
  /**
   * `problems.fieldErrors.inclusions`, rendered ON THE ROW.
   *
   * ⚠️ **It is here because a set of zero renders NOTHING, and the owner's
   * at-least-one rule fires on exactly that set.** The editor lives inside the
   * collapsed **Details** panel, so its `ErrorBlock` is the only home this error
   * has — and a person who picks an offering item and never opens Details would
   * see Save disabled, the row counted in "N rows need attention", and no reason
   * anywhere on the row. That is the unnameable blocked state this repo forbids.
   *
   * ⚠️ **A field error, which is an explicit exception to the no-helper-text
   * prohibition AND to the no-prose-in-a-desktop-cell rule** — both list a
   * validation error among the sentences that stay. It renders only while the row
   * is in breach, never as standing advice under the control. There is no `hint`
   * here and none may be added.
   *
   * Rendered whether or not Details is open, so an expanded row shows it twice.
   * Deliberate: the cell says **which row**, the editor says **where to fix it**,
   * and a message that disappeared on expanding would be the jumpier behaviour.
   */
  error?: string | undefined;
}) {
  /*
   * Built before the emptiness test below, because the empty case is the one that
   * has to render it — see the `error` doc.
   */
  const note =
    error === undefined ? null : (
      <p className="mt-1 text-2xs leading-snug text-danger">{error}</p>
    );
  const entries =
    source === 'draft'
      ? row.draft.inclusions
      : storedInclusionsOf(row);
  /*
   * ⚠️ This is also the whole of "a non-qualifying line shows no count of zero".
   *
   * The emptiness test comes FIRST and is the same on every line, so eligibility
   * never has to be consulted to avoid rendering `0 inclusions` — there is no
   * such rendering. What eligibility must not do here is the opposite: a line
   * that HOLDS inclusions on a product that no longer qualifies still shows
   * them, because they print on the customer's document and a screen that hid
   * them would be describing a different document from the one that was sent.
   * The owner chose *uneditable*, not *invisible*.
   *
   * ⚠️ **`note`, not `null`** — an empty set is precisely the state the
   * at-least-one refusal describes, so returning nothing here would swallow the
   * one message this component exists to carry. It stays silent when there is no
   * error, exactly as before.
   */
  if (entries.length === 0) return note;

  function wordingOf(inclusionId: number, quantity: number): string | null {
    const fromMaster = master.find((candidate) => candidate.id === inclusionId);
    if (fromMaster) return renderInclusion(fromMaster, quantity);
    const fromLine = (sourceLineOf(row)?.inclusions ?? []).find((entry) => entry.id === inclusionId);
    return fromLine ? renderInclusion(fromLine, quantity) : null;
  }

  /*
   * An entry whose wording cannot be resolved from either the active master or
   * the line itself is dropped rather than counted — the same rule the chips
   * have always applied, so the count can never claim more than the chips would
   * show.
   */
  const named = entries
    .map((entry) => ({
      id: entry.inclusionId,
      text: wordingOf(entry.inclusionId, Number(entry.quantity || '1')),
    }))
    .filter((entry): entry is { id: number; text: string } => entry.text !== null);

  if (named.length === 0) return note;

  return (
    <>
      {variant === 'count' ? (
        <p
          className="mt-1 text-2xs text-fg-muted"
          title={named.map((entry) => entry.text).join(' · ')}
        >
          {named.length === 1 ? '1 inclusion' : `${named.length} inclusions`}
        </p>
      ) : (
        <ul className="mt-1 flex flex-wrap gap-1">
          {named.map((entry) => (
            <li key={entry.id}>
              <Badge tone="neutral" variant="outline" wrap>
                {entry.text}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {note}
    </>
  );
}

/**
 * What stands where {@link InclusionsEditor} would have been, on a line whose
 * product does not offer campaign inclusions.
 *
 * Three states, and they are different facts:
 *
 * - **No item has been chosen yet.** ⚠️ **Nothing renders — this component
 *   returns `null`.** There is no product, so there is no true sentence to
 *   write; a heading with no sentence under it would be the same complaint
 *   one line shorter.
 * - **The line holds none.** The denial sentence, alone. ⚠️ A control that
 *   simply vanishes reads as a bug on this feature specifically: whoever is
 *   adding this line has already seen inclusions on another one, so with
 *   nothing here they go looking for a block that was removed on purpose.
 * - **The line already holds some** — `hasOrphanedInclusions`. With the link
 *   table empty this is not an edge case but the state of every line in the
 *   system that carries an inclusion. Their wording, read-only, plus one line
 *   saying it is not editable here — hiding a CONTROL, never the data.
 *
 * ⚠️ **That third state is really TWO, and they make opposite promises.** The
 * sentence under the wording is about what a save preserves, and a save
 * preserves the STORED set — so a row with nothing stored preserves nothing,
 * and saying otherwise is a false statement about a document. `preserved` is
 * the whole test, asked of `row.saved` rather than of what is on screen.
 *
 * The wording comes from {@link InclusionSummary}, which reads
 * `@/lib/inclusions` and must not be re-implemented here — see the
 * pluralisation rule in this file's header.
 *
 * There is no Remove control, deliberately — it would be an edit, and edits
 * are exactly what was hidden; offering one here would also be the only way
 * for this screen to send `inclusions: []` on such a line.
 */
function InclusionsNotOffered({
  row,
  master,
  denialReason,
  orphaned,
}: {
  row: Row;
  master: CampaignInclusion[];
  /**
   * `inclusionsDenialReason(product)`.
   *
   * ⚠️ **Not non-null whenever this component renders.** `null` here means *no
   * item has been chosen*, which this component treats as "render nothing", not
   * as "render the heading and skip the sentence".
   */
  denialReason: string | null;
  orphaned: boolean;
}) {
  /*
   * ⚠️ **NO ITEM CHOSEN — render nothing at all, heading included.**
   *
   * `denialReason === null` with `orphaned` false is reachable from exactly one
   * state — `NO_PRODUCT_CHOSEN` — because every other non-offering answer carries
   * the sentence. It is tested rather than passed as a fourth prop so that this
   * component cannot render a heading with nothing under it, whatever a future
   * caller does.
   *
   * ⚠️ **The heading goes too, and that is the point.** Leaving "Campaign
   * inclusions" standing alone would be the same complaint one line shorter: a
   * feature announced on a row that has not reached it, with no sentence to say
   * why it is empty — and there is no honest sentence to write, because there is
   * no product to say anything about. The row's own "Choose a product." already
   * says what to do next.
   *
   * This is NOT the vanishing-control hazard the header warns about. That warning
   * is about a control taken away from someone who has seen it work; here it has
   * not appeared yet, and it appears the moment an item is chosen.
   */
  if (!orphaned && denialReason === null) return null;

  if (!orphaned) {
    return (
      <div className="grid gap-1">
        <p className="text-xs font-medium text-fg-secondary">Campaign inclusions</p>
        <p className="text-xs text-fg-muted">{denialReason}</p>
      </div>
    );
  }

  /*
   * ⚠️ **What SURVIVES a save is the stored set, and on some rows there is
   * none.** The copy below made one promise for both, and on the rows with
   * nothing stored it was not merely vague, it was the opposite of what happens.
   *
   * `createPayload` omits `inclusions` when the product does not offer them, and
   * **on a POST an omitted key means "no inclusions", not "unchanged"** — the
   * omit-rather-than-send-`[]` rule is a PATCH rule and does not carry across.
   * So a brand-new row that had inclusions ticked while its item still qualified,
   * and then had the item changed, said *"they stay exactly as they are"* and
   * then created a line with none. The person closes the modal believing in a
   * document that does not exist, which is worse than any refusal.
   *
   * ⚠️ **The omit STAYS and the copy moves, deliberately.** Sending them instead
   * was the other candidate and is the wrong repair twice over: it would create a
   * fresh orphan on a product the owner's rule says never carries inclusions —
   * off a choice made about a different item, and uneditable from this app the
   * moment it exists — and `validateRow` skips every inclusion check on exactly
   * this branch, so the set would go out unvalidated and a retired id or a
   * fifty-first entry would come back as a 422 naming a control that is not on
   * screen. Re-enabling `[]` is not a candidate at all: it destroys quote 7's
   * live rows on the PATCH side.
   *
   * So the branch is on whether anything is STORED, not on whether the row is
   * new — a saved line whose inclusions were all ticked in this session has
   * nothing stored either, and gets the same true sentence.
   */
  const savedEntries = storedInclusionsOf(row);
  const preserved = savedEntries.length > 0;
  /*
   * Both halves are true at once and both need saying: the stored set survives,
   * AND anything ticked after the item changed does not. A single sentence about
   * the first would leave the second as a silent loss.
   */
  const draftDiverged =
    preserved && inclusionsKey(row.draft.inclusions) !== inclusionsKey(savedEntries);

  return (
    <div className="grid gap-1">
      <p className="text-xs font-medium text-fg-secondary">
        Campaign inclusions{' '}
        <span className="font-normal text-fg-muted">— on this line, and read-only</span>
      </p>
      <InclusionSummary row={row} master={master} source={preserved ? 'saved' : 'draft'} />
      <p className="text-2xs text-fg-muted">
        {denialReason !== null && `${denialReason} `}
        {preserved
          ? 'They stay exactly as they are; saving from here leaves them untouched.'
          : 'Not stored yet, and this item offers none — saving will not record them.'}
      </p>
      {draftDiverged && (
        <p className="text-2xs text-fg-muted">
          Changes made after the item was switched will not be saved.
        </p>
      )}
    </div>
  );
}

/* ── The component ────────────────────────────────────────────────────────── */

interface RunSummary {
  saved: number;
  failed: number;
  /** True when the run stopped early — a 401 or 403 means the rest would fail too. */
  halted: boolean;
}

export function QuoteLineItemsTable(props: QuoteLineItemsTableProps) {
  const {
    cities,
    /*
     * ⚠️ `hasProject` is intentionally NOT destructured, and the prop is
     * intentionally still declared.
     *
     * Its only reader was the paragraph above the table explaining that a line
     * names its own project rather than borrowing the quote's — and Project is
     * a visible column now, so the sentence described what the grid shows.
     * The prop stays because three call sites pass it and because the moment a
     * header-project affordance is wanted (seeding a new row, say) this is the
     * fact it needs. Do not delete it from the interface to silence a linter;
     * do not add the paragraph back.
     */
    currency,
    canEdit,
    canDelete,
    deleteDenialReason = DEFAULT_DELETE_DENIAL,
    contextNote,
    /*
     * Destructured plainly rather than narrowed on `props.mode` like the four
     * saved-only callbacks below: it is a COMMON prop, so every mode may carry
     * it, and the modes that cannot write a line simply never reach a fire
     * site. See its doc comment for why that is by construction rather than by
     * a `never`.
     */
    onRowsSaved,
  } = props;

  /*
   * `mode` is deliberately NOT destructured above: the union narrows on
   * `props.mode`, and destructuring it would sever that link and make every
   * mode-only prop optional at every call site inside.
   */
  const draftMode = props.mode === 'draft';
  const convertMode = props.mode === 'convert';
  /**
   * `null` in draft and convert mode, and it is read as the "issue no request"
   * switch throughout rather than as a missing value — there is no quote to
   * address, so there is no id, and every request path guards on it.
   */
  const quoteId = props.mode === 'draft' || props.mode === 'convert' ? null : props.quoteId;
  const onLinesChanged =
    props.mode === 'draft' || props.mode === 'convert' ? undefined : props.onLinesChanged;
  const onXeroPush =
    props.mode === 'draft' || props.mode === 'convert' ? undefined : props.onXeroPush;
  const onUnsavedChange =
    props.mode === 'draft' || props.mode === 'convert' ? undefined : props.onUnsavedChange;
  const registerSave =
    props.mode === 'draft' || props.mode === 'convert' ? undefined : props.registerSave;
  const onDraftLinesChange = props.mode === 'draft' ? props.onDraftLinesChange : undefined;
  const onDraftBlockedChange = props.mode === 'draft' ? props.onDraftBlockedChange : undefined;
  /*
   * ⚠️ **READ ONCE, IN THE `useState` INITIALISER BELOW, AND NOWHERE ELSE.**
   * It is deliberately NOT in any dependency array: an effect that re-ran would
   * put back rows the person had deleted, and a parent building the array
   * inline — which is what a modal will do — would re-seed on every keystroke
   * in its own header fields. Same hazard `emitLines`'s ref guards against,
   * from the other direction.
   */
  const initialDraftRows = props.mode === 'draft' ? props.initialDraftRows : undefined;
  /*
   * ── CONVERT MODE ──────────────────────────────────────────────────────────
   *
   * The parent owns the read, so this mode takes its three states as props
   * rather than fetching: `POST /deals/{id}/convert` is the parent's request
   * and the deal's basket is what it is describing. Nothing below writes a line.
   */
  const dealLines = props.mode === 'convert' ? props.dealLines : null;
  const dealLinesLoading = props.mode === 'convert' ? props.dealLinesLoading : false;
  const dealLinesError = props.mode === 'convert' ? props.dealLinesError : null;
  const convertLocked = props.mode === 'convert' ? props.convertLocked : false;
  const convertLineErrors = props.mode === 'convert' ? props.convertLineErrors : EMPTY_LINE_ERRORS;
  const onConvertLinesChange = props.mode === 'convert' ? props.onConvertLinesChange : undefined;
  const onConvertBlockedChange =
    props.mode === 'convert' ? props.onConvertBlockedChange : undefined;
  /*
   * The COMPANY figure the Tax rate column reads out in convert mode — and, on
   * every other mode, the figure a NEW row's blank percentage will be given by
   * the server. One prop, read once, and never sent anywhere.
   *
   * ⚠️ **`?? COMPANY_SALES_TAX_UNREAD` is the compatibility half and it is not
   * defensive padding.** The prop is optional on the common props so a mount
   * with no company read of its own — `QuoteDetailPage` — keeps rendering
   * exactly as it did. `{ known: false }` must therefore be indistinguishable
   * from the state before this prop existed, everywhere it is read.
   */
  const companySalesTax = props.companySalesTax ?? COMPANY_SALES_TAX_UNREAD;

  /*
   * ⚠️ **SEEDED IN THE INITIALISER, NEVER IN AN EFFECT.** A lazy initialiser
   * runs exactly once per mount, which is the only place a seed can be applied
   * without ever fighting the person using the grid. The saved-mode load effect
   * returns early while `quoteId` is null, and the convert-mode seeding effect
   * is gated on `convertMode`, so nothing downstream can overwrite these rows.
   */
  const [rows, setRows] = useState<Row[]>(() => {
    const seeded = (initialDraftRows ?? []).map(seededRowOf);
    // A new quote opens with one blank row to fill in (owner, 2026-10-12).
    return draftMode && seeded.length === 0 ? [blankRowOf(DEFAULT_BLANK_ROW_KEY)] : seeded;
  });
  /**
   * What the seeded products are CALLED, captured at mount beside the rows.
   *
   * ⚠️ **`useProducts()` fetches `active: true`, so a product retired since the
   * plan was written is NOT in the option list** — and a seeded row is the first
   * draft row that can hold a `productId` its owner never picked from that list.
   * Without this the Item cell renders BLANK over a real id: Save stays enabled,
   * nothing is refused here, and the line is POSTed naming a product the person
   * was never shown. `productOptionsFor` already keeps exactly this promise for
   * a SAVED row — *"so a line on a retired product renders itself rather than
   * blank"* — and this is that same rule extended to the population that can now
   * reach it.
   *
   * The name costs no lookup: `PlannedProductLine` carries it, for the reason
   * the prefill's company and contact carry theirs.
   *
   * ⚠️ **One-shot, like the rows.** No dependency array, because the parent maps
   * the seed inline and a memo over it would rebuild on every keystroke.
   */
  const [seededProducts] = useState<ReadonlyMap<number, ProductRow>>(
    () =>
      new Map(
        (initialDraftRows ?? []).map((line) => [
          line.productId,
          {
            id: line.productId,
            productName: line.productName,
            productCode: line.productCode,
            /*
             * ⚠️ `null` — a PLAN CARRIES NO PRICE, and this must never be read
             * as one. It reaches the option list only, never a payload and never
             * `previewInputOf`'s `catalogPrice`, which resolves off
             * `products.rows`. A figure invented here would preview a total the
             * catalogue is going to contradict.
             */
            unitPrice: null,
            /*
             * ⚠️ `null` for the same reason: **the seeder never reads this map.**
             * A recommendation is resolved off `products.rows` through
             * `recommendedDiscounts`, where a product absent from the catalogue
             * is UNKNOWN and is asked again when the read lands. Were this map
             * consulted, a plan carrying no recommendation would answer
             * "recommends nothing" — a decision, final, and wrong — for every
             * seeded line.
             *
             * 🔴 **THAT HAZARD IS LIVE NOW RATHER THAN HYPOTHETICAL — 2026-09-13.**
             * This paragraph was written while a seeded row could never be seeded
             * at all ({@link seedsAtFirstSight}), so the wrong answer it describes
             * had no path to the screen. A seeded row now takes the
             * recommendation, so pointing the seeder at this map — the obvious
             * tidy, since the map is right here and already keyed by product id —
             * would silently withhold the recommendation from **every** planned
             * line, on money a customer is billed against, and the catalogue read
             * that would have answered correctly would never be waited for.
             */
            recommendedDiscountAmount: null,
            // Option-list only, like the two above: the unit rules read
            // `productsById`, which is built from the catalogue.
            quantityStep: null,
            unitLabel: null,
          },
        ]),
      ),
  );
  /**
   * What the seeded PROJECTS are called, captured at mount beside the rows.
   *
   * ⚠️ **THE EXACT COUNTERPART OF `seededProducts`, AND FOR A WIDER FAILURE.**
   * A `Combobox` falls back to its placeholder when `value` is absent from
   * `options`, and this picker's list is narrower than the catalogue's in four
   * ways at once: ONE page of 100 projects, ACTIVE only, filtered to the row's
   * product, then **split by cycle scope and opened on `current`**. So a seeded
   * project is missing from what the row can see whenever it sits on the
   * `previous` pill — routine for a board whose rows are projects across
   * cycles — or has been retired, or the fetch has simply not landed, which is
   * true on every first paint. Without a name to pin, the control renders BLANK
   * over a real `draft.projectId` that `createPayload` still sends. **The
   * screen would say no project and the wire would say one**, which is the
   * whole failure the seed exists to prevent, arriving one layer down.
   *
   * The name costs no lookup: `PlannedProductLine.project` carries it, for the
   * reason `productName` beside it does.
   *
   * ⚠️ **One-shot, like the rows and like `seededProducts`.** No dependency
   * array — the parent maps the seed inline, so a memo over it would rebuild on
   * every keystroke.
   */
  const [seededProjects] = useState<ReadonlyMap<number, string>>(
    () =>
      new Map(
        (initialDraftRows ?? [])
          .map((line) => line.project)
          .filter((project) => project !== undefined)
          .map((project) => [project.id, project.name]),
      ),
  );
  /*
   * Nothing is fetched in draft or convert mode, so there is nothing to wait
   * for — starting `true` would render the skeleton for ever over a grid that is
   * already ready. Convert mode has a loading state all the same; it arrives as
   * a prop, because the request belongs to the parent.
   */
  const [loading, setLoading] = useState(!draftMode && !convertMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState<RunSummary | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [newRowSeq, setNewRowSeq] = useState(0);
  /*
   * ⚠️ **There is no separate `typedDescriptions` state, and there must not be
   * one.** `description` is a real column, so its typed text lives on
   * `row.draft.description` like every other value on the line — which is what
   * makes it dirty the row, reach a payload, survive a reload, and revert with
   * everything else. A second store here would be a value that looks saved and
   * is not.
   */
  /*
   * ⚠️ **THE `discountAmounts` SCREEN-STATE MAP IS GONE, AND ITS OWN REASONING
   * IS WHY.** It lived here rather than on {@link Draft} because — in the
   * sentence it carried — "there is no column, nothing may ever send it, and
   * the value it produces, a percentage, already lives on `draft.discount`".
   * `discount_amount` exists now and IS sent, so the amount is a `Draft` key
   * like `description` before it: it dirties the row, reaches the payload,
   * survives a reload, and reverts with everything else.
   */
  const products = useProducts();
  const inclusions = useCampaignInclusions();
  /*
   * One request for the whole grid, filtered per row by that row's product. See
   * `useActiveProjects` for why this is not `useLineProject` per row.
   */
  const projects = useActiveProjects();
  /*
   * Which cycles sit under Current and which under Previous. ONE read for the
   * whole grid -- the pill is per row, the /cycles fetch behind it is not, and
   * a hook per row is a request per row against a shared rate limit.
   *
   * A null member means the cycles could not be read and EVERY project stays on
   * offer under that pill -- see useCycleScopeIds. Never an empty set.
   */
  const cycleScopeIds = useCycleScopeIds();
  /*
   * One read of the `/regions` master for the whole grid, for the same reason
   * `useActiveProjects` is one read: a hook per row is one request per row of a
   * list that is identical on all of them.
   *
   * It is unconditional. The master is six rows and the request is cheap, and
   * gating it on “does any row show the control” would make the picker empty on
   * the first render after somebody switches a line to a projectless product —
   * the one moment it is wanted.
   */
  const regions = useReferenceOptions('regions');
  /*
   * The tax codes are the TENANT's, so there is nothing to ask while Xero is
   * disconnected — and a request that can only fail would put a red block on a
   * table whose real state is "Xero is not connected", a different sentence
   * with a different action.
   */
  const xero = useXero();
  const xeroConnected = !xero.loading && xero.connection.connected;
  /*
   * The line-note gate on a STORED line: `quotes.update` alone, as on the
   * invoice grid. `canEdit` also folds in the page's lines gate, and a note is
   * allowed on a locked quote whose other cells are read-only.
   */
  const { ability } = useAuth();
  const canUpdateLineNotes = ability.canUpdate('quotes');
  /*
   * ⚠️ **NOT FETCHED IN CONVERT MODE, and that is a rate-limit decision rather
   * than tidiness.** `GET /xero/tax-rates` is a rate-limited call against a
   * tenant budget of 60/min and 5,000/day, and the converted row has no tax
   * control to populate — its Tax rate cell is a readout, because the override
   * contract carries no `taxRate` and no `xeroTaxType`. This is the same shape
   * as the branding-theme picker on the New Quote form, where the control was
   * removed and the hook was left firing: **removing the control without gating
   * the hook keeps the whole cost.**
   */
  const taxRates = useXeroTaxRates(xeroConnected && !convertMode);

  /*
   * `GET /quotes/{id}/products` is NOT paginated — the response is the whole
   * document, ordered `sort_order ASC, id ASC`, which is the order `convert`
   * copies the lines in. So every line on the quote is a row in this table,
   * which is what makes the client-side duplicate-pair check complete rather
   * than a guess about a page.
   */
  const load = useCallback(
    (signal: AbortSignal) => {
      /*
       * ⚠️ Draft mode reads NOTHING. There is no quote and therefore no basket
       * to load; the rows are whatever the person has typed and nothing else.
       */
      if (quoteId === null) return Promise.resolve();
      /*
       * `loading` is raised by whatever CAUSED the fetch — the initial state
       * on mount, the retry button on a retry — never synchronously inside the
       * effect, which is what turns one render into a cascade of them.
       */
      return api
        .listQuoteProducts(quoteId, signal)
        .then((result) => {
          if (signal.aborted) return;
          setRows(result.data.map(rowOf));
          setLoadError(null);
        })
        .catch((error: unknown) => {
          if (signal.aborted) return;
          setRows([]);
          setLoadError(describeFailure(error).message);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [quoteId],
  );

  useEffect(() => {
    if (quoteId === null) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, reloadToken, quoteId]);

  /*
   * ⚠️ **CONVERT MODE SEEDS ITS ROWS FROM THE PROP, and re-seeds whenever that
   * array's IDENTITY changes.** The parent reads the deal's basket once and
   * holds it in state, so this runs once per deal — but a parent that rebuilt
   * the array inline would wipe whatever somebody is typing, with no error and
   * nothing on screen to explain it. Said on the prop as well as here.
   *
   * `dealLines === null` is "not read", which is not "none": it leaves the rows
   * alone rather than emptying them, so a failed read cannot present itself as a
   * deal with no products.
   */
  useEffect(() => {
    if (!convertMode || dealLines === null) return;
    setRows(dealLines.map(convertRowOf));
  }, [convertMode, dealLines]);

  const productNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of products.rows) map.set(row.id, row.productName);
    for (const row of rows) {
      const line = sourceLineOf(row);
      if (line) map.set(line.product.id, line.product.productName);
    }
    return map;
  }, [products.rows, rows]);

  /**
   * Every city name this table can honestly resolve, from every source it has.
   *
   * The LINE's own cities come first and are authoritative: they are what the
   * server read back, joined with no `isActive` filter, so they name rows a
   * `?isActive=true` picker will never return. A screen that resolved a stored
   * city against an active list and blanked the misses is how an already-sent
   * document quietly loses its geography.
   */
  const cityNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const city of cities) map.set(city.id, city.name);
    for (const project of projects.rows) {
      for (const city of project.cities) map.set(city.id, city.name);
    }
    for (const row of rows) {
      for (const city of sourceLineOf(row)?.cities ?? []) map.set(city.id, city.name);
    }
    return map;
  }, [cities, projects.rows, rows]);

  /**
   * Every region name this table can honestly resolve.
   *
   * ⚠️ **Read as INPUT to the pinned composer**, and on a PROJECTLESS line it is
   * the only geography the customer's document states — see `geographySegment`
   * in `@/lib/line-description`. A miss here is a wrong document, not a missing
   * nicety: the backend composes the same sentence from the line's own regions,
   * so a name this map cannot resolve is dropped from the preview while the
   * customer's Xero quote still names it.
   *
   * The saved row goes in AFTER the master and the projects, deliberately, and
   * it is the half that matters: `useReferenceOptions('regions')` queries
   * `isActive: true`, so a line sitting on a RETIRED region resolves through
   * nothing else — and reads carry no `is_active` filter, so the backend names
   * it perfectly. The same rule {@link projectNames} follows, for the same
   * reason.
   */
  const regionNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const record of regions.records) map.set(record.id, record.name);
    for (const project of projects.rows) {
      for (const region of project.regions) map.set(region.id, region.name);
    }
    for (const row of rows) {
      for (const region of sourceLineOf(row)?.regions ?? []) map.set(region.id, region.name);
    }
    return map;
  }, [regions.records, projects.rows, rows]);

  /**
   * Every project name this table can honestly resolve.
   *
   * ⚠️ **Only ever read as INPUT to the pinned composer** — see
   * {@link composeLineDescription} and the `LINE_DESCRIPTION_PREFERS_PROJECT_NAME`
   * switch, which is `true`. On a line that names a project AND carries an item
   * code, the name in this map HEADS the description a customer reads, so a
   * miss here is a wrong document rather than a missing nicety.
   *
   * The saved row goes in AFTER the picker's list, deliberately, and it is the
   * half that matters: `useProjectOptions` fetches `?isActive=true`, so a line
   * sitting on a RETIRED project resolves through nothing else. Dropping it
   * would compose the description off the product name instead — silently
   * falling back to the other convention on exactly the lines whose project is
   * least likely to be re-picked.
   */
  const projectNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const project of projects.rows) map.set(project.id, project.name);
    for (const row of rows) {
      const stored = sourceLineOf(row)?.project;
      if (stored) map.set(stored.id, stored.name);
    }
    return map;
  }, [projects.rows, rows]);

  /**
   * Every product CODE this table can resolve — Xero's `ItemCode`.
   *
   * ⚠️ **Read only as input to the pinned composer's item-code GUARD**, never
   * rendered from here. See {@link hasXeroItemCode}: with
   * `LINE_DESCRIPTION_PREFERS_PROJECT_NAME` **now `true`**, a line substitutes
   * its project name for its product name **only if it also carries a code**,
   * because the code is what still tells the customer *what* was sold when the
   * description stops saying it. On the invoice push the backend refuses
   * outright without one (`assertLineItemCode`).
   *
   * ⚠️ **`productCode` is an OPTIONAL parameter on the composer**, so a call
   * site that omits it reads as "no code" and holds the guard permanently shut
   * — an omission here is not a dormant no-op but a live divergence from the
   * string the backend composes at push time.
   *
   * Saved row last, same rule as {@link projectNames}: a retired product is
   * absent from the active catalogue and its code resolves through nothing
   * else.
   */
  const productCodes = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const row of products.rows) map.set(row.id, row.productCode);
    for (const row of rows) {
      const line = sourceLineOf(row);
      if (line) map.set(line.product.id, line.product.productCode);
    }
    return map;
  }, [products.rows, rows]);

  /**
   * Every campaign inclusion this table can name and give a unit for.
   *
   * ⚠️ **Read only as input to the pinned composer, and LIVE** —
   * `LINE_DESCRIPTION_INCLUDES_INCLUSIONS` is `true` and the backend composes
   * the same bracketed segment at push time, on quotes **and** invoices. A name
   * or a unit this map cannot resolve is dropped from the preview while the
   * customer's document still prints it.
   *
   * *SUPERSEDED: "INERT today … held off by `LINE_DESCRIPTION_INCLUDES_INCLUSIONS`,
   * which is `false` because the backend's line input types carry no inclusions
   * at all."* Both halves are false now; the flag is `true` and both backend
   * composers carry inclusions. ⚠️ **A comment saying a wired input is inert is
   * the one that stops the next reader checking it** — it reads as a reason not
   * to look at the very thing that changed.
   *
   * It was wired ahead of the flip for the reason {@link productCodes} was: the
   * parameter is OPTIONAL, so a call site that omits it reads as "this line has
   * no inclusions", and the segment would never fire and be indistinguishable
   * from one nobody built.
   *
   * Saved entries go in AFTER the master's list, the same rule as
   * {@link projectNames} and {@link productCodes}: `useCampaignInclusions`
   * fetches `?isActive=true`, so an inclusion RETIRED since the line was written
   * resolves through nothing else. It still prints on the customer's document,
   * so dropping it would describe a document we are not sending.
   */
  const inclusionMasters = useMemo(() => {
    const map = new Map<number, Pick<LineInclusion, 'name' | 'unitSingular' | 'unitPlural'>>();
    for (const entry of inclusions.rows) map.set(entry.id, entry);
    for (const row of rows) {
      for (const entry of sourceLineOf(row)?.inclusions ?? []) map.set(entry.id, entry);
    }
    return map;
  }, [inclusions.rows, rows]);

  /**
   * Per row: the projects it may choose, the cities that follow, and the
   * wording for each control.
   *
   * ⚠️ **Nothing here is asserted from a list we know is incomplete.** With the
   * project list truncated at 100 or failed outright, `declaredCityIds` is
   * `null` and every downstream check stands down — a guessed refusal would
   * block a save the server would have accepted, over a row the person cannot
   * fix from this screen.
   */
  const rowProjects = useMemo(() => {
    const map = new Map<string, RowProject>();

    /*
      🔴 **The project footnote is GONE — owner's stricter pass, 2026-09-05.**
      It was repeated on every row (a person reading one row cannot see another
      row's footnote) and read "The project list couldn't be loaded, so this
      row's project is locked." on a failed read, or "Showing the first 100
      active projects — there are more." on a truncated one.

      ⚠️ **The TRUNCATED half of that has no subject any more (2026-09-15):**
      the grid pages, so the list is complete or it failed. What that sentence
      would have been saying — 18 projects missing, the same 18 every time — went
      unsaid for as long as it was removed, which is what made the empty
      `Virtual Masterclasses` picker unreportable. See `useActiveProjects`.

      ⚠️ The LOCK on a failed read still happens — `projects.failed` still
      disables the control — but nothing now says why, so it reads as a project
      picker that is simply empty.

      ⚠️ **The footnote slot is in use again as of 2026-09-11 and this decision
      is NOT reversed.** What it now carries is `projectScopeFootnote` — "No
      projects in Current. 5 in Previous." — a fact about a control the reader
      can act on in one click, not a report on the fetch. **Do not read its
      return as permission to re-add the truncation or failed-read sentences**;
      that is the owner's call, not this file's.
    */

    for (const row of rows) {
      const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
      /*
       * THE only filter that changes the answer: a project that does not sell
       * this line's product is a named refusal on save ("This project sells
       * product X, but this line sells product Y"). Filtering here means the two
       * controls cannot disagree.
       *
       * ⚠️ **The test is the project's whole product SET, via
       * `projectSellsProduct`** — migration 156, and the singular key it
       * replaced was only the primary. Matching the primary excluded a project
       * selling this product as a SECONDARY, which is exactly the case 156 was
       * built for: a travel line under a festival project. The exclusion was
       * silent — the project was simply absent, reading as one nobody had set
       * up. One helper for all three grids; three copies of this predicate drift
       * and nothing on screen shows it.
       *
       * ⚠️ **With no item chosen this is the WHOLE list, not an empty one** —
       * the project-first path. Choosing a project here sets the line's item
       * from it (see `patchRow`), so the pair is decided in one action and the
       * disagreement refusal 5 catches is never reachable.
       */
      const forProduct =
        productId === null
          ? projects.rows
          : projects.rows.filter((project) => projectSellsProduct(project, productId));

      /*
       * The period disambiguates: a project's name is NOT unique, because one
       * product genuinely runs twice in a period. A cycle with no intake spans
       * the whole cycle, which is a real state rather than a missing one, so it
       * renders as the cycle alone rather than as a blank half.
       */
      const toProjectOption = (project: (typeof forProduct)[number]): ComboboxOption => {
        const period = project.cycle
          ? project.intake
            ? `${project.cycle.name} · ${project.intake.name}`
            : project.cycle.name
          : null;
        /*
         * On the project-first path the ITEM is what the choice implies, so it
         * leads the hint — picking a project blind and then finding the item
         * box has changed underneath you is the surprise this prevents. Once an
         * item is chosen the list is already filtered to it and repeating the
         * name in every row would be noise.
         *
         * ⚠️ **The PRIMARY is the right read HERE, and `products[0]` IS the
         * primary** — first by the read's own `ORDER BY`, which the backend
         * states as an API guarantee rather than as an incidental ordering. The
         * hint's whole job is to name what picking this project will put in the
         * item box, and that is `productOfProject`, which seeds the primary.
         * Listing the project's whole product set would be a longer hint
         * describing something that does not happen.
         */
        const productName = productId === null ? (project.products[0]?.name ?? null) : null;
        const hint = [productName, period].filter((part) => part !== null).join(' · ');
        return {
          value: String(project.id),
          label: project.name,
          ...(hint !== '' ? { hint } : {}),
        };
      };

      /*
       * A line already on a since-retired project reads its name back happily
       * and re-sending that id is a 422 — so it is injected, labelled, rather
       * than dropped. Dropping it renders the control blank over a real value
       * and the next save silently repoints the line.
       *
       * ⚠️ **Since 2026-09-11 the same injection also carries a line whose
       * project is merely on the OTHER pill**, which is a far commoner case
       * than a retired project: it is pinned into EVERY scope by
       * `projectOptionsByScope`, so switching to Current over a historical line
       * can never blank the control. The label is the same because the reader's
       * situation is the same — this project is not on offer here, and it is
       * still what the row holds.
       */
      const savedProject = sourceLineOf(row)?.project ?? null;

      /*
       * ✅ **THE SAME PIN FOR A SEEDED ROW, which has no `sourceLineOf` to pin
       * from — 2026-09-11.** `productOptionsFor` already carries this branch
       * verbatim for the Item cell; this is it for the project cell, and the
       * reason is stronger here because this list is narrowed four ways (see
       * `seededProjects`) where the catalogue's is narrowed one.
       *
       * ⚠️ **Keyed on the row's CURRENT draft project, never on the seed.**
       * Changing the item clears `projectId` — `patchRow` does it so refusal 5
       * cannot be built out of two choices that each looked right — and a pin
       * keyed on the seed would go on offering a project the row no longer
       * holds, under a product that no longer sells it.
       *
       * `projectOptionsByScope` unshifts only into a scope that does not
       * already list it, so passing this when the project IS on offer changes
       * nothing and shows no label.
       */
      const seededProjectName =
        row.draft.projectId === ''
          ? undefined
          : seededProjects.get(Number(row.draft.projectId));
      const pinnedProject =
        savedProject !== null
          ? { value: String(savedProject.id), label: savedProject.name, hint: 'no longer offered' }
          : seededProjectName !== undefined
            ? {
                value: row.draft.projectId,
                label: seededProjectName,
                hint: 'no longer offered',
              }
            : null;

      /*
       * Current / Previous, owner request 2026-09-11 — Current (which means
       * current AND upcoming) is the default and reproduces the 2026-09-09
       * open-cycles filter exactly. Previous is what makes the closed-cycle
       * projects that filter hid reachable again.
       *
       * SPLIT HERE AND NOT INTO forProduct, WHICH IS THE WHOLE POINT.
       * `selected` below still resolves against the unfiltered list, so a line
       * already on a closed cycle keeps its project, its cities and its
       * regions. Filter forProduct instead and 113 historical lines lose all
       * of it.
       */
      const { options, counts: scopeCounts } = projectOptionsByScope(
        forProduct,
        cycleScopeIds,
        toProjectOption,
        pinnedProject,
      );
      const projectFootnotes: Record<ProjectCycleScope, string | undefined> = {
        current: projectScopeFootnote('current', scopeCounts),
        previous: projectScopeFootnote('previous', scopeCounts),
      };

      const selected =
        row.draft.projectId === ''
          ? null
          : (forProduct.find((project) => String(project.id) === row.draft.projectId) ?? null);

      const resolvable = !projects.loading && !projects.failed && !projects.truncated;
      const declaredCityIds =
        selected !== null
          ? new Set(selected.cities.map((city) => city.id))
          : // No project chosen means no cities are declared — a definite answer,
            // not an unknown, and the only one that does not depend on the list.
            row.draft.projectId === '' && resolvable
            ? new Set<number>()
            : null;

      const cityOptions: MultiComboboxOption[] = (selected?.cities ?? []).map((city) => ({
        value: String(city.id),
        label: cityLabel(city),
      }));
      /*
       * The flag's live answer, taken from the PROJECT rather than from the
       * line — that is what "all of its cities" means, and what the server
       * resolves at read time. A saved flagged line's own `cities` is the same
       * set resolved a moment earlier and stands in where this cannot be
       * computed; see {@link CitiesControl}.
       *
       * `name` and `label` both, because two readers want different things: the
       * composed description prints bare names (the backend's copy of the join
       * does too, and the two must agree byte for byte), while the read-out's
       * `title` wants the country to tell two same-named cities apart.
       */
      const projectCities =
        selected === null
          ? null
          : selected.cities.map((city) => ({ name: city.name, label: cityLabel(city) }));
      /*
       * The project's OWN region set, which is what the region picker offers on a
       * project-backed row — see {@link RowProject.projectRegions}. `null` when
       * the project could not be resolved at all, which is a different fact from
       * a project that declares none.
       */
      const projectRegions =
        selected === null
          ? null
          : selected.regions.map((region) => ({ id: region.id, name: region.name }));
      /*
       * ⚠️ **Three conditions, and all three are load-bearing.** A project must
       * be CHOSEN (an empty box says nothing about a project nobody has picked),
       * RESOLVED (`selected !== null`, so a truncated or failed list cannot be
       * read as "declares nothing"), and its own list empty. Anything short of
       * that is `false`, because this opens a control and the gate above it fails
       * towards hiding.
       */
      const projectHasNoCities =
        row.draft.projectId !== '' && selected !== null && selected.cities.length === 0;
      /*
       * A city the line already names that its project no longer declares.
       * Kept, labelled, and editable: PATCH skips the check entirely when
       * `cityIds` is not sent, which is what keeps a historical line saveable.
       */
      for (const city of sourceLineOf(row)?.cities ?? []) {
        if (!row.draft.cityIds.includes(city.id)) continue;
        if (cityOptions.some((option) => option.value === String(city.id))) continue;
        cityOptions.push({
          value: String(city.id),
          label: cityLabel(city),
          hint: 'no longer on this project',
        });
      }

      const citiesUnavailable = row.draft.projectId === '' || cityOptions.length === 0;
      const cityHint =
        row.draft.projectId === ''
          ? // Said plainly rather than as a fault. Some products are not sold
            // through projects at all and legitimately name no cities, ever.
            'A line names cities through its project. Without one it is not city-specific.'
          : cityOptions.length === 0
            ? selected === null
              ? 'This project’s cities are not listed here.'
              : 'This project declares no cities. Add cities to the project first.'
            : undefined;

      const settled = !projects.loading && !projects.failed;
      const projectHint =
        productId !== null && settled && forProduct.length === 0 && !projects.truncated
          ? // Why the picker is offering nothing — a disabled control with no
            // stated reason reads as broken.
            'No active project sells this product. The line runs without one.'
          : undefined;

      map.set(row.key, {
        options,
        cityOptions,
        projectCities,
        declaredCityIds,
        projectRegions,
        projectHasNoCities,
        projectHint,
        projectFootnotes,
        cityHint,
        citiesUnavailable,
        /*
         * ⚠️ Counted off `forProduct`, the SAME filtered list the picker above
         * is built from — so "this product has projects" and "the picker has
         * options" are one answer rather than two that agree by coincidence.
         * The stored-but-retired project injected into `options` deliberately
         * does NOT count: it is a historical value, not evidence the product is
         * still sold that way, and the row shows its control anyway because
         * {@link showsProjectControl} never hides a value the line holds.
         */
        projectOffer: projectOfferOf(productId, forProduct.length, projects),
      });
    }
    return map;
    /*
     * The four fields are listed rather than `projects` itself, deliberately.
     * `projectOfferOf`'s third parameter is typed `{loading, failed,
     * truncated}` — narrower than what `projects` holds — so the COMPILER
     * guarantees nothing outside these deps can be read, and depending on the
     * object's identity instead would recompute every row's options whenever
     * the hook returned a new reference with the same contents.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rows,
    projects.rows,
    projects.loading,
    projects.failed,
    projects.truncated,
    /*
     * Listed for honesty rather than for reactivity: it is `useState` with no
     * setter, so its identity never changes and it can never trigger a
     * recompute. Leaving a value this memo READS out of an already
     * hand-maintained list is how the next member added beside it gets left out
     * too — and that one may well move.
     */
    seededProjects,
    /*
     * cycleScopeIds ARRIVES LATE, AND LEAVING IT OUT MAKES THE SPLIT INERT.
     * useCycleScopeIds answers UNKNOWN_CYCLE_SCOPES while /cycles is in flight
     * and a real index once it lands, so a memo that does not depend on it
     * computes the UNFILTERED list on first paint and never recomputes -- both
     * pills then show everything, with nothing failing and nothing on screen to
     * show it. Caught by lint, not by review, the last time this changed.
     */
    cycleScopeIds,
  ]);

  const retiredInclusionIds = useMemo(() => {
    const active = new Set(inclusions.rows.map((row) => row.id));
    const retired = new Set<number>();
    for (const row of rows) {
      for (const entry of row.saved?.inclusions ?? []) {
        if (!active.has(entry.id)) retired.add(entry.id);
      }
    }
    return retired;
  }, [inclusions.rows, rows]);

  /**
   * The distinct products this document sells, as the fetch input.
   *
   * Sorted and deduplicated so the array's IDENTITY changes only when the SET
   * does — adding a row, reordering the grid or editing a price must not re-fire
   * the effect that reads from it. See {@link useProductInclusionOffers} on why
   * that memoisation is the caller's job.
   *
   * A row with an empty item box contributes nothing: there is no product to ask
   * about, and that row answers `'not-chosen'` without a request.
   */
  const productIdKey = rows.map((row) => row.draft.productId).join(',');
  const inclusionProductIds = useMemo(() => {
    const ids = new Set<number>();
    for (const id of productIdKey.split(',')) {
      if (id !== '') ids.add(Number(id));
    }
    return [...ids].sort((a, b) => a - b);
    /*
     * ⚠️ Keyed on the joined product ids, NOT on `rows`. The set is what this
     * array means; depending on `rows` would hand a brand-new array to the effect
     * on every keystroke in a description box. That is survivable — the hook's
     * `requested` ref makes a re-run cost nothing — but relying on a downstream
     * guard to absorb an upstream churn is how the guard ends up load-bearing for
     * something nobody meant it to cover.
     */
  }, [productIdKey]);

  const inclusionOffers = useProductInclusionOffers(inclusionProductIds);

  /**
   * Per row: whether campaign inclusions are offered on its product, why not
   * when they are not, and whether the line is holding some anyway.
   *
   * ⚠️ **This is the ONE place the rule is asked**, and it is asked of
   * `@/lib/product-inclusions` — nothing in this file decides it and nothing
   * re-derives it. The input is the product's **link-table offer**; the gate is
   * `productOffersInclusions`, the owner's explicit choice over the lenient
   * `shouldShowInclusionsEditor`. See this file's header for what was traded.
   *
   * It is computed above `localProblems`, `dirtyKeys` and `draftLines` because
   * all three read it, and they must all read the SAME answer: the validation,
   * the dirty test and the payload have to agree about whether this row's
   * inclusions are in play, or the row reads "Unsaved" and sends nothing, or
   * carries an error over a control that is not on screen.
   */
  const inclusionsByKey = useMemo(() => {
    const map = new Map<string, RowInclusions>();
    for (const row of rows) {
      const offer = inclusionsOfferOf(row, inclusionOffers);
      map.set(row.key, {
        offers: productOffersInclusions(offer),
        /*
         * ⚠️ Not `offers && denialReason === null`, which is also true for a
         * product that genuinely offers some. The test is the INPUT, not the
         * verdict: `null` is reached from exactly one state — an item is chosen
         * and no offer came back for it — because an empty box returns the
         * sentinel and a successful read returns an object, `[]` included.
         */
        offerUnread: offer === null,
        /*
         * ⚠️ **Which** inclusions, from the same offer the gate above counts —
         * never a second read and never a filter written here. `null` travels with
         * `offerUnread` and means *do not narrow*; see {@link RowInclusions.offeredIds}.
         */
        offeredIds: offeredInclusionIds(offer),
        denialReason: inclusionsDenialReason(offer),
        /*
         * The DRAFT's count, not the saved one, because that is what is on
         * screen — a row whose set was changed while its product still offered
         * inclusions and then had its item switched is showing those entries, and
         * they are what the read-only block has to describe.
         */
        orphaned: hasOrphanedInclusions(offer, row.draft.inclusions.length),
      });
    }
    return map;
  }, [rows, inclusionOffers]);

  /** Never `undefined` at a call site — see {@link INCLUSIONS_SHOWN}. */
  const inclusionsFor = useCallback(
    (key: string): RowInclusions => inclusionsByKey.get(key) ?? INCLUSIONS_SHOWN,
    [inclusionsByKey],
  );

  /**
   * Whether this row's regions block is on screen — and therefore whether a save
   * may carry `regionIds` at all.
   *
   * ⚠️ **ONE answer, read by the render AND by both payload builders**, the
   * same arrangement `inclusionsFor` has and for the same reason. A second test
   * here would be a second answer, and the two would diverge on exactly the row
   * where it mattered: a hidden control emitting a full REPLACE.
   *
   * The `'unknown'` fallback matches the one `rowControls` uses for a row the
   * resolver has not caught up with, so the gate and the render agree about a
   * row neither of them can say anything about yet.
   */
  const showsRegions = useCallback(
    (row: Row): boolean =>
      showsRegionsControl(
        row,
        showsProjectControl(row, rowProjects.get(row.key)?.projectOffer ?? 'unknown'),
        /*
         * ⚠️ `false` for a row the resolver has not caught up with, which is the
         * opposite default to `projectOffer`'s `'unknown'` two lines up — and
         * deliberately so. That one fails towards SHOWING the project block; this
         * one must fail towards HIDING the regions block, or a row whose project
         * simply has not loaded yet would offer a geography it may have no
         * business recording.
         */
        rowProjects.get(row.key)?.projectHasNoCities ?? false,
      ),
    [rowProjects],
  );

  const duplicatePairs = useMemo(() => duplicatePairsOf(rows), [rows]);

  /**
   * The three generated figures, previewed per row.
   *
   * Computed here rather than in the cell because both layouts render the same
   * row and the detail panel wants the same answer — three call sites deriving
   * one figure is three chances for them to disagree on screen.
   *
   * `null` for any row not worth previewing: no price on the line, an unpriced
   * catalogue product behind an `inherit`, or boxes that are not yet a figure.
   */
  /**
   * Product id → its catalogue `unitPrice`, straight off `/products`.
   *
   * ⚠️ **`has()` is the question, never `get() ?? null`.** A product listed with
   * `unitPrice: null` is UNPRICED — a real answer — and a product missing from
   * the map is UNKNOWN, because the catalogue read failed or was truncated at
   * 100. The Price box says two different things for those two states, so
   * collapsing them with a `??` would report a load failure as a business fact.
   */
  const catalogPrices = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const product of products.rows) map.set(product.id, product.unitPrice);
    return map;
  }, [products.rows]);

  /**
   * Project id → how many cities it declares — the N of the city pro-rata
   * (owner 2026-10-18), off the same `/projects` read the city picker is built
   * from. A project missing from the map is UNKNOWN, never zero.
   */
  const projectCityCounts = useMemo(() => {
    const map = new Map<number, number>();
    for (const project of projects.rows) map.set(project.id, project.cities.length);
    return map;
  }, [projects.rows]);

  /*
   * Project price entries for every project a row names (draft, stored or deal
   * line), one batch read shared across the page (`lib/use-project-prices.ts`).
   */
  const pricedProjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of rows) {
      if (row.draft.projectId !== '') ids.add(Number(row.draft.projectId));
      const source = sourceLineOf(row);
      if (source !== null && source.project !== null) ids.add(source.project.id);
    }
    return [...ids];
  }, [rows]);
  const {
    entriesByProject: priceEntriesByProject,
    tiers: priceTiers,
    defaultTierId,
    error: projectPricesError,
  } = useProjectPrices(pricedProjectIds);

  /*
   * The currency the document had before its latest change, so a saved line
   * priced under it still reads as untouched and follows the new currency.
   * Tracked during render (React's "adjust state on prop change" pattern).
   */
  const [currencyTrack, setCurrencyTrack] = useState<{
    current: string | null;
    previous: string | null;
  }>({ current: currency, previous: null });
  if (currencyTrack.current !== currency) {
    setCurrencyTrack({ current: currency, previous: currencyTrack.current });
  }
  const previousCurrency = currencyTrack.previous;

  /**
   * The price a line inherits for a product, project, city selection, tier and
   * quantity — `computeLinePrice` (owner 2026-10-27): the project price entry
   * for the document currency and tier, else the catalogue price pro-rated over
   * the project's cities. `known: false` while a read it needs (the catalogue,
   * the project's city count, its price entries, the tier master) has not
   * landed; a failed price read falls back to the catalogue rule.
   */
  const inheritedPriceOf = useCallback(
    (
      productId: number | null,
      projectId: number | null,
      selectedCityCount: number,
      allProjectCities: boolean,
      priceTierId: number | null,
      quantity: string,
      currencyCode: string | null = currency,
    ): InheritedPrice => {
      if (productId === null) return INHERITED_UNKNOWN;
      const catalogKnown = catalogPrices.has(productId);
      const catalogUnitPrice = catalogPrices.get(productId) ?? null;
      let entries: readonly ProjectPriceEntry[] = [];
      let projectCityCount = 0;
      if (projectId !== null) {
        const loaded = priceEntriesByProject.get(projectId);
        if (loaded === undefined && projectPricesError === null) return INHERITED_UNKNOWN;
        entries = loaded ?? [];
        const count = projectCityCounts.get(projectId);
        if (count === undefined) {
          // The count matters to a price entry, and to a pro-rata of some cities.
          const needed =
            entries.length > 0 || (catalogUnitPrice !== null && !allProjectCities && selectedCityCount > 0);
          if (needed) return INHERITED_UNKNOWN;
        } else {
          projectCityCount = count;
        }
      }
      if (entries.length > 0 && defaultTierId === null && projectPricesError === null) {
        return INHERITED_UNKNOWN;
      }
      const typedQuantity = quantity.trim();
      const computed = computeLinePrice({
        entries,
        defaultTierId,
        productId,
        currency: currencyCode,
        priceTierId,
        selectedCityCount,
        projectCityCount,
        allProjectCities,
        // A blank box is the server's default of 1; anything unreadable is left
        // to the resolver, which falls back to the catalogue rule.
        quantity: typedQuantity === '' ? 1 : /^\d+$/.test(typedQuantity) ? Number(typedQuantity) : Number.NaN,
        catalogUnitPrice,
      });
      if (computed.source === 'catalogue' && !catalogKnown) return INHERITED_UNKNOWN;
      return { known: true, price: computed.unitPrice, discountAmount: computed.discountAmount };
    },
    [catalogPrices, projectCityCounts, priceEntriesByProject, defaultTierId, projectPricesError, currency],
  );

  /** What the row's own `inherit` resolves to, and whether we know at all. */
  const catalogueFor = useCallback(
    (row: Row): InheritedPrice =>
      inheritedPriceOf(
        row.draft.productId === '' ? null : Number(row.draft.productId),
        row.draft.projectId === '' ? null : Number(row.draft.projectId),
        row.draft.cityIds.length,
        row.draft.allCities,
        row.draft.priceTierId,
        row.draft.quantity,
      ),
    [inheritedPriceOf],
  );

  /**
   * The price a SAVED line will be repriced to by its next save, or `undefined`
   * when the save leaves the stored price alone.
   *
   * The server's rule (owner 2026-10-18): a PATCH that moves the product,
   * project or cities WITHOUT a `unitPrice` restores the pro-rata price for the
   * new selection — but only when the stored price still equals the pro-rata of
   * the PREVIOUS selection, i.e. nobody typed over it. `updatePayload` already
   * omits `unitPrice` while the box holds the stored figure, so nothing extra is
   * sent; this only lets the box and the Amount show what the save will store.
   * A price typed in this session, or one typed before, is kept and never
   * reaches here.
   *
   * A DEAL-CONVERT row (owner 2026-10-18): when the box still holds the deal
   * line's own price, that price equals the pro-rata of the deal line's OWN
   * selection (never typed over; an unknown city count decides nothing), and
   * its product, project or cities moved from the deal line's, `lineSet`
   * carries no `unitPrice` and the server reprices to the pro-rata of the new
   * selection — previewed here the same way.
   *
   * ⚠️ A stored `null` price is an explicit "no price" and is never repriced.
   *
   * ✅ **PROJECT PRICING (owner 2026-10-27).** "Moved" now also covers the
   * TIER and the QUANTITY, and the before/after figures are `computeLinePrice`
   * (project price entry, else the pro-rata). A saved line also follows a
   * CURRENCY change: its price is untouched when it equals the price under the
   * current currency OR the one the document had just before. The reprice is
   * SENT explicitly now (`updatePayload`, and `unitPrice` on a convert row's
   * `lineSet` entry) rather than left to a server rule, and carries the
   * resolver's discount amount when the stored discount was the resolver's too.
   */
  const repriceFor = useCallback(
    (row: Row): Reprice | undefined => {
      const { draft } = row;
      const movedFrom = (base: Draft): boolean =>
        draft.productId !== base.productId ||
        draft.projectId !== base.projectId ||
        draft.allCities !== base.allCities ||
        (!draft.allCities && citiesKey(draft.cityIds) !== citiesKey(base.cityIds)) ||
        draft.priceTierId !== base.priceTierId ||
        (draft.quantity.trim() !== '' && draft.quantity.trim() !== base.quantity);
      if (row.convert !== null) {
        const source = row.convert;
        if (source.unitPrice === null) return undefined;
        if (draft.priceMode !== 'value' || draft.price !== source.unitPrice) return undefined;
        if (!movedFrom(convertRowOf(source).draft)) return undefined;
        // Only a deal price still equal to the inherited price of its OWN selection follows.
        const own = inheritedPriceOf(
          source.product.id,
          source.project === null ? null : source.project.id,
          source.cities.length,
          false,
          source.priceTierId ?? null,
          String(source.quantity),
        );
        if (!own.known || own.price === null || !sameMoney(own.price, source.unitPrice)) {
          return undefined;
        }
        const after = catalogueFor(row);
        return after.known && after.price !== null ? { unitPrice: after.price } : undefined;
      }
      const saved = row.saved;
      if (saved === null || saved.unitPrice === null) return undefined;
      const untyped =
        draft.priceMode === 'inherit' ||
        (draft.priceMode === 'value' && draft.price === saved.unitPrice);
      if (!untyped) return undefined;
      const base = draftOf(saved);
      const moved = movedFrom(base);
      const candidates =
        previousCurrency !== null && previousCurrency !== currency ? [currency, previousCurrency] : [currency];
      let before: InheritedPrice | null = null;
      let pricedUnder: string | null = null;
      for (const candidate of candidates) {
        const figure = inheritedPriceOf(
          saved.product.id,
          saved.project === null ? null : saved.project.id,
          saved.allCities ? 0 : saved.cities.length,
          saved.allCities,
          saved.priceTierId ?? null,
          String(saved.quantity),
          candidate,
        );
        if (figure.known && figure.price !== null && sameMoney(figure.price, saved.unitPrice)) {
          before = figure;
          pricedUnder = candidate;
          break;
        }
      }
      if (before === null) return undefined;
      if (!moved && pricedUnder === currency) return undefined;
      const after = catalogueFor(row);
      if (!after.known) return undefined;
      const reprice: Reprice = { unitPrice: after.price };
      /*
       * The discount follows only when the stored one is the resolver's own:
       * its rounding amount, or no discount at all. A typed discount stays.
       */
      const storedAmount = saved.discountAmount;
      const discountFollows =
        before.discountAmount === null
          ? storedAmount === null && sameMoney(saved.discount, '0')
          : storedAmount !== null && sameMoney(storedAmount, before.discountAmount);
      if (discountFollows && after.price !== null) reprice.discountAmount = after.discountAmount;
      return reprice;
    },
    [inheritedPriceOf, catalogueFor, currency, previousCurrency],
  );

  /** The unit price half of {@link repriceFor}; `undefined` leaves the stored price alone. */
  const repricedFor = useCallback(
    (row: Row): string | null | undefined => {
      const reprice = repriceFor(row);
      return reprice === undefined ? undefined : reprice.unitPrice;
    },
    [repriceFor],
  );

  /**
   * The discount amount a save carries without anybody typing it: a saved
   * line's reprice, or a new `inherit` line's resolver rounding discount.
   * `undefined` = none (the typed discount, or the stored one, stands).
   */
  const pricedDiscountFor = useCallback(
    (row: Row): string | null | undefined => {
      if (row.saved !== null) return repriceFor(row)?.discountAmount;
      if (row.convert !== null || row.draft.priceMode !== 'inherit') return undefined;
      const inherited = catalogueFor(row);
      return inherited.known && inherited.price !== null && inherited.discountAmount !== null
        ? inherited.discountAmount
        : undefined;
    },
    [repriceFor, catalogueFor],
  );

  /**
   * Product id → the discount a line selling it should START at.
   *
   * ⚠️ **`has()` is the question, exactly as it is for {@link catalogPrices}.** A
   * product carrying `null` RECOMMENDS NOTHING — a real answer — while a product
   * missing from the map is UNKNOWN, because the catalogue read failed or was
   * truncated at 100. Both leave the line alone, so collapsing them would be
   * harmless today; they are kept apart because only one of them is final. An
   * unknown product is re-asked when the catalogue lands, a product that
   * recommends nothing is never asked about again.
   */
  const recommendedDiscounts = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const product of products.rows) {
      map.set(product.id, product.recommendedDiscountAmount);
    }
    return map;
  }, [products.rows]);

  /** Product id → the catalogue row, for the per-unit rules (`lib/product-units.ts`). */
  const productsById = useMemo(() => {
    const map = new Map<number, ProductRow>();
    for (const product of products.rows) map.set(product.id, product);
    return map;
  }, [products.rows]);

  const productOfRow = useCallback(
    (row: Row): ProductRow | undefined =>
      row.draft.productId === '' ? undefined : productsById.get(Number(row.draft.productId)),
    [productsById],
  );

  /**
   * The auto-description inputs for a row, or `null` when it gets none — the
   * product names no unit, or the quantity is not a whole count yet.
   *
   * The price lines need a currency to be worth printing, so with none known
   * the price is withheld and the text carries the quantity line alone; it
   * refreshes when the currency arrives.
   */
  const autoArgsFor = useCallback(
    (row: Row, discountAmount: string | null, seededPerUnit: string | null): AutoLineDescriptionArgs | null => {
      const product = productOfRow(row);
      const unitLabel = product?.unitLabel?.trim() ?? '';
      if (product === undefined || unitLabel === '') return null;
      const quantity = row.draft.quantity.trim();
      if (!/^\d+$/.test(quantity) || /^0+$/.test(quantity)) return null;
      const repriced = repricedFor(row);
      const unitPrice =
        currency === null
          ? null
          : repriced !== undefined
            ? repriced
            : row.draft.priceMode === 'value'
              ? row.draft.price.trim() === ''
                ? null
                : row.draft.price.trim()
              : row.draft.priceMode === 'inherit'
                ? catalogueFor(row).price
                : null;
      let discountPerUnit: string | null = null;
      if (seededPerUnit !== null) {
        discountPerUnit = seededPerUnit;
      } else if (discountAmount !== null && discountAmount.trim() !== '') {
        // A hand-typed amount names a per-unit figure only when it divides exactly.
        const split = splitAmountOverQuantity(discountAmount.trim(), Number(quantity));
        if (split !== null && split.exact) discountPerUnit = split.unitPrice;
      }
      return {
        productName: product.productName,
        quantity,
        unitLabel,
        unitPrice,
        discountPerUnit,
        currency: currency ?? '',
      };
    },
    [productOfRow, catalogueFor, repricedFor, currency],
  );

  /**
   * The band question this row may ask, or `''` for *do not ask*.
   *
   * ⛔ **THE INCLUSIONS GATE IS THE FIRST TEST AND IT IS `inclusionsFor`.** A
   * line whose block is not on screen is not asked about and is never seeded:
   * the editor is hidden, so a set appearing there is a set nobody can see, edit
   * or explain — and on a product that does not offer inclusions it would put a
   * fresh orphan on the line rather than describe an existing one. Asking
   * `inclusionsFor` rather than re-deriving the rule is what keeps this agreeing
   * with the render, the validator, the dirty test and both payload builders,
   * which all read that same answer.
   *
   * ⚠️ **`'unknown'` resolves to SHOW, so an unread offer IS seeded** — and that
   * is the same fail-towards-useful direction the block itself takes, not an
   * oversight. The seed lands in the draft in front of somebody, beside the
   * editor's own "could not check" notice, and a wrong one is removable before
   * it is saved.
   */
  const presetAskKeyFor = useCallback(
    (row: Row): string => {
      if (!inclusionsFor(row.key).offers) return '';
      if (row.draft.productId === '') return '';
      const quantity = presetQuantityOf(row.draft.quantity);
      if (quantity === null) return '';
      return presetKeyOf(
        Number(row.draft.productId),
        quantity,
        row.draft.projectId === '' ? null : Number(row.draft.projectId),
      );
    },
    [inclusionsFor],
  );

  /**
   * The bands still WANTED — the pairs some row needs an answer for and has not
   * got one for yet.
   *
   * ⛔ **NOT "every pair on the document", which is the obvious version and asks
   * for the wrong set.** A SAVED row is primed on first sight and a primed row
   * can never seed, so its pair is one whose answer nobody will ever read — a
   * request per line, at open, for nothing. The seeding effect below asks for a
   * pair only once it has a change it cannot answer, which is why a saved
   * document sitting untouched issues none of these at all.
   *
   * ⚠️ **A DRAFT SEEDED FROM `initialDraftRows` DOES ASK AT MOUNT, AND THAT IS
   * THE POINT RATHER THAN THE EXCEPTION — 2026-09-13.** Those rows are seeded on
   * first sight ({@link seedsAtFirstSight}), so their bands are answers somebody
   * WILL read: they are what puts the inclusion preset on a planned line. The
   * request count is still one per distinct `(product, quantity)` pair, not one
   * per row — eight seeded lines selling one product at one count are one
   * request — so this stays well inside the shared budget. ⚠️ *SUPERSEDED:
   * "120/60s."* It is **12,000/60s** (corrected 2026-09-17), so "well inside"
   * is further inside than this was written to claim.
   *
   * De-duplicated and sorted, so the key describes the SET rather than the rows:
   * eight lines selling one product at one count are one request, and
   * re-ordering or deleting a row cannot re-ask a question already answered.
   */
  const [wantedPresetKey, setWantedPresetKey] = useState('');
  const inclusionPresets = useInclusionPresets(wantedPresetKey);

  const seedRecords = useRef<Map<string, LineSeedRecord>>(new Map());

  /**
   * Seeding a line that has just named a product, or just changed its quantity.
   *
   * Two things land in the DRAFT — the product's recommended discount, and the
   * campaign inclusions its quantity band presets — and **both decisions belong
   * to `@/lib/line-presets`**, which the quote, invoice and deal grids share.
   * This effect owns only *when to ask*: the discount follows the PRODUCT, since
   * that is what recommends it, and the band follows the (product, quantity)
   * PAIR, since that is what it is keyed by.
   *
   * ⛔ **A STORED ROW IS PRIMED ON FIRST SIGHT AND NEVER SEEDED THERE.** Without
   * that, a saved line whose inclusions are legitimately empty would be seeded
   * the moment the quote was opened — every row reading **Unsaved** on arrival,
   * with a Save beside it offering to write a set nobody asked for. On a stored
   * line, seeding is a response to a CHANGE somebody made, so the first
   * observation of it records the question and answers nothing.
   *
   * 🔴 **⚠️ *SUPERSEDED 2026-09-13, and it was an absolute: "**A ROW IS PRIMED ON
   * FIRST SIGHT AND NEVER SEEDED THERE.**"* A row with no stored counterpart —
   * an `initialDraftRows` seed, which arrives ALREADY NAMING A PRODUCT — is
   * seeded on first sight instead**, because for it there is no earlier change
   * to be a response to and never will be. The owner's decision and the argument
   * for it are at {@link seedsAtFirstSight}; the priming pass is unchanged for
   * everything it was actually protecting.
   *
   * ⛔ **NOTHING HERE WRITES TO THE SERVER.** The seed is a draft edit like any
   * other: the row goes Unsaved, the figures preview, and the person can revert,
   * retype or delete it before anything is sent.
   *
   * ⚠️ **Every `null` from either function means CHANGE NOTHING and is honoured
   * as such** — never an empty seed, never a clear. That is what stops a
   * recommendation overwriting a negotiated percentage, a band overwriting a set
   * somebody chose, and a quantity in no band emptying a line.
   *
   * ⚠️ **One `setRows`, and it touches the DRAFT ALONE** — not `state`, not
   * `error`, not `refusal`. Those describe the verdict on an older payload and
   * were already cleared by the edit that triggered this; clearing them again
   * from an effect would wipe a refusal that arrived in between.
   */
  useEffect(() => {
    const patches = new Map<string, Partial<Draft>>();
    const live = new Set<string>();
    const wanted = new Set<string>();

    for (const row of rows) {
      live.add(row.key);
      const askKey = presetAskKeyFor(row);
      let record = seedRecords.current.get(row.key);

      if (record === undefined) {
        /*
         * 🔴 **FIRST SIGHT IS NO LONGER UNCONDITIONALLY A PRIMING PASS — owner's
         * decision, 2026-09-13.** A row {@link seedsAtFirstSight} admits opens
         * with BOTH questions unanswered (`null`), so the branches below run on
         * this very pass and a seeded line takes the same recommendation and the
         * same band a hand-added line takes. Everything else — a saved line, a
         * convert carry-over — is primed at the values it already holds and
         * answered without being seeded, exactly as before. **The reasoning for
         * both halves is at `seedsAtFirstSight`; read it before narrowing or
         * widening this.**
         *
         * ⚠️ **The record is kept in hand rather than re-read from the map.** The
         * branches below MUTATE it, and a second `get()` would be a second object
         * only if the map were rebuilt — it is not, so this is about reading
         * plainly rather than about correctness.
         */
        const unprompted = seedsAtFirstSight(row);
        record = {
          discountFor: unprompted ? null : row.draft.productId,
          discountSeeded: null,
          discountQuantity: unprompted ? null : row.draft.quantity,
          inclusionsFor: unprompted ? null : askKey,
          inclusionsSeeded: null,
          autoFor: null,
          autoArgs: null,
          autoPrimePending: !unprompted,
        };
        seedRecords.current.set(row.key, record);
        if (!unprompted) {
          // Prime the auto-description inputs now if the catalogue can answer.
          const primedProduct = productOfRow(row);
          const primedArgs =
            row.draft.productId === '' || primedProduct !== undefined
              ? autoArgsFor(row, row.draft.discountAmount, null)
              : undefined;
          if (primedArgs !== undefined) {
            record.autoFor = primedArgs === null ? '' : JSON.stringify(primedArgs);
            record.autoArgs = primedArgs;
            record.autoPrimePending = false;
          }
          continue;
        }
      }

      const patch: Partial<Draft> = {};

      if (record.discountFor !== row.draft.productId) {
        const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
        if (productId === null) {
          // The item was cleared. There is nothing to recommend and nothing to wait for.
          record.discountFor = row.draft.productId;
        } else if (
          recommendedDiscounts.has(productId) &&
          /*
           * ⚠️ A PER-UNIT recommendation cannot be seeded until the quantity is a
           * whole count — it is multiplied by it. Until then the product stays
           * undecided and is asked about again on the next render.
           */
          !(
            isPerUnitProduct(productsById.get(productId)) &&
            (recommendedDiscounts.get(productId) ?? null) !== null &&
            perUnitDiscountTotal(recommendedDiscounts.get(productId) ?? null, row.draft.quantity) ===
              null
          )
        ) {
          // `?? null` narrows `undefined` away under the `has()` above. Not a default.
          const raw = recommendedDiscounts.get(productId) ?? null;
          const seed = recommendedDiscountSeed(
            isPerUnitProduct(productsById.get(productId))
              ? perUnitDiscountTotal(raw, row.draft.quantity)
              : raw,
            {
              discountAmount: row.draft.discountAmount,
              discount: row.draft.discount,
              seededDiscountAmount: record.discountSeeded,
            },
          );
          if (seed !== null) {
            /*
             * ⚠️ **A STRING PUTS THE LINE IN AMOUNT MODE**, which is the whole
             * point and is why the module refuses to produce one over a real
             * percentage: `discountAmount` and `discount` are mutually exclusive
             * and both non-zero is a 422. `draft.discount` is deliberately left
             * exactly as it is — `discountWriteOf` resolves which figure the wire
             * carries, and nothing here converts between the two.
             *
             * ⚠️ **AND NOTHING IS SAID ABOUT THAT MODE CHANGE, WHICH IS A MATCH
             * RATHER THAN AN OMISSION — 2026-09-13.** A hand-added row seeded
             * here has never carried a note either: the `SegmentedControl` reads
             * `discountModeOf(discountAmount)`, so the cell VISIBLY says Amount
             * and the figure is in the box beside it, on a draft nobody has sent.
             * The mode-flip note in the Amount cell's `commit` is a different
             * case and must not be copied here — it reports that the grid chose a
             * mode CONTRARY to one the reader had deliberately put the line in,
             * which cannot arise on a seed, because `recommendedDiscountSeed`
             * returns `null` over a real percentage rather than overriding it.
             * Now that a seeded line reaches this on a row the person has not yet
             * looked at, the check was re-put and the answer is the same one.
             */
            patch.discountAmount = seed;
            record.discountSeeded = seed;
          }
          record.discountFor = row.draft.productId;
          record.discountQuantity = row.draft.quantity;
        }
        // Otherwise the catalogue has not answered for this product yet. Ask again next render.
      } else if (
        record.discountSeeded !== null &&
        record.discountQuantity !== row.draft.quantity &&
        isPerUnitProduct(productOfRow(row))
      ) {
        /*
         * ✅ **A PER-UNIT SEED FOLLOWS THE QUANTITY.** Only while the line still
         * holds exactly what was seeded: `recommendedDiscountSeed` stands down on
         * anything else, so a hand-typed discount is never rescaled. A quantity
         * that is not a whole count yet (mid-typing) leaves the figure alone and
         * is re-asked on the next keystroke.
         */
        const scaled = perUnitDiscountTotal(
          recommendedDiscounts.get(Number(row.draft.productId)) ?? null,
          row.draft.quantity,
        );
        if (scaled !== null) {
          const seed = recommendedDiscountSeed(scaled, {
            discountAmount: row.draft.discountAmount,
            discount: row.draft.discount,
            seededDiscountAmount: record.discountSeeded,
          });
          if (seed !== null) {
            patch.discountAmount = seed;
            record.discountSeeded = seed;
          }
          record.discountQuantity = row.draft.quantity;
        }
      }

      /*
       * ✅ **THE AUTO DESCRIPTION — a product sold per unit (it names a unit).**
       * Filled on product pick and refreshed on a quantity, price or discount
       * change, but ONLY while the cell is still deriving (`null`) or still holds
       * exactly the auto text for the previous inputs. A hand-edited wording is
       * never written over. Moving to a product with no unit hands an untouched
       * auto text back to the derivation.
       */
      {
        const discountNow =
          patch.discountAmount !== undefined ? patch.discountAmount : row.draft.discountAmount;
        const product = productOfRow(row);
        // The catalogue has not answered for this product yet — decide nothing.
        const known = row.draft.productId === '' || product !== undefined;
        const unitProduct = (product?.unitLabel?.trim() ?? '') !== '';
        const seededPerUnit =
          record.discountSeeded !== null &&
          discountNow === record.discountSeeded &&
          isPerUnitProduct(product)
            ? (recommendedDiscounts.get(Number(row.draft.productId)) ?? null)
            : null;
        const args = known ? autoArgsFor(row, discountNow, seededPerUnit) : null;
        /*
         * A unit product with no args is a quantity mid-typing (not a whole
         * count). Wait, keeping the previous inputs, so the auto text is still
         * recognised once the box settles on a real number.
         */
        if (known && !(unitProduct && args === null)) {
          const argsKey = args === null ? '' : JSON.stringify(args);
          if (record.autoPrimePending) {
            record.autoFor = argsKey;
            record.autoArgs = args;
            record.autoPrimePending = false;
          } else if (record.autoFor !== argsKey) {
            const current = row.draft.description;
            const stillAuto =
              current === null ||
              (record.autoArgs !== null && isAutoDescription(current, record.autoArgs));
            if (stillAuto) {
              if (args !== null) {
                const text = autoLineDescription(args);
                if (current !== text) patch.description = text;
              } else if (current !== null) {
                patch.description = null;
              }
            }
            record.autoFor = argsKey;
            record.autoArgs = args;
          }
        }
      }

      if (record.inclusionsFor !== askKey) {
        if (askKey === '') {
          // No product, no usable quantity, or no block on screen — nothing to ask.
          record.inclusionsFor = askKey;
        } else {
          const resolved = inclusionPresets.get(askKey);
          if (resolved !== undefined) {
            const seed = inclusionPresetSeed(
              resolved,
              presetShapeOf(row.draft.inclusions),
              record.inclusionsSeeded,
            );
            if (seed !== null) {
              patch.inclusions = inclusionDraftsOf(seed);
              record.inclusionsSeeded = seed;
            }
            record.inclusionsFor = askKey;
          } else {
            /*
             * The band has not been read yet. Asking for it here — rather than
             * from a key built out of every row — is what keeps the request count
             * to the changes somebody actually made. A read that FAILED also
             * lands here for ever and is never re-asked: the pair stays in the
             * hook's `requested` set, so this is a standing want that issues no
             * further traffic, and the line simply keeps what it holds.
             */
            wanted.add(askKey);
          }
        }
      }

      if (Object.keys(patch).length > 0) patches.set(row.key, patch);
    }

    /* A deleted row's record would otherwise outlive it for the life of the grid. */
    for (const key of seedRecords.current.keys()) {
      if (!live.has(key)) seedRecords.current.delete(key);
    }

    /*
     * ⚠️ **Compared before it is set, or this effect re-runs itself for ever.**
     * A fresh Set each pass would be a new value every render; the joined string
     * is what makes "the same wants" recognisable as the same wants.
     */
    const nextWanted = [...wanted].sort().join(',');
    if (nextWanted !== wantedPresetKey) setWantedPresetKey(nextWanted);

    if (patches.size === 0) return;
    setRows((current) =>
      current.map((row) => {
        const patch = patches.get(row.key);
        return patch === undefined ? row : { ...row, draft: { ...row.draft, ...patch } };
      }),
    );
  }, [
    rows,
    presetAskKeyFor,
    recommendedDiscounts,
    inclusionPresets,
    wantedPresetKey,
    productsById,
    productOfRow,
    autoArgsFor,
  ]);

  const previewByKey = useMemo(() => {
    const map = new Map<string, GeneratedLinePreview | null>();
    for (const row of rows) {
      map.set(
        row.key,
        computeGeneratedLinePreview(
          previewInputOf(
            row,
            catalogueFor(row).price,
            companySalesTax,
            repricedFor(row),
            pricedDiscountFor(row),
          ),
        ),
      );
    }
    return map;
    /*
     * ⚠️ **`companySalesTax` is a dependency because a new row's preview READS
     * it**, and a company changed under an open form must move the Amount cell
     * with the Tax rate label. It is expected to be a STABLE identity — both
     * parents hand over a memoised object — so this rebuilds when the company
     * read lands, not on every keystroke in their header fields.
     */
  }, [rows, catalogueFor, repricedFor, pricedDiscountFor, companySalesTax]);

  /**
   * Every CONVERT-MODE row's boxes and structure, the seed it is judged against,
   * and whether it has moved — resolved once, and read by the validator, the
   * dirty test and the payload alike.
   *
   * ⚠️ **EVERY row in convert mode, not only the ones seeded from a deal line.**
   * *SUPERSEDED: "Every CONVERTED row's … `if (row.convert === null) continue;`".*
   * A row added on this screen has no `convert` line and IS an entry in the
   * `lineSet` about to be sent, so skipping it would leave the one kind of row
   * that can be sent with no product entirely unjudged. `seed === null` is what
   * marks it as new, and it is the same discriminator
   * {@link convertLineSetEntryOf} reads.
   *
   * ⚠️ **One change test, used everywhere.** `convertLineShapesMatch` against a
   * fresh seed is the same question `convertLineSetEntryOf` asks when it decides
   * which keys to emit, so a row cannot read as unchanged on screen while a key
   * for it is on the wire. A second rule for "has this moved" is exactly how
   * those two come apart.
   *
   * ⚠️ **The seed is built with THIS row's `offersInclusions` / `offersRegions`,
   * not with the deal line's own.** Both sides of the comparison have to agree
   * about which controls are on screen, or changing a row's product — which is
   * what makes those two flags move — would emit a clear of a set nobody can
   * see.
   *
   * Empty in the other two modes, which is what makes every branch below fall
   * through to the behaviour it has always had.
   */
  const convertShapes = useMemo(() => {
    const map = new Map<
      string,
      { shape: ConvertLineShape; seed: ConvertLineShape | null; changed: boolean }
    >();
    if (!convertMode) return map;
    for (const row of rows) {
      const offersInclusions = inclusionsFor(row.key).offers;
      const offersRegions = showsRegions(row);
      const typedShape = convertShapeOf(row.draft, offersInclusions, offersRegions);
      /*
       * Project pricing (owner 2026-10-27): the price an UNTOUCHED row follows
       * is sent as `unitPrice` — the reprice of a carried-over deal line whose
       * selection moved, or the inherited price of a row added here.
       */
      const repriced = row.convert === null ? undefined : repricedFor(row);
      const inherited =
        row.convert === null && row.draft.priceMode === 'inherit' ? catalogueFor(row) : INHERITED_UNKNOWN;
      const shape: ConvertLineShape =
        typeof repriced === 'string'
          ? { ...typedShape, unitPrice: repriced }
          : inherited.known && inherited.price !== null
            ? { ...typedShape, unitPrice: inherited.price }
            : typedShape;
      const seed =
        row.convert === null
          ? null
          : convertShapeOf(convertRowOf(row.convert).draft, offersInclusions, offersRegions);
      map.set(row.key, {
        shape,
        seed,
        /* A row that was never on the deal has nothing to be unchanged from. */
        changed: seed === null || !convertLineShapesMatch(shape, seed),
      });
    }
    return map;
  }, [convertMode, rows, inclusionsFor, showsRegions, repricedFor, catalogueFor]);

    const localProblems = useMemo(() => {
    const map = new Map<string, RowProblems>();
    for (const row of rows) {
      /*
       * A new quote opens with one blank row. Left untouched it is not judged,
       * so it neither blocks the create nor shows "Choose a product" before
       * anybody has started; the draft payload already skips a row with no product.
       */
      if (draftMode && row.saved === null && isUntouchedBlankDraft(row.draft)) {
        map.set(row.key, { fieldErrors: {}, error: null, notice: null });
        continue;
      }
      /*
       * ⚠️ **A CONVERT ROW THAT HAS MOVED IS JUDGED BY `validateRow` NOW, AND AN
       * UNTOUCHED ONE IS STILL NOT JUDGED AT ALL.**
       *
       * *SUPERSEDED: "A CONVERTED ROW IS JUDGED BY THE OVERRIDE CONTRACT, NOT BY
       * `validateRow` … `validateRow` checks the whole quote-line shape — the
       * product, the project, the city set against what the project declares,
       * the tax percentage — and none of that can be overridden here, so a
       * legacy deal line whose project has since stopped visiting one of its
       * cities would disable Convert over a field this screen does not offer to
       * change."*
       *
       * **The first half died with the endpoint.** `lineSet` carries the
       * product, the project, the cities, the regions and the inclusions, so all
       * of that IS offered to change now — and a row whose city set its project
       * no longer declares is a row the server will refuse. Judging it by the
       * five-figure validator would be the "when in doubt, ENABLE" failure in
       * the other direction: a Convert button that stays live and produces a 422
       * naming a control the grid never checked.
       *
       * **The second half survives unchanged, and it is why the skip is on
       * `changed` rather than on the mode.** An untouched carry-over row emits
       * `{dealLineId, sortOrder}` and nothing else, so not one of its stored
       * figures reaches the request and none of them can be refused — checking
       * them anyway is a control refusing to run over a figure the person did
       * not choose. A row somebody ADDED has no seed, so it is always `changed`
       * and always judged, which is what tells them to choose a product on the
       * field rather than through a 422 at `lineSet.3.productId`.
       *
       * ⚠️ The tax percentage `validateRow` checks is `''` on every convert row
       * — the Tax cell is a readout and nothing writes `draft.taxRate` here — so
       * that branch is inert rather than lenient.
       */
      const converted = convertShapes.get(row.key);
      if (converted !== undefined && !converted.changed) {
        map.set(row.key, { fieldErrors: {}, error: null, notice: null });
        continue;
      }
      const problems = validateRow(
        row,
        duplicatePairs,
        retiredInclusionIds,
        rowProjects.get(row.key)?.declaredCityIds ?? null,
        // The same verdict the payload builders take, so a hidden set can
        // never raise an error against a control that is not on screen.
        inclusionsFor(row.key).offers,
      );
      /*
       * ✅ **THE QUANTITY STEP** (`lib/product-units.ts`). Under its own key so the
       * Qty box can show it on blur rather than mid-keystroke; it blocks the row
       * either way. A whole-number refusal already on `quantity` outranks it.
       */
      const stepProblem = quantityStepProblemOf(row, productOfRow(row));
      if (stepProblem !== null && problems.fieldErrors.quantity === undefined) {
        problems.fieldErrors[QUANTITY_STEP_ERROR_KEY] = stepProblem;
      }
      /*
       * ⚠️ **NOTHING IS INJECTED HERE ANY MORE.** A block sat at this point
       * folding the amount box's conversion refusal into the row's problems,
       * because the amount was not on the draft and `validateRow` — pure over
       * the draft — could not see it. `discountAmount` IS on the draft now, so
       * `validateRow` checks it directly and this hand-off is gone with the
       * conversion that needed it.
       */
      map.set(row.key, problems);
    }
    return map;
  }, [
    draftMode,
    rows,
    convertShapes,
    duplicatePairs,
    retiredInclusionIds,
    rowProjects,
    inclusionsFor,
    productOfRow,
  ]);

  const problemsByKey = useMemo(() => {
    const map = new Map<string, RowProblems>();
    for (const row of rows) {
      const local = localProblems.get(row.key) ?? { fieldErrors: {}, error: null, notice: null };
      /*
       * ⚠️ **A convert row's server refusals arrive from the PARENT, keyed by
       * this grid's ROW KEY**, because the array index a 422 names is a position
       * in the `lineSet` that was sent and only the parent holds that array —
       * along with the `rowKeys` array emitted beside it. They are merged the
       * same way and for the same reason as `row.fieldErrors`: the server's word
       * is about the request that was actually refused.
       *
       * ⚠️ *SUPERSEDED: "keyed by `dealLineId` … `convertLineErrors[row.convert.id]`".*
       * A row ADDED on this screen has no `dealLineId`, so an id-keyed map could
       * not address the one kind of row most likely to be refused. The condition
       * is the MODE now, not `row.convert !== null`.
       */
      const fromServer = convertMode ? (convertLineErrors[row.key] ?? {}) : row.fieldErrors;
      /*
       * A field error the SERVER returned outranks the local one: it is about
       * the request that was actually refused, and the project/city refusals in
       * particular say something no client-side check can know —
       * whether the PRODUCT is sold through projects at all is a fact only the
       * server holds, and it decides which of two sentences a project-less line
       * with cities gets.
       */
      map.set(row.key, {
        fieldErrors: { ...local.fieldErrors, ...fromServer },
        error: row.error ?? local.error,
        /*
         * ⚠️ Carried through UNMERGED and never coalesced with `error`. Both can
         * be true at once — a row can be refused by the server AND repeat a
         * product — and an `error ?? notice` would drop the notice exactly when
         * the row is under the most scrutiny.
         */
        notice: local.notice,
      });
    }
    return map;
  }, [rows, localProblems, convertLineErrors, convertMode]);

  const dirtyKeys = useMemo(() => {
    const set = new Set<string>();
    /*
     * ⚠️ `isDirty` asks `updatePayload` the same question with the same flag, so
     * a row whose only difference is a hidden inclusions set is NOT dirty — it
     * would otherwise offer a Save button and then send a body with no keys in
     * it, which is a 422.
     */
    for (const row of rows) {
      /*
       * ⚠️ **`isDirty` answers `true` for every row whose `saved` is `null`, so
       * a convert row must never reach it.** Every carried-over row would read as
       * dirty for ever — "Edited" on the badge, "Recalculated on conversion" in
       * the Amount column, and a blocked count over rows nobody has touched.
       *
       * A row ADDED here is `changed: true` by construction (it has no seed), so
       * it lands in this set the way a draft row does and is judged and counted
       * the same way.
       */
      const converted = convertShapes.get(row.key);
      if (converted !== undefined) {
        if (converted.changed) set.add(row.key);
        continue;
      }
      if (isDirty(row, inclusionsFor(row.key).offers, showsRegions(row), repriceFor(row))) {
        set.add(row.key);
      }
    }
    return set;
  }, [rows, convertShapes, inclusionsFor, showsRegions, repriceFor]);

  /*
   * Rows that cannot be sent at all. Only the LOCAL verdict counts here — a
   * server error from the previous run must not block a re-send, since the
   * user has presumably just changed the thing that was refused.
   */
  const blockedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const key of dirtyKeys) {
      const local = localProblems.get(key);
      if (!local) continue;
      if (Object.keys(local.fieldErrors).length > 0 || local.error !== null) set.add(key);
    }
    return set;
  }, [dirtyKeys, localProblems]);

  /* ── Draft mode: what goes upward ─────────────────────────────────────── */

  /**
   * The rows as POST bodies, in grid order. `null` in saved mode.
   *
   * ⚠️ **Blocked rows are WITHHELD, not emitted with their faults.** The parent
   * creates the quote first and writes these afterwards, so a line the server
   * would refuse becomes a 422 arriving after a real document exists — against
   * a row the person can no longer see, on a screen that has already closed.
   * The count of what is being held back goes up separately, so the parent
   * refuses to submit instead.
   *
   * `sortOrder` is the row's index in the WHOLE grid, gaps included: it is a
   * display order, not a sequence, and keeping the grid's own positions means
   * the document prints in the order the person arranged.
   */
  const draftLines = useMemo(() => {
    if (!draftMode) return null;
    const lines: QuoteProductCreateInput[] = [];
    rows.forEach((row, index) => {
      if (row.draft.productId === '' || blockedKeys.has(row.key)) return;
      // The same builder the saved POST uses. There is no second one.
      lines.push(
        createPayload(
          row.draft,
          index,
          inclusionsFor(row.key).offers,
          showsRegions(row),
          catalogueFor(row),
        ),
      );
    });
    return lines;
  }, [draftMode, rows, blockedKeys, inclusionsFor, showsRegions, catalogueFor]);

  const draftBlockedCount = draftMode ? blockedKeys.size : 0;

  /*
   * The callbacks live in refs so the emission depends on the CONTENT and never
   * on the parent's render. An inline arrow — which is what a modal will pass —
   * has a new identity every render, and in the deps it would re-emit on every
   * keystroke in the quote's own header fields.
   */
  const emitLines = useRef(onDraftLinesChange);
  const emitBlocked = useRef(onDraftBlockedChange);
  useEffect(() => {
    emitLines.current = onDraftLinesChange;
    emitBlocked.current = onDraftBlockedChange;
  });

  useEffect(() => {
    if (draftLines === null) return;
    emitLines.current?.(draftLines);
  }, [draftLines]);

  useEffect(() => {
    if (!draftMode) return;
    emitBlocked.current?.(draftBlockedCount);
  }, [draftMode, draftBlockedCount]);

  /* ── Convert mode: what goes upward ───────────────────────────────────── */

  /**
   * The lines as they stand — **exactly the `lineSet` array
   * `POST /deals/{id}/convert` takes** — in GRID ORDER, with the row key each
   * entry came from and whether the set is untouched.
   *
   * ⚠️ **CORRECTED — IT ITERATES THE ROWS NOW, AND IT HAS TO.** *SUPERSEDED:
   * "`convertLineOverrides` is the ONE builder and it iterates the LINES, so the
   * array's index is a position the server's `lines.<n>.<field>` can be routed
   * back through. Building it off `rows` instead would look identical and would
   * silently mark the wrong row the first time the two orders differed."* That
   * was right for `lines`, whose entries were a SUBSET of the deal's own lines
   * in the deal's own order. `lineSet` is the FINAL list: rows can be added,
   * removed and reordered relative to the deal, so the deal's array can no
   * longer describe it at all. The index is still the routing key — it is now a
   * position in `rows`, and `rowKeys` is what carries that fact upward so the
   * parent never has to reconstruct it.
   *
   * ⚠️ **`entries` and `rowKeys` are built in ONE pass and pushed together.**
   * Two loops would agree the day they were written; one loop makes
   * `entries[i]` and `rowKeys[i]` the same row by construction.
   *
   * ⚠️ **`null` until the deal's lines are read**, and never `[]`. An empty
   * `lineSet` is a quote with NO LINES — a real instruction — so a grid that has
   * not loaded must be unable to say anything rather than able to say *none*.
   *
   * ⚠️ **Blocked rows are INCLUDED, unlike draft mode's.** A row that is refused
   * does not get dropped on the floor, it takes the whole conversion with it —
   * no quote at all rather than a quote missing a line — so the answer is to
   * refuse to submit, which the blocked count below is for. Withholding it would
   * convert without a line somebody had added.
   */
  const convertLineSet = useMemo(() => {
    if (!convertMode || dealLines === null) return null;
    const entries: DealConvertLineSetEntry[] = [];
    const rowKeys: string[] = [];
    rows.forEach((row, index) => {
      const resolved = convertShapes.get(row.key);
      if (resolved === undefined) return;
      const entry: DealConvertLineSetEntry = convertLineSetEntryOf(
        resolved.shape,
        resolved.seed,
        row.convert?.id ?? null,
        index,
      );
      /*
       * ⚠️ **The internal note rides OUTSIDE the shape, deliberately.** The
       * server copies a carried-over line's note by itself, so the key goes out
       * only when it MOVED (or on a new row that has one) — and a note-only edit
       * must not mark the row changed, which would put an untouched legacy line
       * through `validateRow`. An entry carrying `notes` is no longer a pure
       * carry-over, so `pristine` below goes false and the `lineSet` is sent.
       */
      const notes = convertLineNotesWrite(
        row.draft.notes,
        row.convert === null ? null : row.convert.notes,
      );
      if (notes !== undefined) entry.notes = notes;
      entries.push(entry);
      rowKeys.push(row.key);
    });
    /*
     * ⚠️ **PRISTINE IS READ OFF THE BUILT ENTRIES, plus the one thing they
     * cannot say: that no row was REMOVED.** An entry is a carry-over when it
     * holds nothing but its `dealLineId` and its position — but a grid holding
     * two untouched rows out of the deal's three produces two carry-over entries
     * and is emphatically not pristine, and sending no `lineSet` for it would
     * silently restore the line somebody deleted. Hence the length check, and it
     * is against the DEAL's own array rather than against the row count, which
     * is the same number by definition.
     */
    const pristine =
      entries.length === dealLines.length && entries.every(convertLineSetEntryIsCarryOver);
    return { entries, rowKeys, pristine };
  }, [convertMode, rows, convertShapes, dealLines]);

  const convertBlockedCount = convertMode ? blockedKeys.size : 0;

  /*
   * Refs for the same reason the draft emitters use them: the emission depends
   * on the CONTENT and never on the parent's render identity, so an inline arrow
   * in the modal cannot re-emit on every keystroke in the quote's header.
   */
  const emitConvertLines = useRef(onConvertLinesChange);
  const emitConvertBlocked = useRef(onConvertBlockedChange);
  useEffect(() => {
    emitConvertLines.current = onConvertLinesChange;
    emitConvertBlocked.current = onConvertBlockedChange;
  });

  /*
   * ⚠️ **A `null` IS EMITTED, where the old array-only version returned early.**
   * *SUPERSEDED: `if (convertOverrides === null) return;`.* That was safe when
   * `null` only ever meant "not convert mode"; it now ALSO means "the deal's
   * lines have not been read", and a parent left holding the previous value
   * would keep a `lineSet` from a deal it is no longer looking at. The guard is
   * therefore on the MODE, and the null travels.
   */
  useEffect(() => {
    if (!convertMode) return;
    emitConvertLines.current?.(convertLineSet);
  }, [convertMode, convertLineSet]);

  useEffect(() => {
    if (!convertMode) return;
    emitConvertBlocked.current?.(convertBlockedCount);
  }, [convertMode, convertBlockedCount]);

  /*
   * The document total sums the SERVER's own `lineTotal` values and nothing
   * else. The database rounds per line, so rounding once at the end gives a
   * different answer — and the database's is what Xero reads.
   */
  const savedTotal = useMemo(
    () => sumDecimalStrings(rows.map((row) => row.saved?.lineTotal ?? null)),
    [rows],
  );

  /**
   * The draft footer total — the sum of the PREVIEWS, and `null` in saved mode.
   *
   * ⚠️ **This is our arithmetic, not the server's, and the footer says so.**
   * There is no stored figure to sum in draft mode: `lineTotal` is a generated
   * column and no line exists yet. Summing the per-line previews is the same
   * operation `savedTotal` performs on the server's own values — each line
   * rounded first, then added — so it will agree with what the database stores,
   * but it is still a preview and is never labelled as a total.
   *
   * A row with no price contributes nothing rather than zero: its preview is
   * `null`, which is "no figure", not "0.00".
   */
  const draftPreviewTotal = useMemo(
    () =>
      draftMode
        ? sumDecimalStrings(rows.map((row) => previewByKey.get(row.key)?.lineTotal ?? null))
        : null,
    [draftMode, rows, previewByKey],
  );

  const readOnly = !canEdit;
  /**
   * Whether ROWS may be added or removed at all.
   *
   * ⚠️ **CORRECTED — TRUE IN CONVERT MODE NOW, AND THE ENDPOINT STILL DECIDES
   * IT.** *SUPERSEDED: "`false` in convert mode, and the ENDPOINT decides it
   * rather than taste. An override on `POST /deals/{id}/convert` names an
   * EXISTING deal line by its `dealLineId`; there is no key that adds a line and
   * none that removes one. An Add row control here would build a row nothing
   * could carry, and a Delete control would offer an action that cannot be
   * performed … ⚠️ It is NOT `readOnly`. The five editable figures stay live; it
   * is the row SET that is fixed."*
   *
   * **Every clause of that was a true statement about `lines`.** `lineSet` is
   * the complete final list: an entry with no `dealLineId` IS an added line, and
   * a row simply left out of the array is a removed one. So both controls now
   * have a request to make, and the reasoning is unchanged — it is still the
   * endpoint deciding, and the answer it gives has changed.
   *
   * ⚠️ **`convertLocked` still forces it false**, and that half is not
   * cosmetic: once the conversion has landed the quote exists, a retried Save
   * re-sends the HEADER alone, and an Add row control there would take a line
   * somebody typed and silently drop it.
   */
  const canManageRows = !readOnly && !convertLocked;
  const dirtyCount = dirtyKeys.size;

  /*
   * ── TELLING THE PARENT ────────────────────────────────────────────────────
   *
   * Both effects are inert in draft and convert mode: neither prop exists
   * there, so `undefined?.()` is the whole of it and no branch is needed.
   *
   * ⚠️ **Placed here rather than beside the render, because THREE early returns
   * sit between this point and the JSX** — loading, a failed deal read, a failed
   * line read. A hook below any of them would run on some renders and not
   * others, which React refuses; and a grid that stopped reporting the moment it
   * hit a loading state would hand the parent a stale count of unsaved rows and
   * let it close over them.
   */
  const emitUnsaved = useRef(onUnsavedChange);
  useEffect(() => {
    emitUnsaved.current = onUnsavedChange;
  });
  useEffect(() => {
    emitUnsaved.current?.({ dirty: dirtyCount, blocked: blockedKeys.size });
  }, [dirtyCount, blockedKeys.size]);
  /*
   * ⚠️ **REFRESHED ON EVERY RENDER, deliberately, and this is the whole reason
   * the ref exists.** `saveAll` closes over `rows`, `dirtyKeys` and
   * `blockedKeys` as they were at the render that made it. Handing the parent
   * the function itself once would freeze the row set at first mount, so a save
   * pressed from up there would write whatever was on screen when the modal
   * opened — silently, with a 200 per row.
   */
  const latestSaveAll = useRef(saveAll);
  useEffect(() => {
    latestSaveAll.current = saveAll;
  });
  useEffect(() => {
    if (registerSave === undefined) return;
    registerSave(() => latestSaveAll.current());
    /*
     * ⚠️ Cleared on unmount, so a parent that outlives this grid cannot call a
     * save into a component that is gone. The parent must treat `null` as "no
     * grid", never as "nothing to save".
     */
    return () => registerSave(null);
  }, [registerSave]);

  /**
   * What Xero will print for this line **while `description` is null**.
   *
   * ⚠️ **This is the DERIVATION, not the cell, and the join is NOT ours.**
   * `composeLineDescription` is the one pinned copy of it, and the backend
   * composes the same string independently at push time — so a joiner written
   * here instead would let a line read one way in EMERGE and another on the
   * customer's Xero document, silently, on every line that has not been typed
   * on. That is the common case. See the import.
   *
   * Once `description` holds a string this stops being what Xero prints: the
   * stored wording goes verbatim and nothing is appended. The derivation is then
   * only what `Reset to derived` would put back.
   *
   * ⚠️ **EVERY city, joined — one EMERGE line is one Xero line however many it
   * names.** The line's single price rides on that one Xero line, which is what
   * makes a quote and the invoice raised from it reconcile line for line.
   * Rendering the first city only would show a description the customer's copy
   * does not have.
   *
   * A city id with no name we can honestly resolve is DROPPED rather than
   * printed as a number or an "Unknown" — this string is a preview of a customer
   * document, and a placeholder in it reads as something the customer will see.
   */
  function descriptionOf(row: Row): string | null {
    const productId = row.draft.productId === '' ? null : Number(row.draft.productId);
    const productName =
      productId === null
        ? null
        : (productNames.get(productId) ?? sourceLineOf(row)?.product.productName ?? null);
    if (productName === null) return null;
    /*
     * ⚠️ **A flagged line's cities are RESOLVED, and the description still
     * lists them BY NAME.** There is no "all" vocabulary in a line description
     * anywhere and there must not be: the customer reads where the work
     * happens, and a document from March has to keep reading as what was agreed
     * in March. The backend composes the same string from the same resolved set
     * at push time, so deriving from this row's (deliberately empty) explicit
     * set would show one description here and print another on the customer's
     * Xero quote — silently, on every flagged line.
     *
     * The project is the live source; a saved flagged line's own `cities` is
     * the same set resolved a moment earlier and stands in when the project
     * cannot be resolved. A name that cannot be resolved honestly is DROPPED
     * rather than printed as a number, exactly as in the explicit branch.
     */
    const named = row.draft.allCities
      ? (rowProjects.get(row.key)?.projectCities?.map((city) => city.name) ??
        (row.saved?.allCities === true ? row.saved.cities.map((city) => city.name) : []))
      : row.draft.cityIds
          .map((id) => cityNames.get(id))
          .filter((name): name is string => name !== undefined);
    /*
     * The line's own project name, or null — resolved the same way the product
     * name is, and DROPPED rather than guessed when it cannot be resolved
     * honestly. The composer falls back to the product name for a null, so an
     * unresolvable project degrades to today's wording rather than printing a
     * placeholder into a preview of a customer's document.
     */
    const projectId = row.draft.projectId === '' ? null : Number(row.draft.projectId);
    const projectName = projectId === null ? null : (projectNames.get(projectId) ?? null);
    /*
     * The pinned composer, never a template literal here. It owns the separator,
     * the join, the no-cities fallback AND which name heads the sentence,
     * because the backend's copy does too.
     *
     * ⚠️ Named arguments, not positional: `productName`, `projectName` and
     * `productCode` are all strings, so a positional call is a silent swap
     * waiting to happen — and a swap here would print the wrong name onto a
     * customer's Xero document with nothing failing.
     *
     * `productCode` is passed because it is OPTIONAL on the composer: omitting
     * it reads as "no item code", which holds the substitution guard shut for
     * ever. That was a dormant no-op while the switch was off; the switch is
     * `true` now, so it would instead print the product name here and the
     * project name on the customer's document. See {@link productCodes}.
     */
    const productCode = productId === null ? null : (productCodes.get(productId) ?? null);
    /*
     * The line's inclusions, in the DRAFT's own order — which is the order that
     * becomes each entry's `sortOrder` on save, so it is the order the customer's
     * document prints in. Never re-sorted here; the composer's own comment says
     * why, and an order-only divergence is the one nobody reports.
     *
     * An id we cannot name is DROPPED, the same rule the cities follow two
     * statements up: this string previews a customer document, and a number or
     * an "Unknown" in it reads as something the customer will see.
     *
     * The quantity box holds a STRING and `''` means "omit the key", which the
     * server stores as 1 — `Number('')` is `0`, which would print `0 campuses`
     * for a line that will carry one. `effectiveInclusionQuantity` is that rule.
     *
     * ⚠️ `LINE_DESCRIPTION_INCLUDES_INCLUSIONS` is `true`, so this **is** on the
     * customer's document. *SUPERSEDED: "and it is `false` — so this changes no
     * rendered character today."* See {@link inclusionMasters}.
     */
    const lineInclusions = row.draft.inclusions
      .map((entry) => {
        const master = inclusionMasters.get(entry.inclusionId);
        return master === undefined
          ? null
          : { ...master, quantity: effectiveInclusionQuantity(entry.quantity) };
      })
      .filter((entry): entry is LineDescriptionInclusion => entry !== null);
    /*
     * The line's own regions, resolved to names in the DRAFT's order.
     *
     * ⚠️ **Consumed only on a line with NO cities** — `geographySegment` is
     * cities-else-regions, an EITHER, so on a project-backed row this array is
     * built and never printed. It is passed unconditionally anyway because the
     * member is OPTIONAL: omitting it reads as "this line names no regions",
     * which on a projectless line shows a bare product name here while the
     * customer's Xero quote carries the geography. That was the live divergence
     * this argument closed.
     *
     * An id we cannot name is DROPPED, the same rule the cities and the
     * inclusions follow: this string previews a customer document, and a bare
     * number in it reads as something the customer will see.
     */
    const namedRegions = row.draft.regionIds
      .map((id) => regionNames.get(id))
      .filter((name): name is string => name !== undefined);
    return composeLineDescription({
      productName,
      projectName,
      productCode,
      cityNames: named,
      regionNames: namedRegions,
      inclusions: lineInclusions,
    });
  }

  /**
   * The products this row may choose — **the whole catalogue**.
   *
   * ⚠️ **Deliberately excludes nothing** — see "One line per product PER
   * PROJECT" in this file's header for why excluding taken products deadlocks
   * the project-first path. The clash is caught across rows instead, at row
   * level, in the server's own words.
   *
   * The row's own stored product is injected back when it is not in the active
   * catalogue, so a line on a retired product renders itself rather than blank.
   */
  function productOptionsFor(row: Row): ComboboxOption[] {
    const list = products.rows.map((product) => ({
      value: String(product.id),
      label: product.productName,
      ...(product.productCode ? { hint: product.productCode } : {}),
    }));
    const saved = sourceLineOf(row);
    if (saved !== null && !list.some((option) => option.value === String(saved.product.id))) {
      list.unshift({
        value: String(saved.product.id),
        label: saved.product.productName,
        ...(saved.product.productCode ? { hint: saved.product.productCode } : {}),
      });
    }
    /*
     * ⚠️ **THE SAME PIN FOR A SEEDED ROW, which has no `sourceLineOf` to pin
     * from.** A planned product retired since the plan was written is absent
     * from the active catalogue, and an unmatched value renders as an EMPTY Item
     * cell over a real `productId` — a line POSTed naming a product nobody was
     * shown. See `seededProducts`.
     *
     * Guarded on `!list.some` like the branch above, so a planned product that
     * is still active is not listed twice; and keyed on the row's CURRENT
     * product, so a row switched to another item stops matching and gets no pin.
     */
    const seeded = seededProducts.get(Number(row.draft.productId));
    if (
      row.draft.productId !== '' &&
      seeded !== undefined &&
      !list.some((option) => option.value === row.draft.productId)
    ) {
      list.unshift({
        value: row.draft.productId,
        label: seeded.productName,
        ...(seeded.productCode ? { hint: seeded.productCode } : {}),
      });
    }
    return list;
  }

  /**
   * Options for one row's region multiselect.
   *
   * ⚠️ **A region the LINE holds that the master no longer offers is pinned
   * back in, under the name the READ gave us.** Reads carry no `is_active`
   * filter, so the line still resolves a retired region perfectly; dropping it
   * from the options would render a bare id AND — far worse — silently remove it
   * from the set the next save writes, because `regionIds` is a full REPLACE.
   * A value nobody touched, lost with a 200.
   *
   * ⚠️ **Only CALLED retired once the master has settled.** An in-flight or
   * failed fetch has empty `records`, where every stored region looks absent and
   * none of them is actually retired — so the hint is withheld rather than
   * asserted, exactly as the deals modal does it.
   */
  function regionOptionsFor(row: Row, resolved: RowProject): MultiComboboxOption[] {
    const settled = !regions.loading && !regions.failed;
    /*
     * ⚠️ **Two sources, and which one is in play is the owner's decision.** A
     * row whose project resolved offers THAT PROJECT'S regions — the line stays
     * inside the geography the project actually covers. A row with no project,
     * or one whose project could not be read, keeps the `/regions` master,
     * because there is no set to narrow against and narrowing on an unknown is
     * how a stored value silently leaves a whole-set REPLACE.
     */
    const narrowed = resolved.projectRegions !== null;
    const source = narrowed
      ? (resolved.projectRegions ?? [])
      : regions.records.map((record) => ({ id: record.id, name: record.name }));

    const list: MultiComboboxOption[] = source.map((region) => ({
      value: String(region.id),
      label: region.name,
    }));

    /*
     * Names, master first and the line's own read LAST so it wins. Reads carry
     * no `is_active` filter, so the name the line was GIVEN is the right one for
     * a region the master has since retired — looking it up in an active-only
     * list and blanking it is the failure this whole block exists to prevent.
     */
    const nameOf = new Map<number, string>();
    for (const record of regions.records) nameOf.set(record.id, record.name);
    for (const region of sourceLineOf(row)?.regions ?? []) nameOf.set(region.id, region.name);

    /*
     * ⚠️ **Everything the ROW HOLDS is pinned back in — draft and saved both.**
     * `regionIds` is a full REPLACE, so an id missing from these options is not
     * merely unlabelled: the next save writes the set without it, and a value
     * nobody touched is gone with a `200`. That was already true of a region the
     * master retired; narrowing to a project makes it true of every region the
     * project does not declare as well, which is a far more reachable state.
     *
     * The saved set is included alongside the draft because a person can remove
     * a chip and put it back, and the option has to survive the round trip.
     */
    const held = new Set<number>(row.draft.regionIds);
    for (const region of sourceLineOf(row)?.regions ?? []) held.add(region.id);
    for (const id of held) {
      if (list.some((option) => option.value === String(id))) continue;
      /*
       * ⚠️ Only ever CALLED retired once the master has settled. An in-flight or
       * failed fetch has empty `records`, where every region looks absent and
       * none of them is actually retired. Under a narrowed picker the honest
       * fallback is the fact we do know for certain — the resolved project does
       * not declare it — which holds whether or not the master ever came back.
       */
      const retired = settled && !nameOf.has(id);
      const hint = retired ? 'No longer offered' : narrowed ? 'Not on this project' : undefined;
      list.push({
        value: String(id),
        /*
         * A bare id rather than dropping the option. An unnameable region is a
         * visible oddity somebody reports; a missing one is a silent deletion on
         * the next save.
         */
        label: nameOf.get(id) ?? `Region #${id}`,
        ...(hint ? { hint } : {}),
      });
    }
    return list;
  }

    /**
   * Switching the Disc box between a percentage and a money amount.
   *
   * ⚠️ **NEITHER DIRECTION CONVERTS ANYTHING, AND NEITHER MAY.** The switch
   * moves `draft.discountAmount` between `null` and `''` and leaves the other
   * box exactly as it was, so flipping back and forth is free and loses
   * nothing. Pre-filling the amount from the percentage — which is what this
   * used to do — is the lossy conversion in the other direction, and the second
   * column exists so that neither has to happen.
   *
   * ⚠️ **A switch on its own is not a save.** Both boxes still hold what they
   * held; `discountWriteOf` resolves which one the wire carries, and
   * `updatePayload` emits nothing until a figure actually moves.
   */
  function setDiscountMode(key: string, toAmount: boolean) {
    patchRow(key, { discountAmount: toAmount ? '' : null });
  }

  function patchRow(key: string, patch: Partial<Draft>) {
    setSummary(null);
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        /*
         * ⚠️ Changing the PRODUCT invalidates the project, and the project
         * carries the cities with it. A project sells exactly one product, so a
         * project left behind after a product change is the server's
         * wrong-product refusal ("This project sells product X, but this line
         * sells product Y") waiting to fire on a control the person did not
         * touch — ⚠️ this comment called it "the fourth refusal" while three
         * others in this file call the same sentence refusal 5; the sentence is
         * the durable name, the ordinal is not. Cleared
         * here rather than explained later.
         */
        const invalidated =
          patch.productId !== undefined && patch.productId !== row.draft.productId
            ? /*
               * ⚠️ The FLAG goes with them. "All cities" is resolved against
               * the line's project and the project has just been thrown away,
               * so a flag left standing is `validateRow`'s "choose this line's
               * project" firing on a control nobody touched — the same shape as
               * the cities, cleared here rather than explained afterwards.
               */
              { projectId: '', cityIds: [], allCities: false }
            : {};

        /*
         * ⚠️ PROJECT-FIRST: choosing a project on a row with no item sets the
         * item from that project, here, in the same update.
         *
         * A project sells exactly one product, so the two are one fact and not
         * two that have to agree. Left to be reconciled later they can disagree,
         * and a disagreement is refusal 5 — "this project sells product X, but
         * this line sells product Y" — reached by two choices that each looked
         * correct when it was made. Setting it means that state is unreachable
         * rather than explained afterwards.
         *
         * Derived in the reducer rather than in the control so `patch.productId`
         * stays undefined and the invalidation above cannot fire against it and
         * throw away the project that was just chosen. Only ever fills a BLANK
         * item — it never overwrites a choice already made, which is the
         * product-first path and stays exactly as it was.
         *
         * Resolved through `productOfProject()` from `@/lib/use-line-project`,
         * which is the ONE copy of "a project implies its product". It is a
         * pure function over a list, not a hook, so it works here even though
         * this grid deliberately fetches projects once for the whole table
         * rather than per row.
         *
         * ⚠️ It returns `null` for three different reasons and every one of
         * them means *do not set a product*: no project chosen, the project is
         * not in the list (a stale id, a different search term, or past the
         * 100-row cap), or the project's `products` came back empty — which it
         * should not, since the server re-asserts the primary into the set on
         * every write, so an empty one means a server predating migration 156.
         * It never guesses a plausible default — a wrong product here silently
         * changes what the customer is being sold.
         */
        const impliedProduct =
          patch.projectId !== undefined &&
          patch.projectId !== '' &&
          row.draft.productId === '' &&
          patch.productId === undefined
            ? (productOfProject(projects.rows, Number(patch.projectId))?.id ?? null)
            : null;

        /*
         * ✅ **A NEW line picking a product sold in multiples starts at the step**
         * (`defaultQuantityFor`), unless the quantity already in the box is a
         * valid multiple. Never on a stored line: its quantity is the customer's,
         * and the step is only checked once somebody changes it.
         */
        const pickedProductId =
          patch.productId !== undefined && patch.productId !== row.draft.productId
            ? patch.productId
            : impliedProduct !== null
              ? String(impliedProduct)
              : null;
        const pickedProduct =
          pickedProductId === null || pickedProductId === ''
            ? undefined
            : productsById.get(Number(pickedProductId));
        const currentQuantity = patch.quantity ?? row.draft.quantity;
        const steppedQuantity =
          row.saved === null &&
          patch.quantity === undefined &&
          pickedProduct !== undefined &&
          pickedProduct.quantityStep !== null &&
          (currentQuantity.trim() === '' || quantityStepError(pickedProduct, currentQuantity) !== null)
            ? { quantity: defaultQuantityFor(pickedProduct) }
            : {};

        return {
          ...row,
          draft: {
            ...row.draft,
            ...patch,
            ...invalidated,
            ...(impliedProduct !== null ? { productId: String(impliedProduct) } : {}),
            ...steppedQuantity,
          },
          // Editing clears the previous verdict — it was about an older payload.
          state: row.state === 'saving' ? row.state : ('idle' as RowState),
          error: null,
          fieldErrors: {},
          refusal: null,
        };
      }),
    );
  }

  /*
   * ⚠️ **The description goes through `patchRow` now, and it deliberately did
   * NOT before.**
   *
   * The old note here said typing must not clear the row's server verdict —
   * its error, its field errors, its refusal — because nothing typed in that box
   * reached a payload, so clearing a real refusal on a keystroke would hide a
   * failure the person still had to fix. **That reasoning inverted with the
   * column.** Typing is now an edit to the body this row will send, so the
   * verdict on the older payload is genuinely stale and clearing it is the same
   * correct behaviour every other control has. Two functions and a second store
   * went with it.
   *
   * `Reset to derived` is `{ description: null }` — the state, and the payload,
   * are the same word.
   */

  /**
   * Writes a line's INTERNAL note.
   *
   * ⚠️ **A SAVED line is written at once, as a PATCH carrying `notes` and
   * nothing else** — the one body the server lets through every lock (Sent,
   * pushed, billed) and that skips the Xero push, repricing and totals. It is
   * therefore NOT part of the row's draft-and-Save cycle: nothing else on the
   * row is sent with it, and the row's unsaved edits and verdict are left
   * exactly as they were. Only `saved.notes` and `draft.notes` move, so Revert
   * cannot put the old note back on screen.
   *
   * A row with no stored line (a new row, a draft-mode row, a convert row)
   * holds the note in its draft, and the create payload or the `lineSet` entry
   * carries it. Throws on a refusal, so {@link LineNoteField} can show it.
   */
  async function saveLineNote(row: Row, next: string | null): Promise<void> {
    const saved = row.saved;
    if (saved === null || quoteId === null) {
      patchRow(row.key, { notes: next });
      return;
    }
    const result = await api.updateQuoteProduct(quoteId, saved.id, { notes: next });
    const stored = result.data.notes === undefined ? next : result.data.notes;
    setRows((current) =>
      current.map((one) =>
        one.key === row.key && one.saved !== null
          ? {
              ...one,
              saved: { ...one.saved, notes: stored },
              draft: { ...one.draft, notes: stored },
            }
          : one,
      ),
    );
  }

  /**
   * The note control for a row, or `null` when there is nothing to render.
   *
   * ⚠️ **Not `disabled` by a lock, only by the permission.** A Sent or pushed
   * quote refuses the other cells server-side, but a notes-only PATCH is let
   * through, so a STORED line's note follows `quotes.update` alone — even when
   * the page renders the grid read-only for its lines gate. Any other row
   * follows the grid's own edit gate. `convertLocked` still freezes it: once
   * the conversion has landed nothing on this screen is sent again.
   *
   * A stored line whose `notes` is `undefined` comes from an API that predates
   * the column, and renders nothing.
   */
  function noteControl(row: Row, className?: string): ReactNode {
    const value =
      row.saved !== null
        ? row.saved.notes
        : row.convert !== null && row.convert.notes === undefined
          ? undefined
          : row.draft.notes;
    if (value === undefined) return null;
    return (
      <LineNoteField
        value={value}
        onSave={(next) => saveLineNote(row, next)}
        disabled={
          row.saved !== null && quoteId !== null
            ? /*
               * Frozen while the row's full save is in flight, as on the invoice
               * grid: a notes-only PATCH would race that save's response.
               */
              !canUpdateLineNotes || convertLocked || row.state === 'saving'
            : readOnly || convertLocked || (row.saved === null && row.state === 'saving')
        }
        {...(className === undefined ? {} : { className })}
      />
    );
  }

  function addRow() {
    const key = `new-${newRowSeq}`;
    setNewRowSeq((value) => value + 1);
    setSummary(null);
    setRows((current) => [...current, blankRowOf(key)]);
    setExpanded((current) => new Set(current).add(key));
  }

  function toggleExpanded(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function discardRow(key: string) {
    setRows((current) => current.filter((row) => row.key !== key));
  }

  function revertRow(key: string) {
    /*
     * Revert means "back to the stored line", and the description is part of
     * that line — the seeder restores the stored wording, or restores `null`
     * and puts the cell back on the derivation, whichever the record says.
     *
     * ⚠️ **In convert mode the stored line is the DEAL's**, and reverting is
     * what takes a row's override off the wire entirely: the seed is what the
     * change test compares against, so a row rebuilt from it matches and
     * produces no entry at all. Rebuilding it from `draftOf` — which takes a
     * `QuoteProduct` — is not merely a type error, it is the wrong document.
     */
    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        const draft =
          row.convert !== null
            ? convertRowOf(row.convert).draft
            : row.saved !== null
              ? draftOf(row.saved)
              : null;
        if (draft === null) return row;
        return { ...row, draft, state: 'idle', error: null, fieldErrors: {}, refusal: null };
      }),
    );
  }

  /**
   * Saves every changed row, one request at a time.
   *
   * Sequential rather than parallel for three reasons that all matter: each
   * write rewrites the quote's four header totals inside its own transaction,
   * the rate limit is shared per IP across the office, and a partial
   * failure has to be reportable row by row rather than as one rejected
   * promise.
   *
   * ⚠️ *SUPERSEDED: "the rate limit is 120/60s per IP."* It is **12,000/60s**
   * (corrected 2026-09-17), so that middle reason carries almost no weight on a
   * basket this size — a grid holds a handful of rows, not a hundred thousand.
   * ⛔ **The other two are untouched and are what the sequencing rests on**: two
   * parallel line writes each rewrite the same four header totals, and one
   * rejected promise cannot say WHICH row was refused. **Do not re-derive
   * "sequential is unnecessary" from the corrected arithmetic** — the
   * arithmetic was never what made it necessary.
   *
   * A row that succeeds becomes a saved row immediately, so a second press
   * cannot re-post it — which would 409 on a clashing pair and, for any row
   * whose project differs, quietly create a genuine duplicate line.
   */
  async function saveAll(): Promise<boolean> {
    /*
     * ⚠️ **The boolean is "did everything the run targeted land", and NOTHING
     * ELSE.** It exists for `registerSave`, so a parent can decide whether to
     * close; it is not a summary and it is not what a person reads — the run
     * report below is, row by row.
     *
     * Both early returns answer `true` deliberately. "There was nothing to
     * save" is not a failure, and returning `false` there would leave a caller
     * holding a form open over a grid with nothing wrong with it.
     */
    /*
     * Draft mode never reaches this — the Save control is not rendered — but the
     * guard is here rather than only in the markup, because "no request is
     * issued" is the contract of that mode and a contract enforced only by a
     * hidden button is enforced by nothing.
     */
    if (quoteId === null) return true;
    const targets = rows.filter((row) => dirtyKeys.has(row.key) && !blockedKeys.has(row.key));
    if (targets.length === 0) return true;

    setSaving(true);
    setSummary(null);
    setRows((current) =>
      current.map((row) =>
        targets.some((target) => target.key === row.key)
          ? { ...row, state: 'saving', error: null, fieldErrors: {}, refusal: null }
          : row,
      ),
    );

    let saved = 0;
    let failed = 0;
    let halted = false;
    /*
     * ⚠️ The FIRST `failed` push report of the run, and nothing else — see
     * {@link QuoteLineItemsSavedProps.onXeroPush} for why a run of N saves
     * reports one refusal rather than a tally. It stays `undefined` when every
     * report was `pushed` or `skipped`, and `undefined` is what makes the page
     * say nothing.
     *
     * ⚠️ It is deliberately NOT overwritten by a later failure: keeping the
     * first one means the sentence a person reads belongs to the earliest row
     * that hit it, which is the one they will look at.
     */
    let firstFailedPush: XeroPushReport | undefined;

    for (const target of targets) {
      try {
        /*
         * ⚠️ The link-table verdict, threaded into BOTH builders.
         *
         * `false` means the inclusions block is not on screen, and both builders
         * then omit the key outright rather than sending `[]`. The write is a
         * REPLACE — an omitted key leaves the stored set alone and `[]` clears it
         * — so this is the difference between a line that already holds
         * inclusions keeping them through an unrelated edit and losing them
         * silently with a 200. See this file's header.
         */
        const offersInclusions = inclusionsFor(target.key).offers;
        /*
         * ⚠️ The same gate, for the same class of key: `regionIds` is a full
         * REPLACE whose `[]` CLEARS the set, so a control that is not on screen
         * contributes nothing. See {@link showsRegions}.
         */
        const offersRegions = showsRegions(target);
        const result =
          target.saved === null
            ? await api.createQuoteProduct(
                quoteId,
                createPayload(
                  target.draft,
                  undefined,
                  offersInclusions,
                  offersRegions,
                  catalogueFor(target),
                ),
              )
            : await api.updateQuoteProduct(
                quoteId,
                target.saved.id,
                updatePayload(
                  target.draft,
                  target.saved,
                  offersInclusions,
                  offersRegions,
                  repriceFor(target),
                ),
              );
        saved += 1;
        /*
         * ⚠️ A push report rides on a response that SUCCEEDED, so it is read
         * here and never in the `catch`: an `ApiError` means the LINE was not
         * written at all, which is a different fact and is already rendered on
         * the row itself.
         */
        if (result.xeroPush?.status === 'failed' && firstFailedPush === undefined) {
          firstFailedPush = result.xeroPush;
        }
        /*
         * The response is read back from the database rather than echoed —
         * inclusions fully resolved, an omitted quantity filled in as 1, the
         * three generated columns computed. Render from it; never keep the
         * array that was posted.
         */
        setRows((current) =>
          current.map((row) =>
            row.key === target.key ? { ...rowOf(result.data), key: row.key, state: 'saved' } : row,
          ),
        );
      } catch (error: unknown) {
        failed += 1;
        const described = describeFailure(error);
        setRows((current) =>
          current.map((row) =>
            row.key === target.key
              ? {
                  ...row,
                  state: 'failed',
                  error: described.message,
                  fieldErrors: described.fieldErrors,
                  refusal: described.refusal,
                }
              : row,
          ),
        );
        /*
         * A 401 or 403 is not about this row — the session or the role changed
         * under a live token, and every remaining request would fail the same
         * way. Stopping keeps the report honest instead of printing one
         * refusal against six rows.
         */
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          halted = true;
          setRows((current) =>
            current.map((row) => (row.state === 'saving' ? { ...row, state: 'idle' } : row)),
          );
          break;
        }
      }
    }

    setSaving(false);
    setSummary({ saved, failed, halted });
    /*
     * ⚠️ **A BLOCKED ROW COUNTS AGAINST THE RUN even though it was never a
     * target.** `targets` skips them, so a run can refuse nothing and still
     * leave work on the grid — and a caller reading `true` there would close the
     * form over a row that was never sent. The parent's own gate should stop
     * this from arising; this makes it unreachable rather than unlikely.
     */
    const everythingLanded = failed === 0 && !halted && blockedKeys.size === 0;
    if (saved > 0) {
      onLinesChanged?.();
      /*
       * ⚠️ **Gated on `saved > 0`, NOT on `everythingLanded`.** A run that wrote
       * three rows and was refused on a fourth has moved the document just as
       * surely as a clean one — and a grandparent list showing a stale
       * server-computed field after a PARTIAL save is the most misleading state
       * of the three, because the row beside it is reporting a failure.
       *
       * ⚠️ Fired HERE rather than by the parent when its modal closes. The
       * grid's own "Save N rows" writes without the parent's Save ever being
       * pressed, and the person then leaves through Cancel, the X, Escape or
       * the overlay — four exits, one of which gets forgotten. This is the one
       * call site that cannot be missed.
       */
      onRowsSaved?.();
      /*
       * ⚠️ Reported only when something actually landed, and gated on the same
       * condition as the refetch on purpose. A run in which every row was
       * refused by the API produced NO push report at all, so there is nothing
       * true to hand up — and passing `undefined` there would CLEAR a standing
       * report about an earlier save that is still the last thing known about
       * Xero.
       */
      onXeroPush?.(firstFailedPush);
    }
    return everythingLanded;
  }

  async function confirmDelete() {
    const target = pendingDelete;
    // Draft rows are discarded locally and never reach here; see `deleteControl`.
    if (quoteId === null || target === null || target.saved === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      /*
       * ⚠️ **A DELETE PUSHES AND REPORTS NOTHING, and that is the endpoint
       * rather than an omission here.** `DELETE /quotes/{id}/products/{lineId}`
       * answers `204`, and a `204` has no body — the server still pushes the
       * quote, it simply has nowhere to say so. Widening it to a `200` would
       * change a contract every client already handles.
       *
       * So `onXeroPush` is NOT called here, in either direction: there is no
       * report to hand up, and handing up `undefined` would clear a standing
       * failure on the strength of a push nobody was told the outcome of.
       */
      await api.deleteQuoteProduct(quoteId, target.saved.id);
      setRows((current) => current.filter((row) => row.key !== target.key));
      setPendingDelete(null);
      onLinesChanged?.();
      /*
       * ⚠️ **A DELETE STALES A PARENT LIST EXACTLY AS A SAVE DOES.** Removing
       * the only line that named a project moves the document's project status
       * as much as adding one did, so a fix wired only into `saveAll` would
       * leave half of this live. Deletion is a separate function reached by a
       * separate control, which is precisely why it is easy to miss.
       */
      onRowsSaved?.();
    } catch (error: unknown) {
      setDeleteError(describeFailure(error).message);
    } finally {
      setDeleting(false);
    }
  }

  /* ── States ───────────────────────────────────────────────────────────── */

  /*
   * ⚠️ **THE THREE STATES STAY APART, in convert mode as well.** `loading` gets
   * skeletons; a failed read gets the error and NO table, because every one of
   * the deal's lines still crosses whether or not we could show them; an empty
   * array is the deal genuinely having no products. Collapsing the middle into
   * "no lines" shows somebody an empty basket for a deal about to bring five
   * products across.
   */
  if (loading || dealLinesLoading) {
    return (
      <div className="grid min-w-0 gap-2" aria-busy="true">
        <Skeleton className="h-8 w-40" />
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
        <span className="sr-only">Loading line items</span>
      </div>
    );
  }

  if (dealLinesError !== null) {
    /*
     * ⚠️ **No "Try again" here, and that is not an omission.** The read belongs
     * to the parent — this grid issues no request at all in convert mode — so a
     * retry button would be a control with nothing to call. The sentence says
     * the thing that actually matters instead: the lines cross regardless.
     */
    return (
      <div className="grid min-w-0 gap-2">
        <ErrorBlock title="The products on this deal could not be read.">
          <p>{dealLinesError}</p>
          <p>All of them still come across — they simply cannot be listed or edited here.</p>
        </ErrorBlock>
      </div>
    );
  }

  if (loadError !== null) {
    return (
      <div className="grid min-w-0 gap-2">
        <ErrorBlock title="This quote’s line items could not be loaded.">
          <p>{loadError}</p>
        </ErrorBlock>
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setLoading(true);
              setReloadToken((n) => n + 1);
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Xero's row, column for column: Item, Description, Qty, Price, Disc, Tax
   * rate, Amount, then the status/actions cell that carries our trash control.
   *
   * ⚠️ **EIGHT, not ten. Project and Cities are not columns** — they are a
   * stack inside the Item cell. This number spans the error row and the detail
   * panel, so it has to move with the `<colgroup>`; leave it at ten and both of
   * those rows overhang the table by two columns.
   *
   * ⚠️ Moving the RENDERING did not split the patch: a project change still
   * clears the cities in the same `onChange` — see `ProjectControl`.
   */
  const columnCount = 8;

  function rowControls(row: Row, compact: boolean) {
    const problems: RowProblems = problemsByKey.get(row.key) ?? {
      fieldErrors: {},
      error: null,
      notice: null,
    };
    /*
     * ⚠️ **`convertLocked` belongs HERE and not on the parent's own submit
     * button.** Once the conversion has landed the quote's lines are already
     * written from whatever overrides were sent, and a retried Save re-sends the
     * header PATCH alone — so a box left live is one somebody types a new price
     * into, presses Save, and watches succeed against the old price.
     */
    const disabled = readOnly || convertLocked || row.state === 'saving' || saving;
    /*
     * ⚠️ **CORRECTED — THE STRUCTURE OF A CONVERT LINE IS A FIELD NOW.**
     * *SUPERSEDED, and it was the load-bearing sentence of the whole mode: "THE
     * STRUCTURE OF A CONVERTED LINE IS A FACT, NOT A FIELD. The override
     * contract carries five keys — quantity, price, the discount pair and the
     * description — and none of them is `productId`, `projectId`, `cityIds`,
     * `regionIds` or `inclusions`. Those are copied verbatim by the server
     * inside the transaction that creates the quote, so a live picker here would
     * take a choice and discard it with a 201 and nothing on screen to say so …
     * Changing them is the resulting quote's own line grid."* The
     * `const structural = disabled || convertMode;` that enforced it is gone
     * with it.
     *
     * `lineSet` carries all five, so every one of those pickers has somewhere to
     * put its answer and a lock on them would now be the opposite defect: a
     * control refusing an edit the request can express.
     *
     * ⚠️ **TWO LOCKS SURVIVE ON THIS PATH AND MUST NOT BE SWEPT UP WITH IT**,
     * because `lineSet` genuinely cannot carry either: the TAX cell (a readout —
     * see {@link ConvertedTaxRateCell}) and the **"All cities" FLAG**, which has
     * no key at all and is disabled through `allCitiesLocked` while the city
     * PICKER beside it stays live.
     */
    const onChange = (patch: Partial<Draft>) => patchRow(row.key, patch);
    const shared = { row, problems, disabled, compact, onChange };
    /*
     * The line's OWN project decides its cities — never `quote.project`. One
     * resolved answer, handed to both controls, so the two cells cannot
     * disagree about what the project declares.
     */
    const resolved = rowProjects.get(row.key) ?? {
      options: NO_PROJECT_OPTIONS,
      cityOptions: [],
      // Nothing was resolved, so nothing can be claimed about what "all" means.
      projectCities: null,
      declaredCityIds: null,
      /*
       * Nothing resolved, so the picker falls back to the `/regions` master
       * rather than narrowing against a project it could not read — narrowing on
       * an unknown is how a stored region silently disappears from a REPLACE.
       */
      projectRegions: null,
      // ⚠️ Nothing resolved is not "declares none". See `showsRegionsControl`.
      projectHasNoCities: false,
      projectHint: undefined,
      projectFootnotes: NO_PROJECT_FOOTNOTES,
      cityHint: undefined,
      citiesUnavailable: true,
      /*
       * ⚠️ Nothing was resolved, so nothing may be denied. `unknown` SHOWS the
       * control — see {@link showsProjectControl}; a block that disappears
       * because a map lookup missed is indistinguishable from a bug.
       */
      projectOffer: 'unknown' as ProjectOffer,
    };
    /*
     * ⚠️ **The gate on the project block, and it decides RENDERING only.**
     * Nothing below reads it, no payload takes it, and no state moves with it —
     * which is what keeps a hidden block from sending `cityIds: []` and
     * destroying a line's stored cities on the next unrelated save.
     */
    const showProject = showsProjectControl(row, resolved.projectOffer);
    /*
     * ⚠️ **The SAME `projectHasNoCities` the regions gate reads, off the SAME
     * resolved row.** A project that declares no cities takes the city block
     * away and puts the region block up, in one render, from one value — see
     * {@link showsCitiesControl}. Two tests of that idea would come apart on
     * exactly the row that has both, and `resolved` already defaults it to
     * `false` for anything merely unresolved.
     */
    const showCities = showsCitiesControl(row, showProject, resolved.projectHasNoCities);
    /*
     * ⚠️ The COMPLEMENT of `showProject`, not a third independent gate — see
     * {@link showsRegionsControl}. Read through `showsRegions` rather than
     * recomputed, so this render and the two payload builders cannot disagree
     * about whether a hidden control may write.
     */
    const showRegions = showsRegions(row);
    return {
      problems,
      disabled,
      showProject,
      showCities,
      showRegions,
      product: (
        <ProductControl
          {...shared}
          options={productOptionsFor(row)}
          /*
            🔴 **The product footnote is GONE — owner's stricter pass,
            2026-09-05.** It read "The product list could not be loaded." on a
            failed read and "Showing the first 100 products — the catalogue
            holds more." on a truncated one.

            ⚠️ **A product missing from this list is now indistinguishable from
            a product that does not exist**, whichever of the two caused it.
            `products.failed` and `products.truncated` are both still computed.
          */
        />
      ),
      /*
       * ⚠️ Derived on every render, so an untouched cell follows the item, the
       * project and the cities live. `row.draft.description === null` is the
       * "still deriving" state; anything else, `''` included, is the person's own
       * wording and is never written over on screen — `''` is folded back to
       * `null` at the payload, by `descriptionForWrite`, and nowhere earlier.
       *
       * `onReset` sets the draft key to `null`, which is the same `null` the
       * PATCH sends. One word, one meaning, in the state and on the wire.
       */
      description: (
        <DescriptionControl
          disabled={disabled}
          compact={compact}
          derived={descriptionOf(row)}
          description={row.draft.description}
          /*
           * ⚠️ **`row.saved`, never `row.convert` or a planned seed.** The
           * question this answers is "what wording is STORED on this line right
           * now" — i.e. what the customer's document carries — and only a saved
           * line has one. A converted or seeded row has never been written, so
           * it has no original to go back to and the control correctly does not
           * appear.
           */
          saved={row.saved?.description ?? null}
          error={problems.fieldErrors.description}
          onTyped={(value) => onChange({ description: value })}
          onReset={() => onChange({ description: null })}
          onRestore={() => onChange({ description: row.saved?.description ?? null })}
        />
      ),
      /*
       * The Price tier picker sits directly under the project picker, in both
       * layouts, once the line names a product and a project and the tier
       * master has loaded. On the convert path it is locked: `lineSet` has no
       * tier key, and the server copies the deal line's tier.
       */
      project: (
        <>
          <ProjectControl {...shared} resolved={resolved} />
          {row.draft.productId !== '' && row.draft.projectId !== '' && priceTiers.length > 0 && (
            <PriceTierControl
              {...shared}
              options={tierOptionsFor(
                priceEntriesByProject.get(Number(row.draft.projectId)) ?? [],
                priceTiers,
                Number(row.draft.productId),
                currency,
              )}
              defaultTierId={defaultTierId}
              allTiers={priceTiers}
              lockedReason={convertMode ? 'The tier comes from the deal line on conversion.' : null}
            />
          )}
        </>
      ),
      cities: (
        <CitiesControl
          {...shared}
          resolved={resolved}
          /*
           * ⚠️ **The FLAG only.** `lineSet` carries `cityIds` and has no
           * `allCities` key, so ticking the box would collect a choice the
           * conversion cannot express — while the picker underneath it is one
           * of the five things this change exists to unlock.
           */
          allCitiesLocked={convertMode}
        />
      ),
      /*
       * `loading` only while the list is genuinely empty: once the six rows are
       * in, a background refetch must not blank a picker somebody is using.
       * `regions.failed` deliberately does NOT hide the control — a row that
       * holds regions still renders them from `regionOptionsFor`'s pinned
       * entries, and a control that vanishes on a failed read is
       * indistinguishable from a bug.
       */
      regions: (
        <RegionsControl
          {...shared}
          options={regionOptionsFor(row, resolved)}
          loading={regions.loading && regions.records.length === 0}
          /*
           * ⚠️ The SAME `resolved` the project and city controls read, so the
           * three cells cannot disagree about what this line's project declares.
           * It decides both which list the picker offers and whether "All
           * regions" has a bound to mean anything — see {@link RegionsControl}.
           */
          projectRegions={resolved.projectRegions}
        />
      ),
      quantity: (
        <NumberControl
          {...shared}
          field="quantity"
          label="Qty"
          placeholder="1"
          quantityStep={productOfRow(row)?.quantityStep ?? null}
        />
      ),
      /*
       * ⚠️ **Built HERE rather than at the two JSX call sites** so it takes the
       * same `disabled` and the same `onChange` as every other control on the
       * row. The table and the sub-`md` card list both render it, and a cell
       * that could be edited in one layout and not the other is the
       * "indistinguishable from a removed feature" failure this file records.
       */
      amount: (
        <AmountCell
          row={row}
          dirty={dirtyKeys.has(row.key)}
          currency={currency}
          convertMode={convertMode}
          preview={previewByKey.get(row.key) ?? null}
          input={previewInputOf(
            row,
            catalogueFor(row).price,
            companySalesTax,
            repricedFor(row),
            pricedDiscountFor(row),
          )}
          disabled={disabled}
          onChange={onChange}
        />
      ),
      price: (
        <PriceControl
          {...shared}
          currency={currency}
          catalogue={catalogueFor(row)}
          repriced={repricedFor(row)}
          /*
           * ⚠️ **The tri-state Price box loses its third state on the convert
           * path.** There is no `unitPrice: null` key on the override contract,
           * so "record no price at all" is a state the request cannot express —
           * see {@link convertEditOf}.
           */
          allowNoPrice={!convertMode}
          unitLabel={productOfRow(row)?.unitLabel ?? null}
        />
      ),
      /*
       * ⚠️ TWO real columns, mutually exclusive — `draft.discountAmount === null`
       * is percentage mode. The control writes whichever box is on screen and
       * never the other; `discountWriteOf` turns the pair into a payload.
       */
      discount: (
        <DiscountControl {...shared} onModeChange={(toAmount) => setDiscountMode(row.key, toAmount)} />
      ),
      /*
       * ONE control, exactly as Xero has one "Tax rate" column — it writes both
       * `taxRate` and `xeroTaxType`, and falls back to the bare `%` input when
       * the tenant's rates cannot be read. See `TaxRateControl`.
       */
      tax: convertMode ? (
        /*
         * ⚠️ **A READOUT, not a disabled dropdown — see
         * {@link ConvertedTaxRateCell}.** The override contract has no `taxRate`
         * and no `xeroTaxType`, so a live control would discard a choice
         * silently and a disabled `TaxRateControl` would render a frozen `0%`
         * that nothing measured. The rate comes from the COMPANY, server-side.
         */
        <ConvertedTaxRateCell compact={compact} rate={companySalesTax} />
      ) : (
        <TaxRateControl
          {...shared}
          rates={taxRates.rates}
          ratesLoading={taxRates.loading}
          ratesError={taxRates.error?.message ?? null}
          connected={xeroConnected}
          /*
           * ⚠️ **DISPLAY ONLY.** It words the empty option and names the
           * company's default in the popover; it reaches no payload. On a mount
           * that passes none this is `{ known: false }` and the control renders
           * exactly as it always has.
           */
          companySalesTax={companySalesTax}
        />
      ),
    };
  }

  function deleteControl(row: Row) {
    /*
     * ⚠️ **CORRECTED — A CONVERT ROW IS DISCARDABLE, AND IT TAKES THE `saved ===
     * null` BRANCH BELOW.** *SUPERSEDED: "Nothing at all on a converted row.
     * `POST /deals/{id}/convert` copies every one of the deal's lines and takes
     * no key that omits one, so there is no request this control could make and
     * no local row to discard either — the row IS the deal's line … Removing a
     * line is done on the deal, or on the quote that results."* The `if
     * (row.convert !== null) return null;` that enforced it is gone.
     *
     * `lineSet` is the complete final list, so leaving a row OUT of it is
     * exactly how a line is removed — no request of its own, and nothing to
     * confirm, because discarding it destroys no record on either document.
     *
     * ⚠️ **IT DOES NOT TOUCH THE DEAL, and this is the one place somebody will
     * assume it does.** The deal's basket is untouched by a conversion in every
     * direction; a row discarded here is a line the QUOTE will not have.
     *
     * ⚠️ **Gated by `canDelete` nowhere on this path** — see the `saved ===
     * null` branch. A convert row has never been sent anywhere, so gating it on
     * the admin-tier delete ability would trap a `basic_user` with a line they
     * do not want and no way to take it off the form.
     */
    if (row.saved === null) {
      // Never sent, so there is nothing to destroy and nothing to confirm.
      /*
       * ⚠️ **`canManageRows`, not `!readOnly`.** Once a conversion has landed
       * the rows are already on a real quote and Save re-sends the HEADER alone
       * — so a trash can there would remove a line from a list nothing will send
       * again, and report nothing. `canManageRows` is the one flag that carries
       * both that lock and the read-only one.
       */
      return canManageRows ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          // `iconOnly` DROPS children — the icon has to arrive as `leadingIcon`
          // or the control renders as a blank 32×32 square. Modal's close
          // button is the pattern.
          leadingIcon={<TrashIcon />}
          aria-label={row.convert === null ? 'Discard this row' : 'Leave this line off the quote'}
          onClick={() => discardRow(row.key)}
        />
      ) : null;
    }
    if (!canDelete) {
      /*
       * Shown disabled rather than hidden. Create and update take any write
       * role and delete takes admin, so a basic_user can add a line and cannot
       * remove it — an asymmetry that has to be visible before the row is
       * built, not discovered after.
       */
      return (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconOnly
          leadingIcon={<TrashIcon />}
          disabled
          title={deleteDenialReason}
          aria-label={`Delete this line — ${deleteDenialReason}`}
        />
      );
    }
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        leadingIcon={<TrashIcon />}
        aria-label={`Delete the ${row.saved.product.productName} line`}
        onClick={() => {
          setDeleteError(null);
          setPendingDelete(row);
        }}
      />
    );
  }

  function rowActions(row: Row) {
    return (
      // `flex-wrap` so a third control (Revert, on a dirty saved row) drops to
      // a second line INSIDE the 94px column rather than trying to widen it —
      // under `table-fixed` the `<colgroup>` is authoritative, so without the
      // wrap the buttons would overflow the cell instead.
      <div className="flex flex-wrap items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={expanded.has(row.key)}
          onClick={() => toggleExpanded(row.key)}
        >
          {expanded.has(row.key) ? 'Hide' : 'Details'}
        </Button>
        {/*
          ⚠️ **Offered on a CONVERTED row too, and it is worth more there.**
          Revert rebuilds the row from the deal line, which is the seed the change
          test compares against — so it is the only way to take a row's override
          back off the wire once somebody has typed into it. `row.saved !== null`
          alone would withhold it from every converted row, since none of them has
          a saved quote line and never will.

          ⚠️ Withheld while `convertLocked`, like every other control on the row:
          after the conversion has landed there is nothing left for a revert to
          change and it would read as a way to undo the quote.
        */}
        {!readOnly && !convertLocked && dirtyKeys.has(row.key) && sourceLineOf(row) !== null && (
          <Button type="button" variant="ghost" size="sm" onClick={() => revertRow(row.key)}>
            Revert
          </Button>
        )}
        {deleteControl(row)}
      </div>
    );
  }

  function detailPanel(row: Row) {
    const { problems, disabled: rowDisabled } = rowControls(row, true);
    const dirty = dirtyKeys.has(row.key);
    const preview = previewByKey.get(row.key) ?? null;
    /*
     * Which set of three is honest depends on the row, and a changed row used
     * to show the STORED three with no marker at all — the worst of the three
     * options, because they describe a payload the user has already replaced.
     */
    /*
     * ⚠️ **A converted row shows NEITHER, and both halves are deliberate.**
     * `f_deal_products` has ONE generated column — `line_total` — and no
     * `line_subtotal` and no `tax_rate` at all, so there is no stored Subtotal
     * and Tax pair to show; and the preview is the wrong document's answer for
     * the reason {@link AmountCell} gives at length. The Amount column already
     * carries the one figure that is honest here.
     */
    const figures =
      row.convert !== null
        ? null
        : dirty
          ? preview
          : row.saved === null
            ? null
            : {
                lineSubtotal: row.saved.lineSubtotal,
                lineTax: row.saved.lineTax,
                lineTotal: row.saved.lineTotal,
              };
    /*
     * ⚠️ **The link-table gate, and the ONE place the editor is decided.**
     *
     * `offers` is `productOffersInclusions` — the owner's explicit choice over
     * `shouldShowInclusionsEditor`, which would keep the editor on a
     * non-offering line that already holds inclusions. See this file's header
     * for what was traded; do not swap the predicate back.
     *
     * ⚠️ **Not rendering the editor does NOT touch `row.draft.inclusions`**, and
     * that is the whole safety of this change. `patchRow` is only ever called by
     * the editor's own controls, so a hidden block cannot move the draft; the
     * stored fingerprint therefore still matches, and `updatePayload` omits the
     * key — which LEAVES the stored set alone. Clearing state here instead would
     * send `inclusions: []`, and that CLEARS it: any line already holding
     * inclusions — with the link table empty, every one of them — would
     * lose them on the next unrelated save, with a 200 and
     * no warning. Never normalise, reset or empty a draft because you stopped
     * rendering its control.
     */
    const { offers, offerUnread, offeredIds, denialReason, orphaned } = inclusionsFor(row.key);
    return (
      <div className="grid gap-4 border-t border-border bg-surface-sunken px-3 py-3">
        {offers ? (
          <InclusionsEditor
            row={row}
            problems={problems}
            /*
             * ⚠️ **CORRECTED — `disabled`, and there is no second flag any
             * more.** *SUPERSEDED: "`structural`, not `disabled`. An inclusions
             * write is a whole-set REPLACE and the override contract carries no
             * `inclusions` key at all, so a live editor here would collect ticks
             * the conversion discards — and the deal's own set is what crosses
             * either way. Read-only rather than hidden: they print on the
             * customer's document."* `lineSet` carries `inclusions`, so the
             * editor is live on the convert path too. The whole-set REPLACE half
             * is still exactly true and is why `convertShapeOf` refuses to build
             * the key at all when this block is not on screen.
             */
            disabled={rowDisabled}
            master={inclusions.rows}
            masterFailed={inclusions.failed}
            offerUnread={offerUnread}
            offeredIds={offeredIds}
            onChange={(patch) => patchRow(row.key, patch)}
          />
        ) : (
          <InclusionsNotOffered
            row={row}
            master={inclusions.rows}
            denialReason={denialReason}
            orphaned={orphaned}
          />
        )}
        {/*
          ⚠️ **Two figures, not three.** `lineTotal` is already the Amount
          COLUMN on this row, so repeating it here was one number in two places
          — and the two-line caption underneath it was the third. Subtotal and
          Tax are the pair the column cannot show, which is the only reason this
          block earns its space at all.

          The two still reconcile with the Amount exactly — lineSubtotal +
          lineTax === lineTotal, always — because the preview repeats the
          database's own three steps in its own order. Neither is recomputed
          here, and nothing adds them up on screen.
        */}
        {figures !== null && (
          <dl className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-xs">
            <div className="flex items-baseline gap-1.5">
              <dt className="text-fg-muted">Subtotal</dt>
              <dd className="tabular-nums text-fg">{formatDecimalString(figures.lineSubtotal)}</dd>
            </div>
            <div className="flex items-baseline gap-1.5">
              <dt className="text-fg-muted">Tax</dt>
              <dd className="tabular-nums text-fg">{formatDecimalString(figures.lineTax)}</dd>
            </div>
            <span className="text-2xs text-fg-subtle">{dirty ? 'Preview' : 'As saved'}</span>
          </dl>
        )}
        {figures === null && (
          /*
            A changed row with nothing to preview — no price on the line, an
            unpriced catalogue product behind a blank box, or boxes that are not
            yet a figure. Saying so beats showing the stored figures, which are
            about the older payload, and beats zeroes, which would assert a
            price of nothing. ⚠️ The `none` branch keeps its own sentence
            because it is a different FACT, not a longer way of saying the same
            one: no price recorded is not a price of zero, and collapsing the
            two would be the collapse the whole `PriceMode` type exists to stop.
          */
          <p className="text-2xs text-fg-subtle">
            {row.convert !== null
              ? /* ⚠️ "on conversion", never "on save" — nothing on this path is saved
                   from here, and a sentence promising a save sends somebody looking
                   for a control that does not exist. */
                row.draft.priceMode === 'none'
                ? 'No price on this line — not zero.'
                : 'Calculated on conversion.'
              : row.draft.priceMode === 'none'
                ? 'No price on this line — not zero.'
                : 'Calculated on save.'}
          </p>
        )}
      </div>
    );
  }

  const runReport =
    summary === null ? null : summary.failed === 0 ? (
      <Notice tone="success" role="status">
        {summary.saved} of {summary.saved} {summary.saved === 1 ? 'line' : 'lines'} saved.
      </Notice>
    ) : (
      <ErrorBlock
        title={`${summary.saved} of ${summary.saved + summary.failed} lines saved. Fix the ${
          summary.failed === 1 ? 'row' : 'rows'
        } marked below and save again.`}
      >
        <p>
          The {summary.saved === 1 ? 'line' : 'lines'} that saved
          {summary.saved === 1 ? ' is' : ' are'} already stored.
          {summary.halted ? ' The run stopped early — the rest were not attempted.' : ''}
        </p>
      </ErrorBlock>
    );

  return (
    <div className="grid min-w-0 gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          {/*
            ⚠️ **CORRECTED — "copied from the deal" was made false by `lineSet`.**
            *SUPERSEDED: "Named for what the rows ARE on this path. \"Line items\"
            over a deal's basket reads as the quote's own lines, which these are
            not yet."* They ARE the quote's own lines now, in every sense but
            existing yet: rows can be added, removed and repointed at a different
            product, so "copied from the deal" describes only how the list
            STARTED. Naming it for the deal would tell somebody who just deleted a
            row that they had changed the deal's basket, which is the one thing a
            conversion never does.

            ⚠️ Still not "Line items" bare, and still not the quote's: nothing
            here is POSTed and nothing exists until Convert is pressed, which is
            what the heading has to keep saying.
          */}
          <h3 className="text-sm font-semibold text-fg">
            {convertMode ? 'Line items for the new quote' : 'Line items'}
          </h3>
          {/* A count and a status, and nothing else — said once, not in two places worded two ways. */}
          <p className="text-xs text-fg-muted">
            {rows.length === 0
              ? 'None yet.'
              : /*
                 * Never "unsaved" in draft mode — nothing on this grid has
                 * anywhere to be saved to yet, and calling it unsaved would
                 * imply a Save control the person is failing to find.
                 */
                `${rows.length} ${rows.length === 1 ? 'line' : 'lines'}${
                  convertMode
                    ? dirtyCount > 0
                      ? /* ⚠️ "edited or added", never "unsaved". Nothing here is saved
                           from this screen; what has changed is what the conversion
                           will create. Both words are needed because `dirtyKeys` now
                           holds two kinds of row — a carry-over somebody changed, and
                           one that was never on the deal at all — and calling a brand
                           new row "edited" sends its author looking for the deal line
                           they supposedly altered. */
                        ` · ${dirtyCount} edited or added`
                      : ''
                    : !draftMode && dirtyCount > 0
                      ? ` · ${dirtyCount} unsaved`
                      : ''
                }`}
          </p>
        </div>
        {canManageRows && (
          <div className="flex shrink-0 items-center gap-2">
            {/* Add row lives below the rows, full width — owner's request, 2026-10-12. */}
            {/*
              ⚠️ No Save control in draft mode, and not a disabled one either. A
              line cannot be written before the quote has an id, so there is no
              request this button could make — the quote form's own submit is
              what writes these rows, and a second save-looking control beside it
              would read as a step the person has to take first.

              🔴 **AND NONE WHEN A PARENT HAS TAKEN THE SAVE** (`registerSave`),
              which is the same reason one step out: the form's footer "Save
              changes" runs this grid's own `saveAll` before its PATCH, so a
              button here would be a SECOND control performing the identical
              run. Owner's instruction, 10 September 2026 — one button, and it
              says "Save changes".

              ⚠️ **The label no longer counts rows.** It read `Save ${n} rows`
              while dirty and "Save changes" while clean, so the one control
              renamed itself under the person using it — and on a form it sat
              beside a footer control of the *other* name, reading as two
              different jobs. It is one job on every screen and says so.

              ⚠️ **It IS still rendered on the detail page**, which mounts this
              grid with no `registerSave` and has no save of its own. Removing it
              unconditionally would leave every line edit there with nowhere to
              go — measured before this change, not assumed.
            */}
            {!draftMode && registerSave === undefined && (
              <Button
                type="button"
                variant="primary"
                loading={saving}
                disabled={saving || dirtyCount === 0 || blockedKeys.size > 0}
                onClick={() => {
                  void saveAll();
                }}
              >
                Save changes
              </Button>
            )}
          </div>
        )}
      </div>

      {/*
        ⚠️ **ONE explanatory line above this table, and this is it.** Xero's
        quote grid has none, and three of ours have gone: the create/delete
        asymmetry note, which is answered where it bites — on the disabled trash
        control's own `title` and `aria-label`; the "each line names its own
        project" note, which describes a picker now sitting directly under the
        item it belongs to; and the suffix on the header count that said this
        same sentence again in different words.

        This one survives because it is not commentary — it is the only thing on
        screen that answers "will closing this modal lose what I have typed?",
        and someone entering lines in a New Quote modal has no other way to
        tell. A person who assumes wrong makes a second quote.
      */}
      {draftMode && rows.length > 0 && (
        <p className="text-xs text-fg-muted">
          These lines are written when the quote is created.
        </p>
      )}

      {/*
        ⚠️ **The convert path's one line, and it survives the same test the draft
        one does: it answers a question nothing else on screen can.** Somebody
        looking at boxes over a deal's basket has no way to tell whether they are
        editing the DEAL or the quote about to be made from it — and the answer
        decides whether they go back and check the deal afterwards. It is also
        where the row set being fixed is stated, which is why there is no Add row
        control beside it.
      */}
      {convertMode && rows.length > 0 && (
        <p className="text-xs text-fg-muted">
          The deal keeps its own figures — these become the new quote’s lines. Every line
          crosses; none can be added or removed here.
        </p>
      )}

      {contextNote}
      {runReport}
      {blockedKeys.size > 0 && (
        <ErrorBlock
          title={
            convertMode
              ? /*
                 * ⚠️ **"the conversion cannot run", not "would not be written".**
                 * A refused converted row is not dropped on the floor the way a
                 * blocked draft row is — the 422 lands on `POST
                 * /deals/{id}/convert` itself, so the cost is NO QUOTE AT ALL
                 * rather than a quote missing a line. Wording it like the draft
                 * banner would understate it by a whole document.
                 */
                `${blockedKeys.size} ${
                  blockedKeys.size === 1 ? 'row needs' : 'rows need'
                } attention — the conversion cannot run until ${
                  blockedKeys.size === 1 ? 'it is' : 'they are'
                } fixed.`
              : draftMode
                ? `${blockedKeys.size} ${
                    blockedKeys.size === 1 ? 'row is' : 'rows are'
                  } not ready and would not be written with the quote.`
                : `${blockedKeys.size} ${
                    blockedKeys.size === 1 ? 'row needs' : 'rows need'
                  } attention before anything can be saved.`
          }
        >
          {convertMode && (
            <p>
              Correct {blockedKeys.size === 1 ? 'it' : 'them'}, or Revert{' '}
              {blockedKeys.size === 1 ? 'the row' : 'the rows'} to the deal’s own figures.
            </p>
          )}
          {draftMode && (
            <p>
              Fix or discard {blockedKeys.size === 1 ? 'it' : 'them'} before creating the quote.
            </p>
          )}
        </ErrorBlock>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<LinesIcon />}
          title={
            convertMode
              ? 'This deal has no products'
              : draftMode
                ? 'No line items yet'
                : 'No line items on this quote'
          }
          description={
            convertMode
              ? /*
                 * ⚠️ **A deal with no products converts perfectly well**, so this
                 * is a state rather than a fault — and there is no action to
                 * offer, because a line cannot be added on this path. It says
                 * where lines are added instead, which is the question somebody
                 * reading an empty basket actually has.
                 */
                'The quote will be created with none. Add products on the deal, or on the quote afterwards.'
              : readOnly
                ? 'Nothing has been quoted yet.'
                : draftMode
                  ? 'Add a row to quote a product. Lines are written when the quote is created.'
                  : 'Add a row to quote a product.'
          }
          action={
            canManageRows ? (
              <Button type="button" variant="secondary" onClick={addRow}>
                Add row
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/*
            ── At 320px this is not a table ──────────────────────────────────
            An eight-column grid of text inputs cannot be operated on a phone,
            and scrolling horizontally past a Combobox to reach the price box
            is worse than no grid at all. So the grid is the DESKTOP layout and
            a stacked card per row is the mobile one — same controls, same
            state, same validation, laid out vertically. Above `md` the table
            scrolls inside its own TableWrap, and the whole component is a
            `min-w-0` grid so a wide table cannot widen its parent track.
          */}
          {/*
            ⚠️ **`table-fixed` + a `<colgroup>`, and BOTH halves are load-bearing.
            A `w-[…]` on a `<th>` in an AUTO-layout table is a suggestion the
            cell's own content outranks, and it was being overruled every time.**

            Measured on quote 7 at 1440×900, under `table-layout: auto`, with the
            `w-[…]` values below already in the markup and being ignored:

            | column | asked | GOT | consequence |
            | --- | --- | --- | --- |
            | Item | 190 | **277** | a Combobox holding a long product name |
            | Description | 210 | **106** | textarea 82px wide — and SCROLLING |
            | Tax rate | 145 | **197** | a long trigger label |
            | Qty | 72 | **49** | `2` needed 8px and had 3 — **clipped** |
            | Disc % | 80 | **68** | `0.00` needed 28px and had 22 — **clipped** |
            | Price | 148 | 148 | ✅ the ONE that held |

            Item and Tax rate have large intrinsic minimum widths, so the browser
            gave them what they demanded and reclaimed it from the neighbours
            whose content compresses — the textarea and the small number boxes.
            **That is why three passes of re-cutting `w-[…]` values kept not
            working, and why a fourth would not have worked either.**

            Price held because a previous pass put a `min-w-` on the CELL'S
            CONTENT rather than only a `w-` on the cell. A content minimum raises
            the cell's min-content, which the layout algorithm may not go under;
            a column width is merely a preference. So both mechanisms are used
            here now, deliberately and everywhere:

            1. **`table-fixed` + `<colgroup>`.** Under fixed layout the `<col>`
               widths are authoritative and content cannot renegotiate them. The
               `<colgroup>` is the single place the ten numbers live — the `<th>`
               cells no longer carry widths at all, so there is one list to move
               rather than three that drift.
            2. **A `min-w-` on every cell's content**, equal to its column minus
               `TD`'s `px-3` twice (24px). Belt and braces: it stops a control
               collapsing inside its cell, and it is the thing that demonstrably
               worked.

            ⚠️ **The list is EIGHT now.** Project (165) and Cities (125) left
            the table for the Item cell, and their 290px was spent rather than
            saved — the owner asked for a wider Description, and the three
            controls now stacked in the Item cell need room of their own:

            | column | was | is | why |
            | --- | --- | --- | --- |
            | Item | 190 | **280** | now holds the item, project and city pickers stacked |
            | Description | 236 | **352** | the column the owner asked to widen: a 328px textarea |
            | Qty | 84 | 84 | unchanged — 38px of digits against ~30 for four |
            | Price | 148 | **160** | the placeholder is now a FIGURE, `123,456.00` ≈ 71 |
            | Discount | 94 → 148 | **240** | the mode switch is a two-word segmented control now |
            | Tax rate | 142 | 142 | unchanged |
            | Amount | 122 | **140** | `123,456.00 GBP` ≈ 96, and 98 was two pixels of slack |
            | actions | 94 | 94 | ⚠️ also `PIN_AMOUNT`'s `right-[94px]` |

            280 + 352 + 84 + 160 + 240 + 142 + 140 + 94 = **1492**, so
            `min-w-[1492px]` IS the sum and not a guess. Move it with any column
            you add or widen, and move the matching content `min-w-` too.

            ⚠️ **The total moved from 1400 to 1492 on 2026-08-31 and only the
            Discount column moved (148 → 240, +92).** The mode switch stopped
            being a `%`/`EUR` glyph inside the input and became a segmented
            control reading **Percentage** and **Amount**, under it; two words
            plus a reserved tick do not fit in 124px of content. **The pinning
            geometry is untouched** — `PIN_AMOUNT`'s `right-[94px]` is the
            ACTIONS column's width, not the table's total, so growing the table
            cannot disturb it. The invoice grid moved by the same 92 in the same
            commit, so the two still share every width and both still total
            1492.

            ⚠️ **92 IS A MEASUREMENT AND THE FIRST GUESS WAS 56, WHICH WOULD
            HAVE SHIPPED A CLIPPED CONTROL.** Cut from the type scale the
            segmented control "should" have been ~170 wide; driven in Chrome at
            768 and 1440 it is **193** — `Percentage` 101 + `Amount` 84 + a 2px
            inter-segment gap + `p-0.5` + a 2px border. Both buttons carry a
            reserved 12px tick and `px-2.5`, and the tick is what the arithmetic
            kept forgetting. **Measure this cell; do not derive it.**

            ⚠️ **The last number is written down in a second place.** Amount and
            the actions column are PINNED to the trailing edge — 1400 does not
            fit in any wrapper this page has — and Amount's offset IS the actions
            column's 94. Change that `<col>` and change `PIN_AMOUNT`'s
            `right-[94px]` in the same edit, or the two pinned columns overlap by
            the difference. See the block by `PIN_AMOUNT` for why the answer is
            pinning rather than a fourth pass of narrowing: the saving available
            from trimming is ~128px against a 202px shortfall, so narrowing
            cannot reach it without clipping a value again.

            **The numbers are cut from what each cell must SHOW**, at
            `--text-sm` (13px, tabular-nums), leaving headroom for a six-figure
            price and a four-digit quantity rather than for the two values that
            happen to be in the database today:

            | column | text room | must show |
            | --- | --- | --- |
            | Qty | 84−24−22 = **38** | 4 digits ≈ 30 |
            | Price | 160−24−48 = **88** | `123,456.00` ≈ 71 as a PLACEHOLDER now (`pr-9` reserves 36 for the ⊘ toggle) |
            | Discount | 240−24 = **216** | the SEGMENTED CONTROL, not the figure — **measured at 193** (`Percentage` 101 + `Amount` 84 + gap + `p-0.5` + border), leaving 23px for a font that renders wider than Chrome-on-Windows does. The box above it is the easy half — `123456.00` ≈ 66 with the whole 216 to itself, now that nothing sits in its trailing slot |
            | Amount | 140−24 = **116** | `123,456.00` ≈ 71 + ` GBP` ≈ 25 = **96** |
            | Description | textarea 352−24 = **328** | ~3 lines unscrolled |
            | Item | 280−24 = **256** | three stacked pickers; every trigger label truncates |

            ⚠️ **Amount was 122 and the sum was ~96 against 98.** Two pixels is
            not headroom, it is a coincidence: the earlier pass cut this column
            against the figure alone (~71) and left nothing for the ` GBP` beside
            it. 140 is the same worst case with room to be wrong about the font
            by a few percent.

            ⚠️ *SUPERSEDED — the REASON given for those two pixels, which was a
            claim about another module: "`formatDecimalString` renders thousands
            separators AND the currency code".* **It has never printed a code.**
            `formatDecimalString(value: string | null)` in `@/lib/format` takes
            ONE argument and groups the integer part, nothing else (measured
            2026-09-12). Every code in this grid is concatenated by the call site
            that wants one. **The width conclusion is untouched, because the
            ~25px it reserves is real**: the READ-ONLY Amount cell still renders
            the code inline, beside the figure, which is exactly what the table
            row above measures.

            ⚠️ **The EDITABLE Amount cell stacks the code UNDER the box instead,
            and a stacked code costs HEIGHT, not width — so no column is re-cut
            for it.** An input cannot carry a unit inside it; the reasoning is at
            the block in `EditableAmount`.

            **Item and Tax rate can no longer expand, so their content must
            DEGRADE rather than overflow.** Both are Comboboxes whose trigger
            label is `min-w-0 flex-1 truncate`, so it ellipses; both carry the
            full wording on a `title`, which is also where the
            no-prose-in-a-grid-cell rule already put it.

            Xero's own quote grid scrolls horizontally and so does this one,
            inside its `TableWrap`. The `md:hidden` card below is NOT a table and
            `table-fixed` must never reach it.
          */}
          <div className="hidden min-w-0 md:block">
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table className="min-w-[1492px] table-fixed">
                {/*
                  ⚠️ **The ONE list of widths.** Under `table-fixed` these are
                  authoritative — no `<th>` or `<td>` below carries a `w-` any
                  more, precisely so there is nothing for these to disagree with.
                  Sum = 1492, which is the table's `min-w-`.
                */}
                <colgroup>
                  {/* Item — the item, project and city pickers, stacked. */}
                  <col className="w-[280px]" />
                  <col className="w-[352px]" />
                  <col className="w-[84px]" />
                  <col className="w-[160px]" />
                  {/* Discount — sized by the SEGMENTED CONTROL, not the figure. */}
                  <col className="w-[240px]" />
                  <col className="w-[142px]" />
                  <col className="w-[140px]" />
                  {/* ⚠️ 94 is also `PIN_AMOUNT`'s `right-[94px]`. Move both. */}
                  <col className="w-[94px]" />
                </colgroup>
                <THead>
                  <TR>
                    {/*
                      ⚠️ The header says "Item", singular, and the cell beneath
                      it holds three controls. Naming all three here — "Item,
                      project and cities" — would be a column header describing
                      a form; each control keeps its own accessible label, which
                      is what a screen reader actually announces.
                    */}
                    <TH>Item</TH>
                    <TH>Description</TH>
                    <TH numeric>Qty</TH>
                    <TH numeric>Price</TH>
                    {/* Not "Disc %": the box takes either, and the segmented
                        control in the cell says which it is currently taking. */}
                    <TH numeric>Discount</TH>
                    <TH>Tax rate</TH>
                    {/*
                      Pinned, so the header stays over its own column while the
                      middle of the table scrolls beneath. The frozen literal
                      carries `THead`'s own surface — a header cell that took the body's
                      would read as a hole in the header row.
                    */}
                    {/*
                      ⚠️ **`frozen`, not the `PIN_*` classes, since the header
                      is frozen too (2026-10-18).** Under `TableWrap
                      frozenHeader` every TH gets `sticky top-0` from `TH`
                      itself, and a hand-written `sticky right-…` beside it is a
                      second positioning literal whose z-index and shadow fight
                      the frozen one. `frozen="right"` is TH's own combined
                      top-and-right literal; the offsets are the same 94 and 0
                      the body cells' `PIN_AMOUNT` / `PIN_ACTIONS` use.
                    */}
                    <TH numeric frozen="right" frozenOffset={94}>
                      Amount
                    </TH>
                    <TH frozen="right" frozenOffset={0}>
                      <span className="sr-only">Status and actions</span>
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map((row) => {
                    const controls = rowControls(row, true);
                    const dirty = dirtyKeys.has(row.key);
                    const note = noteControl(row);
                    return (
                      <Fragment key={row.key}>
                        <TR className={cx(row.state === 'failed' && 'bg-danger-soft/40')}>
                          {/*
                            Every content `min-w-` below is its column minus
                            `TD`'s `px-3` twice (24px). The `<colgroup>` fixes
                            the column; this stops the control collapsing inside
                            it. Move the two together or neither.
                          */}
                          <TD className="align-top">
                            {/*
                              ⚠️ **THE STACK the owner asked for**: item, then
                              the project directly beneath it, then the
                              all-cities toggle and the city picker beneath
                              that. One cell, because the three are one decision
                              about what this line sells and where.

                              Each of the two lower blocks renders only when it
                              can be operated — see `showsProjectControl` and
                              `showsCitiesControl`. **Neither gate touches
                              state**, so a hidden block cannot send
                              `cityIds: []` and clear a line's stored cities.
                            */}
                            <div className="grid min-w-[256px] gap-1.5">
                              {controls.product}
                              {controls.showProject && controls.project}
                              {controls.showCities && controls.cities}
                              {/* ⚠️ The other half of the same decision: the
                                  geography a line carries when it has no CITIES
                                  to carry one — either because the product is
                                  not sold through projects at all, or because
                                  its project declares none. Gated by
                                  `showsRegionsControl`, which never touches
                                  state on the way to hiding.

                                  ⚠️ *SUPERSEDED TWICE, and the second correction
                                  is the owner's: "the complement of the project
                                  gate above", then "it now renders BESIDE the
                                  project and city blocks on a row whose project
                                  is not city-specific."* Neither holds. It is
                                  not the complement — the project block STAYS on
                                  such a row, so the project is still editable —
                                  and it does not sit beside the CITY block,
                                  because `showsCitiesControl` now reads the same
                                  `projectHasNoCities` and takes that block away.
                                  City controls out, region controls in, one
                                  value, one render. The two blocks appear
                                  together only on the never-hide-data row that
                                  already holds cities. */}
                              {controls.showRegions && controls.regions}
                              {/* ⚠️ A COUNT, not the chips. The chips were the
                                  widest thing in this cell and the Price box paid
                                  for them — see the width block above. Every
                                  wording is on this line's own `title`, in full,
                                  and spelled out again in the detail panel.

                                  ⚠️ **Deliberately NOT gated on the per-product
                                  rule.** This is a read-back of what the line
                                  holds, not a control: a non-offering line with
                                  inclusions must still show them (the owner chose
                                  uneditable, not invisible), and one with none
                                  renders nothing at all because the summary's own
                                  emptiness test comes first — so there is no
                                  "0 inclusions" for a gate to suppress.

                                  ⚠️ It DOES carry the inclusions field error,
                                  which is the one thing this cell must show on a
                                  set of zero: the editor is inside the collapsed
                                  Details panel, so without this the at-least-one
                                  refusal blocks Save with no reason on the row. */}
                              <InclusionSummary
                                row={row}
                                master={inclusions.rows}
                                variant="count"
                                error={controls.problems.fieldErrors.inclusions}
                              />
                            </div>
                          </TD>
                          {/*
                            Xero's Description, and a real textarea like Xero's
                            — pre-filled from the line's project or item and its
                            cities, and re-derived live until somebody types in
                            it. What is typed IS stored and IS sent to Xero
                            verbatim; see this file's header.

                            ⚠️ A row that has been typed on grows one short
                            muted line under the box, saying the wording no
                            longer follows the cities. It renders only in that
                            state, so the grid is unchanged on every other row —
                            which is the whole reason it is allowed here at all.

                            ⚠️ **352 − 24 = a 328px textarea.** `rows={2}` in the
                            grid holds two 20px lines, and 328px of box is
                            ~300px of text, so the live `Sales - Business School
                            Festivals — Mumbai, Bangalore` fits on two lines with
                            room, and most descriptions on one.
                          */}
                          <TD className="align-top">
                            <div className="min-w-[328px]">{controls.description}</div>
                          </TD>
                          {/* 84 − 24 = 60px of box, less `px-2.5` twice and two
                              borders = 38px of digits, against ~30 for a
                              four-digit quantity. */}
                          <TD className="align-top">
                            <div className="min-w-[60px]">{controls.quantity}</div>
                          </TD>
                          {/*
                            ⚠️ **The column that already held, and the reason the
                            rest of this table now works the same way.** A `w-` on
                            the cell is a preference an auto-layout table
                            overrules; a `min-w-` on the CONTENT raises the cell's
                            min-content, which it may not go under. Price was
                            measured at its asked-for 148 while every neighbour
                            drifted, and this line is why.

                            136 + `px-3` twice = the 160 the column asks for. The
                            box is `w-full`, so it is 136px, of which `pr-9`
                            reserves 36 for the ⊘ toggle and `px-2.5` 10 for the
                            leading gutter — leaving ~88px of digits, comfortably
                            more than the ~71 a six-figure `123,456.00` needs.
                            ⚠️ **It went from 148 to 160 because the placeholder
                            is now a FIGURE** — the catalogue price, with
                            thousands separators — and a placeholder that ellipses
                            is a number nobody can read. Cut any of these and the
                            owner's original defect comes straight back.
                          */}
                          <TD className="align-top">
                            <div className="min-w-[136px]">{controls.price}</div>
                          </TD>
                          {/*
                            ⚠️ **148, not 94, because this box now takes an
                            AMOUNT as well as a percentage.** 124 of content,
                            less `pr-9`'s 36 for the unit toggle and `px-2.5`'s
                            10 — ~76px of digits, against `123456.00` ≈ 66 typed
                            without separators. A percentage needs 40 of that and
                            always did; the amount is what set the width.
                          */}
                          <TD className="align-top">
                            <div className="min-w-[216px]">{controls.discount}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[118px]">{controls.tax}</div>
                          </TD>
                          {/* 140 − 24 = 116px, against `123,456.00` (~71) plus
                              the currency code (~25) = ~96.

                              ⚠️ **THERE IS AN INPUT HERE NOW — 2026-09-12.**
                              *SUPERSEDED: "No input here, so the whole content
                              box is text."* The Amount cell is typed into (see
                              {@link EditableAmount}), so 116px is a BOX plus a
                              muted `incl. tax` line and, transiently, a refusal
                              — all of which wrap rather than overflow, which is
                              what the existing field errors in the Price and
                              Discount cells already do at similar widths.

                              ⚠️ **The column was NOT re-cut for it, deliberately.**
                              Moving it means the `<col>`, this `min-w-` and the
                              `min-w-[1492px]` sum together, plus taking the
                              width off a neighbour — and the file's own rule is
                              that those three move together or not at all. The
                              invoice grid gives the same cell only 98px of
                              content, so the two grids now disagree about a
                              column they used to share; that asymmetry is
                              recorded rather than silently resolved. */}
                          {/*
                            ⚠️ **Pinned. See the block by `PIN_AMOUNT`.** The
                            background is picked by a ternary rather than by two
                            `bg-` utilities on one element: conflicting
                            same-property utilities resolve by stylesheet order,
                            not by the order they are written, so passing both
                            and expecting the second to win is a coin toss. The
                            failed variant is the exact composite of the row's
                            own `bg-danger-soft/40` over `bg-surface`, so a
                            failed row still reads as failed in the two columns
                            that never leave the screen.
                          */}
                          <TD
                            numeric
                            className={cx(
                              'align-top',
                              PIN_AMOUNT,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            <div className="min-w-[116px]">
                              {controls.amount}
                            </div>
                          </TD>
                          <TD
                            className={cx(
                              'align-top',
                              PIN_ACTIONS,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            <div className="grid min-w-[70px] justify-items-end gap-1">
                              <StatusBadge row={row} dirty={dirty} />
                              {/* Said on the row, not only in the confirmation — nothing
                                  refuses the delete of a billed line. */}
                              {row.saved?.invoiced && <Badge tone="info">Billed</Badge>}
                              {rowActions(row)}
                            </div>
                          </TD>
                        </TR>
                        {/*
                          The INTERNAL line note, full width under its row and
                          not pinned, for the reason the two `colSpan` rows
                          below give. Live on locked lines — see `noteControl`.
                        */}
                        {note !== null && (
                          <TR className={cx(row.state === 'failed' && 'bg-danger-soft/40')}>
                            <TD colSpan={columnCount} className="pt-0">
                              {note}
                            </TD>
                          </TR>
                        )}
                        {/*
                          ⚠️ **The two `colSpan` rows are deliberately NOT
                          pinned, and they are the classic place this breaks.**
                          A cell spanning all eight columns cannot be split into a
                          scrolling part and a pinned part, so there is nothing
                          to pin. It is safe because sticky positioning is
                          horizontal here and confined to its own row: these rows
                          sit in their own vertical band, so nothing above them
                          can overlap them however far the table is scrolled.
                          Giving them a pinned cell, or a `z-index` to "fix" a
                          collision, would be repairing something that does not
                          happen — and would break the full-width layout the
                          error block and the detail panel both depend on.
                        */}
                        {/*
                          `text-warning`, not an `ErrorBlock`: nothing here is
                          refused and nothing is wrong. A danger tone beside a
                          real refusal would make the two indistinguishable,
                          which is the whole reason this is a separate field
                          rather than a softened `error`. Rendered BEFORE the
                          error block and never instead of it — a row can carry
                          both.
                        */}
                        {controls.problems.notice !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              <p className="text-xs break-words text-warning">
                                {controls.problems.notice}
                              </p>
                            </TD>
                          </TR>
                        )}
                        {controls.problems.error !== null && (
                          <TR>
                            <TD colSpan={columnCount} className="pt-0">
                              <ErrorBlock title={controls.problems.error} />
                            </TD>
                          </TR>
                        )}
                        {expanded.has(row.key) && (
                          <TR>
                            <TD colSpan={columnCount} className="p-0">
                              {detailPanel(row)}
                            </TD>
                          </TR>
                        )}
                      </Fragment>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          <ul className="grid gap-3 md:hidden">
            {rows.map((row) => {
              const controls = rowControls(row, false);
              const dirty = dirtyKeys.has(row.key);
              return (
                <li
                  key={row.key}
                  className={cx(
                    'rounded-lg border border-border bg-surface',
                    row.state === 'failed' && 'border-danger-border',
                  )}
                >
                  <div className="grid gap-3 px-3 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge row={row} dirty={dirty} />
                        {row.saved?.invoiced && <Badge tone="info">Billed</Badge>}
                      </div>
                      <div className="text-right">
                        <span className="block text-2xs text-fg-muted">
                          Amount
                        </span>
                        {controls.amount}
                      </div>
                    </div>
                    {controls.product}
                    {/*
                      ⚠️ **The same stack as the grid's Item cell**, in the same
                      order — item, project, then the all-cities toggle and the
                      city picker — and gated by the same two predicates. This
                      layout was ALWAYS a stack: what changed above `md` is that
                      the grid now agrees with it, so there is one arrangement
                      to reason about rather than two.

                      `compact` is false here, so both keep their visible labels
                      and their hints; that is the only thing the flag does.
                    */}
                    {controls.showProject && controls.project}
                    {controls.showCities && controls.cities}
                    {/* The same block as the grid's Item cell, behind the same
                        predicate. ⚠️ *SUPERSEDED: "it can now sit BESIDE the
                        project and city controls above rather than only in place
                        of them."* It sits beside the PROJECT control — that one
                        stays on a project declaring no cities — but the CITY
                        control is gone there, because `showsCitiesControl` reads
                        the same `projectHasNoCities`. `compact` is false here,
                        so the control keeps its label, the projectless sentence,
                        and the note that says "All regions" is a snapshot rather
                        than a live rule. */}
                    {controls.showRegions && controls.regions}
                    {/*
                      The chips, because the card is full width — same read-back
                      the grid renders as a count, and ungated for the same
                      reason: inclusions already on a non-offering line still
                      print on the customer's document, and a line with none
                      renders nothing rather than a zero. The per-product rule
                      governs the EDITOR, which lives in the detail panel below.

                      ⚠️ It carries the inclusions field error for the same reason
                      the grid's count does — on a set of zero this is the only
                      thing on the card that can say why Save is refused.
                    */}
                    <InclusionSummary
                      row={row}
                      master={inclusions.rows}
                      error={controls.problems.fieldErrors.inclusions}
                    />
                    {/* The card is full width, so the description keeps its
                        visible label and its hint — `compact` is false here and
                        that is the only thing the flag does. The hint is where
                        the city clause lives in this layout: typing replaces the
                        whole string, cities included, so a description that has
                        been typed on stops following them. The grid has no hint
                        and renders that clause as its own line instead. */}
                    {controls.description}
                    {/* The internal line note — the same control as the grid's
                        full-width row, live on locked lines. */}
                    {noteControl(row)}
                    {/*
                      ⚠️ **Qty and Disc were a two-column pair here and are not
                      any more.** At 320px a half-width box is ~140px, and both
                      Price and Disc now carry a trailing toggle that reserves
                      `max-sm:pr-12` — 48px — leaving ~80px for a figure that can
                      be `123,456.00`. Full width costs three rows of card height
                      and removes the only place in this layout a number could be
                      clipped. The Disc cell's money read-out also needs the
                      width to stay one or two lines rather than five.
                    */}
                    {controls.quantity}
                    {controls.price}
                    {controls.discount}
                    {controls.tax}
                    {controls.problems.notice !== null && (
                      <p className="text-xs break-words text-warning">
                        {controls.problems.notice}
                      </p>
                    )}
                    {controls.problems.error !== null && (
                      <ErrorBlock title={controls.problems.error} />
                    )}
                    {rowActions(row)}
                  </div>
                  {expanded.has(row.key) && detailPanel(row)}
                </li>
              );
            })}
          </ul>

          {/*
            Xero's footer is a label and a number. So is this one.

            ⚠️ **The three paragraphs are gone; the CLAIM each one made is not.**
            The label itself now says which figure this is, in three words, and
            that is the half that was load-bearing — a previewed total reading as
            a total is the number somebody quotes down the phone. What each
            label means, and where the longer version now lives:

            - "Preview total" — draft mode. Our arithmetic, because no line
              exists yet and `lineTotal` is a generated column. A line with no
              price adds nothing rather than zero; that is `sumDecimalStrings`'s
              own null handling and is documented on `draftPreviewTotal`.
            - "Total of saved lines" — saved mode with unsaved rows on screen.
              Unsaved rows are not in it, which is exactly what the label says.
            - "Total" — saved mode, nothing outstanding.

            The quote's own four header totals are the SERVER's and are rewritten
            by it on every line write. This figure has never been them, and the
            sentence saying so was explaining a field that is not on this screen.
          */}
          {/*
            ⚠️ **NO FOOTER TOTAL IN CONVERT MODE, and it is a decision that was
            already taken once on this screen.** The quote's four figures are
            already on the form, in its Totals panel, derived one way; a second
            sum here could disagree with it, and a disagreement between two
            totals on one form is the failure the whole totals note exists to
            prevent. There is also nothing honest to sum: the deal's stored
            `lineTotal` values are the DEAL's total, and the quote's own will be
            recomputed server-side with a tax rate this screen has never been
            told. Do not "finish" this by adding one.
          */}
          {canManageRows && (
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={addRow}
              disabled={saving}
            >
              Add row
            </Button>
          )}
          {!convertMode && (
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-border pt-2">
              <span className="text-xs text-fg-muted">
                {draftMode
                  ? 'Preview total'
                  : dirtyCount > 0
                    ? 'Total of saved lines'
                    : 'Total'}
              </span>
              <span className="tabular-nums text-sm font-semibold text-fg">
                {formatDecimalString(draftMode ? draftPreviewTotal : savedTotal)}
                {(draftMode ? draftPreviewTotal : savedTotal) !== null && currency && (
                  <span className="ml-1 text-xs font-normal text-fg-muted">{currency}</span>
                )}
              </span>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => {
          void confirmDelete();
        }}
        title={
          pendingDelete?.saved
            ? `Delete the ${pendingDelete.saved.product.productName} line?`
            : 'Delete this line item?'
        }
        tone="danger"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
        confirmLabel="Delete line"
        description={
          pendingDelete?.saved ? (
            <>
              <p>
                <strong>{pendingDelete.saved.product.productName}</strong>
                {pendingDelete.saved.project
                  ? ` under ${pendingDelete.saved.project.name}`
                  : ''}{' '}
                will be removed from this quote.
              </p>
              {/*
                EVERY city, named. The confirmation is the last chance to notice
                the wrong line is selected, and on a document where one product
                can appear twice under two projects the geography is often the
                only thing telling them apart. `cities.length === 0` is the whole
                test — the key is always present.

                ⚠️ Named as what it IS. On a flagged line these are the project's
                cities resolved at read time rather than a set somebody chose, so
                presenting them as chosen would misdescribe the line at the last
                moment anyone can check it.
              */}
              {pendingDelete.saved.cities.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-fg-muted">
                    {pendingDelete.saved.allCities
                      ? 'It runs in every city its project declares — today'
                      : 'It runs in'}{' '}
                    {pendingDelete.saved.cities.length}{' '}
                    {pendingDelete.saved.cities.length === 1 ? 'city' : 'cities'}:
                  </p>
                  <div className="mt-1">
                    <CityChips cities={pendingDelete.saved.cities} />
                  </div>
                </div>
              )}
              {pendingDelete.saved.inclusions.length > 0 && (
                <p className="mt-2">
                  Its {pendingDelete.saved.inclusions.length}{' '}
                  {pendingDelete.saved.inclusions.length === 1
                    ? 'campaign inclusion goes'
                    : 'campaign inclusions go'}{' '}
                  with it.
                </p>
              )}
              {/*
                ⚠️ Being billed does NOT block the delete. The backend has no
                guard at all — `f_invoice_products.quote_product_id` is
                ON DELETE SET NULL, there is no 409, no warning and no count —
                so warning here is the only protection there is. `invoiced` is
                per LINE under staged billing, never per quote.
              */}
              {pendingDelete.saved.invoiced && (
                <p className="mt-2">
                  <strong>This line has already been billed</strong>
                  {pendingDelete.saved.invoicedOn
                    ? ` on ${pendingDelete.saved.invoicedOn.subject}`
                    : ''}
                  . The invoice keeps its money but loses its link back to this line, for good.
                </p>
              )}
              <p className="mt-2">
                Header totals are recalculated; removing the last line takes them to 0.00.
              </p>
            </>
          ) : null
        }
      />
    </div>
  );
}
